// Dodanie języka (np. niemieckiego): dopisz kod do `locales`, dołóż słownik poniżej.
// Reszta aplikacji podchwyci go sama — teksty nigdy nie są wpisywane wprost w ekranach.
export const locales = ['pl', 'en', 'uk', 'ru'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'pl';

// Języki pokazujemy ich własnymi nazwami — nigdy flagami.
export const localeNames: Record<Locale, string> = {
  pl: 'Polski',
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
};

const pl = {
  documentNotifications: {
    enable: 'Włącz dźwięk dokumentów',
    mute: 'Wycisz dźwięk dokumentów',
    blocked: 'Dźwięk wymaga włączenia w tej przeglądarce.',
    unsupported: 'Ta przeglądarka nie obsługuje dźwięku powiadomień.',
    ready: 'Dźwięk nowych dokumentów włączony',
    muted: 'Dźwięk dokumentów wyciszony',
  },
  appName: 'Firmeto',
  language: 'Język',
  signIn: 'Zaloguj się',
  signOut: 'Wyloguj',
  signingIn: 'Logowanie…',
  loading: 'Wczytuję…',

  /** Nazwy grup w bocznej nawigacji — czyta się je jak drogę dokumentu przez firmę. */
  shell: {
    navigation: 'Nawigacja firmy',
    sales: 'Sprzedaż',
    purchases: 'Zakupy',
    books: 'Księgi',
    settlements: 'Rozliczenia',
    setup: 'Ustawienia',
    density: 'Gęstość',
    comfortable: 'Komfortowa',
    dense: 'Gęsta',
    back: 'Wróć do listy firm',
  },
  start: {
    kicker: 'Zacznij',
    claim: 'Nie zarabiasz — nie płacisz.',
    lead: 'Księgowość online dla małych firm i biur rachunkowych w Polsce. Faktury, podatki, ZUS i terminy w jednym miejscu.',
    promises: (trialMonths: number, capPercent: number) => [
      'W miesiącu bez przychodu opłata wynosi 0 zł',
      `Opłata nigdy nie przekracza ${capPercent}% przychodu`,
      trialMonths === 1
        ? 'Pierwszy miesiąc bezpłatnie, bez umowy na czas określony'
        : `Pierwsze ${trialMonths} miesiące bezpłatnie, bez umowy na czas określony`,
    ],
    euServers: 'Serwery w Unii Europejskiej',
    whoAreYou: 'Kim jesteś?',
    whoAreYouLead: 'Od tego zależy, co zobaczysz po zalogowaniu. Wybór można zmienić później.',
    paths: {
      company: {
        title: 'Prowadzę firmę',
        lead: 'Jednoosobowa działalność albo spółka. Faktury, koszty, podatki i terminy.',
      },
      office: {
        title: 'Prowadzę biuro rachunkowe',
        lead: 'Panel z firmami klientów, kroki miesiąca i przydział księgowych.',
      },
      founding: {
        title: 'Zakładam firmę',
        lead: 'Pomożemy wybrać formę opodatkowania i przeprowadzimy przez pierwsze rozliczenie.',
      },
    },
    createAccount: 'Załóż konto',
    haveAccount: 'Mam już konto — zaloguj się',
    invitationHint: 'Masz zaproszenie od biura rachunkowego? Otwórz link z wiadomości.',
  },
  register: {
    claim: 'Trzy kroki do pierwszej faktury.',
    stepNames: ['Konto', 'Firma', 'Rozliczenia'],
    euData: 'Dane pozostają w Unii Europejskiej',
    backToStart: 'Wróć na początek',
    signIn: 'Zaloguj się',
    signInToContinue: 'Firmę zakłada się już jako zalogowany. Zaloguj się hasłem, które przed chwilą ustawiłeś.',
    codeSent: 'Wysłaliśmy na Twój adres sześciocyfrowy kod. Jest ważny 24 godziny.',
    codeResent: 'Kod wysłany ponownie.',
    account: {
      title: 'Załóż konto',
      lead: 'Zajmie mniej niż minutę. Danych firmy jeszcze nie potrzebujemy.',
      firstName: 'Imię',
      lastName: 'Nazwisko',
      email: 'Adres e-mail',
      emailHint: 'Na ten adres wyślemy potwierdzenie i przypomnienia o terminach.',
      password: 'Hasło',
      passwordHint: 'Minimum 12 znaków.',
      passwordRules: {
        length: 'Co najmniej 12 znaków',
        notEmail: 'Inne niż Twój adres e-mail',
        maxLength: 'Najwyżej 128 znaków',
      },
      showPassword: 'Pokaż',
      hidePassword: 'Ukryj',
      generate: 'Wygeneruj',
      generated: 'Hasło wpisane w oba pola i skopiowane do schowka. Zapisz je w menedżerze haseł.',
      repeatPassword: 'Powtórz hasło',
      passwordsDiffer: 'Hasła nie są takie same.',
      uiLocale: 'Język interfejsu',
      uiLocaleHint: 'Język zapisujemy przy Twoim koncie, nie przy firmie. Zmienisz go wyżej.',
      terms: 'Akceptuję regulamin i politykę prywatności. Dane księgowe przetwarzamy wyłącznie w Unii Europejskiej.',
      lawUpdates: 'Chcę dostawać wiadomości o zmianach w przepisach dotyczących mojej formy opodatkowania.',
      next: 'Dalej — potwierdzenie adresu',
    },
    code: {
      title: 'Potwierdź adres',
      lead: (email: string) =>
        `Wpisz kod, który wysłaliśmy na ${email}. Bez potwierdzonego adresu nie założysz firmy.`,
      label: 'Kod z wiadomości',
      hint: 'Kod jest ważny 24 godziny.',
      confirm: 'Potwierdź i zaloguj',
      resend: 'Wyślij kod jeszcze raz',
    },
    company: {
      title: 'Dane firmy',
      lead: 'Numer NIP wyznacza firmę w rejestrach i na fakturach — resztę można poprawić później.',
      nip: 'NIP',
      nipLength: (digits: number) => `Numer NIP ma 10 cyfr. Wpisano ${digits}.`,
      noRegistryTitle: 'Dane wpisujesz sam',
      noRegistryLead:
        'Pobierania danych z GUS jeszcze nie podłączyliśmy. Do czasu, aż zacznie działać, wpisz nazwę i adres ręcznie — sprawdzimy je później w rejestrze.',
      legalForm: 'Forma prawna',
      jdg: 'Jednoosobowa działalność',
      spZoo: 'Spółka z o.o.',
      name: 'Nazwa firmy',
      nameHint: 'Tak, jak ma wyglądać na fakturze.',
      address: 'Ulica i numer',
      postalCode: 'Kod pocztowy',
      city: 'Miejscowość',
      startedOn: 'Data rozpoczęcia działalności',
      startedOnHint: 'Od niej liczy się ulga na start i limit zwolnienia z VAT w pierwszym roku.',
      next: 'Dalej — rozliczenia',
    },
    settlements: {
      title: 'Jak rozliczasz firmę?',
      lead: 'Nie wiesz? Wybierz „Nie jestem pewien” — ustalimy to przed pierwszym rozliczeniem, a wszystko inne będzie działać.',
      perMonth: (net?: string) => (net ? `${net} zł netto / mies.` : '—'),
      unsurePrice: 'ustalimy razem',
      plans: {
        RYCZALT: { title: 'Ryczałt', lead: 'Ewidencja przychodów, stawka ryczałtu, bez kosztów.' },
        KPIR: { title: 'KPiR', lead: 'Księga przychodów i rozchodów, koszty z odliczeniem VAT.' },
        FULL: { title: 'Pełna księgowość', lead: 'Dziennik, plan kont, dekretacja.' },
        UNSURE: { title: 'Nie jestem pewien', lead: 'Firma powstanie od razu, ustawienia podatkowe uzupełnimy później.' },
      },
      incomeTax: 'Podatek dochodowy',
      skala: 'Według skali — 12% i 32%',
      liniowy: 'Liniowy — 19%',
      cit: 'CIT — spółka',
      vat: 'VAT',
      vatActive: 'Czynny podatnik VAT',
      vatExemptLimit: 'Zwolnienie do limitu (art. 113)',
      vatExemptKind: 'Zwolnienie przedmiotowe (art. 43)',
      vatPeriod: 'Okres rozliczenia VAT',
      monthly: 'Miesięczny — JPK_V7M',
      quarterly: 'Kwartalny — JPK_V7K',
      zus: 'Składki ZUS',
      zusSchemes: {
        ULGA_NA_START: 'Ulga na start — pierwsze 6 miesięcy',
        PREFERENCYJNY: 'Preferencyjny — 24 miesiące',
        MALY_ZUS_PLUS: 'Mały ZUS Plus',
        PELNY: 'Pełne składki',
        BRAK: 'Nie opłacam składek z tej działalności',
      },
      from: 'Od kiedy rozliczasz w Firmeto',
      fromHint: 'Wcześniejsze miesiące można dodać później — ustawienia mają datę obowiązywania.',
      feeRule: (trialMonths: number, capPercent: number) =>
        `Rozumiem zasadę opłaty: 0 zł w miesiącu bez przychodu, nigdy więcej niż ${capPercent}% przychodu. ` +
        (trialMonths === 1 ? 'Pierwszy miesiąc bezpłatnie.' : `Pierwsze ${trialMonths} miesiące bezpłatnie.`),
      finish: 'Załóż firmę i zacznij',
      unsureTitle: 'Ustawienia podatkowe uzupełnisz później',
      unsureLead:
        'Firma powstanie od razu i będziesz mógł wpisywać dokumenty. Podatku nie policzymy, dopóki nie będzie wiadomo, jak się rozliczasz — pulpit będzie o tym przypominał.',
    },
    done: {
      title: 'Firma gotowa',
      lead: 'Możesz zaczynać. Opłata naliczy się dopiero w miesiącu z przychodem.',
      next: [
        { title: 'Dodaj rachunek bankowy', lead: 'Numer wchodzi na faktury i pozwala dopasować wpłaty' },
        { title: 'Wystaw pierwszą fakturę', lead: 'Albo dopisz koszt, który już masz' },
        { title: 'Zaproś księgową', lead: 'Dostęp z rolą Księgowa, bez wglądu w rozliczenia z Firmeto' },
      ],
      enter: 'Przejdź do firmy',
    },
  },
  emailUndeliverable:
    'Wiadomości na Twój adres wracają do nas. Sprawdź go — bez działającego adresu nie zmienisz hasła ani nie odbierzesz powiadomień.',
  companies: {
    settlement: 'Rozliczenie',
    title: 'Moje firmy',
    subtitle: 'Firmy, do których masz dostęp. Wybierz jedną, żeby wejść w jej dokumenty i księgi.',
    addHint: 'Wpisz NIP, resztę pobierzemy z rejestru',
    bookkeeping: 'Kto będzie prowadzić księgi?',
    bookkeepingChoices: { SELF: 'Samodzielnie', ACCOUNTANT: 'Moja księgowa', OFFICE: 'Biuro rachunkowe' },
    bookkeepingSelfFull: 'Spółka prowadzi pełne księgi, a te prowadzi wyłącznie rola Księgowa. '
      + 'Nadasz ją sobie na następnym ekranie — księgi własnej firmy wolno prowadzić bez certyfikatu.',
    bookkeepingSelfSimple: 'Księgę przychodów i rozchodów oraz ryczałt prowadzisz sam, bez dodatkowych ról.',
    bookkeepingAccountantHint: 'Zaprosisz ją na następnym ekranie — dostanie rolę Księgowa i dostęp do ksiąg.',
    bookkeepingOfficeHint: 'Przekaż biuru NIP firmy. Biuro doda ją do swoich klientów, '
      + 'a Ty zatwierdzisz propozycję obsługi w Osobach.',
    empty: 'Nie masz jeszcze żadnej firmy. Dodaj pierwszą, żeby zacząć.',
    add: 'Dodaj firmę',
    nip: 'NIP',
    fetch: 'Pobierz z rejestru',
    fetching: 'Szukam…',
    fetched: (source: string) => `Dane wypełnione z rejestru: ${source}. Sprawdź je przed zapisaniem.`,
    fetchedWithVat: (source: string, vat: string) =>
      `Dane wypełnione z rejestru: ${source}. Status VAT: ${vat}. Sprawdź je przed zapisaniem.`,
    name: 'Nazwa',
    legalForm: 'Forma prawna',
    city: 'Miejscowość',
    businessStartedOn: 'Data rozpoczęcia działalności',
    businessStartedOnHint: 'Potrzebna do limitu zwolnienia z VAT w pierwszym roku.',
    addressLine: 'Ulica i numer',
    postalCode: 'Kod pocztowy',
    save: 'Zapisz firmę',
    saving: 'Zapisuję…',
    cancel: 'Anuluj',
    roles: 'Twoja rola',
    jdg: 'Jednoosobowa działalność',
    spzoo: 'Spółka z o.o.',
    forms: {
      JDG: 'Jednoosobowa działalność',
      SP_CYWILNA: 'Spółka cywilna',
      SP_JAWNA: 'Spółka jawna',
      SP_PARTNERSKA: 'Spółka partnerska',
      SP_KOMANDYTOWA: 'Spółka komandytowa',
      SP_KOMANDYTOWO_AKCYJNA: 'Spółka komandytowo-akcyjna',
      SP_ZOO: 'Spółka z o.o.',
      PROSTA_SA: 'Prosta spółka akcyjna',
      SA: 'Spółka akcyjna',
      SPOLDZIELNIA: 'Spółdzielnia',
      STOWARZYSZENIE: 'Stowarzyszenie',
      FUNDACJA: 'Fundacja',
    } as Record<string, string>,
  },

  settings: {
    partners: {
      title: 'Wspólnicy',
      lead: 'W spółce cywilnej, jawnej i partnerskiej podatek płacą wspólnicy — każdy od swojego udziału '
        + 'w zysku i każdy według własnej formy opodatkowania. Bez tej kartoteki liczymy jedną zaliczkę '
        + 'tak, jakby cały dochód zarobiła jedna osoba.',
      firstName: 'Imię',
      lastName: 'Nazwisko',
      share: 'Udział w zysku',
      shareHint: 'Ułamek albo procent: 0,5 albo 50%. Udziały wszystkich wspólników powinny dać całość.',
      taxation: 'Forma opodatkowania',
      zus: 'Schemat ZUS',
      from: 'Od',
      to: 'Do',
      add: 'Dodaj wspólnika',
      end: 'Zakończ udział',
      endPrompt: 'Ostatni dzień udziału w spółce:',
      none: 'Kartoteka wspólników jest pusta — zaliczkę liczymy wtedy jak dla jednej osoby.',
      sum: 'Suma udziałów',
    },
    lead: 'Sposób rozliczenia obowiązuje od wskazanej daty. Zmiana formy w trakcie roku to nowy wpis, nie nadpisanie poprzedniego.',
    periods: 'Okresy obowiązywania',
    to: 'Obowiązuje do',
    openEnded: 'bezterminowo',
    inForce: 'Obowiązuje',
    future: 'Przyszły',
    yearNotCovered: (od: string, doKiedy: string) =>
      `Rok obrotowy ${od} – ${doKiedy} nie jest w całości objęty ustawieniami`,
    yearNotCoveredHint: (odKiedy: string, poczatek: string) =>
      `Ustawienia obowiązują od ${odKiedy}, a rok obrotowy zaczyna się ${poczatek}. Dla miesięcy `
      + 'sprzed tej daty nie wiemy, jak firma się rozlicza — plan kont i podatki za tę część roku '
      + 'nie mają się o co oprzeć. Jeśli firma rozliczała się tak samo od początku roku, cofnij datę.',
    coverFromYearStart: (poczatek: string) => `Popraw na ${poczatek}`,
    edit: 'Popraw',
    editWarning: 'Poprawiasz istniejący wpis',
    editWarningHint: 'Zmiana decyzji to nowy wpis, a nie poprawka — historia musi zostać, żeby '
      + 'przeliczenie starego miesiąca dało ten sam wynik. Popraw wpis tylko wtedy, gdy się '
      + 'pomyliłeś przy wpisywaniu. Miesiąca już zamkniętego zmiana nie ruszy.',
    past: 'Historyczny',
    title: 'Ustawienia podatkowe',
    open: 'Ustawienia',
    close: 'Zwiń',
    none: 'Brak ustawień. Uzupełnij je, zanim zaczniesz księgować.',
    from: 'Obowiązuje od',
    taxationForm: 'Forma opodatkowania',
    booksType: 'Księgi',
    vatStatus: 'VAT',
    vatExemptionBasis: 'Podstawa zwolnienia',
    zusScheme: 'ZUS właściciela',
    cashMethod: 'Kasowy PIT',
    smallTaxpayer: 'Mały podatnik',
    incomeTaxPeriod: 'Zaliczki na podatek dochodowy',
    incomeTaxPeriodHint:
      'Kwartalne przysługują małemu podatnikowi i podatnikowi rozpoczynającemu działalność (art. 25 ust. 1b ustawy o CIT, art. 44 ust. 3g ustawy o PIT). Warunku nie sprawdzamy — zależy od przychodu poprzedniego roku. Okres VAT-u ustawia się osobno.',
    advancePeriods: { MONTHLY: 'Miesięczne', QUARTERLY: 'Kwartalne' },
    exportData: 'Pobierz wszystkie dane',
    exportHint: 'Wszystko, co mamy o tej firmie, możesz zabrać w jednym pliku.',
    sicknessInsurance: 'Dobrowolne ubezpieczenie chorobowe',
    add: 'Dodaj ustawienia',
    save: 'Zapisz ustawienia',
    history: 'Historia zmian',
    monthHint: 'Ustawienia obowiązują od pierwszego dnia miesiąca; wcześniejsze okresy zostają bez zmian.',
    forms: { ryczalt: 'Ryczałt', skala: 'Skala podatkowa', liniowy: 'Podatek liniowy', cit: 'CIT' },
    books: { ryczalt: 'Ewidencja przychodów', kpir: 'KPiR', full: 'Pełne księgi' },
    vat: { active_monthly: 'Czynny, miesięcznie', active_quarterly: 'Czynny, kwartalnie', exempt: 'Zwolniony' },
    zus: {
      ulga_na_start: 'Ulga na start',
      preferencyjny: 'Preferencyjny',
      maly_zus_plus: 'Mały ZUS Plus',
      pelny: 'Pełny',
      brak: 'Bez ZUS',
    },
  },
  access: {
    servedBy: 'Obsługa przez biuro rachunkowe',
    officeAccountants: 'Dostęp do ksiąg',
    nobodyYet: 'nikt jeszcze nie został przydzielony',
    endOffice: 'Zakończ współpracę',
    endOfficeHint: 'Dostęp biura znika tego samego dnia. Księgi zostają w firmie.',
    confirmEndOffice: (office: string) =>
      `Zakończyć współpracę z biurem ${office}? Jego księgowe stracą dostęp do ksiąg tej firmy.`,
    officeProposals: 'Propozycje obsługi',
    noBookkeeper: 'Nikt nie prowadzi ksiąg tej firmy',
    ownDocumentsOnly: 'Widzi tylko dokumenty, które sam wprowadzi',
    ownDocumentsOnlyShort: 'tylko własne',
    ownDocumentsOnlyHint: 'Przy kilku handlowcach każdy widziałby prowizje, marże i kontrahentów pozostałych. '
      + 'Zawężenie zostawia mu wyłącznie faktury, koszty i pliki, które sam dodał.',
    selfAccountant: 'Rolę Księgowa możesz wziąć także dla siebie',
    selfAccountantHint: 'Księgi własnej firmy wolno prowadzić bez certyfikatu. Odpowiedzialność za nie '
      + 'zostaje jednak po stronie kierownika jednostki, a rola Księgowa wymaga drugiego składnika logowania.',
    takeAccountantRole: 'Nadaj mi rolę Księgowa',
    officeProposalsHint:
      'Biuro rachunkowe chce prowadzić księgi tej firmy. Do czasu Twojej zgody nie widzi żadnych danych i nie może przydzielić księgowej.',
    office: 'Biuro',
    whoPays: 'Kto płaci za Firmeto',
    payerOffice: 'Biuro',
    payerCompany: 'Firma',
    acceptProposal: 'Przyjmij',
    rejectProposal: 'Odrzuć',
    lead: 'Dostęp do tej firmy. Każde nadanie i odebranie dostępu zapisujemy w historii zmian.',
    person: 'Osoba',
    whatEachRoleSees: 'Co widzi każda rola',
    permission: 'Uprawnienie',
    permissions: {
      documents: 'Faktury i koszty',
      books: 'Księga i JPK',
      settings: 'Ustawienia podatkowe',
      people: 'Osoby i dostęp',
      billing: 'Opłaty za Firmeto',
    },
    levels: { full: 'pełne', add: 'dodawanie', view: 'podgląd' },
    permissionsNote:
      '„—" oznacza, że pozycja w ogóle nie pojawia się w nawigacji tej osoby. Nie pokazujemy wyszarzonych przycisków, których nie da się kliknąć.',
    title: 'Osoby i zaproszenia',
    open: 'Osoby',
    people: 'Mają dostęp',
    invitations: 'Zaproszenia',
    invite: 'Zaproś osobę',
    send: 'Wyślij zaproszenie',
    email: 'Adres e-mail',
    role: 'Rola',
    remove: 'Odbierz dostęp',
    revoke: 'Odwołaj',
    expires: 'Ważne do',
    noInvitations: 'Brak zaproszeń.',
    hint: 'Zaproszenie jest ważne 7 dni i przyjmie je tylko osoba zalogowana na wskazany adres.',
    statuses: { pending: 'Oczekuje', accepted: 'Przyjęte', revoked: 'Odwołane', expired: 'Wygasło' },
    roles: {
      OWNER: 'Właściciel',
      ADMIN: 'Administrator',
      ACCOUNTANT: 'Księgowy',
      EMPLOYEE: 'Pracownik',
      BOARD_MEMBER: 'Członek zarządu',
      VIEWER: 'Podgląd',
    },
    /*
     * Nazwy ról w rodzaju żeńskim. Wybieramy je, gdy z imienia i nazwiska wynika, że chodzi
     * o kobietę — „Księgowa" przy Sławomirze Marcinkowskim czytało się jak pomyłka w danych.
     * „Podgląd" nie jest nazwą osoby, więc zostaje bez odmiany.
     */
    rolesFemale: {
      OWNER: 'Właścicielka',
      ADMIN: 'Administratorka',
      ACCOUNTANT: 'Księgowa',
      EMPLOYEE: 'Pracownica',
      BOARD_MEMBER: 'Członkini zarządu',
      VIEWER: 'Podgląd',
    },
  },
  counterparties: {
    lead: 'Kartoteka używana przy wystawianiu faktur i wprowadzaniu kosztów.',
    dialogLead: 'Kontrahent zapisze się od razu i wskoczy do dokumentu — szkic zostaje, gdzie był.',
    active: 'Aktywny',
    typeHint: 'Rodzaj decyduje o stawkach dostępnych na fakturze.',
    nipHint: 'Sprawdzimy sumę kontrolną.',
    ratesTitle: 'Rodzaj a stawki VAT',
    ratesHint: 'Zestawienie wynika ze stawek dostępnych na fakturze w tej aplikacji.',
    open: 'Kontrahenci',
    title: 'Kartoteka kontrahentów',
    search: 'Szukaj po nazwie lub numerze',
    add: 'Dodaj kontrahenta',
    none: 'Kartoteka jest pusta.',
    showInactive: 'Pokaż nieaktywnych',
    name: 'Nazwa',
    nip: 'NIP',
    vatEuId: 'Numer VAT UE',
    country: 'Kraj',
    city: 'Miejscowość',
    type: 'Rodzaj',
    deactivate: 'Oznacz jako nieaktywnego',
    inactive: 'nieaktywny',
    save: 'Zapisz kontrahenta',
    types: {
      DOMESTIC: 'Firma krajowa',
      EU: 'Firma z Unii',
      NON_EU: 'Firma spoza Unii',
      PERSON: 'Osoba prywatna',
    },
  },
  products: {
    lead: 'Pozycje podpowiadane przy wystawianiu faktury.',
    showInactive: 'Pokaż nieaktywne',
    grossPrice: 'Cena brutto',
    grossPreview: 'Brutto',
    retire: 'Wycofaj',
    open: 'Towary i usługi',
    title: 'Kartoteka towarów i usług',
    search: 'Szukaj po nazwie lub indeksie',
    add: 'Dodaj pozycję',
    edit: 'Edytuj',
    none: 'Kartoteka jest pusta.',
    code: 'Indeks',
    namePl: 'Nazwa (polski)',
    nameEn: 'Nazwa (angielski)',
    unit: 'Jednostka',
    vatRate: 'Stawka VAT',
    gtu: 'Kod GTU',
    price: 'Cena',
    save: 'Zapisz pozycję',
    rates: { '23': '23%', '8': '8%', '5': '5%', '0': '0%', zw: 'zwolniona', np: 'nie podlega', oo: 'odwrotne obciążenie' },
  },
  wizard: {
    open: 'Kreator',
    preview: 'Podgląd dokumentu',
    previewOpen: 'Otwórz oryginał',
    previewUnsupported: 'Tego pliku nie pokażemy w podglądzie — otwórz oryginał w nowej karcie.',
    title: 'Kreator księgowania',
    documentNumber: 'Numer dokumentu',
    issueDate: 'Data dokumentu',
    category: 'Rodzaj wydatku',
    vatRate: 'Stawka VAT',
    net: 'Netto',
    vat: 'VAT',
    gross: 'Brutto',
    stepOf: (step: number, total: number) => `Krok ${step} z ${total}`,
    steps: { kind: 'Co to za dokument', counterparty: 'Od kogo', document: 'Dokument i kwoty', payment: 'Płatność', summary: 'Podsumowanie', statement: 'Wyciąg' } as Record<string, string>,
    kinds: { COST_INVOICE: 'Faktura kosztowa', RECEIPT: 'Paragon', BANK_STATEMENT: 'Wyciąg bankowy' } as Record<string, string>,
    kindHints: {
      COST_INVOICE: 'Faktura od dostawcy z NIP-em — VAT do odliczenia, koszt do księgi.',
      RECEIPT: 'Paragon bez faktury — koszt bez odliczenia VAT.',
      BANK_STATEMENT: 'Plik z banku (MT940) — wpłaty i wypłaty do dopasowania.',
    } as Record<string, string>,
    counterpartyHint: 'Wybierz dostawcę z listy albo dodaj nowego po NIP-ie — dane dociągniemy z rejestru.',
    supplier: 'Dostawca',
    newSupplier: 'Nowy dostawca',
    dueDate: 'Termin płatności',
    deduction: 'Odliczenie VAT',
    deductions: { FULL: 'w całości', HALF: 'połowa (pojazd)', NONE: 'bez odliczenia' } as Record<string, string>,
    paid: 'Dokument jest już zapłacony',
    paidOn: 'Data zapłaty',
    method: 'Sposób',
    methods: { transfer: 'przelew', cash: 'gotówka', card: 'karta' },
    unpaidHint: 'Zostanie jako zobowiązanie do zapłaty — z terminem, jeśli go podałeś.',
    summaryLead: 'Tak to zapiszemy. Nic jeszcze nie powstało — sprawdź i zatwierdź.',
    costAmount: 'W koszty',
    deductibleVat: 'VAT do odliczenia',
    paidShort: 'zapłacony',
    unpaidShort: 'niezapłacony — czeka w rozrachunkach',
    summaryFull: 'Przy pełnych księgach dekret trafi na listę „do zaksięgowania” w dzienniku — nic nie księguje się bez spojrzenia.',
    summarySimple: 'Koszt trafi do księgi za miesiąc z daty dokumentu.',
    statementLead: 'Wczytamy wyciąg i pokażemy, które wpłaty pasują do których dokumentów. Dopasowania zatwierdza człowiek.',
    importStatement: 'Wczytaj wyciąg',
    back: 'Wstecz',
    next: 'Dalej',
    finish: 'Zatwierdź i zapisz',
    later: 'Dokończę później',
    close: 'Zamknij',
    doneFull: 'Dokument kosztowy zapisany. Dekret czeka na zaksięgowanie w dzienniku.',
    doneSimple: 'Dokument kosztowy zapisany i wprowadzony do księgi.',
    doneStatement: 'Wyciąg wczytany. Dopasuj wpłaty do dokumentów.',
    goJournal: 'Przejdź do dziennika',
    goCosts: 'Przejdź do kosztów',
    goMatch: 'Przejdź do dopasowań',
  },
  documents: {
    noneHint: 'Wgrane pliki pojawią się tutaj z datą, rozmiarem i informacją, czy zostały już wprowadzone jako dokument.',
    scanFutureText:
      'Docelowo wgrany plik trafia do kolejki „Czeka na wprowadzenie", a z jego treści podpowiadamy dostawcę, numer, daty i kwoty przy dodawaniu kosztu. Dziś plik jest wyłącznie załącznikiem.',
    open: 'Dokumenty',
    title: 'Archiwum dokumentów',
    lead: 'Zdjęcia i PDF-y trzymamy w postaci, w jakiej przyszły. Przy kontroli liczy się oryginał.',
    drop: 'Przeciągnij tu zdjęcia albo pliki PDF',
    choose: 'Wybierz pliki',
    accepted: 'JPEG, PNG, HEIC albo PDF, do 25 MB.',
    none: 'Archiwum jest puste.',
    fileName: 'Plik',
    added: 'Dodany',
    size: 'Rozmiar',
    state: 'Stan',
    waiting: 'Czeka na wprowadzenie',
    posted: 'Wprowadzony do kosztów',
    delete: 'Usuń',
    uploaded: (added: number) => `Dodane do archiwum: ${added}.`,
    uploadedWithKnown: (added: number, known: number) =>
      `Dodane do archiwum: ${added}. Już tam były: ${known} — nie robimy drugiej kopii.`,
    confirmDelete: (fileName: string) => `Usunąć ${fileName} z archiwum? Tej czynności nie da się cofnąć.`,
    inboxTitle: 'Skrzynka dokumentów',
    inboxLead: 'Wszystko, co przyszło — z przeglądarki, z telefonu i z poczty — czeka tu, aż ktoś je wprowadzi albo odrzuci z powodem. Oryginał zostaje w archiwum.',
    statuses: { NEW: 'nowy', IN_PROGRESS: 'w toku', BOOKED: 'zaksięgowany', REJECTED: 'odrzucony' } as Record<string, string>,
    tag: 'Etykieta',
    source: 'Skąd',
    sourceUpload: 'wgrany',
    sourceEmail: 'e-mail',
    accept: 'Oznacz jako w toku',
    reject: 'Odrzuć',
    rejectPrompt: 'Powód odrzucenia — właściciel ma wiedzieć, co poprawić:',
    rejected: 'Odrzucony',
    intakeTitle: 'Dokumenty pocztą',
    mailboxConfig: 'Konfiguracja skrzynki',
    intakeLead: 'Wyślij wiadomość z załącznikami na adres firmy, a pliki trafią do skrzynki. Adresów może być kilka — każdy z własną etykietą. Przyjmujemy tylko od osób z dostępem do firmy i zatwierdzonych nadawców; obce wiadomości odrzucamy bez odpowiedzi.',
    intakeAddress: 'Adres',
    intakeTag: 'Etykieta załączników',
    intakeTagHint: 'np. faktury, wyciągi, paragony',
    intakeCreate: 'Wygeneruj adres',
    intakeRevoke: 'Unieważnij',
    intakeRevoked: 'unieważniony',
    intakeRevokeConfirm: (address: string) => `Unieważnić ${address}? Od tej chwili wiadomości na ten adres nie będą przyjmowane. Możesz wygenerować nowy.`,
    intakeNone: 'Nie ma jeszcze żadnego adresu.',
    intakeCopied: 'Skopiowano.',
    sendersTitle: 'Zatwierdzeni nadawcy',
    sendersLead: 'Osoby z dostępem do firmy są zatwierdzone automatycznie. Tu dopisz inne adresy, np. skrzynkę biura albo skanera.',
    senderEmail: 'Adres e-mail',
    senderAdd: 'Dopisz',
    senderRevoke: 'Usuń',
    sendersNone: 'Tylko osoby z dostępem do firmy.',
  },
  annual: {
    cit: {
      title: 'Podatek dochodowy za rok (CIT-8)',
      hint: 'Liczymy z tej samej podstawy, co nota podatkowa w sprawozdaniu: wynik z ksiąg, minus przychody '
        + 'zwolnione, plus koszty niestanowiące kosztów uzyskania. Różnice przejściowe i stratę z lat ubiegłych '
        + 'podaje księgowa — bez nich kwota jest wstępna.',
      base: 'Podstawa',
      rate: 'Stawka',
      taxForYear: 'Podatek za rok',
      fromAdvances: 'Pokryte zaliczkami',
      difference: 'Do dopłaty przy CIT-8',
      priorLoss: 'Strata z lat ubiegłych do odliczenia',
      recalculate: 'Przelicz',
      notApplicable: 'Ta firma nie płaci CIT albo nie prowadzi pełnych ksiąg.',
    },
    activity: {
      title: 'Sprawozdanie zarządu',
      lead: 'Dokument zarządu, nie księgowej: podpisuje go kierownik jednostki, a przy zarządzie '
        + 'wieloosobowym wszyscy jego członkowie. Liczby liczymy z tej samej projekcji, co sprawozdanie '
        + 'finansowe — nie przepisujemy ich.',
      indicators: 'Wskaźniki',
      indicator: 'Wskaźnik',
      value: 'Wartość',
      basis: 'Z czego',
      suggestions: 'Propozycje z ksiąg',
      suggestionsHint: 'Żaden akapit nie wchodzi do dokumentu, dopóki go nie przyjmiesz. Propozycja dopisuje się '
        + 'do treści sekcji, gdzie można ją poprawić albo skasować.',
      add: 'Dopisz do sekcji',
      sections: 'Sekcje dokumentu',
      required: 'wymagana',
      optional: 'nieobowiązkowa',
      fromData: 'treść z danych',
      proposal: 'propozycja — nieprzyjęta',
      save: 'Zapisz treści',
      saved: 'Treści zapisane.',
      missing: 'Bez tych sekcji dokumentu nie złożymy:',
      ready: 'Dokument gotowy do złożenia',
      pdf: 'Złóż PDF',
      docx: 'Złóż DOCX',
      download: 'Pobierz dokument',
      signers: 'Podpisują',
    },
    blank: {
      title: 'Pusty wzór sprawozdania',
      hint: 'Wszystkie pozycje wybranego załącznika bez kwot — do rozmowy z klientem albo do zebrania danych '
        + 'poza aplikacją. Nie tworzy sprawozdania i niczego nie zmienia.',
      pdf: 'Pobierz PDF',
      xlsx: 'Pobierz arkusz',
    },
    modes: {
      title: 'Skąd kwoty',
      BOOKS: 'Z ksiąg w Firmeto',
      DORMANT: 'Spółka bez działalności',
      MANUAL: 'Księgi poza Firmeto',
      booksHint: 'Kwoty liczy projekcja z sald — tak jak na ekranie bilansu.',
      dormantHint: 'Rok bez obrotów: kilka kwot wpisanych ręcznie (kapitał, środki pieniężne, zobowiązania).',
      manualHint: 'Księgi za ten rok są prowadzone gdzie indziej — kwoty pozycji wpisujesz ręcznie.',
      template: 'Wzór sprawozdania',
      templates: { ZAL_4: 'Jednostka mikro (załącznik 4)', ZAL_5: 'Jednostka mała (załącznik 5)', ZAL_1: 'Jednostka inna (załącznik 1)' },
      variant: 'Wariant rachunku zysków i strat',
      variants: { BY_KIND: 'porównawczy', BY_FUNCTION: 'kalkulacyjny' },
    },
    amounts: {
      title: 'Kwoty sprawozdania',
      lead: 'Kwoty wpisuje się tylko w pozycje składowe: sumy i pozycje liczone formułą wylicza aplikacja. '
        + 'Wynik z rachunku zysków i strat trafia do pasywów sam — dlatego nie da się złożyć sprawozdania, '
        + 'w którym bilans mówi co innego niż rachunek wyników.',
      position: 'Pozycja',
      amount: 'Kwota',
      previous: 'Rok poprzedni',
      save: 'Zapisz kwoty',
      saved: 'Kwoty zapisane.',
      balanced: 'Bilans się domyka',
      unbalanced: 'Bilans się nie domyka',
      difference: 'Różnica',
      totals: 'Aktywa · pasywa · wynik',
      onlyManual: 'Ten ekran dotyczy sprawozdania składanego poza księgami. To sprawozdanie liczy się z ksiąg.',
    },
    office: {
      title: 'Rok obrotowy klientów',
      year: 'Rok',
      client: 'Klient',
      status: 'Sprawozdanie',
      prepared: 'Sporządzone',
      approved: 'Zatwierdzone',
      filed: 'Złożone',
      confirmed: 'Potwierdzone',
      mention: 'Wzmianka w KRS',
      noReport: 'brak',
      none: 'Żaden klient nie ma roku obrotowego kończącego się w tym roku.',
    },
    open: 'Sprawozdanie roczne',
    title: 'Sprawozdanie roczne do KRS',
    lead: 'Kreator prowadzi czynnościami, nie polami. Kwoty bilansu i rachunku zysków i strat biorą się '
      + 'z ksiąg — tutaj nie ma pola, w które dałoby się je wpisać.',
    tabs: {
      year: 'Rok i wersja',
      form: 'Wprowadzenie i noty',
      file: 'Plik sprawozdania',
      signatures: 'Podpisy',
      resolutions: 'Uchwały',
      krs: 'Złożenie w KRS',
      registers: 'Zarząd i wspólnicy',
    },
    statuses: {
      draft: 'Szkic',
      assembled: 'Plik złożony',
      signing: 'W podpisach',
      signed: 'Podpisane',
      approved: 'Zatwierdzone',
      filed: 'Złożone w KRS',
      filed_unapproved: 'Złożone niezatwierdzone',
      confirmed: 'Potwierdzone',
      abandoned: 'Porzucone',
    },
    year: {
      pick: 'Rok obrotowy',
      noYears: 'Ta firma nie ma jeszcze roku obrotowego.',
      noYearsHint: 'Rok obrotowy zakłada się na ekranie „Okresy”. Bez niego nie ma z czego sporządzić sprawozdania.',
      start: 'Rozpocznij sprawozdanie',
      noReport: 'Za ten rok nie ma jeszcze sprawozdania',
      noReportHint: 'Sprawozdanie powstaje z ksiąg tego roku. Zamknięcie roku nie jest do tego konieczne, ale '
        + 'sprawozdanie z otwartego roku nosi znak „wersja robocza”.',
      versions: 'Wersje',
      version: (numer: number) => `wersja ${numer}`,
      versionReason: 'Powód',
      draftMark: 'wersja robocza',
      draftMarkHint: 'Rok jest otwarty albo plik złożyła osoba, która nie prowadzi ksiąg — dokument mówi o tym '
        + 'w treści, bo inaczej trafiłby do wspólników jako gotowe sprawozdanie.',
      stale: 'Księgi zmieniły się po wygenerowaniu pliku',
      staleHint: 'Stanu z tego powodu nie zmieniamy i pliku nie kasujemy. Zanim ktokolwiek go podpisze, złóż plik od nowa.',
      calendar: 'Terminarz roczny',
      calendarHint: 'Przy każdym terminie dwie daty: ta z ustawy i ostatni dzień roboczy przed nią. Nie wiadomo, '
        + 'czy termin przesuwa się w dzień wolny, a w sobotę nie ma komu złożyć dokumentu.',
      statutory: 'Termin z ustawy',
      safe: 'Ostatni dzień roboczy',
      state: 'Stan',
      done: 'zrobione',
      overdue: 'po terminie',
      daysLeft: (dni: number) => `za ${dni} dni`,
      obligations: {
        statement_prepared: 'Sporządzenie sprawozdania',
        cit8: 'CIT-8',
        approval: 'Zatwierdzenie przez zgromadzenie',
        krs_filing: 'Złożenie w KRS',
        krs_confirmation: 'Wzmianka albo potwierdzenie',
      },
    },
    form: {
      title: 'Wprowadzenie i informacja dodatkowa',
      lead: 'Pola pochodzą ze wzoru sprawozdania: ich kody to nazwy elementów ze schemy Ministerstwa Finansów, '
        + 'a nie nazwy wymyślone przez nas.',
      save: 'Zapisz odpowiedzi',
      saved: 'Odpowiedzi zapisane.',
      missing: 'Bez tych pól plik nie przejdzie schemy:',
      complete: 'Formularz jest kompletny.',
      sections: {
        INTRODUCTION: 'Wprowadzenie',
        NOTES: 'Informacja dodatkowa',
        SEAT: 'Siedziba',
        TAX: 'Rozliczenie podatkowe',
      },
    },
    file: {
      title: 'Plik sprawozdania',
      lead: 'Plik powstaje z ksiąg i z formularza, a potem leży w archiwum firmy razem ze swoim skrótem.',
      generate: 'Złóż plik z ksiąg',
      regenerate: 'Złóż plik od nowa',
      taxReconciliation: 'Dołącz rozliczenie wyniku z podstawą opodatkowania',
      download: 'Pobierz XML',
      pdf: 'Złóż wydruk (PDF)',
      pdfDownload: 'Pobierz PDF',
      pdfHint: 'Wydruk do czytania: bilans, rachunek zysków i strat i treści z formularza. Do rejestru idzie plik XML — wydruk zostaje w firmie i nie jest dokumentem, pod którym zbiera się podpisy.',
      files: 'Pliki kompletu',
      role: 'Rola',
      sha256: 'Skrót',
      generatedAt: 'Powstał',
      roles: {
        statement_xml: 'Sprawozdanie (XML)',
        statement_xml_signed: 'Sprawozdanie z podpisami',
        statement_pdf: 'Sprawozdanie (PDF)',
        activity_report: 'Sprawozdanie zarządu',
        resolution_approval: 'Uchwała o zatwierdzeniu',
        resolution_profit: 'Uchwała o podziale zysku',
        resolution_discharge: 'Absolutorium',
        minutes: 'Protokół zgromadzenia',
        statement_52_2b: 'Oświadczenie z art. 52 ust. 2b',
        auditor_report: 'Sprawozdanie z badania',
        rdf_confirmation: 'Potwierdzenie z repozytorium',
      },
      newVersion: 'Nowa wersja',
      reason: 'Powód nowej wersji',
      reasonHint: 'Powód zostaje przy wersji na zawsze — po roku nikt nie pamięta, dlaczego sprawozdanie ma dwie.',
      noAmounts: 'Kwot nie wpisuje się tutaj ani nigdzie indziej w kreatorze: zmiana idzie przez księgi, '
        + 'a plik składa się od nowa.',
    },
    signatures: {
      title: 'Podpisy sprawozdania',
      lead: 'Sprawozdanie podpisuje osoba prowadząca księgi i wszyscy członkowie zarządu '
        + '(art. 52 ust. 2 ustawy o rachunkowości).',
      deadline: 'Termin sporządzenia',
      person: 'Osoba',
      function: 'Funkcja',
      state: 'Stan',
      method: 'Sposób',
      when: 'Kiedy',
      states: {
        missing: 'nie podpisał',
        signed: 'podpisał',
        statement: 'złożył oświadczenie',
        refusal: 'odmówił oświadczenia',
      },
      methods: {
        qualified: 'podpis kwalifikowany',
        trusted_profile: 'profil zaufany',
        personal: 'podpis osobisty',
        statement: 'oświadczenie z art. 52 ust. 2b',
        refusal: 'odmowa oświadczenia',
      },
      capacities: { board_member: 'członek zarządu', bookkeeper: 'osoba prowadząca księgi' },
      downloadToSign: 'Pobierz plik do podpisania',
      downloadHint: 'Pobierasz plik z podpisami zebranymi do tej pory: kolejny podpis dokłada się do poprzednich, '
        + 'a podpisanie czystego pliku zgubiłoby te już złożone.',
      upload: 'Wgraj podpisany plik',
      capacity: 'W jakim charakterze',
      officer: 'Osoba z kartoteki zarządu',
      personName: 'Imię i nazwisko',
      note: 'Uzasadnienie albo uwaga',
      send: 'Wgraj',
      complete: 'Komplet podpisów',
      incomplete: 'Podpisy nie są jeszcze kompletne',
      extra: 'Podpisy osób spoza zarządu',
      noStatement: 'Najpierw złóż plik sprawozdania — bez niego nie ma czego podpisywać.',
      declaration: {
        title: 'Oświadczenie albo odmowa z art. 52 ust. 2b',
        hint: 'Gdy sprawozdanie podpisuje jeden członek zarządu, pozostali składają oświadczenia, że spełnia ono '
          + 'wymagania ustawy, albo odmowy z uzasadnieniem. Generujemy treść; podpis składa się poza aplikacją '
          + 'i wgrywa z powrotem.',
        who: 'Kto składa oświadczenie',
        kind: 'Rodzaj',
        statement: 'Oświadczenie',
        refusal: 'Odmowa',
        justification: 'Uzasadnienie (wymagane przy odmowie)',
        generate: 'Wygeneruj treść (PDF)',
        generated: (plik: string) => `Dokument ${plik} leży w archiwum firmy — podpisz go i wgraj z powrotem.`,
      },
    },
    resolutions: {
      title: 'Uchwały zgromadzenia',
      lead: 'Komplet uchwał wynika z wyniku roku i z kapitałów, więc kwoty przychodzą wyliczone — nie ma ich '
        + 'do wpisania.',
      reason: 'Dlaczego',
      create: 'Utwórz projekt',
      adopt: 'Zapisz jako podjętą',
      adoptedOn: 'Data podjęcia',
      number: 'Numer',
      pdf: 'PDF',
      draft: 'projekt',
      adopted: 'podjęta',
      gaps: 'Czego brakuje w danych:',
      minutes: {
        title: 'Protokół zgromadzenia',
        date: 'Data zgromadzenia',
        place: 'Miejsce',
        chairman: 'Przewodniczący',
        secretary: 'Protokolant',
        unanimous: 'Uchwały podjęto jednogłośnie',
        generate: 'Złóż protokół (PDF)',
        download: 'Pobierz protokół',
        needsResolutions: 'Protokół opisuje przebieg zgromadzenia — najpierw podejmij choć jedną uchwałę.',
      },
    },
    krs: {
      title: 'Złożenie w Repozytorium Dokumentów Finansowych',
      lead: 'Interfejsu maszynowego do składania nie ma, więc aplikacja robi wszystko poza ostatnim kliknięciem: '
        + 'składa paczkę, mówi, czego brakuje, i prowadzi krok po kroku przez Portal Rejestrów Sądowych.',
      checklist: 'Lista kontrolna kompletu',
      required: 'wymagane',
      optional: 'nieobowiązkowe',
      present: 'jest',
      missing: 'brakuje',
      ready: 'Komplet gotowy do złożenia',
      notReady: 'Komplet nie jest jeszcze gotowy',
      people: 'Kto może podpisać zgłoszenie',
      pesel: 'PESEL ujawniony w KRS',
      instructions: 'Krok po kroku w Portalu Rejestrów Sądowych',
      deadlines: 'Terminy',
      zip: 'Pobierz paczkę (ZIP)',
      filing: {
        title: 'Zapisz złożenie',
        submittedOn: 'Data złożenia',
        number: 'Numer zgłoszenia',
        record: 'Zapisz złożenie',
        unapproved: 'złożone przed zatwierdzeniem — po zatwierdzeniu trzeba złożyć ponownie '
          + '(art. 69 ust. 2 ustawy o rachunkowości)',
      },
      confirmation: {
        title: 'Potwierdzenie z repozytorium',
        confirmedOn: 'Data potwierdzenia',
        upload: 'Wgraj potwierdzenie',
      },
      register: {
        title: 'Wzmianki w rejestrze',
        lead: 'Wzmianka w KRS to dowód złożenia niezależny od nas i od klienta. Milczenie rejestru niczego nie cofa.',
        number: 'Numer KRS',
        noNumber: 'Firma nie ma wpisanego numeru KRS — uzupełnij go w ustawieniach firmy.',
        checkedAt: 'Sprawdzono',
        never: 'jeszcze nie pytaliśmy',
        check: 'Sprawdź w rejestrze',
        mentions: 'Wzmianki',
        period: 'Okres',
        filedOn: 'Złożono',
        approval: 'Uchwała',
        activity: 'Sprawozdanie zarządu',
        audit: 'Badanie',
        missingYears: 'Lata bez wzmianki o sprawozdaniu:',
        none: 'Rejestr nie ma jeszcze żadnej wzmianki o sprawozdaniach tej spółki.',
      },
    },
    registers: {
      officers: {
        title: 'Zarząd',
        lead: 'Kartoteka ma okresy, a nie stan na dziś: uchwały za dany rok dotyczą zarządu z tamtego roku.',
        firstName: 'Imię',
        lastName: 'Nazwisko',
        role: 'Funkcja',
        from: 'Od',
        to: 'Do',
        pesel: 'PESEL w KRS',
        peselHint: 'Bez osoby z numerem PESEL ujawnionym w KRS bezpłatnego zgłoszenia do repozytorium nie podpisze '
          + 'nikt z zarządu.',
        add: 'Dodaj osobę',
        end: 'Zakończ funkcję',
        roles: {
          PRESIDENT: 'prezes zarządu',
          BOARD_MEMBER: 'członek zarządu',
          PROXY: 'prokurent',
          LIQUIDATOR: 'likwidator',
        },
        none: 'Kartoteka zarządu jest pusta — bez niej nie wiemy, kto ma podpisać sprawozdanie.',
      },
      shareholders: {
        title: 'Wspólnicy',
        lead: 'Z kartoteki wspólników powstaje lista obecności w protokole i podział zysku.',
        name: 'Wspólnik',
        shares: 'Udziały',
        votes: 'Głosy',
        nominal: 'Wartość nominalna',
        capital: 'Kapitał',
        add: 'Dodaj wspólnika',
        end: 'Zakończ',
        none: 'Kartoteka wspólników jest pusta.',
      },
    },
  },
  periods: {
    closedMonths: 'Miesięcy zamkniętych',
    settled: 'rozliczany',
    stillRunning: 'jeszcze trwa',
    noneClosed: 'Żaden miesiąc nie jest zamknięty',
    noneClosedHint:
      'Dopóki miesiąc jest otwarty, wsteczny zapis zmieni kwoty w deklaracji, która już poszła. Zamykaj miesiąc po wysłaniu JPK.',
    open: 'Okresy',
    title: 'Zamknięcie okresów',
    lead: 'Do zamkniętego miesiąca nie trafi żaden nowy zapis. Zmianę wprowadza się wtedy korektą '
      + 'z bieżącą datą — dzięki temu księga zgadza się z deklaracją, która poszła do urzędu.',
    years: 'Rok obrotowy',
    noYears: 'Nie ma jeszcze żadnego roku obrotowego.',
    yearMissing: (rok: number) => `Rok obrotowy ${rok} nie jest jeszcze otwarty`,
    yearMissingHint: 'Rok obrotowy powstaje raz i trzeba otworzyć kolejny. Bez niego plan kont '
      + 'i dziennik pokazują rok sprzed roku, do którego dzisiejsze ustawienia podatkowe nie sięgają.',
    openYear: (rok: number) => `Otwórz rok ${rok}`,
    addYear: 'Dodaj rok obrotowy',
    saveYear: 'Zapisz rok',
    yearHint: 'Rok obrotowy jednoosobowej działalności jest zawsze kalendarzowy. Spółka może mieć inny, '
      + 'ale nie dłuższy niż osiemnaście miesięcy.',
    closeYear: 'Zamknij rok',
    readiness: {
      blocked: 'Roku nie da się zamknąć, dopóki to nie zostanie zrobione:',
      warnings: 'Lista kontrolna ma uwagi:',
      confirm: 'Zamknąć rok mimo tych uwag?',
      result: (numer: number, kwota: string) => `Wynik roku przeksięgowano zapisem nr ${numer}: ${kwota} zł`,
      noResult: 'Rok zamknięty. Nie było zapisów wynikowych, więc nie ma czego przeksięgowywać.',
      checks: {
        months_open: 'Otwarte miesiące',
        unposted_invoices: 'Niezaksięgowane faktury',
        unposted_costs: 'Niezaksięgowane dokumenty kosztowe',
        trial_balance: 'Zestawienie obrotów i sald nie domyka się',
        accounts_without_position: 'Konta z saldem bez pozycji sprawozdania',
        opening_balance: 'Brak bilansu otwarcia',
        draft_invoices: 'Faktury w szkicu',
        inbox_documents: 'Dokumenty czekające w skrzynce',
        settlements_without_counterparty: 'Wiersze rozrachunków bez kontrahenta',
        depreciation: 'Środki trwałe bez zaksięgowanej amortyzacji',
        closing_inventory: 'Brak spisu z natury na koniec roku',
        open_provisions: 'Rezerwy z saldem po zakończeniu okresu',
        result_transfer: 'Wynik do przeksięgowania',
      },
    },
    months: 'Miesiące',
    month: 'Miesiąc',
    state: 'Stan',
    from: 'Od',
    to: 'Do',
    closedAt: 'Zamknięto',
    open_: 'Otwarty',
    closed: 'Zamknięty',
    close: 'Zamknij',
    reopen: 'Otwórz',
    reopenTitle: (period: string) => `Otwarcie okresu ${period}`,
    reason: 'Powód',
    reasonHint: 'Powód trafia do dziennika i zostaje tam na zawsze.',
    cancel: 'Anuluj',
    confirmClose: (period: string) => `Zamknąć ${period}? Po zamknięciu nie dopiszesz do tego miesiąca dokumentu.`,
    confirmWithDrafts: (period: string, drafts: number) =>
      `W miesiącu ${period} zostały szkice faktur: ${drafts}. Po zamknięciu nie da się ich wystawić. Zamknąć mimo to?`,
    confirmCloseYear: (from: string, to: string) =>
      `Zamknąć rok ${from} — ${to}? Zamknie to wszystkie miesiące tego roku.`,
  },
  costs: {
    open: 'Koszty',
    bookAll: 'Zaksięguj wszystkie',
    list: 'Lista',
    allMonths: 'Wszystkie',
    column: 'kol.',
    draftsWaiting: 'Dokumenty czekają na zaksięgowanie',
    draftsWaitingHint:
      'Dopóki są szkicami, nie wchodzą do KPiR ani do VAT — dlatego koszty miesiąca na pulpicie mogą wynosić zero.',
    draftAfterSave: 'Zapisany dokument trafia do szkiców. Do KPiR i VAT wchodzi dopiero po zaksięgowaniu.',
    draftAfterSaveHint: 'Po zapisaniu dokument ma stan „Szkic" i nie wpływa jeszcze na żadną z tych pozycji.',
    document: 'Dokument',
    qualification: 'Kwalifikacja',
    rates: 'Rozbicie na stawki VAT',
    addRate: 'Dodaj stawkę',
    summary: 'Podsumowanie',
    goesTo: 'Trafi do',
    kpir: 'KPiR',
    vatRegister: 'Ewidencja VAT',
    purchases: 'zakupy',
    vatInput: 'VAT naliczony',
    saveAndNext: 'Zapisz i dodaj kolejny',
    costDateHint: 'Decyduje o miesiącu w KPiR.',
    supplierHint: 'Nowego dodasz w kartotece kontrahentów.',
    fixedAssetHint: 'Wprowadź do ewidencji środków trwałych',
    intraCommunity: 'Nabycie wewnątrzwspólnotowe',
    intraCommunityHint: 'Zakup towarów od kontrahenta z Unii — wejdzie do informacji VAT-UE (import usług nie)',
    scanFutureTitle: 'Odczyt ze skanu',
    scanFutureText:
      'Docelowo skan z zakładki Dokumenty wypełni numer, dostawcę, daty i kwoty, a pola o niskiej pewności odczytu zostaną podświetlone do potwierdzenia. Dziś skan jest tylko załącznikiem — wszystkie pola wpisuje się ręcznie.',
    title: 'Dokumenty kosztowe',
    new: 'Dodaj koszt',
    none: 'Brak dokumentów kosztowych.',
    number: 'Numer dokumentu',
    supplier: 'Dostawca',
    chooseSupplier: 'Wybierz dostawcę',
    issueDate: 'Data dokumentu',
    costDate: 'Data poniesienia',
    category: 'Rodzaj wydatku',
    deduction: 'Odliczenie VAT',
    fixedAsset: 'Środek trwały',
    net: 'Netto',
    vat: 'VAT',
    deductible: 'VAT do odliczenia',
    costAmount: 'W koszty',
    correctionTo: 'Korekta do dokumentu',
    correctionHint: 'Wpisz kwoty różnicy, tak jak są na dokumencie od dostawcy — zwykle ze znakiem minus.',
    causes: {
      MISTAKE: 'Pomyłka na dokumencie pierwotnym',
      EVENT: 'Zdarzenie późniejsze (rabat, zwrot)',
    },
    status: 'Stan',
    save: 'Zapisz koszt',
    book: 'Zaksięguj',
    delete: 'Usuń',
    hint: 'W koszty wchodzi netto powiększone o VAT, którego nie da się odliczyć — przy połowie odliczenia i u firmy zwolnionej wychodzą różne kwoty.',
    categories: {
      GOODS: 'Towary i materiały (kol. 10)',
      PURCHASE_COSTS: 'Koszty uboczne zakupu (kol. 11)',
      SALARIES: 'Wynagrodzenia (kol. 12)',
      OTHER: 'Pozostałe wydatki (kol. 13)',
    },
    deductions: { FULL: 'Pełne (100%)', HALF: 'Połowa (pojazd)', NONE: 'Bez odliczenia' },
    statuses: { DRAFT: 'Szkic', BOOKED: 'Zaksięgowany' },
  },
  dashboard: {
    fixSettings: 'Uzupełnij ustawienia',
    noDeadlines: 'Brak terminów w tym miesiącu.',
    open: 'Pulpit',
    title: 'Podsumowanie miesiąca',
    month: 'Miesiąc',
    revenue: 'Przychód',
    costs: 'Koszty',
    income: 'Dochód',
    vatToPay: 'VAT do zapłaty',
    vatToCarry: 'VAT do przeniesienia',
    obligations: 'Terminy',
    toPay: 'Do zapłaty w tym miesiącu',
    goToSettlements: 'Przejdź do rozrachunków',
    goToPeople: 'Przejdź do osób',
    NO_BOOKKEEPER: 'Pełne księgi, a nikt nie ma roli Księgowa — nie ma komu prowadzić ksiąg.',
    period: 'Okres',
    dueDate: 'Termin',
    VAT: 'VAT i JPK',
    PIT_ADVANCE: 'Zaliczka na podatek dochodowy',
    CIT_ADVANCE: 'Zaliczka na CIT',
    HEALTH_ANNUAL: 'Roczne rozliczenie składki zdrowotnej',
    citBase: 'Podstawa CIT narastająco',
    citNotes: {
      ESTIMATE: 'Liczymy z ksiąg w Firmeto — to szacunek, nie deklaracja.',
      LOSS_NOT_DEDUCTED: 'Straty z lat ubiegłych nie odliczamy; uwzględnij ją przy rozliczeniu rocznym.',
      TRANSITIONAL_NOT_INCLUDED: 'Różnic przejściowych rachunek nie zna — wchodzą dopiero do CIT-8.',
      SMALL_RATE_FROM_SETTINGS: 'Stawka 9% z ustawień firmy; limitu przychodu na tę stawkę nie sprawdzamy.',
      LOSS_PERIOD: 'Narastająco wyszła strata — zaliczki nie ma.',
      CAPITAL_GAINS_NOT_SEPARATED: 'Dochodu z zysków kapitałowych nie rozdzielamy — z ksiąg nie widać, które przychody nim są.',
      QUARTERLY: 'Kwota dotyczy całego kwartału i płaci się ją po jego zakończeniu.',
      QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Zaliczki kwartalne przysługują małemu podatnikowi, a firma nie jest tak oznaczona w ustawieniach.',
    } as Record<string, string>,
    ZUS: 'Składki ZUS',
    movedFrom: 'ustawowy termin {date} wypada w dzień wolny',
    today: 'dziś',
    tomorrow: 'jutro',
    overdue: 'po terminie',
    daysLeft: 'zostało dni:',
    NO_SETTINGS: 'Brak ustawień podatkowych — bez nich nie policzymy podatku ani nie pokażemy terminów.',
    NO_DOCUMENTS: 'W tym miesiącu nie ma jeszcze żadnych dokumentów.',
    OVERDUE_RECEIVABLES: 'Masz faktury po terminie płatności — to pieniądze, na które czekasz dłużej, niż powinieneś.',
    OVERDUE_PAYABLES: 'Masz zobowiązania po terminie płatności — to pieniądze, które jesteś winien dłużej, niż obiecałeś.',
    aging: 'Rozrachunki — wiekowanie',
    agingLead: (asOf: string) => `Stan na ${asOf} z rozrachunków w dzienniku: kto ile jest winien i od kiedy.`,
    agingReceivables: 'Należności',
    agingPayables: 'Zobowiązania',
    agingOpen: 'otwartych',
    agingOverdue: 'po terminie',
    goToJournal: 'Przejdź do rozrachunków w dzienniku',
    receivables: 'Nieopłacone faktury',
    receivablesOverdue: 'w tym po terminie',
    exemptionTitle: 'Limit zwolnienia z VAT',
    exemptionUsed: 'Wykorzystane od początku roku',
    exemptionLimit: 'Limit',
    exemptionRemaining: 'Zostało',
    VAT_EXEMPTION_NEAR_LIMIT: 'Zbliżasz się do limitu zwolnienia z VAT — po jego przekroczeniu trzeba zarejestrować się do VAT.',
    VAT_EXEMPTION_EXCEEDED: 'Limit zwolnienia z VAT został przekroczony. Zwolnienie kończy się na transakcji, która go przekroczyła.',
    SUSPENDED: 'Działalność była w tym miesiącu zawieszona w całości, więc nie ma ani składek, ani zaliczki na podatek. Plik JPK_V7 może być jednak potrzebny — przy nabyciu towarów z UE, imporcie usług i korektach (art. 99 ust. 7a i 7b ustawy o VAT).',
    goToSuspensions: 'Zobacz zawieszenia',
    zusTitle: 'Składki ZUS za miesiąc',
    zusSocial: 'Społeczne',
    zusSickness: 'Chorobowe',
    zusLaborFund: 'Fundusz Pracy',
    zusHealth: 'Zdrowotna',
    zusTotal: 'Razem do zapłaty',
    draDownload: 'Deklaracja ZUS DRA',
    draHint: 'Plik do wczytania w eZUS albo w Płatniku — nie wysyłamy go za Ciebie.',
    advanceTitle: 'Zaliczka na podatek dochodowy',
    advanceBase: 'Podstawa narastająco',
    advanceAmount: 'Do zapłaty za ten miesiąc',
    partners: 'Wspólnicy',
    partner: 'Wspólnik',
    share: 'Udział w zysku',
    taxationForm: 'Rozlicza się',
    ESTIMATE: 'Kwoty wyliczamy z dokumentów w Firmeto i zakładamy, że wcześniejsze składki i zaliczki zostały zapłacone w terminie.',
    RYCZALT_NOT_SUPPORTED: 'Podatku na ryczałcie jeszcze nie liczymy — potrzebna jest ewidencja przychodów ze stawkami.',
    MALY_ZUS_PLUS_APPROXIMATED: 'Mały ZUS Plus liczy się z dochodu poprzedniego roku, a tego roku nie ma w Firmeto — pokazujemy pełną podstawę, bo niższa nie wynikałaby z niczego.',
    UNKNOWN_YEAR: 'Nie mamy stawek i progów na ten rok, więc nie liczymy kwot.',
    COMPANY_TAX: 'Podatek spółki i składki wspólników liczy się poza tym zestawieniem.',
    PARTNERS_SPLIT: 'Spółka osobowa nie płaci podatku dochodowego — płacą go wspólnicy, każdy od swojego udziału.',
    PARTNERS_MISSING: 'Spółka osobowa bez kartoteki wspólników: liczymy jak dla jednej osoby, a tak nie jest. Dopisz wspólników w ustawieniach firmy.',
    PARTNERS_SHARE_MISMATCH: 'Udziały wspólników nie sumują się do całości — czyjegoś udziału brakuje albo jest za duży.',
    QUARTERLY_ADVANCE: 'Zaliczka jest kwartalna: kwota dotyczy całego kwartału i płaci się ją do 20. dnia miesiąca po jego zakończeniu. Składki ZUS zostają miesięczne.',
    QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Zaliczki kwartalne przysługują małemu podatnikowi, a firma nie jest tak oznaczona w ustawieniach.',
    BAD_DEBT_IN_INCOME: 'Dochód uwzględnia zapisane korekty złych długów: niezapłacony koszt wraca do dochodu, niezapłacony przychód z niego wychodzi (art. 26i ustawy o PIT).',
    BAD_DEBT_NOT_IN_RYCZALT: 'Przy ryczałcie ulga na złe długi zmienia przychód według stawki (art. 11 ustawy o ryczałcie), a my nie wiemy, do której stawki przypisać niezapłacony dokument — więc zapisanych korekt tu nie liczymy.',
    MALY_ZUS_PLUS_FROM_PREVIOUS_YEAR: 'Podstawa składek społecznych policzona z dochodu poprzedniego roku: przeciętny dochód miesięczny podzielony na pół (Mały ZUS Plus, art. 18c ustawy o systemie ubezpieczeń społecznych).',
    MALY_ZUS_PLUS_AT_FLOOR: 'Z dochodu wyszłaby podstawa niższa od dolnej granicy, więc bierzemy 30% minimalnego wynagrodzenia — tyle samo, co przy składkach preferencyjnych.',
    MALY_ZUS_PLUS_AT_CAP: 'Z dochodu wyszłaby podstawa wyższa od pełnej, więc bierzemy pełną: przy tym dochodzie ulga nie zmienia składki.',
    MALY_ZUS_PLUS_REVENUE_OVER_LIMIT: 'Przychód poprzedniego roku przekroczył limit ulgi (120 000 zł), więc liczymy pełną podstawę.',
    MALY_ZUS_PLUS_TOO_FEW_DAYS: 'Działalność w poprzednim roku trwała krócej niż 60 dni, a przy takim roku ulga nie przysługuje.',
    MALY_ZUS_PLUS_DAYS_ASSUMED: 'Nie znamy dnia rozpoczęcia działalności, więc przyjęliśmy, że poprzedni rok był pełny. Jeśli firma zaczęła w jego trakcie, podstawa jest zaniżona — uzupełnij datę w danych firmy.',
    MALY_ZUS_PLUS_FROM_REVENUE: 'Przy ryczałcie ewidencja nie zna dochodu, więc wyprowadzamy go z przychodu współczynnikiem z przepisu.',
    MALY_ZUS_PLUS_LIMIT_NOT_CHECKED: 'Nie sprawdzamy dwóch warunków, których z naszych danych nie widać: 36 miesięcy korzystania z ulgi w ciągu ostatnich 60 i wykonywania działalności dla byłego pracodawcy. Tego trzeba pilnować samemu.',
    MALY_ZUS_PLUS_SUSPENSION_DAYS: 'Dni zawieszenia działalności nie są dniami jej prowadzenia, więc odjęliśmy je przy dzieleniu dochodu — a przez to podstawa wyszła wyższa niż z samego dochodu rocznego.',
    SUSPENDED_MONTH: 'Działalność była w tym miesiącu zawieszona w całości, więc nie ma ubezpieczeń: ani społecznych, ani zdrowotnego.',
    SUSPENDED_PART_OF_MONTH: 'Część miesiąca była zawieszona, więc podstawę składek społecznych zmniejszyliśmy proporcjonalnie do dni ubezpieczenia (art. 18 ust. 9 ustawy o systemie ubezpieczeń społecznych). Składka zdrowotna zostaje cała — jest miesięczna i niepodzielna.',
  },
  ryczalt: {
    lead: 'Ewidencję przychodów prowadzi się przy ryczałcie od przychodów ewidencjonowanych.',
    otherForm: 'Ta firma rozlicza się inaczej',
    goToLedger: 'Przejdź do księgi przychodów',
    changeForm: 'Zmień formę opodatkowania',
    byRate: 'Według stawek',
    open: 'Ewidencja',
    title: 'Ewidencja przychodów',
    month: 'Miesiąc',
    date: 'Data',
    document: 'Dokument',
    counterparty: 'Kontrahent',
    rate: 'Stawka',
    amount: 'Przychód',
    total: 'Razem',
    none: 'Brak przychodów w tym miesiącu.',
  },
  settlements: {
    lead: 'Należności i zobowiązania w podziale na dokumenty. Wpłaty wprowadza się ręcznie albo z wyciągu.',
    receivablesTotal: 'Należności ogółem',
    payablesTotal: 'Zobowiązania ogółem',
    longestDelay: 'Najdłuższe opóźnienie (dni)',
    noDelay: 'nic nie jest po terminie',
    delay: 'Opóźnienie',
    filter: 'Filtruj: dokument, kontrahent',
    loadStatement: 'Wczytaj wyciąg',
    matchFutureTitle: 'Dopasowanie wpłat do faktur',
    matchFutureText:
      'Podpowiadamy dopasowanie, ale zatwierdza je człowiek. Docelowo plik z banku sam wskaże, która wpłata pokrywa którą fakturę, razem z kwotami częściowymi.',
    open: 'Rozrachunki',
    title: 'Rozrachunki',
    receivables: 'Należności',
    payables: 'Zobowiązania',
    onlyOpen: 'Tylko nierozliczone',
    bank: 'Wyciąg',
    bankHint: 'Wgraj wyciąg MT940 z banku. Niczego nie księgujemy sami — podpowiadamy dopasowanie, a Ty je zatwierdzasz.',
    chooseFile: 'Plik wyciągu',
    importedCount: 'Nowych operacji',
    skippedCount: 'Już znanych',
    noTransactions: 'Brak operacji czekających na dopasowanie.',
    valueDate: 'Data',
    details: 'Tytuł przelewu',
    proposal: 'Podpowiedź',
    noProposal: 'brak podpowiedzi',
    reasons: {
      NUMBER_AND_AMOUNT: '(numer i kwota)',
      NUMBER_ONLY: '(numer w tytule)',
      AMOUNT_ONLY: '(zgodna kwota)',
      COUNTERPARTY_AND_AMOUNT: '(kontrahent i kwota)',
    },
    document: 'Dokument',
    counterparty: 'Kontrahent',
    dueDate: 'Termin',
    amountDue: 'Do zapłaty',
    paid: 'Zapłacono',
    remaining: 'Zostało',
    status: 'Stan',
    overdueDays: 'dni po terminie:',
    addPayment: 'Zapisz wpłatę',
    savePayment: 'Zapisz',
    exchangeRate: 'Kurs z dnia zapłaty',
    exchangeRateHint: 'Dokument jest w walucie obcej: podaj średni kurs NBP z ostatniego dnia roboczego przed '
      + 'dniem zapłaty. Z różnicy wobec kursu z dokumentu powstaje przychód albo koszt finansowy.',
    paidOn: 'Data wpłaty',
    amount: 'Kwota',
    none: 'Nic nie czeka na rozliczenie.',
    statuses: {
      UNPAID: 'nieopłacone',
      PARTIAL: 'częściowo',
      PAID: 'rozliczone',
      OVERPAID: 'nadpłata',
    },
  },
  payments: {
    title: 'Sposób płatności',
    officePays: 'Za ten miesiąc płaci biuro rachunkowe — sposób płatności i obciążenia są po stronie biura.',
    officeTitle: 'Rozliczenie z Firmeto',
    officeLead: 'Biuro płaci jedną kwotą za firmy w pakiecie — najwyżej do sufitu — oraz opłatę z umowy indywidualnej, jeśli jest.',
    officeSubtotal: 'Firmy w pakiecie',
    officeCeiling: 'Sufit pakietu',
    officeAgreement: 'Umowa indywidualna',
    officeTotal: 'Do zapłaty',
    officeCompanies: (count: number) => `firm: ${count}`,
    current: 'Teraz płacisz',
    none: 'Nie ma jeszcze sposobu płatności. Bez niego nie pobierzemy opłaty, a po dwóch tygodniach zaległości konto będzie tylko do odczytu.',
    method: 'Forma płatności',
    methods: { card: 'Karta', blik: 'BLIK' } as Record<string, string>,
    provider: 'Operator',
    providerName: (provider: string) => ({ payu: 'PayU', przelewy24: 'Przelewy24', stripe: 'Stripe', fake: 'Operator testowy' })[provider] ?? provider,
    recommended: 'polecany',
    add: 'Dodaj',
    change: 'Zmień',
    revoke: 'Odwołaj zgodę',
    formAtProvider: 'Dane karty wpisujesz u operatora płatności — Firmeto ich nie widzi.',
    onlyOwner: 'Sposób płatności ustala właściciel albo administrator.',
    expires: (month: string) => `ważna do ${month}`,
    waitingTitle: 'Czekamy na potwierdzenie od operatora',
    waitingText: 'Zgoda zwykle staje się aktywna po kilku sekundach. Odśwież stronę, jeśli nadal jej nie widać.',
    charges: 'Pobrane opłaty',
    noCharges: 'Jeszcze nic nie pobraliśmy.',
    period: 'Za miesiąc',
    amount: 'Kwota',
    status: 'Stan',
    statuses: { pending: 'w toku', succeeded: 'pobrana', failed: 'nieudana', refunded: 'zwrócona' } as Record<string, string>,
    arrearTitle: (month: string) => `Opłata za ${month} czeka na zapłatę`,
    arrearText: (sum: string, reason: string) => `${sum} — ${reason}.`,
    readOnlyFrom: (day: string) => `Ponowimy próbę za kilka dni. Od ${day} konto będzie tylko do odczytu, dopóki opłata nie wpłynie.`,
    readOnlyTitle: 'Konto jest tylko do odczytu',
    readOnlyText: 'Dane widzisz i pobierzesz, ale zapisywanie wróci po opłaceniu zaległości. Dodaj albo zmień sposób płatności poniżej.',
    reasons: { mandate_missing: 'brak sposobu płatności', card_declined: 'operator odrzucił obciążenie', insufficient_funds: 'brak środków', expired_card: 'karta wygasła', authentication_required: 'bank wymaga potwierdzenia', declined: 'operator odrzucił obciążenie' } as Record<string, string>,
  },
  billing: {
    multiCompany: (count: number) => `Zniżka za ${count} działalności`,
    lead: 'Nie zarabiasz — nie płacisz. Opłata nigdy nie przekracza 20% przychodu z danego miesiąca.',
    revenueSub: 'podstawa wyliczenia opłaty',
    howMuch: 'Jak wyszła ta kwota',
    capOf: (revenue: string) => `20% przychodu ${revenue}`,
    lower: 'Niższa z dwóch kwot',
    plan: 'Plan',
    payer: 'Płaci',
    channel: 'Zakup',
    noRevenueTitle: 'Miesiąc bez przychodu kosztuje 0 zł',
    noRevenueText: 'Nie doliczamy opłaty później i nie przenosimy jej na kolejny miesiąc.',
    open: 'Opłaty',
    title: 'Opłaty za Firmeto',
    month: 'Miesiąc',
    revenue: 'Przychód',
    planPrice: 'Cena planu',
    cap: 'Limit 20%',
    toPay: 'Do zapłaty',
    history: 'Historia naliczeń',
    charged: 'Naliczono',
    note: 'Uwagi',
    surcharge: 'Wyrównanie',
    credit: 'Kredyt',
    none: 'Nie ma jeszcze żadnych naliczeń.',
    plans: { RYCZALT: 'Plan ryczałt', KPIR: 'Plan KPiR', FULL: 'Plan pełna księgowość' },
    payers: { COMPANY: 'płaci firma', OFFICE: 'płaci biuro' },
    channels: { WEB: 'zakup przez stronę', APP_STORE: 'subskrypcja App Store', GOOGLE_PLAY: 'subskrypcja Google Play' },
    reasons: {
      TRIAL: 'Okres próbny — nie pobieramy opłaty.',
      NO_REVENUE: 'Brak przychodu w tym miesiącu — nie pobieramy opłaty.',
      BELOW_MINIMUM: 'Wyliczona opłata jest niższa niż 5 zł — nie pobieramy jej.',
      FREE_AFTER_LEAVING_OFFICE: 'Okres bezpłatny po odejściu z biura rachunkowego.',
      STORE_SUBSCRIPTION: 'Subskrypcja opłacana w sklepie z aplikacjami — rozliczenie idzie przez sklep.',
    },
    statuses: {
      PENDING_CHARGE: 'do doliczenia',
      CHARGED: 'doliczone',
      PENDING_APPROVAL: 'czeka na zatwierdzenie',
      APPROVED: 'zatwierdzony',
      REJECTED: 'odrzucony',
    },
  },
  accruals: {
    title: 'Rozliczenia międzyokresowe kosztów',
    lead: 'Polisa na rok albo abonament zapłacony z góry jest kosztem tych miesięcy, których dotyczy — nie tylko tego, w którym przyszła faktura. Koszt schodzi z konta kosztowego na rozliczenia międzyokresowe i wraca po jednej racie na miesiąc, więc każda zaliczka na podatek widzi ratę, a nie całą fakturę.',
    none: 'Nie ma jeszcze żadnego harmonogramu.',
    noneHint:
      'Harmonogram zakłada się na dokumencie, który jest już zaksięgowany w dzienniku — z dekretu bierzemy konto kosztowe, bo z konta, którego dokument nie dotknął, nie ma czego zdjąć.',
    add: 'Rozłóż koszt na miesiące',
    cancel: 'Anuluj',
    save: 'Załóż harmonogram',
    document: 'Dokument',
    documentHint: 'Tylko dokumenty zaksięgowane w dzienniku. Jeden dokument ma jeden harmonogram.',
    description: 'Czego dotyczy',
    amount: 'Kwota do rozłożenia',
    amountHint:
      'Domyślnie tyle, ile dekret zapisał na koncie kosztowym. Mniej, gdy faktura obejmuje też coś, co należy do tego miesiąca — na przykład opłatę manipulacyjną obok polisy.',
    from: 'Pierwszy miesiąc',
    to: 'Ostatni miesiąc',
    period: 'Okres',
    monthly: 'Rata',
    costAccount: 'Konto kosztowe',
    accrualAccount: 'Konto rozliczeń międzyokresowych',
    accountsHint:
      'Puste znaczy: koszt z dekretu dokumentu, a rozliczenia międzyokresowe z pozycji bilansu (w planie wzorcowym 640). Wskaż konto, gdy dekret obciążył kilka kont albo gdy koszt ma czekać dłużej niż rok.',
    month: 'Miesiąc',
    release: 'Zaksięguj raty',
    released: 'Zaksięgowano rat',
    releasedTotal: 'Zaksięgowano',
    through: 'do',
    skipped: 'Pominięte harmonogramy',
    plan: 'Harmonogram',
    showPlan: 'Harmonogram',
    hidePlan: 'Ukryj harmonogram',
    instalment: 'Rata',
    remaining: 'Zostaje',
    entry: 'Zapis',
    waiting: 'czeka',
    remove: 'Wycofaj harmonogram',
    removeReason: 'Powód wycofania harmonogramu — storno przeniesienia kosztu na rozliczenia międzyokresowe:',
  },
  provisions: {
    title: 'Rozliczenia międzyokresowe bierne',
    lead: 'Koszt dotyczy tego okresu, a faktury jeszcze nie ma: badanie sprawozdania za ten rok, premie roczne, usługa wykonana i niezafakturowana (art. 39 ust. 2 ustawy o rachunkowości). Naliczenie wpisuje koszt do miesiąca, którego dotyczy, i odkłada zobowiązanie w pasywach; rozliczenie zdejmuje rezerwę, gdy faktura przyjdzie albo gdy zobowiązanie nie powstanie.',
    none: 'Nie ma jeszcze żadnej rezerwy.',
    noneHint:
      'Rezerwa nie powstaje z dokumentu, bo dokumentu jeszcze nie ma — kwotę, konto i miesiące podaje księgowa, razem z uzasadnieniem szacunku.',
    add: 'Załóż rezerwę',
    addHint:
      'Tu nie wskazuje się dokumentu: rzecz w tym, że faktury jeszcze nie ma. Kwota jest szacunkiem, więc obok niej stoi pytanie, z czego wyszła.',
    cancel: 'Anuluj',
    save: 'Załóż rezerwę',
    description: 'Czego dotyczy',
    basis: 'Z czego wyszła kwota',
    basisHint:
      'Na przykład: „umowa z biurem audytorskim z 12.11.2026, 30 000 zł netto”. Szacunek bez uzasadnienia jest nieobronny przy badaniu sprawozdania i przy kontroli, a to jedyne miejsce, w którym uzasadnienie zostaje.',
    amount: 'Kwota',
    from: 'Pierwszy miesiąc',
    to: 'Ostatni miesiąc',
    periodHint:
      'Ten sam miesiąc jest w porządku: rezerwa na badanie sprawozdania dotyczy jednego miesiąca. Premie roczne narastają przez dwanaście.',
    period: 'Okres',
    costAccount: 'Konto kosztowe',
    costAccountHint:
      'Konto, na które koszt wejdzie — to samo, na którym stanie później faktura. Wskazuje je księgowa, bo nie ma dekretu, z którego dałoby się je wziąć.',
    provisionAccount: 'Konto rezerwy',
    provisionAccountHint:
      'Puste znaczy: konto z rolą „rozliczenia międzyokresowe bierne” (w planie wzorcowym 641). Etykieta pozycji bilansu tu nie wystarczy, bo rezerwa i przychody przyszłych okresów stoją w tej samej pozycji pasywów.',
    month: 'Miesiąc',
    accrue: 'Nalicz rezerwy',
    accrued: 'Naliczono',
    accruedTotal: 'Naliczono',
    through: 'do',
    balance: 'Saldo rezerwy',
    closed: 'rozliczona',
    skipped: 'Pominięte rezerwy',
    plan: 'Naliczenia',
    showPlan: 'Naliczenia',
    hidePlan: 'Ukryj naliczenia',
    instalment: 'Rata',
    remaining: 'Zostaje',
    entry: 'Zapis',
    waiting: 'czeka',
    settle: 'Rozlicz',
    settleAccount:
      'Na jakie konto zdjąć rezerwę? Przy fakturze — konto kosztowe, które rezerwa obciążała (podpowiedziane). Gdy zobowiązanie nie powstało — konto pozostałych przychodów operacyjnych.',
    settlements: 'Rozliczenia rezerwy',
    settledOn: 'Data',
    settledAmount: 'Kwota',
    settledAccount: 'Konto',
    remove: 'Wycofaj rezerwę',
    removeReason: 'Powód wycofania rezerwy — możliwe tylko dopóki nic nie weszło do ksiąg:',
  },
  assets: {
    lead: 'Ewidencja środków trwałych oraz odpisy amortyzacyjne trafiające do kosztów.',
    noneHint:
      'Środek trwały możesz dodać tutaj albo zaznaczyć przy wprowadzaniu kosztu — wtedy dokument od razu trafi do ewidencji zamiast w koszty jednorazowo.',
    depreciating: 'Amortyzowany',
    open: 'Środki trwałe',
    title: 'Środki trwałe i amortyzacja',
    none: 'Nie ma jeszcze żadnych środków trwałych.',
    add: 'Dodaj środek trwały',
    save: 'Zapisz',
    name: 'Nazwa',
    inventoryNumber: 'Numer inwentarzowy',
    kstCode: 'Symbol KŚT',
    acquired: 'Data nabycia',
    inService: 'Przyjęcie do używania',
    value: 'Wartość początkowa',
    method: 'Metoda',
    rate: 'Stawka roczna',
    rateHint: 'Ułamek: 0.20 oznacza 20%. Stawkę bierzemy z Wykazu stawek amortyzacyjnych.',
    month: 'Miesiąc',
    bookDepreciation: 'Zaksięguj odpisy',
    booked: 'Zaksięgowano odpisów',
    includeDisposed: 'Pokaż wycofane',
    showPlan: 'Plan',
    hidePlan: 'Ukryj plan',
    plan: 'Plan amortyzacji',
    period: 'Miesiąc',
    writeOff: 'Odpis',
    remaining: 'Pozostało',
    bookedTotal: 'Zaksięgowano łącznie',
    inLedger: 'W księdze',
    inJournal: 'W dzienniku',
    posted: 'Zapisów w dzienniku',
    skipped: 'Środki pominięte w dzienniku',
    removedFromBooks: 'Środek zszedł z ksiąg',
    removedAccumulated: 'umorzenie',
    removedNet: 'wartość netto w kosztach',
    removedEntry: 'zapis',
    removedMissing: 'Środek wycofany z ewidencji, ale nie zszedł z ksiąg:',
    through: 'do',
    dispose: 'Wycofaj',
    disposalReason: 'Powód wycofania (sprzedaż, likwidacja):',
    disposed: 'wycofany',
    methods: { LINIOWA: 'liniowa', JEDNORAZOWA: 'jednorazowa', DEGRESYWNA: 'degresywna' },
  },
  pitAnnual: {
    title: 'Rozliczenie roczne',
    priorYearLoss: 'Strata z lat ubiegłych',
    revenue: 'Przychód roku',
    income: 'Dochód roku',
    badDebt: 'Korekta złych długów',
    social: 'Składki społeczne zapłacone',
    health: 'Składka zdrowotna zapłacona',
    deducted: 'Odliczone od podstawy',
    taxBase: 'Podstawa opodatkowania',
    tax: 'Podatek za rok',
    advances: 'Zaliczki zapłacone w ciągu roku',
    toPay: 'Do dopłaty',
    toRefund: 'Nadpłata do zwrotu',
    forms: { PIT_36: 'PIT-36 · skala podatkowa', PIT_36L: 'PIT-36L · podatek liniowy', PIT_28: 'PIT-28 · ryczałt' },
    notes: {
      ESTIMATE: 'Liczymy z dokumentów w Firmeto — to szacunek, a nie wypełnione zeznanie.',
      ADVANCES_ASSUMED_PAID: 'Zakładamy, że zaliczki policzone w ciągu roku zostały zapłacone.',
      BUSINESS_INCOME_ONLY: 'To podatek od działalności. Zeznanie obejmuje też inne źródła — etat, najem, zlecenia — których nie znamy.',
      NO_RELIEFS: 'Ulg i odliczeń nie liczymy: prorodzinnej, IP Box, darowizn, wspólnego rozliczenia z małżonkiem ani 1,5% dla organizacji pożytku publicznego.',
      LOSS_FROM_USER: 'Stratę z lat ubiegłych uwzględniamy tylko wtedy, gdy ją wpiszesz — zależy od rozliczeń, których u nas nie ma.',
      HEALTH_NOT_DEDUCTIBLE_ON_SCALE: 'Przy skali podatkowej składki zdrowotnej nie odlicza się od 2022 roku.',
      RYCZALT_HEALTH_HALF: 'Przy ryczałcie od przychodu odlicza się połowę zapłaconej składki zdrowotnej.',
      INVENTORY_MISSING: 'Brakuje zatwierdzonego remanentu, więc dochód roczny może być nieprawdziwy — towar na półce nie jest kosztem tego roku.',
      FULL_BOOKS_NOT_SUPPORTED: 'Przy pełnych księgach dochód roczny ustala się z ksiąg; tego jeszcze nie liczymy.',
      PARTNERS_SPLIT: 'Spółka osobowa: każdy wspólnik rozlicza się sam, od swojej części dochodu i własną formą.',
      UNKNOWN_YEAR: 'Nie mamy wskaźników podatkowych na ten rok.',
      BAD_DEBT_IN_INCOME: 'Dochód uwzględnia zapisane korekty złych długów w podatku dochodowym (art. 26i ustawy o PIT).',
      BAD_DEBT_NOT_IN_RYCZALT: 'Przy ryczałcie ulga na złe długi zmienia przychód według stawki (art. 11 ustawy o ryczałcie) — nie wiemy, do której stawki przypisać niezapłacony dokument, więc jej nie liczymy.',
    },
  },
  healthAnnual: {
    title: 'Roczna składka zdrowotna',
    deadline: (rok: number) => `rozliczenie w deklaracji za kwiecień — do 20 maja ${rok}`,
    income: 'Dochód roku',
    revenue: 'Przychód roku',
    months: 'Miesiące podlegania',
    basis: 'Podstawa roczna',
    rate: 'Stawka',
    due: 'Składka za rok',
    paid: 'Składki zapłacone w ciągu roku',
    toPay: 'Do dopłaty',
    toRefund: 'Nadpłata do zwrotu',
    forms: {
      SKALA: 'Skala podatkowa · 9% dochodu',
      LINIOWY: 'Podatek liniowy · 4,9% dochodu',
      RYCZALT: 'Ryczałt · próg przychodu',
      CIT: 'CIT',
    },
    notes: {
      ESTIMATE: 'Liczymy z dokumentów w Firmeto — to szacunek, a nie wypełniona deklaracja DRA.',
      MONTHLY_ASSUMED_PAID: 'Zakładamy, że składki policzone w ciągu roku zostały zapłacone.',
      BUSINESS_ONLY: 'Znamy tylko działalność. Jeśli masz inny tytuł do ubezpieczenia zdrowotnego — etat, drugą firmę, spółkę — rozliczenie obejmuje także jego.',
      MINIMUM_APPLIED: 'Wyszła składka minimalna: 9% minimalnego wynagrodzenia za każdy miesiąc podlegania. Przy niskim dochodzie płaci się tyle, a nie mniej — także przy podatku liniowym, gdzie minimum liczy się stawką 9%, a nie 4,9%.',
      RYCZALT_BRACKET_FROM_REVENUE: 'Przy ryczałcie próg ustala **rzeczywisty przychód roku**. Jeśli w trakcie roku płaciłeś według przychodu poprzedniego roku albo według innego progu, właśnie stąd bierze się różnica.',
      SOCIAL_NOT_DEDUCTED: 'Podstawy nie obniżamy o zapłacone składki społeczne — u nas są kosztem w księdze.',
      BAD_DEBT_NOT_IN_BASIS: 'Korekty złych długów zmieniają dochód podatkowy, ale podstawy zdrowotnej nie ruszamy.',
      INVENTORY_MISSING: 'Brakuje zatwierdzonego remanentu, więc dochód roczny może być nieprawdziwy — a z nim składka.',
      FULL_BOOKS_NOT_SUPPORTED: 'Przy pełnych księgach dochód roczny ustala się z ksiąg; tego jeszcze nie liczymy.',
      FORM_CHANGED: 'Forma opodatkowania zmieniła się w trakcie roku. Wtedy rozliczenie roczne robi się osobno dla każdej formy, a my liczymy jedną — sprawdź tę kwotę z księgową.',
      PARTNERS_SPLIT: 'Spółka osobowa: każdy wspólnik rozlicza składkę sam, od swojej części dochodu.',
      UNKNOWN_YEAR: 'Nie mamy wskaźników ZUS na ten rok.',
    },
  },
  suspensions: {
    title: 'Zawieszenie działalności',
    lead: 'Zawieszenie zgłasza się w CEIDG i trwa co najmniej 30 dni (art. 22–25 ustawy Prawo przedsiębiorców). Nie zamyka firmy: NIP, księgi i niezapłacone faktury zostają. Zmienia natomiast składki, terminy i amortyzację, dlatego trzymamy tu okresy, a nie przełącznik.',
    none: 'Nie ma zapisanego zawieszenia.',
    noneHint: 'Wpisz okres, w którym działalność była zawieszona — wtedy za te miesiące nie policzymy składek ani zaliczki, a odpisy amortyzacyjne się zatrzymają.',
    add: 'Wpisz zawieszenie',
    save: 'Zapisz',
    cancel: 'Anuluj',
    startsOn: 'Pierwszy dzień zawieszenia',
    endsOn: 'Ostatni dzień zawieszenia',
    endsOnHint: 'Puste znaczy „nadal zawieszona" — w CEIDG zawieszenie nie ma górnej granicy czasu. Datę z przyszłości też można wpisać: zawieszenie zgłasza się z wyprzedzeniem.',
    note: 'Powód',
    noteHint: 'Na przykład „opieka nad dzieckiem", „przerwa sezonowa". Przy kontroli tłumaczy, dlaczego za te miesiące nie ma składek.',
    days: 'Dni',
    open: 'nadal zawieszona',
    tooShort: 'krócej niż 30 dni',
    resume: 'Podjęcie działalności',
    resumePrompt: 'Ostatni dzień zawieszenia (dzień przed podjęciem działalności):',
    remove: 'Usuń zawieszenie',
    removeReason: 'Usunięcie zmieni składki i odpisy za tamte miesiące. Podaj powód (np. pomyłka w dacie):',
    effects: 'Co z tego wynika: za miesiąc zawieszony w całości nie ma składek ani zaliczki na podatek, a za miesiąc zawieszony częściowo podstawa społecznych maleje proporcjonalnie do dni (zdrowotna zostaje cała). Odpisy amortyzacyjne wstrzymują się od miesiąca następującego po zawieszeniu. Dni zawieszenia nie liczą się do dni prowadzenia działalności przy Małym ZUS Plus. Plik JPK_V7 może być potrzebny nawet w zawieszeniu — przy nabyciu towarów z UE, imporcie usług i korektach.',
  },
  inventory: {
    lead: 'Remanent początkowy i końcowy wchodzą do wyliczenia dochodu za cały rok, nie za miesiąc.',
    noneHint:
      'Spis sporządza się na pierwszy i ostatni dzień roku. Przy braku towarów handlowych i materiałów robi się go z wartością zerową — ale zrobić go trzeba.',
    items: 'Pozycje spisu',
    open: 'Remanent',
    title: 'Spis z natury i dochód roczny',
    year: 'Rok',
    none: 'W tym roku nie ma jeszcze żadnego spisu.',
    add: 'Nowy spis',
    save: 'Zapisz spis',
    approve: 'Zatwierdź',
    delete: 'Usuń',
    kind: 'Rodzaj',
    takenOn: 'Data spisu',
    value: 'Wartość',
    status: 'Stan',
    itemName: 'Nazwa',
    quantity: 'Ilość',
    unit: 'Jm',
    unitPrice: 'Cena',
    revenue: 'Przychód',
    purchases: 'Zakupy towarów',
    opening: 'Remanent początkowy',
    closing: 'Remanent końcowy',
    costOfGoodsSold: 'Wartość sprzedanych towarów',
    income: 'Dochód',
    kinds: { OPENING: 'początkowy', CLOSING: 'końcowy', INTERIM: 'w trakcie roku' },
    statuses: { DRAFT: 'szkic', APPROVED: 'zatwierdzony' },
    warnings: {
      NO_OPENING_INVENTORY: 'Brak zatwierdzonego remanentu początkowego — dochód może być zawyżony.',
      NO_CLOSING_INVENTORY: 'Brak zatwierdzonego remanentu końcowego — towar na półce liczy się dziś jako koszt, więc dochód jest zaniżony.',
      DRAFT_INVENTORY: 'Jest spis w wersji roboczej. Do wyliczenia dochodu bierzemy tylko zatwierdzone.',
      NOT_KPIR: 'Ta firma nie prowadzi KPiR — dochód ustala się dla niej inaczej.',
    },
  },
  verification: {
    notice: 'Potwierdź adres e-mail kodem, który wysłaliśmy. Bez tego nie założysz firmy.',
    code: 'Kod z wiadomości',
    confirm: 'Potwierdź',
    resend: 'Wyślij kod ponownie',
    resent: 'Kod wysłany.',
  },
  badDebt: {
    title: 'Ulga na złe długi',
    lead: 'Niezapłacona faktura po 90 dniach od terminu płatności zmienia VAT po obu stronach. Nabywca **musi** zmniejszyć podatek naliczony (art. 89b ust. 1 ustawy o VAT) i zwiększyć go po zapłacie; sprzedawca **może** skorygować podatek należny (art. 89a ust. 1). Korekta należy do okresu, w którym minął dziewięćdziesiąty dzień.',
    none: 'W tym okresie nie ma czego korygować.',
    noneHint: 'Pokazujemy tu faktury, którym minęło 90 dni od terminu płatności, i te zapłacone po korekcie. Żadna z nich nie czeka na decyzję.',
    purchases: 'Zakupy — korekta obowiązkowa',
    purchasesHint: 'Za te faktury firma nie zapłaciła w ciągu 90 dni od terminu. Podatek naliczony trzeba zmniejszyć — pominięcie zawyża odliczenie.',
    sales: 'Sprzedaż — ulga do rozważenia',
    salesHint: 'Za te faktury nie zapłacił klient. Ulga jest prawem, nie obowiązkiem, i ma warunki, których z naszych danych nie widać (np. czy dłużnik nie jest w upadłości) — decyzja należy do Ciebie.',
    restorations: 'Zapłacone po korekcie — podatek wraca',
    restorationsHint: 'Dług został zapłacony, więc skorygowany podatek wraca w okresie zapłaty (art. 89b ust. 4 i art. 89a ust. 4). Przy zapłacie części długu wraca część podatku.',
    document: 'Dokument',
    counterparty: 'Kontrahent',
    dueDate: 'Termin płatności',
    ninetiethDay: '90. dzień',
    unpaid: 'Niezapłacone',
    vat: 'VAT korekty',
    pick: 'Zaznacz',
    apply: 'Zapisz korekty',
    applyHint: 'Zapisane korekty wejdą do pliku JPK za właściwy okres.',
    skip: 'Nie koryguję',
    skipReason: 'Dlaczego nie korygujesz? Powód zostanie zapisany (np. dłużnik w restrukturyzacji, art. 89b ust. 1b):',
    late: 'korekta należy do',
    recorded: 'Zapisane w tym okresie',
    kind: 'Rodzaj',
    reason: 'Powód',
    undo: 'Wycofaj decyzję',
    kinds: { DECREASE: 'zmniejszenie podatku', INCREASE: 'powrót po zapłacie', SKIPPED: 'bez korekty' },
    taxes: 'Podatek',
    taxNames: { VAT: 'VAT', INCOME: 'Podatek dochodowy' },
    income: {
      lead: 'Ta sama niezapłacona faktura zmienia też podatek dochodowy. Dłużnik **musi** doliczyć niezapłacony koszt do podstawy (art. 18f ust. 1 pkt 2 ustawy o CIT, art. 26i ust. 1 pkt 2 ustawy o PIT), wierzyciel **może** odjąć od niej niezapłacony przychód (ust. 1 pkt 1). Kwoty są netto, a decyzja nie idzie do żadnego pliku — wchodzi do zaliczki i do rozliczenia rocznego.',
      purchasesHint: 'Za te faktury firma nie zapłaciła w ciągu 90 dni od terminu. Niezapłacony koszt trzeba doliczyć do dochodu — pominięcie zaniża zaliczkę.',
      salesHint: 'Za te faktury nie zapłacił klient. Niezapłacony przychód można wyjąć z dochodu; to prawo, nie obowiązek, i ma warunki, których z naszych danych nie widać (np. czy dłużnik nie jest w upadłości).',
      restorationsHint: 'Dług został zapłacony, więc korekta wraca w okresie zapłaty: dłużnik odzyskuje koszt, wierzyciel wykazuje przychód. Przy zapłacie części długu wraca część kwoty.',
      amount: 'Kwota netto',
      applyHint: 'Zapisane korekty zmienią zaliczkę na podatek dochodowy i rozliczenie roczne. Do pliku JPK nie wchodzą.',
      skipReason: 'Dlaczego nie korygujesz? Powód zostanie zapisany (np. dłużnik w upadłości albo wierzytelność już odpisana):',
    },
  },
  ledger: {
    ordinal: 'Lp.',
    entries: 'Zapisy w księdze',
    columnsNote: 'kolumny zgodne z rozporządzeniem o KPiR',
    revenueColumn: 'kolumna 7 — sprzedaż towarów i usług',
    open: 'Księga',
    title: 'Księga i rejestry VAT',
    month: 'Miesiąc',
    date: 'Data',
    document: 'Dokument',
    description: 'Opis',
    counterparty: 'Kontrahent',
    column: 'Kol.',
    amount: 'Kwota',
    none: 'Brak zapisów w tym miesiącu.',
    revenue: 'Przychód',
    costsTotal: 'Koszty',
    result: 'Dochód',
    vatTitle: 'VAT za okres',
    vatDue: 'Należny',
    vatDeductible: 'Naliczony',
    vatToPay: 'Do zapłaty',
    vatToCarry: 'Nadwyżka do przeniesienia',
    quarterly: 'rozliczenie kwartalne',
  },
  office: {
    served: 'Obsługiwana',
    awaitingConsent: 'Czeka na zgodę firmy',
    tab: 'Biuro',
    companiesTab: 'Moje firmy',
    title: 'Panel biura',
    none: 'Nie pracujesz jeszcze w żadnym biurze rachunkowym.',
    subtitle: 'Miejsce pracy biura rachunkowego: firmy klientów, kroki miesiąca, przydział księgowych.',
    noneHint:
      'Załóż biuro, jeśli obsługujesz księgowo cudze firmy. Jeśli ktoś ma Cię do biura dodać, poproś o zaproszenie — pojawi się na Twoim adresie e-mail.',
    create: 'Załóż biuro',
    name: 'Nazwa biura',
    nip: 'NIP biura',
    month: 'Miesiąc',
    onlyMine: 'Tylko moi klienci',
    client: 'Klient',
    assigned: 'Prowadzi',
    assignments: 'Przydziały księgowych',
    assignmentsHint: 'Umowa mówi, że biuro obsługuje firmę. Dopiero przydział mówi, kto z biura ma wejść '
      + 'do jej ksiąg — i do jednego klienta może ich być kilku.',
    assignmentRoles: { LEAD: 'prowadząca', BACKUP: 'zastępstwo', VIEWER: 'podgląd' },
    assignmentRole: 'Rola przy kliencie',
    assignPerson: 'Wybierz osobę',
    assign: 'Przydziel',
    endAssignment: 'Zakończ przydział',
    nobodyAssigned: 'Nikt jeszcze nie przydzielony',
    noServedClients: 'Żadna firma nie przyjęła jeszcze propozycji obsługi.',
    invoices: 'Faktury',
    costs: 'Koszty',
    revenue: 'Przychód',
    costsAmount: 'Koszty',
    state: 'Stan',
    ready: 'gotowa do rozliczenia',
    toDo: 'do zrobienia',
    noClients: 'Biuro nie ma jeszcze klientów.',
    members: 'Zespół',
    clients: 'Klienci',
    role: 'Rola',
    roles: {
      OFFICE_OWNER: 'Właściciel biura',
      OFFICE_ADMIN: 'Administrator',
      ACCOUNTANT: 'Księgowa',
      ASSISTANT: 'Asystent',
    },
  },
  invoices: {
    openInvoice: 'Otwórz',
    backToList: 'Wróć do listy',
    detail: {
      preview: 'Podgląd faktury',
      previewOpen: 'Otwórz PDF',
      pdf: 'Pobierz PDF',
      draftNoDocument: 'Szkic nie ma jeszcze numeru — dokument powstanie po zatwierdzeniu.',
      payments: 'Zapłaty',
      due: 'Do zapłaty',
      paid: 'Zapłacono',
      remaining: 'Pozostało',
      addPayment: 'Zapisz zapłatę',
      paidOn: 'Data zapłaty',
      amount: 'Kwota',
      exchangeRate: 'Kurs z dnia zapłaty',
      exchangeRateHint: 'Dokument jest w walucie obcej: podaj średni kurs NBP z ostatniego dnia roboczego '
        + 'przed dniem zapłaty. Z różnicy wobec kursu z faktury powstaje przychód albo koszt finansowy.',
      method: 'Sposób',
      note: 'Uwaga',
      noPayments: 'Nie ma jeszcze żadnej zapłaty.',
      partialHint: 'Kwota mniejsza od pozostałej to wpłata częściowa: rozliczy tyle, ile wpłacono, '
        + 'a reszta zostanie w rozrachunkach.',
      deletePayment: 'Usuń',
      confirmDeletePayment: (kwota: string) => `Usunąć zapłatę ${kwota}? Wróci do rozrachunków jako nierozliczona.`,
      internalNote: 'Notatka wewnętrzna',
      internalNoteHint: 'Notatka zostaje w firmie: nie ma jej na wydruku faktury ani w pliku KSeF. '
        + 'Dlatego wolno ją dopisać także do faktury już wystawionej.',
      saveNote: 'Zapisz notatkę',
      noteSaved: 'Notatka zapisana.',
    },
    /** Etykieta kolumny i wiersza podsumowania; `gross` to to samo słowo do wnętrza zdania. */
    grossAmount: 'Brutto',
    netAmount: 'Netto',
    open: 'Faktury',
    title: 'Faktury sprzedaży',
    tabAll: 'Wszystkie',
    tabUnpaid: 'Nieopłacone',
    tabCorrections: 'Korekty',
    filter: 'Filtruj: numer, kontrahent, kwota',
    allYears: 'Wszystkie lata',
    payment: 'Płatność',
    soon: 'stan przyszły — nie ma w kodzie',
    ksefFutureTitle: 'Wysyłka do KSeF',
    ksefFutureText:
      'Dziś działa wyłącznie „Plik do KSeF": pobranie dokumentu w formacie FA(3), który wysyłasz sam. Gdy powstanie wysyłka przez bramkę, w kolumnie „Stan" pojawi się numer referencyjny KSeF i potwierdzenie przyjęcia.',
    new: 'Nowa faktura',
    none: 'Brak faktur.',
    number: 'Numer',
    status: 'Stan',
    issueDate: 'Data wystawienia',
    saleDate: 'Data sprzedaży',
    dueDate: 'Termin płatności',
    counterparty: 'Nabywca',
    viesTitle: 'Numer VAT-UE:',
    viesCheck: 'Sprawdź w VIES',
    viesWarning:
      'Przy stawce 0% albo „np” dla kontrahenta z Unii prawo do niej zależy od ważnego numeru VAT-UE (art. 42 ust. 1 pkt 1 ustawy o VAT). Sprawdź numer albo miej inny dowód — fakturę wystawisz tak czy inaczej.',
    chooseCounterparty: 'Wybierz kontrahenta',
    priceMode: 'Ceny podawane',
    lines: 'Pozycje',
    addLine: 'Dodaj pozycję',
    removeLine: 'Usuń',
    lineName: 'Nazwa',
    quantity: 'Ilość',
    unit: 'Jm',
    unitPrice: 'Cena',
    netUnitPrice: 'Cena netto',
    grossUnitPrice: 'Cena brutto',
    vatAmount: 'Kwota VAT',
    vatTotal: 'VAT razem',
    vatRate: 'VAT',
    ryczaltRate: 'Ryczałt',
    correct: 'Korekta',
    ksefFile: 'Plik do KSeF',
    correction: 'korekta',
    correctionTo: 'Faktura korygująca do',
    correctionHint: 'Podaj pozycje takie, jakie mają być po korekcie. Różnicę policzymy sami.',
    correctionCause: 'Przyczyna',
    correctionReason: 'Opis przyczyny',
    linesAfter: 'Pozycje po korekcie',
    causes: {
      MISTAKE: 'Pomyłka na fakturze pierwotnej',
      EVENT: 'Zdarzenie po wystawieniu (rabat, zwrot)',
    },
    amount: 'Wartość',
    fromCatalog: 'Z kartoteki',
    addToCatalog: 'Dodaj do kartoteki',
    editDraft: 'Edytuj szkic',
    draftUnavailable: 'To nie jest już szkic. Odśwież listę faktur.',
    noMatches: 'Brak wyników',
    searchFailed: 'Nie udało się pobrać wyników. Spróbuj ponownie.',
    refineSearch: 'Więcej wyników — wpisz dokładniejszą frazę.',
    noCatalog: 'Bez kartoteki',
    saveDraft: 'Zapisz szkic',
    issue: 'Zatwierdź i nadaj numer',
    delete: 'Usuń szkic',
    totals: 'Razem',
    net: 'netto',
    vat: 'VAT',
    gross: 'brutto',
    draftHint: 'Szkic możesz poprawiać dowolnie. Po zatwierdzeniu faktura dostaje numer i nie da się jej zmienić — poprawkę wprowadza się korektą.',
    statuses: { DRAFT: 'Szkic', ISSUED: 'Wystawiona', CANCELLED: 'Anulowana' },
    priceModes: { NET: 'od netto', GROSS: 'od brutto' },
  },
  bank: {
    checksumOk: 'Suma kontrolna poprawna',
    whiteListTitle: 'Biała lista podatników VAT',
    whiteListText:
      'Docelowo przy zapisie sprawdzimy, czy rachunek widnieje na wykazie Ministerstwa Finansów — dla płatności powyżej 15 000 zł ma to skutki podatkowe. Dziś sprawdzamy tylko sumę kontrolną numeru.',
    open: 'Rachunki',
    title: 'Rachunki bankowe firmy',
    iban: 'Numer rachunku',
    currency: 'Waluta',
    purpose: 'Przeznaczenie',
    bankName: 'Bank',
    add: 'Dodaj rachunek',
    save: 'Zapisz rachunek',
    none: 'Brak rachunków.',
    remove: 'Usuń z listy',
    hint: 'Numer podstawowego rachunku trafia na faktury. Sprawdzamy sumę kontrolną przy wpisaniu.',
    purposes: { MAIN: 'Podstawowy', VAT: 'Rachunek VAT', TAX_REFUND: 'Do zwrotu podatku' },
  },
  numbering: {
    lead: 'Numerację można zmieniać, dopóki w danym roku nie wyszedł żaden dokument danego typu.',
    markers: 'Znaczniki wzorca',
    issued: 'Wystawiono',
    locked: 'Zablokowany — wyszły dokumenty',
    changeable: 'Można zmieniać',
    open: 'Numeracja',
    title: 'Numeracja dokumentów',
    document: 'Dokument',
    pattern: 'Wzorzec',
    next: 'Następny numer',
    save: 'Zapisz wzorzec',
    reset: 'Sposób',
    resetYear: 'Roczna',
    resetMonth: 'Miesięczna',
    hint: 'W miejsce {nr} wchodzi kolejny numer, {rok} to rok, {rr} dwie cyfry roku. Miesiąc: {mm} → 09, {m} → 9, {mr} → IX, {msc} → WRZ. Numeracja miesięczna zaczyna każdy miesiąc od jedynki, więc numer musi zawierać miesiąc.',
    types: { INVOICE: 'Faktura', CORRECTION: 'Korekta', PROFORMA: 'Proforma', RECEIPT: 'Rachunek' },
  },
  journal: {
    open: 'Dziennik',
    title: 'Dziennik',
    lead: 'Chronologiczna lista zapisów. Każde zdarzenie stoi po dwóch stronach: ta sama kwota '
      + 'po stronie Wn jednego konta i Ma drugiego.',
    view: 'Widok',
    year: 'Rok obrotowy',
    month: 'Miesiąc',
    trialBalance: 'Obroty i salda',
    pending: 'Do zaksięgowania',
    pendingHint: 'Dokument nie księguje się sam. Zapis w dzienniku jest nieodwracalny — poprawka to '
      + 'storno — więc dekret widać tu w całości, zanim trafi do ksiąg.',
    nothingPending: 'Wszystko zaksięgowane.',
    postAll: 'Zaksięguj wszystkie',
    postPicked: (ile: number) => `Zaksięguj zaznaczone (${ile})`,
    trialBalanceHint: 'Bilans otwarcia stoi osobno od obrotów roku — to stan przeniesiony, '
      + 'a nie zdarzenie, które w tym roku zaszło.',
    newEntry: 'Nowy zapis',
    formHint: 'Numer konta bierze się z planu kont. Księguje się na koncie najniższego poziomu, '
      + 'a konto rozrachunkowe wymaga kontrahenta.',
    entryNo: 'Nr',
    postingDate: 'Data księgowania',
    description: 'Opis',
    lines: 'Wiersze',
    account: 'Konto',
    name: 'Nazwa',
    side: 'Strona',
    amount: 'Kwota',
    debit: 'Wn',
    credit: 'Ma',
    addLine: 'Dodaj wiersz',
    removeLine: 'Usuń',
    balanced: 'Bilansuje się',
    difference: 'Różnica',
    save: 'Zapisz zapis',
    cancel: 'Anuluj',
    empty: 'W tym okresie nie ma jeszcze żadnego zapisu.',
    opening: 'bilans otwarcia',
    reversal: 'storno',
    reverse: 'Storno',
    reverseTitle: 'Storno zamiast poprawki',
    reverseHint: 'Zapisu się nie zmienia. Storno odwraca strony nowym zapisem, a oryginał zostaje — '
      + 'dzięki temu widać nie tylko stan końcowy, ale i to, co się po drodze wydarzyło.',
    reverseReason: 'Powód storna',
    openingDebit: 'BO Wn',
    openingCredit: 'BO Ma',
    turnoverDebit: 'Obroty Wn',
    turnoverCredit: 'Obroty Ma',
    closingDebit: 'Saldo Wn',
    closingCredit: 'Saldo Ma',
    totals: 'Razem',
    controlOk: 'Kontrola się zgadza',
    controlFailed: 'Kontrola się nie zgadza',
    offBalanceNote: 'Konta pozabilansowe są w wierszach, ale poza sumami kontrolnymi: zapis '
      + 'pozabilansowy jest jednostronny i nigdy się nie zbilansuje.',
    entryCount: (count: number) => `Zapisów: ${count}`,
    fx: {
      tab: 'Wycena walut',
      title: 'Wycena walut na dzień',
      lead: 'Na dzień bilansowy środki pieniężne i rozrachunki w walutach obcych wycenia się po kursie z tego dnia (art. 30 ust. 1 ustawy o rachunkowości) — inaczej bilans pokazuje wartość, której już nie ma. Kursów nie podpowiadamy: wpisz średni kurs NBP obowiązujący na dzień wyceny.',
      asOf: 'Dzień wyceny',
      rate: 'Kurs',
      item: 'Pozycja',
      account: 'Konto',
      balanceCurrency: 'Saldo w walucie',
      balance: 'W księgach',
      revalued: 'Po wycenie',
      difference: 'Różnica',
      gain: 'Przychody finansowe',
      loss: 'Koszty finansowe',
      preview: 'Przelicz',
      post: 'Zaksięguj wycenę',
      posted: 'Wycena zaksięgowana — zapis nr',
      nothingToPost: 'Kursy się nie zmieniły, więc nie ma czego księgować.',
      exists: 'Wycena na ten dzień już jest w księgach — zapis nr',
      none: 'Nie ma czego wyceniać.',
      noneHint: 'Wycena dotyczy rachunków walutowych i otwartych rozrachunków w walucie obcej. Na ten dzień firma nie ma ani jednego z nich.',
      taxNote:
        'Różnice z wyceny są niezrealizowane. Jeśli firma ustala różnice kursowe metodą podatkową (art. 9b ust. 1 pkt 1 ustawy o CIT), nie są one przychodem ani kosztem podatkowym — a zaliczkę liczymy z ksiąg, więc dziś ich nie wyłączamy.',
      kinds: { ACCOUNT: 'rachunek walutowy', SETTLEMENT: 'rozrachunek' },
    },
    settlements: 'Rozrachunki',
    settlementsLead: 'Kto ile jest winien i od kiedy. Pozycja powstaje przy zaksięgowaniu dokumentu, wpłata z wyciągu ją rozlicza, a saldo liczy się z zapisów — nic nie jest trzymane osobno.',
    asOf: 'Na dzień',
    onlyOpen: 'Tylko nierozliczone',
    receivables: 'Należności',
    payables: 'Zobowiązania',
    bucket: {
      NOT_DUE: 'w terminie',
      DAYS_1_30: '1–30 dni',
      DAYS_31_60: '31–60 dni',
      DAYS_61_90: '61–90 dni',
      OVER_90: 'ponad 90 dni',
    } as Record<string, string>,
    total: 'Razem',
    document: 'Dokument',
    documentDate: 'Data',
    dueDate: 'Termin',
    settled: 'Rozliczono',
    remaining: 'Pozostało',
    overdue: (days: number) => `${days} dni po terminie`,
    noItems: 'Nie ma otwartych rozrachunków.',
    counterparty: 'Kontrahent',
    counterpartyNone: '— bez kontrahenta —',
    settlement: 'Rozrachunek',
    settlementNew: '— nowa pozycja —',
    offset: 'Kompensata',
    offsetHint: 'Należność i zobowiązanie tego samego kontrahenta znoszą się jednym zapisem — do wysokości mniejszej z nich albo mniej.',
    offsetReceivable: 'Należność',
    offsetPayable: 'Zobowiązanie',
    offsetAmount: 'Kwota',
    offsetDate: 'Data',
    offsetSave: 'Zapisz kompensatę',
    receivableLabel: 'należność',
    payableLabel: 'zobowiązanie',
    openingTitle: 'Bilans otwarcia z poprzedniego roku',
    openingLead: 'Salda kont bilansowych z ostatniego dnia poprzedniego roku, rozrachunki pozycjami z dokumentem i terminem, wynik poprzedniego roku na koncie wyniku lat ubiegłych — jednym zapisem technicznym.',
    openingPreview: 'Pokaż, co przejdzie',
    openingRecord: 'Zaksięguj bilans otwarcia',
    openingReplace: 'Zastąp: storno i nowy zapis',
    openingExists: 'Ten rok ma już bilans otwarcia. Zastąpienie wystornuje go i policzy od nowa ze stanu poprzedniego roku na dziś.',
    openingResult: 'Wynik poprzedniego roku',
    openingCounterparty: 'Kontrahent',
    openingDocument: 'Dokument',
    openingDone: 'Bilans otwarcia zaksięgowany.',
  },
  reports: {
    open: 'Sprawozdania',
    title: 'Bilans i rachunek zysków i strat',
    lead: 'Obie tabele liczą się z tych samych sald, co zestawienie obrotów — sprawozdanie nie ma '
      + 'jak powiedzieć czegoś innego niż księgi.',
    year: 'Rok obrotowy',
    month: 'Stan na koniec',
    balanceSheet: 'Bilans',
    incomeStatement: 'Rachunek zysków i strat',
    assets: 'Aktywa',
    liabilities: 'Pasywa',
    revenues: 'Przychody',
    costs: 'Koszty',
    account: 'Konto',
    name: 'Nazwa',
    amount: 'Kwota',
    total: 'Razem',
    result: 'Wynik roku',
    profit: 'Zysk',
    loss: 'Strata',
    resultNote: 'Wynik stoi po stronie pasywów — to on domyka równanie bilansu.',
    balanced: 'Bilans się domyka',
    notBalanced: 'Bilans się nie domyka',
    notBalancedHint: 'Aktywa nie równają się pasywom powiększonym o wynik. Zacznij od zestawienia '
      + 'obrotów i sald — to tam widać, które konto odstaje.',
    empty: 'W tym roku nie ma jeszcze żadnego zapisu, więc sprawozdanie jest puste.',
    goToJournal: 'Otwórz dziennik',
    statutoryNote: 'To układ według kont, a nie według załącznika do ustawy o rachunkowości. Kwoty '
      + 'i sumy są ostateczne; do wersji urzędowej brakuje wyłącznie przypisania kont do pozycji '
      + '„A. Aktywa trwałe", „B.II. Należności krótkoterminowe" i tak dalej.',
    settlementsNote: 'W układzie ustawowym rozrachunki są brutto — saldo per kontrahent, należności osobno od zobowiązań (zakaz kompensowania). Układ według kont pokazuje jedno saldo na konto.',
    previousYear: 'Poprzedni rok',
    currentYear: 'Rok bieżący',
    previousNoChart: 'Poprzedni rok nie ma planu kont, więc kolumna porównawcza jest pusta.',
    previousDifferentTemplate: 'Poprzedni rok był w innym załączniku — pozycje nie odpowiadają sobie, więc kolumna porównawcza w układzie ustawowym jest pusta.',
    layout: 'Układ',
    layoutStatutory: 'Według ustawy',
    layoutAccounts: 'Według kont',
    templates: { ZAL_1: 'Załącznik 1 — układ pełny', ZAL_4: 'Załącznik 4 — jednostka mikro', ZAL_5: 'Załącznik 5 — jednostka mała' },
    position: 'Pozycja',
    unmappedTitle: (ile: number) => ile === 1 ? 'Jedno konto z saldem nie ma pozycji w sprawozdaniu' : `${ile} kont z saldem nie ma pozycji w sprawozdaniu`,
    unmappedHint: 'Ich kwot nie ma w tabeli, więc sumy poniżej są niepełne. Przypisz pozycje w planie kont — dla salda Wn i osobno dla salda Ma.',
    unmappedGoToChart: 'Przypisz w planie kont',
    computedNote: 'Pozycje wytłuszczone są liczone z innych pozycji („Zysk (strata) ze sprzedaży (A − B)"), a pozycje „w tym" są częścią kwoty nadrzędnej, nie jej składnikiem.',
    noChart: 'Ten rok nie ma planu kont, więc nie ma czego zwinąć w układ ustawowy.',
    accountsNote: 'To układ według kont — odniesienie, w którym nic nie może zginąć. Układ ustawowy stoi obok.',
  },
  chart: {
    open: 'Plan kont',
    positionDebit: 'Pozycja sprawozdania — saldo Wn',
    positionCredit: 'Pozycja sprawozdania — saldo Ma',
    positionNone: '— nieprzypisane',
    positionHint: 'Konto aktywno-pasywne trafia w różne pozycje zależnie od strony salda. Konto bez pozycji sprawozdanie pokaże jako nieprzypisane.',
    sections: { ASSETS: 'Aktywa', LIABILITIES: 'Pasywa', INCOME: 'Rachunek zysków i strat' },
    title: 'Zakładowy plan kont',
    lead: 'Plan należy do roku obrotowego. Księguje się na kontach najniższego poziomu — konto '
      + 'z analityką jest sumą swoich kont analitycznych.',
    year: 'Rok obrotowy',
    empty: 'Ten rok nie ma jeszcze planu kont',
    emptyHint: 'Zacznij od wzorca albo skopiuj plan z poprzedniego roku i popraw, co trzeba.',
    create: 'Załóż plan kont',
    source: 'Skąd',
    sourceTemplate: 'Z wzorca',
    sourcePrevious: 'Kopia poprzedniego roku',
    sourceEmpty: 'Pusty',
    previewHint: (count: number) => `Tak będzie wyglądał plan — kont: ${count}. Po założeniu dodasz analitykę.`,
    previewEmpty: 'Pusty plan zakłada się wtedy, gdy żaden wzorzec nie pasuje. Konta dopiszesz sam.',
    template: 'Wzorzec',
    templateName: 'Nazwa wzorca',
    saveAsTemplate: 'Zapisz jako wzorzec',
    number: 'Konto',
    numberHint: 'Numer to trzycyfrowa syntetyka i analityka po myślniku, np. 402-1. Syntetyka musi '
      + 'istnieć, zanim założysz do niej analitykę.',
    name: 'Nazwa',
    kind: 'Rodzaj',
    side: 'Saldo',
    features: 'Cechy',
    tax: 'Podatkowo',
    role: 'Rola w księgowaniu',
    sum: 'Suma analityki',
    settlement: 'Rozrachunkowe',
    currency: 'Walutowe',
    inactive: 'wycofane',
    activeAccount: 'Podpowiadaj przy księgowaniu',
    add: 'Dodaj konto',
    edit: 'Zmień',
    delete: 'Usuń',
    save: 'Zapisz',
    cancel: 'Anuluj',
    noYear: 'Nie ma jeszcze roku obrotowego, a plan kont należy do roku.',
    goToPeriods: 'Przejdź do okresów',
    notFullBooks: 'Ta firma nie prowadzi pełnych ksiąg',
    booksUnknown: 'Nie wiemy jeszcze, jakie księgi prowadzi ta firma',
    booksUnknownHint: 'Brakuje ustawień podatkowych obowiązujących dziś. Plan kont zakłada się '
      + 'przy pełnych księgach — uzupełnij ustawienia, a pokażemy resztę.',
    notFullBooksHint: 'Plan kont jest narzędziem pełnej księgowości. Rodzaj ksiąg zmienia się w ustawieniach firmy.',
    onlyAccountant: 'Plan kont prowadzi księgowa',
    onlyAccountantHint: 'Przy pełnej księgowości księguje wyłącznie osoba z rolą Księgowa. '
      + 'Tutaj widzisz plan, ale go nie zmieniasz.',
    accountCount: (count: number) => `Kont w planie: ${count}`,
    kinds: { BALANCE: 'Bilansowe', RESULT: 'Wynikowe', OFFBALANCE: 'Pozabilansowe' },
    sides: { DEBIT: 'Aktywne', CREDIT: 'Pasywne', BOTH: 'Aktywno-pasywne', NONE: '—' },
    taxes: {
      DEDUCTIBLE: 'Koszt uzyskania',
      NON_DEDUCTIBLE: 'Nie stanowi kosztu',
      TAXABLE: 'Przychód podatkowy',
      NON_TAXABLE: 'Przychód niepodatkowy',
    },
    statements: { ZAL_1: 'Załącznik 1', ZAL_4: 'Załącznik 4 — jednostka mikro', ZAL_5: 'Załącznik 5 — jednostka mała' },
    variants: { BY_KIND: 'Rachunek porównawczy', BY_FUNCTION: 'Rachunek kalkulacyjny', BOTH: 'Oba warianty' },
    scopes: { SYSTEM: 'Firmeto', OFFICE: 'Biuro', COMPANY: 'Ta firma' },
    groups: {
      0: 'Aktywa trwałe',
      1: 'Środki pieniężne i rachunki bankowe',
      2: 'Rozrachunki i roszczenia',
      3: 'Materiały i towary',
      4: 'Koszty według rodzajów',
      5: 'Koszty według typów działalności',
      6: 'Produkty i rozliczenia międzyokresowe',
      7: 'Przychody i koszty ich osiągnięcia',
      8: 'Kapitały, rezerwy i wynik finansowy',
      9: 'Konta pozabilansowe',
    },
    roles: {
      RECEIVABLES: 'Rozrachunki z odbiorcami',
      PAYABLES: 'Rozrachunki z dostawcami',
      VAT_DUE: 'VAT należny',
      VAT_INPUT: 'VAT naliczony',
      VAT_SETTLEMENT: 'Rozliczenie VAT',
      SALES_SERVICES: 'Sprzedaż usług',
      SALES_GOODS: 'Sprzedaż towarów',
      COST_OF_GOODS: 'Wartość sprzedanych towarów',
      GOODS: 'Towary',
      COSTS_MATERIALS: 'Zużycie materiałów i energii',
      COSTS_SERVICES: 'Usługi obce',
      COSTS_TAXES: 'Podatki i opłaty',
      COSTS_SALARIES: 'Koszty wynagrodzeń',
      COSTS_SOCIAL: 'Ubezpieczenia społeczne i inne świadczenia',
      COSTS_OTHER: 'Pozostałe koszty rodzajowe',
      FIXED_ASSETS: 'Środki trwałe',
      INTANGIBLE_ASSETS: 'Wartości niematerialne i prawne',
      ACCUMULATED_DEPRECIATION: 'Umorzenie środków trwałych',
      ACCUMULATED_AMORTISATION: 'Umorzenie wartości niematerialnych',
      DEPRECIATION_COST: 'Amortyzacja (koszt)',
      CASH: 'Kasa',
      BANK: 'Rachunek bieżący',
      INCOME_TAX: 'Podatek dochodowy',
      SOCIAL_SECURITY: 'Rozrachunki z ZUS',
      PAYROLL: 'Wynagrodzenia',
      RESULT: 'Wynik finansowy',
      PRIOR_YEARS_RESULT: 'Wynik lat ubiegłych',
    },
  },
  vatUe: {
    title: 'Informacja VAT-UE',
    lead: 'Kto sprzedaje towary albo usługi podatnikom z Unii, składa informację podsumowującą — **co miesiąc, także przy kwartalnym rozliczeniu VAT** (art. 100 ustawy o VAT). Bez transakcji wewnątrzwspólnotowych informacji się nie składa.',
    none: 'W tym miesiącu nie ma transakcji wewnątrzwspólnotowych.',
    noneHint: 'Wchodzą tu faktury dla kontrahentów z Unii ze stawką „np" (usługi) albo 0% (dostawy towarów) oraz dokumenty kosztowe oznaczone jako nabycie wewnątrzwspólnotowe.',
    supplies: 'Dostawy towarów',
    acquisitions: 'Nabycia towarów',
    services: 'Świadczenie usług',
    counterparty: 'Kontrahent',
    country: 'Kraj',
    vatNumber: 'Numer VAT',
    amount: 'Wartość',
    download: 'Pobierz VAT-UE',
    vies: 'VIES',
    viesValid: 'ważny',
    viesInvalid: 'nieważny',
    viesUnknown: 'nie sprawdzono',
    checkVies: 'Sprawdź numery w VIES',
    viesUnavailable: 'Rejestr nie odpowiedział dla numerów:',
    problems: 'Pozycje, których nie da się wykazać',
    notes: {
      TRIANGULAR_NOT_DETECTED: 'Transakcji trójstronnych nie rozpoznajemy — w każdej pozycji stoi „nie dotyczy”.',
      VIES_ON_DEMAND: 'Numery sprawdzasz w VIES przyciskiem obok — wynik zapisuje się z datą, bo to on jest dowodem należytej staranności.',
      CALL_OFF_NOT_SUPPORTED: 'Przemieszczeń w procedurze magazynu call-off nie wykazujemy.',
      MONTHLY_ALWAYS: 'Informację składa się co miesiąc, niezależnie od tego, czy VAT rozliczasz miesięcznie, czy kwartalnie.',
    },
  },
  officeJpk: {
    title: 'JPK klientów za miesiąc',
    lead: 'Przygotuj pliki wszystkich klientów naraz, pobierz paczkę plików metadanych, podpisz je podpisem biura (pełnomocnictwo UPL-1) i wgraj podpisane pliki — Firmeto dopasuje je po nazwie i wyśle.',
    prepare: 'Przygotuj dla klientów',
    download: 'Pobierz paczkę do podpisu',
    upload: 'Wgraj podpisane pliki',
    problems: 'Nie przygotowano',
    sent: (count: number) => `Wysłano: ${count}`,
  },
  jpkSend: {
    title: 'Wysyłka do Ministerstwa Finansów',
    personLead: 'Jako osoba fizyczna podpisujesz plik kwotą przychodu z zeznania podatkowego sprzed dwóch lat (np. w 2026 — z zeznania za 2024). Imię, nazwisko i datę urodzenia bierzemy z danych do deklaracji. Kwoty nie zapisujemy.',
    companyLead: 'Spółka podpisuje plik metadanych podpisem kwalifikowanym albo zaufanym. Przygotuj wysyłkę, pobierz plik, podpisz go (np. na podpis.gov.pl, w formacie XAdES) i wgraj podpisany plik tutaj.',
    income: 'Przychód z zeznania sprzed dwóch lat (zł)',
    send: 'Wyślij do urzędu',
    prepare: 'Przygotuj do podpisu',
    downloadToSign: 'Pobierz plik do podpisu',
    uploadSigned: 'Wgraj podpisany plik',
    history: 'Wysyłki',
    none: 'Ten plik nie był jeszcze wysyłany.',
    reference: 'Numer referencyjny',
    upo: 'Pobierz UPO',
    test: 'środowisko testowe',
    statuses: { AWAITING_SIGNATURE: 'czeka na podpis', SENDING: 'wysyłanie', PROCESSING: 'urząd przetwarza', ACCEPTED: 'przyjęty, jest UPO', REJECTED: 'odrzucony', FAILED: 'nie wysłano' } as Record<string, string>,
  },
  jpk: {
    goToFiling: 'Przejdź do danych do deklaracji',
    title: 'Pliki JPK',
    lead: 'JPK_V7M za wybrany miesiąc — ewidencja VAT razem z deklaracją. Plik możesz pobrać albo wysłać do urzędu stąd.',
    leadQuarterly:
      'JPK_V7K za wybrany miesiąc. Przy rozliczeniu kwartalnym ewidencję składa się co miesiąc, a deklarację raz na kwartał: plik za pierwszy i drugi miesiąc kwartału niesie samą ewidencję, a plik za trzeci — ewidencję tego miesiąca i deklarację za cały kwartał. Plik podpisujesz i wysyłasz własnym narzędziem.',
    period: 'Miesiąc',
    download: 'Pobierz JPK_V7M',
    downloadQuarterly: 'Pobierz JPK_V7K',
    beforeDownload: 'Zanim pobierzesz plik, sprawdź dane do deklaracji — bez kodu urzędu skarbowego plik nie powstanie.',
  },
  filing: {
    title: 'Dane do deklaracji',
    lead: 'Potrzebne wyłącznie do plików JPK i deklaracji. Do wystawienia faktury ani do policzenia podatku nie są używane.',
    taxOffice: 'Urząd skarbowy',
    taxOfficeHint: 'Wpisz miasto albo fragment nazwy i wybierz urząd z listy. Zapisujemy czterocyfrowy kod, którego wymagają JPK i deklaracje.',
    taxOfficeSearch: 'Miasto albo nazwa urzędu',
    taxOfficeNone: 'Żaden urząd nie pasuje. Spróbuj samą nazwą miasta.',
    taxOfficeSpecialised: 'dla największych podatników',
    taxOfficeSpecialisedHint: 'Urzędy wyspecjalizowane obsługują podatników o przychodach powyżej 50 mln euro, banki i grupy kapitałowe. Jeśli to nie Twoja firma, wybierz zwykły urząd dla swojej gminy.',
    taxOfficeClear: 'Wyczyść',
    firstName: 'Imię podatnika',
    lastName: 'Nazwisko podatnika',
    birthDate: 'Data urodzenia',
    personHint: 'Jednoosobowa działalność przedstawia się w JPK imieniem, nazwiskiem i datą urodzenia; spółka samą nazwą.',
    email: 'Adres e-mail do deklaracji',
    phone: 'Telefon kontaktowy',
    zusInsuranceTitleCode: 'Kod tytułu ubezpieczenia (ZUS DRA)',
    zusInsuranceTitleCodeHint:
      'Sześć cyfr z deklaracji ZUS: cztery cyfry kodu, prawo do emerytury lub renty, stopień niepełnosprawności. Przy pełnych składkach zwykle 051000, przy preferencyjnych 057000 — ale kodu nie wpisujemy za Ciebie, bo przy Małym ZUS Plus i uldze na start słownik ZUS-u nie jest jednoznaczny.',
    save: 'Zapisz dane',
    saved: 'Zapisane.',
  },
  invitation: {
    title: 'Zaproszenie do firmy',
    lead: (who: string) => `${who} zaprasza Cię do obsługi firmy w Firmeto.`,
    invitedBy: 'Zaprasza',
    invitesAs: 'zaprasza jako',
    company: 'Firma',
    forEmail: 'Dla adresu',
    roleScope: {
      OWNER: 'Pełny dostęp do firmy, w tym do rozliczeń z Firmeto i zmiany dostępów.',
      ADMIN: 'Dokumenty, ustawienia i dostępy. Bez zmiany właściciela firmy.',
      ACCOUNTANT: 'Księgowanie i rozliczenia podatkowe, pełny podgląd dokumentów. Bez dostępu do rozliczeń z Firmeto.',
      EMPLOYEE: 'Wystawianie faktur i wpisywanie kosztów. Bez wglądu w podatki i rozliczenia.',
      BOARD_MEMBER: 'Podgląd dokumentów i wyników spółki. Bez wprowadzania zmian.',
      VIEWER: 'Sam podgląd — bez wprowadzania i zmieniania czegokolwiek.',
    },
    accept: 'Przyjmij zaproszenie',
    signInToAccept: 'Zaloguj się, aby przyjąć',
    expires: (date: string) => `Zaproszenie wygasa ${date}. Przyjęcie zapisujemy w dzienniku zmian firmy.`,
    accepted: 'Zaproszenie przyjęte. Firma jest już na Twojej liście.',
    back: 'Przejdź do firm',
  },
};

// Kształt polskiego słownika wyznacza typ — brakujące hasło w innym języku nie przejdzie kompilacji
export type Dictionary = typeof pl;

const en: Dictionary = {
  documentNotifications: {
    enable: 'Enable document sound',
    mute: 'Mute document sound',
    blocked: 'Sound needs activation in this browser.',
    unsupported: 'This browser does not support notification sound.',
    ready: 'New document sound enabled',
    muted: 'Document sound muted',
  },
  appName: 'Firmeto',
  language: 'Language',
  signIn: 'Sign in',
  signOut: 'Sign out',
  signingIn: 'Signing in…',
  loading: 'Loading…',

  shell: {
    navigation: 'Company navigation',
    sales: 'Sales',
    purchases: 'Purchases',
    books: 'Books',
    settlements: 'Settlements',
    setup: 'Setup',
    density: 'Density',
    comfortable: 'Comfortable',
    dense: 'Dense',
    back: 'Back to companies',
  },
  start: {
    kicker: 'Get started',
    claim: 'No revenue — no fee.',
    lead: 'Online accounting for small businesses and accounting offices in Poland. Invoices, taxes, social security and deadlines in one place.',
    promises: (trialMonths: number, capPercent: number) => [
      'In a month without revenue the fee is zero',
      `The fee never exceeds ${capPercent}% of revenue`,
      trialMonths === 1
        ? 'First month free, no fixed-term contract'
        : `First ${trialMonths} months free, no fixed-term contract`,
    ],
    euServers: 'Servers in the European Union',
    whoAreYou: 'Who are you?',
    whoAreYouLead: 'This sets what you see after signing in. You can change it later.',
    paths: {
      company: {
        title: 'I run a business',
        lead: 'Sole trader or a company. Invoices, costs, taxes and deadlines.',
      },
      office: {
        title: 'I run an accounting office',
        lead: 'A panel with client companies, monthly steps and accountant assignment.',
      },
      founding: {
        title: 'I am starting a business',
        lead: 'We will help you pick a taxation form and walk you through the first settlement.',
      },
    },
    createAccount: 'Create an account',
    haveAccount: 'I already have an account — sign in',
    invitationHint: 'Invited by an accounting office? Open the link from the message.',
  },
  register: {
    claim: 'Three steps to your first invoice.',
    stepNames: ['Account', 'Company', 'Settlements'],
    euData: 'Your data stays in the European Union',
    backToStart: 'Back to the start',
    signIn: 'Sign in',
    signInToContinue: 'A company is created while signed in. Sign in with the password you have just set.',
    codeSent: 'We have sent a six-digit code to your address. It is valid for 24 hours.',
    codeResent: 'Code sent again.',
    account: {
      title: 'Create an account',
      lead: 'Takes less than a minute. We do not need company details yet.',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'E-mail address',
      emailHint: 'We will send the confirmation and deadline reminders to this address.',
      password: 'Password',
      passwordHint: 'At least 12 characters.',
      passwordRules: {
        length: 'At least 12 characters',
        notEmail: 'Different from your e-mail address',
        maxLength: 'At most 128 characters',
      },
      showPassword: 'Show',
      hidePassword: 'Hide',
      generate: 'Generate',
      generated: 'The password is in both fields and copied to the clipboard. Save it in your password manager.',
      repeatPassword: 'Repeat the password',
      passwordsDiffer: 'The passwords are not the same.',
      uiLocale: 'Interface language',
      uiLocaleHint: 'The language belongs to your account, not to the company. Change it above.',
      terms: 'I accept the terms of service and the privacy policy. Accounting data is processed only in the European Union.',
      lawUpdates: 'Send me updates about changes in the law affecting my taxation form.',
      next: 'Next — confirm your address',
    },
    code: {
      title: 'Confirm your address',
      lead: (email: string) =>
        `Enter the code we sent to ${email}. Without a confirmed address you cannot create a company.`,
      label: 'Code from the message',
      hint: 'The code is valid for 24 hours.',
      confirm: 'Confirm and sign in',
      resend: 'Send the code again',
    },
    company: {
      title: 'Company details',
      lead: 'The tax number identifies the company in registries and on invoices — the rest can be corrected later.',
      nip: 'Tax number (NIP)',
      nipLength: (digits: number) => `A NIP has 10 digits. You entered ${digits}.`,
      noRegistryTitle: 'You fill the details in yourself',
      noRegistryLead:
        'The lookup in the national business registry is not connected yet. Until it is, type the name and address by hand — we will verify them against the registry later.',
      legalForm: 'Legal form',
      jdg: 'Sole trader',
      spZoo: 'Limited liability company',
      name: 'Company name',
      nameHint: 'As it should appear on the invoice.',
      address: 'Street and number',
      postalCode: 'Postal code',
      city: 'City',
      startedOn: 'Business start date',
      startedOnHint: 'It determines the social-security relief and the first-year VAT exemption limit.',
      next: 'Next — settlements',
    },
    settlements: {
      title: 'How do you settle the business?',
      lead: 'Not sure? Pick “I am not sure” — we will settle it before the first filing, and everything else will work.',
      perMonth: (net?: string) => (net ? `${net} PLN net / month` : '—'),
      unsurePrice: 'we will work it out',
      plans: {
        RYCZALT: { title: 'Lump sum', lead: 'Revenue record, lump-sum rate, no costs.' },
        KPIR: { title: 'Revenue and expense ledger', lead: 'Ledger of revenue and expenses, costs with VAT deduction.' },
        FULL: { title: 'Full accounting', lead: 'Journal, chart of accounts, posting.' },
        UNSURE: { title: 'I am not sure', lead: 'The company is created right away, tax settings can wait.' },
      },
      incomeTax: 'Income tax',
      skala: 'Tax scale — 12% and 32%',
      liniowy: 'Flat — 19%',
      cit: 'Corporate income tax',
      vat: 'VAT',
      vatActive: 'Registered VAT payer',
      vatExemptLimit: 'Exempt up to the limit (art. 113)',
      vatExemptKind: 'Exempt by type of activity (art. 43)',
      vatPeriod: 'VAT settlement period',
      monthly: 'Monthly — JPK_V7M',
      quarterly: 'Quarterly — JPK_V7K',
      zus: 'Social security',
      zusSchemes: {
        ULGA_NA_START: 'Start relief — first 6 months',
        PREFERENCYJNY: 'Preferential — 24 months',
        MALY_ZUS_PLUS: 'Small ZUS Plus',
        PELNY: 'Full contributions',
        BRAK: 'I pay no contributions from this business',
      },
      from: 'Settling in Firmeto from',
      fromHint: 'Earlier months can be added later — settings carry an effective date.',
      feeRule: (trialMonths: number, capPercent: number) =>
        `I understand the fee rule: zero in a month without revenue, never more than ${capPercent}% of revenue. ` +
        (trialMonths === 1 ? 'First month free.' : `First ${trialMonths} months free.`),
      finish: 'Create the company and start',
      unsureTitle: 'Tax settings can wait',
      unsureLead:
        'The company is created right away and you can enter documents. We will not compute tax until we know how you settle — the dashboard will keep reminding you.',
    },
    done: {
      title: 'Company ready',
      lead: 'You can start. The fee appears only in a month with revenue.',
      next: [
        { title: 'Add a bank account', lead: 'The number goes on invoices and lets us match payments' },
        { title: 'Issue your first invoice', lead: 'Or enter a cost you already have' },
        { title: 'Invite your accountant', lead: 'Accountant role, without access to Firmeto settlements' },
      ],
      enter: 'Go to the company',
    },
  },
  emailUndeliverable:
    'Messages to your address keep bouncing. Please check it — without a working address you cannot reset your password or receive notifications.',
  companies: {
    settlement: 'Settlement',
    title: 'My companies',
    subtitle: 'Companies you have access to. Pick one to open its documents and books.',
    addHint: 'Enter the NIP, we will fetch the rest from the register',
    bookkeeping: 'Who will keep the books?',
    bookkeepingChoices: { SELF: 'I will', ACCOUNTANT: 'My accountant', OFFICE: 'An accounting office' },
    bookkeepingSelfFull: 'A limited company keeps full accounts, and only the Accountant role may do that. '
      + 'You will grant it to yourself on the next screen — keeping your own company\u2019s books needs no certificate.',
    bookkeepingSelfSimple: 'You keep the tax book or lump-sum records yourself, with no extra roles.',
    bookkeepingAccountantHint: 'You will invite them on the next screen — they get the Accountant role and access to the books.',
    bookkeepingOfficeHint: 'Give the office your NIP. The office adds the company to its clients '
      + 'and you approve the proposal under People.',
    empty: 'You have no companies yet. Add the first one to get started.',
    add: 'Add company',
    nip: 'Tax ID (NIP)',
    fetch: 'Fetch from the registry',
    fetching: 'Searching…',
    fetched: (source: string) => `Filled in from the registry: ${source}. Check the data before saving.`,
    fetchedWithVat: (source: string, vat: string) =>
      `Filled in from the registry: ${source}. VAT status: ${vat}. Check the data before saving.`,
    name: 'Name',
    legalForm: 'Legal form',
    city: 'City',
    businessStartedOn: 'Business start date',
    businessStartedOnHint: 'Needed for the VAT exemption limit in the first year.',
    addressLine: 'Street and number',
    postalCode: 'Postal code',
    save: 'Save company',
    saving: 'Saving…',
    cancel: 'Cancel',
    roles: 'Your role',
    jdg: 'Sole proprietorship',
    spzoo: 'Limited liability company',
    forms: {
      JDG: 'Sole proprietorship',
      SP_CYWILNA: 'Civil partnership',
      SP_JAWNA: 'General partnership',
      SP_PARTNERSKA: 'Professional partnership',
      SP_KOMANDYTOWA: 'Limited partnership',
      SP_KOMANDYTOWO_AKCYJNA: 'Limited joint-stock partnership',
      SP_ZOO: 'Limited liability company',
      PROSTA_SA: 'Simple joint-stock company',
      SA: 'Joint-stock company',
      SPOLDZIELNIA: 'Cooperative',
      STOWARZYSZENIE: 'Association',
      FUNDACJA: 'Foundation',
    },
  },

  settings: {
    partners: {
      title: 'Partners',
      lead: 'In a civil, general or professional partnership the tax is paid by the partners - each on their '
        + 'own share of the profit and each under their own form of taxation. Without this register we compute '
        + 'a single advance as if one person earned everything.',
      firstName: 'First name',
      lastName: 'Last name',
      share: 'Share in profit',
      shareHint: 'A fraction or a percentage: 0.5 or 50%. The shares of all partners should add up to the whole.',
      taxation: 'Form of taxation',
      zus: 'Social security scheme',
      from: 'From',
      to: 'To',
      add: 'Add a partner',
      end: 'End the share',
      endPrompt: 'Last day in the partnership:',
      none: 'The partner register is empty - the advance is then computed as if for one person.',
      sum: 'Shares total',
    },
    lead: 'The settlement setup applies from the given date. Changing the form mid-year is a new entry, not an overwrite of the previous one.',
    periods: 'Periods in force',
    to: 'In force until',
    openEnded: 'open-ended',
    inForce: 'In force',
    future: 'Upcoming',
    yearNotCovered: (od: string, doKiedy: string) =>
      `Financial year ${od} – ${doKiedy} is not fully covered by any settings`,
    yearNotCoveredHint: (odKiedy: string, poczatek: string) =>
      `The settings start on ${odKiedy}, while the financial year begins on ${poczatek}. For the `
      + 'months before that date we do not know how the company is taxed, so the chart of accounts '
      + 'and the taxes for that part of the year have nothing to stand on. If nothing changed, move the date back.',
    coverFromYearStart: (poczatek: string) => `Change to ${poczatek}`,
    edit: 'Correct',
    editWarning: 'You are correcting an existing entry',
    editWarningHint: 'A change of decision is a new entry, not a correction — the history has to stay '
      + 'so that recalculating an old month gives the same result. Correct an entry only when you '
      + 'mistyped it. A month already closed will not be touched.',
    past: 'Historical',
    title: 'Tax settings',
    open: 'Settings',
    close: 'Collapse',
    none: 'No settings yet. Fill them in before you start bookkeeping.',
    from: 'Effective from',
    taxationForm: 'Taxation form',
    booksType: 'Books',
    vatStatus: 'VAT',
    vatExemptionBasis: 'Exemption basis',
    zusScheme: "Owner's social security",
    cashMethod: 'Cash-basis PIT',
    smallTaxpayer: 'Small taxpayer',
    incomeTaxPeriod: 'Income tax advances',
    incomeTaxPeriodHint:
      'Quarterly advances are available to a small taxpayer and to one starting a business (art. 25 s. 1b of the CIT Act, art. 44 s. 3g of the PIT Act). We do not verify the condition — it depends on last year\u2019s revenue. The VAT period is set separately.',
    advancePeriods: { MONTHLY: 'Monthly', QUARTERLY: 'Quarterly' },
    exportData: 'Download all data',
    exportHint: 'Everything we hold about this company, in a single file.',
    sicknessInsurance: 'Voluntary sickness insurance',
    add: 'Add settings',
    save: 'Save settings',
    history: 'Change history',
    monthHint: 'Settings take effect on the first day of a month; earlier periods stay untouched.',
    forms: { ryczalt: 'Lump sum', skala: 'Tax scale', liniowy: 'Flat tax', cit: 'CIT' },
    books: { ryczalt: 'Revenue records', kpir: 'Revenue and expense ledger', full: 'Full accounting' },
    vat: { active_monthly: 'Registered, monthly', active_quarterly: 'Registered, quarterly', exempt: 'Exempt' },
    zus: {
      ulga_na_start: 'Start relief',
      preferencyjny: 'Preferential',
      maly_zus_plus: 'Small ZUS Plus',
      pelny: 'Full',
      brak: 'None',
    },
  },
  access: {
    servedBy: 'Served by an accounting office',
    officeAccountants: 'Access to the books',
    nobodyYet: 'nobody has been assigned yet',
    endOffice: 'End the cooperation',
    endOfficeHint: 'The office loses access the same day. The books stay with the company.',
    confirmEndOffice: (office: string) =>
      `End the cooperation with ${office}? Its accountants will lose access to this company\u2019s books.`,
    officeProposals: 'Service proposals',
    noBookkeeper: 'Nobody keeps this company\u2019s books',
    ownDocumentsOnly: 'Sees only the documents they enter',
    ownDocumentsOnlyShort: 'own only',
    ownDocumentsOnlyHint: 'With several sales people each would see the others\u2019 margins, commissions and '
      + 'customers. This leaves them only the invoices, costs and files they added themselves.',
    selfAccountant: 'You can take the Accountant role yourself',
    selfAccountantHint: 'Keeping your own company\u2019s books needs no certificate. Responsibility for them '
      + 'still rests with the head of the entity, and the Accountant role requires two-factor sign-in.',
    takeAccountantRole: 'Give me the Accountant role',
    officeProposalsHint:
      'An accounting office wants to keep this company\u2019s books. Until you agree it sees no data and cannot assign an accountant.',
    office: 'Office',
    whoPays: 'Who pays for Firmeto',
    payerOffice: 'The office',
    payerCompany: 'The company',
    acceptProposal: 'Accept',
    rejectProposal: 'Reject',
    lead: 'Access to this company. Every grant and revocation is written to the change history.',
    person: 'Person',
    whatEachRoleSees: 'What each role sees',
    permission: 'Permission',
    permissions: {
      documents: 'Invoices and costs',
      books: 'Ledger and JPK',
      settings: 'Tax settings',
      people: 'People and access',
      billing: 'Firmeto fees',
    },
    levels: { full: 'full', add: 'adding', view: 'view only' },
    permissionsNote:
      '"—" means the item does not appear in that person’s navigation at all. We do not show greyed-out buttons that cannot be clicked.',
    title: 'People and invitations',
    open: 'People',
    people: 'With access',
    invitations: 'Invitations',
    invite: 'Invite someone',
    send: 'Send invitation',
    email: 'Email address',
    role: 'Role',
    remove: 'Revoke access',
    revoke: 'Cancel',
    expires: 'Valid until',
    noInvitations: 'No invitations.',
    hint: 'An invitation is valid for 7 days and can only be accepted by someone signed in with that address.',
    statuses: { pending: 'Pending', accepted: 'Accepted', revoked: 'Cancelled', expired: 'Expired' },
    rolesFemale: {
      OWNER: 'Owner',
      ADMIN: 'Administrator',
      ACCOUNTANT: 'Accountant',
      EMPLOYEE: 'Employee',
      BOARD_MEMBER: 'Board member',
      VIEWER: 'Read-only',
    },
    roles: {
      OWNER: 'Owner',
      ADMIN: 'Administrator',
      ACCOUNTANT: 'Accountant',
      EMPLOYEE: 'Employee',
      BOARD_MEMBER: 'Board member',
      VIEWER: 'Viewer',
    },
  },
  counterparties: {
    lead: 'The contacts file used when issuing invoices and entering costs.',
    dialogLead: 'The counterparty is saved straight away and selected in the document — your draft stays as it was.',
    active: 'Active',
    typeHint: 'The type decides which VAT rates are available on an invoice.',
    nipHint: 'We will verify the checksum.',
    ratesTitle: 'Type and VAT rates',
    ratesHint: 'The list follows the rates available on an invoice in this application.',
    open: 'Contacts',
    title: 'Counterparties',
    search: 'Search by name or number',
    add: 'Add counterparty',
    none: 'No counterparties yet.',
    showInactive: 'Show inactive',
    name: 'Name',
    nip: 'Tax ID (NIP)',
    vatEuId: 'EU VAT number',
    country: 'Country',
    city: 'City',
    type: 'Type',
    deactivate: 'Mark as inactive',
    inactive: 'inactive',
    save: 'Save counterparty',
    types: {
      DOMESTIC: 'Domestic company',
      EU: 'EU company',
      NON_EU: 'Non-EU company',
      PERSON: 'Private person',
    },
  },
  products: {
    lead: 'Items suggested while issuing an invoice.',
    showInactive: 'Show retired',
    grossPrice: 'Gross price',
    grossPreview: 'Gross',
    retire: 'Retire',
    open: 'Goods and services',
    title: 'Goods and services',
    search: 'Search by name or code',
    add: 'Add item',
    edit: 'Edit',
    none: 'No items yet.',
    code: 'Code',
    namePl: 'Name (Polish)',
    nameEn: 'Name (English)',
    unit: 'Unit',
    vatRate: 'VAT rate',
    gtu: 'GTU code',
    price: 'Price',
    save: 'Save item',
    rates: { '23': '23%', '8': '8%', '5': '5%', '0': '0%', zw: 'exempt', np: 'not subject', oo: 'reverse charge' },
  },
  wizard: {
    open: 'Wizard',
    preview: 'Document preview',
    previewOpen: 'Open the original',
    previewUnsupported: 'We cannot show this file here — open the original in a new tab.',
    title: 'Posting wizard',
    documentNumber: 'Document number',
    issueDate: 'Document date',
    category: 'Expense type',
    vatRate: 'VAT rate',
    net: 'Net',
    vat: 'VAT',
    gross: 'Gross',
    stepOf: (step: number, total: number) => `Step ${step} of ${total}`,
    steps: { kind: 'What is this document', counterparty: 'From whom', document: 'Document and amounts', payment: 'Payment', summary: 'Summary', statement: 'Statement' },
    kinds: { COST_INVOICE: 'Purchase invoice', RECEIPT: 'Receipt', BANK_STATEMENT: 'Bank statement' },
    kindHints: {
      COST_INVOICE: 'A supplier invoice with a tax ID — VAT to deduct, cost to the books.',
      RECEIPT: 'A receipt without an invoice — cost without VAT deduction.',
      BANK_STATEMENT: 'A file from the bank (MT940) — payments to match.',
    },
    counterpartyHint: 'Pick the supplier from the list or add a new one by tax ID — we fetch the details from the register.',
    supplier: 'Supplier',
    newSupplier: 'New supplier',
    dueDate: 'Due date',
    deduction: 'VAT deduction',
    deductions: { FULL: 'in full', HALF: 'half (vehicle)', NONE: 'none' },
    paid: 'The document is already paid',
    paidOn: 'Payment date',
    method: 'Method',
    methods: { transfer: 'bank transfer', cash: 'cash', card: 'card' },
    unpaidHint: 'It stays as a payable — with the due date, if you gave one.',
    summaryLead: 'This is how we will record it. Nothing exists yet — check and confirm.',
    costAmount: 'Into costs',
    deductibleVat: 'Deductible VAT',
    paidShort: 'paid',
    unpaidShort: 'unpaid — waits in open items',
    summaryFull: 'With full books the entry goes to the “to be posted” list in the journal — nothing is posted without a look.',
    summarySimple: 'The cost goes to the ledger for the month of the document date.',
    statementLead: 'We will import the statement and show which payments match which documents. A person confirms the matches.',
    importStatement: 'Import the statement',
    back: 'Back',
    next: 'Next',
    finish: 'Confirm and save',
    later: 'Finish later',
    close: 'Close',
    doneFull: 'Cost document saved. The entry awaits posting in the journal.',
    doneSimple: 'Cost document saved and entered into the ledger.',
    doneStatement: 'Statement imported. Match the payments to documents.',
    goJournal: 'Go to the journal',
    goCosts: 'Go to costs',
    goMatch: 'Go to matching',
  },
  documents: {
    noneHint: 'Uploaded files will appear here with the date, the size and whether they have already been entered as a document.',
    scanFutureText:
      'Eventually an uploaded file lands in the "Waiting to be entered" queue, and its contents suggest the supplier, number, dates and amounts while adding a cost. Today a file is only an attachment.',
    open: 'Documents',
    title: 'Document archive',
    lead: 'Photos and PDFs are kept exactly as they arrived. During an audit the original is what counts.',
    drop: 'Drag photos or PDF files here',
    choose: 'Choose files',
    accepted: 'JPEG, PNG, HEIC or PDF, up to 25 MB.',
    none: 'The archive is empty.',
    fileName: 'File',
    added: 'Added',
    size: 'Size',
    state: 'State',
    waiting: 'Waiting to be entered',
    posted: 'Entered as a cost',
    delete: 'Delete',
    uploaded: (added: number) => `Added to the archive: ${added}.`,
    uploadedWithKnown: (added: number, known: number) =>
      `Added to the archive: ${added}. Already there: ${known} — we do not make a second copy.`,
    confirmDelete: (fileName: string) => `Delete ${fileName} from the archive? This cannot be undone.`,
    inboxTitle: 'Document inbox',
    inboxLead: 'Everything that arrived — from the browser, the phone and e-mail — waits here until someone enters it or rejects it with a reason. The original stays in the archive.',
    statuses: { NEW: 'new', IN_PROGRESS: 'in progress', BOOKED: 'booked', REJECTED: 'rejected' },
    tag: 'Label',
    source: 'From',
    sourceUpload: 'uploaded',
    sourceEmail: 'e-mail',
    accept: 'Mark as in progress',
    reject: 'Reject',
    rejectPrompt: 'Reason for rejection — the owner should know what to fix:',
    rejected: 'Rejected',
    intakeTitle: 'Documents by e-mail',
    mailboxConfig: 'Inbox settings',
    intakeLead: 'Send a message with attachments to the company address and the files land in the inbox. There can be several addresses, each with its own label. We accept only from people with access to the company and approved senders; other messages are dropped without a reply.',
    intakeAddress: 'Address',
    intakeTag: 'Attachment label',
    intakeTagHint: 'e.g. invoices, statements, receipts',
    intakeCreate: 'Generate an address',
    intakeRevoke: 'Revoke',
    intakeRevoked: 'revoked',
    intakeRevokeConfirm: (address: string) => `Revoke ${address}? From now on messages to this address will not be accepted. You can generate a new one.`,
    intakeNone: 'There is no address yet.',
    intakeCopied: 'Copied.',
    sendersTitle: 'Approved senders',
    sendersLead: 'People with access to the company are approved automatically. Add other addresses here, e.g. the office mailbox or a scanner.',
    senderEmail: 'E-mail address',
    senderAdd: 'Add',
    senderRevoke: 'Remove',
    sendersNone: 'Only people with access to the company.',
  },
  annual: {
    cit: {
      title: 'Corporate income tax for the year (CIT-8)',
      hint: 'Computed from the same base as the tax note in the statement: the result from the books, less '
        + 'exempt revenue, plus costs that are not tax deductible. Timing differences and losses from earlier '
        + 'years are supplied by the accountant - without them the amount is provisional.',
      base: 'Base',
      rate: 'Rate',
      taxForYear: 'Tax for the year',
      fromAdvances: 'Covered by advances',
      difference: 'Left to pay with the return',
      priorLoss: 'Loss from earlier years to deduct',
      recalculate: 'Recompute',
      notApplicable: 'This company does not pay corporate income tax or does not keep full books.',
    },
    activity: {
      title: 'Management report',
      lead: 'A document of the board, not of the bookkeeper: it is signed by the head of the entity, and by '
        + 'every board member when the board has more than one. The figures are computed from the same '
        + 'projection as the financial statement - they are not copied over.',
      indicators: 'Indicators',
      indicator: 'Indicator',
      value: 'Value',
      basis: 'From what',
      suggestions: 'Suggestions from the books',
      suggestionsHint: 'No paragraph enters the document until you accept it. A suggestion is appended to the '
        + 'text of its section, where it can be edited or deleted.',
      add: 'Append to the section',
      sections: 'Sections of the document',
      required: 'required',
      optional: 'optional',
      fromData: 'content from data',
      proposal: 'suggestion — not accepted',
      save: 'Save the texts',
      saved: 'Texts saved.',
      missing: 'Without these sections the document cannot be built:',
      ready: 'The document is ready',
      pdf: 'Build the PDF',
      docx: 'Build the DOCX',
      download: 'Download the document',
      signers: 'Signed by',
    },
    blank: {
      title: 'Blank statement template',
      hint: 'Every position of the chosen annex without figures — for a conversation with the client or to '
        + 'collect the data outside the application. It creates no statement and changes nothing.',
      pdf: 'Download the PDF',
      xlsx: 'Download the spreadsheet',
    },
    modes: {
      title: 'Where the figures come from',
      BOOKS: 'From the books in Firmeto',
      DORMANT: 'Company with no activity',
      MANUAL: 'Books kept elsewhere',
      booksHint: 'The figures come from the ledger projection — the same one the balance sheet screen shows.',
      dormantHint: 'A year with no turnover: a few figures typed in (capital, cash, liabilities).',
      manualHint: 'The books for this year are kept elsewhere — you type the figures into the positions.',
      template: 'Statement template',
      templates: { ZAL_4: 'Micro entity (annex 4)', ZAL_5: 'Small entity (annex 5)', ZAL_1: 'Other entity (annex 1)' },
      variant: 'Profit and loss variant',
      variants: { BY_KIND: 'by nature', BY_FUNCTION: 'by function' },
    },
    amounts: {
      title: 'Statement figures',
      lead: 'Figures go only into component positions: totals and formula positions are computed. The result '
        + 'from the profit and loss account lands in equity by itself - which is why a statement whose balance '
        + 'sheet disagrees with its profit and loss cannot be filed.',
      position: 'Position',
      amount: 'Amount',
      previous: 'Previous year',
      save: 'Save the figures',
      saved: 'Figures saved.',
      balanced: 'The balance sheet balances',
      unbalanced: 'The balance sheet does not balance',
      difference: 'Difference',
      totals: 'Assets · equity and liabilities · result',
      onlyManual: 'This screen is for a statement drawn up outside the books. This one comes from the books.',
    },
    office: {
      title: 'Clients financial year',
      year: 'Year',
      client: 'Client',
      status: 'Statement',
      prepared: 'Drawn up',
      approved: 'Approved',
      filed: 'Filed',
      confirmed: 'Confirmed',
      mention: 'Mention in the register',
      noReport: 'none',
      none: 'No client has a financial year ending in this year.',
    },
    open: 'Annual statement',
    title: 'Annual financial statement for the register',
    lead: 'The wizard guides you through actions, not fields. Balance sheet and profit and loss figures come '
      + 'from the books — there is no field here to type them into.',
    tabs: {
      year: 'Year and version',
      form: 'Introduction and notes',
      file: 'Statement file',
      signatures: 'Signatures',
      resolutions: 'Resolutions',
      krs: 'Filing with the register',
      registers: 'Board and shareholders',
    },
    statuses: {
      draft: 'Draft',
      assembled: 'File built',
      signing: 'Collecting signatures',
      signed: 'Signed',
      approved: 'Approved',
      filed: 'Filed with the register',
      filed_unapproved: 'Filed before approval',
      confirmed: 'Confirmed',
      abandoned: 'Abandoned',
    },
    year: {
      pick: 'Financial year',
      noYears: 'This company has no financial year yet.',
      noYearsHint: 'Financial years are set up on the Periods screen. Without one there is nothing to draw up '
        + 'a statement from.',
      start: 'Start the statement',
      noReport: 'There is no statement for this year yet',
      noReportHint: 'The statement is built from this year in the books. Closing the year is not required, but '
        + 'a statement from an open year carries a draft mark.',
      versions: 'Versions',
      version: (numer: number) => `version ${numer}`,
      versionReason: 'Reason',
      draftMark: 'draft',
      draftMarkHint: 'The year is open, or the file was built by someone who does not keep the books — the document '
        + 'says so in its text, otherwise it would reach the shareholders as a finished statement.',
      stale: 'The books changed after the file was built',
      staleHint: 'This changes no status and deletes no file. Build the file again before anyone signs it.',
      calendar: 'Annual calendar',
      calendarHint: 'Each deadline carries two dates: the statutory one and the last working day before it. Nobody '
        + 'has confirmed whether such a deadline moves when it falls on a day off, and on a Saturday there is '
        + 'no one to file with.',
      statutory: 'Statutory deadline',
      safe: 'Last working day',
      state: 'State',
      done: 'done',
      overdue: 'overdue',
      daysLeft: (dni: number) => `in ${dni} days`,
      obligations: {
        statement_prepared: 'Drawing up the statement',
        cit8: 'CIT-8 return',
        approval: 'Approval by the shareholders',
        krs_filing: 'Filing with the register',
        krs_confirmation: 'Register mention or confirmation',
      },
    },
    form: {
      title: 'Introduction and notes',
      lead: 'The fields come from the official statement template: their codes are element names from the Ministry '
        + 'of Finance schema, not names we made up.',
      save: 'Save answers',
      saved: 'Answers saved.',
      missing: 'Without these fields the file will not pass the schema:',
      complete: 'The form is complete.',
      sections: {
        INTRODUCTION: 'Introduction',
        NOTES: 'Notes',
        SEAT: 'Registered office',
        TAX: 'Tax reconciliation',
      },
    },
    file: {
      title: 'Statement file',
      lead: 'The file is built from the books and the form, and then sits in the company archive together with '
        + 'its checksum.',
      generate: 'Build the file from the books',
      regenerate: 'Build the file again',
      taxReconciliation: 'Include the reconciliation of the result with the tax base',
      download: 'Download XML',
      pdf: 'Build the printout (PDF)',
      pdfDownload: 'Download the PDF',
      pdfHint: 'A copy to read: balance sheet, profit and loss and the texts from the form. The XML file goes to the register; the printout stays in the company and is not the document that gets signed.',
      files: 'Files in the set',
      role: 'Role',
      sha256: 'Checksum',
      generatedAt: 'Built',
      roles: {
        statement_xml: 'Statement (XML)',
        statement_xml_signed: 'Statement with signatures',
        statement_pdf: 'Statement (PDF)',
        activity_report: 'Management report',
        resolution_approval: 'Approval resolution',
        resolution_profit: 'Profit distribution resolution',
        resolution_discharge: 'Discharge resolution',
        minutes: 'Meeting minutes',
        statement_52_2b: 'Statement under art. 52 sec. 2b',
        auditor_report: 'Auditor report',
        rdf_confirmation: 'Confirmation from the repository',
      },
      newVersion: 'New version',
      reason: 'Reason for the new version',
      reasonHint: 'The reason stays with the version forever — a year later nobody remembers why the statement '
        + 'has two.',
      noAmounts: 'Amounts are not entered here or anywhere else in the wizard: a change goes through the books '
        + 'and the file is built again.',
    },
    signatures: {
      title: 'Signatures on the statement',
      lead: 'The statement is signed by the person keeping the books and by every board member '
        + '(art. 52 sec. 2 of the Accounting Act).',
      deadline: 'Deadline for drawing up',
      person: 'Person',
      function: 'Function',
      state: 'State',
      method: 'Method',
      when: 'When',
      states: {
        missing: 'not signed',
        signed: 'signed',
        statement: 'filed a statement',
        refusal: 'refused to state',
      },
      methods: {
        qualified: 'qualified signature',
        trusted_profile: 'trusted profile',
        personal: 'personal signature',
        statement: 'statement under art. 52 sec. 2b',
        refusal: 'refusal to state',
      },
      capacities: { board_member: 'board member', bookkeeper: 'person keeping the books' },
      downloadToSign: 'Download the file to sign',
      downloadHint: 'You get the file with the signatures collected so far: each signature is added to the previous '
        + 'ones, and signing a clean file would lose those already given.',
      upload: 'Upload the signed file',
      capacity: 'In what capacity',
      officer: 'Person from the board register',
      personName: 'Full name',
      note: 'Justification or note',
      send: 'Upload',
      complete: 'Signatures complete',
      incomplete: 'Signatures are not complete yet',
      extra: 'Signatures of people outside the board',
      noStatement: 'Build the statement file first — there is nothing to sign without it.',
      declaration: {
        title: 'Statement or refusal under art. 52 sec. 2b',
        hint: 'When one board member signs the statement, the others file statements that it meets the requirements '
          + 'of the act, or refusals with justification. We generate the text; the signature is made outside the '
          + 'application and uploaded back.',
        who: 'Who files it',
        kind: 'Kind',
        statement: 'Statement',
        refusal: 'Refusal',
        justification: 'Justification (required for a refusal)',
        generate: 'Generate the text (PDF)',
        generated: (plik: string) => `${plik} is in the company archive — sign it and upload it back.`,
      },
    },
    resolutions: {
      title: 'Shareholder resolutions',
      lead: 'Which resolutions are needed follows from the result of the year and from the capitals, so the amounts '
        + 'arrive calculated — there is nothing to type in.',
      reason: 'Why',
      create: 'Create a draft',
      adopt: 'Record as adopted',
      adoptedOn: 'Date adopted',
      number: 'Number',
      pdf: 'PDF',
      draft: 'draft',
      adopted: 'adopted',
      gaps: 'What is missing in the data:',
      minutes: {
        title: 'Meeting minutes',
        date: 'Meeting date',
        place: 'Place',
        chairman: 'Chair',
        secretary: 'Secretary',
        unanimous: 'The resolutions were adopted unanimously',
        generate: 'Build the minutes (PDF)',
        download: 'Download the minutes',
        needsResolutions: 'The minutes describe what happened at the meeting — adopt at least one resolution first.',
      },
    },
    krs: {
      title: 'Filing with the Financial Documents Repository',
      lead: 'There is no machine interface for filing, so the application does everything but the last click: it '
        + 'builds the package, says what is missing and walks you through the Court Registers Portal.',
      checklist: 'Checklist for the set',
      required: 'required',
      optional: 'optional',
      present: 'present',
      missing: 'missing',
      ready: 'The set is ready to file',
      notReady: 'The set is not ready yet',
      people: 'Who can sign the filing',
      pesel: 'PESEL disclosed in the register',
      instructions: 'Step by step in the Court Registers Portal',
      deadlines: 'Deadlines',
      zip: 'Download the package (ZIP)',
      filing: {
        title: 'Record the filing',
        submittedOn: 'Date filed',
        number: 'Filing number',
        record: 'Record the filing',
        unapproved: 'filed before approval — after approval it has to be filed again '
          + '(art. 69 sec. 2 of the Accounting Act)',
      },
      confirmation: {
        title: 'Confirmation from the repository',
        confirmedOn: 'Date of confirmation',
        upload: 'Upload the confirmation',
      },
      register: {
        title: 'Mentions in the register',
        lead: 'A mention in the register is proof of filing independent of us and of the client. Silence from the '
          + 'register takes nothing back.',
        number: 'Register number',
        noNumber: 'The company has no register number — add it in the company settings.',
        checkedAt: 'Checked',
        never: 'we have not asked yet',
        check: 'Check the register',
        mentions: 'Mentions',
        period: 'Period',
        filedOn: 'Filed',
        approval: 'Resolution',
        activity: 'Management report',
        audit: 'Audit',
        missingYears: 'Years with no mention of a statement:',
        none: 'The register carries no mention of this company statements yet.',
      },
    },
    registers: {
      officers: {
        title: 'Board',
        lead: 'The register keeps periods, not a state as of today: resolutions for a given year concern the board '
          + 'of that year.',
        firstName: 'First name',
        lastName: 'Last name',
        role: 'Function',
        from: 'From',
        to: 'To',
        pesel: 'PESEL in the register',
        peselHint: 'Without a person whose PESEL is disclosed in the register, nobody from the board can sign the '
          + 'free filing to the repository.',
        add: 'Add a person',
        end: 'End the function',
        roles: {
          PRESIDENT: 'president of the board',
          BOARD_MEMBER: 'board member',
          PROXY: 'commercial proxy',
          LIQUIDATOR: 'liquidator',
        },
        none: 'The board register is empty — without it we do not know who has to sign the statement.',
      },
      shareholders: {
        title: 'Shareholders',
        lead: 'The attendance list in the minutes and the profit distribution are built from the shareholder register.',
        name: 'Shareholder',
        shares: 'Shares',
        votes: 'Votes',
        nominal: 'Nominal value',
        capital: 'Capital',
        add: 'Add a shareholder',
        end: 'End',
        none: 'The shareholder register is empty.',
      },
    },
  },
  periods: {
    closedMonths: 'Months closed',
    settled: 'being settled',
    stillRunning: 'still running',
    noneClosed: 'No month is closed',
    noneClosedHint:
      'While a month stays open, a backdated entry changes the amounts in a return that has already been filed. Close the month once the JPK has been sent.',
    open: 'Periods',
    title: 'Closing periods',
    lead: 'A closed month accepts no new entries. Changes are made by a correction dated today — '
      + 'so the books keep matching the return already filed with the tax office.',
    years: 'Fiscal year',
    noYears: 'There is no fiscal year yet.',
    yearMissing: (rok: number) => `Financial year ${rok} is not open yet`,
    yearMissingHint: 'A financial year is created once and the next one has to be opened. Without it '
      + 'the chart of accounts and the journal show last year, which today\u2019s tax settings do not reach.',
    openYear: (rok: number) => `Open ${rok}`,
    addYear: 'Add a fiscal year',
    saveYear: 'Save the year',
    yearHint: 'For a sole trader the fiscal year is always the calendar year. A company may have a different one, '
      + 'but no longer than eighteen months.',
    closeYear: 'Close the year',
    readiness: {
      blocked: 'The year cannot be closed until this is done:',
      warnings: 'The checklist has remarks:',
      confirm: 'Close the year despite these remarks?',
      result: (numer: number, kwota: string) => `The result was transferred by entry no. ${numer}: ${kwota} PLN`,
      noResult: 'Year closed. There were no result entries, so there was nothing to transfer.',
      checks: {
        months_open: 'Months still open',
        unposted_invoices: 'Invoices not posted',
        unposted_costs: 'Cost documents not posted',
        trial_balance: 'The trial balance does not add up',
        accounts_without_position: 'Accounts with a balance and no statement position',
        opening_balance: 'No opening balance',
        draft_invoices: 'Invoices left as drafts',
        inbox_documents: 'Documents waiting in the inbox',
        settlements_without_counterparty: 'Settlement lines without a counterparty',
        depreciation: 'Fixed assets with no depreciation posted',
        closing_inventory: 'No year-end stocktaking',
        open_provisions: 'Provisions still carrying a balance after their period ended',
        result_transfer: 'Result to be transferred',
      },
    },
    months: 'Months',
    month: 'Month',
    state: 'State',
    from: 'From',
    to: 'To',
    closedAt: 'Closed on',
    open_: 'Open',
    closed: 'Closed',
    close: 'Close',
    reopen: 'Reopen',
    reopenTitle: (period: string) => `Reopening ${period}`,
    reason: 'Reason',
    reasonHint: 'The reason goes into the audit log and stays there for good.',
    cancel: 'Cancel',
    confirmClose: (period: string) => `Close ${period}? After closing you cannot add a document to this month.`,
    confirmWithDrafts: (period: string, drafts: number) =>
      `${period} still has invoice drafts: ${drafts}. After closing they cannot be issued. Close anyway?`,
    confirmCloseYear: (from: string, to: string) =>
      `Close the year ${from} — ${to}? This closes every month of that year.`,
  },
  costs: {
    open: 'Costs',
    bookAll: 'Book all',
    list: 'List',
    allMonths: 'All',
    column: 'col.',
    draftsWaiting: 'Documents waiting to be booked',
    draftsWaitingHint:
      'While they are drafts they enter neither the KPiR nor VAT — which is why the month costs on the dashboard may be zero.',
    draftAfterSave: 'A saved document lands in drafts. It enters the KPiR and VAT only once booked.',
    draftAfterSaveHint: 'After saving, the document is a draft and does not affect any of these yet.',
    document: 'Document',
    qualification: 'Tax treatment',
    rates: 'Split by VAT rate',
    addRate: 'Add a rate',
    summary: 'Summary',
    goesTo: 'Goes to',
    kpir: 'KPiR',
    vatRegister: 'VAT register',
    purchases: 'purchases',
    vatInput: 'Input VAT',
    saveAndNext: 'Save and add another',
    costDateHint: 'Decides the month in the KPiR.',
    supplierHint: 'Add a new one in the contacts file.',
    fixedAssetHint: 'Enter into the fixed asset register',
    intraCommunity: 'Intra-community acquisition',
    intraCommunityHint: 'Purchase of goods from an EU supplier — goes into the VAT-UE summary (imported services do not)',
    scanFutureTitle: 'Reading from a scan',
    scanFutureText:
      'Eventually a scan from the Documents tab will fill in the number, supplier, dates and amounts, and fields read with low confidence will be highlighted for confirmation. Today a scan is only an attachment — every field is typed by hand.',
    title: 'Cost documents',
    new: 'Add cost',
    none: 'No cost documents yet.',
    number: 'Document number',
    supplier: 'Supplier',
    chooseSupplier: 'Choose a supplier',
    issueDate: 'Document date',
    costDate: 'Cost date',
    category: 'Expense type',
    deduction: 'VAT deduction',
    fixedAsset: 'Fixed asset',
    net: 'Net',
    vat: 'VAT',
    deductible: 'Deductible VAT',
    costAmount: 'Booked as cost',
    correctionTo: 'Correction to document',
    correctionHint: 'Enter the difference exactly as stated on the supplier document — usually negative.',
    causes: {
      MISTAKE: 'Mistake on the original document',
      EVENT: 'Later event (discount, return)',
    },
    status: 'Status',
    save: 'Save cost',
    book: 'Post to books',
    delete: 'Delete',
    hint: 'The cost equals net plus the VAT that cannot be deducted — half deduction and VAT-exempt companies give different amounts.',
    categories: {
      GOODS: 'Goods and materials (col. 10)',
      PURCHASE_COSTS: 'Purchase side costs (col. 11)',
      SALARIES: 'Salaries (col. 12)',
      OTHER: 'Other expenses (col. 13)',
    },
    deductions: { FULL: 'Full (100%)', HALF: 'Half (vehicle)', NONE: 'None' },
    statuses: { DRAFT: 'Draft', BOOKED: 'Posted' },
  },
  dashboard: {
    fixSettings: 'Complete the settings',
    noDeadlines: 'No deadlines this month.',
    open: 'Dashboard',
    title: 'The month at a glance',
    month: 'Month',
    revenue: 'Revenue',
    costs: 'Costs',
    income: 'Income',
    vatToPay: 'VAT to pay',
    vatToCarry: 'VAT carried forward',
    obligations: 'Deadlines',
    toPay: 'Due this month',
    goToSettlements: 'Go to settlements',
    goToPeople: 'Go to people',
    NO_BOOKKEEPER: 'Full accounts, and nobody holds the Accountant role — there is nobody to keep the books.',
    period: 'Period',
    dueDate: 'Due',
    VAT: 'VAT and JPK',
    PIT_ADVANCE: 'Income tax advance',
    CIT_ADVANCE: 'Corporate income tax advance',
    HEALTH_ANNUAL: 'Annual health contribution settlement',
    citBase: 'CIT base, year to date',
    citNotes: {
      ESTIMATE: 'Computed from the books kept here - an estimate, not a return.',
      LOSS_NOT_DEDUCTED: 'Losses from earlier years are not deducted; include them in the annual return.',
      TRANSITIONAL_NOT_INCLUDED: 'Timing differences are not known here - they enter the annual return.',
      SMALL_RATE_FROM_SETTINGS: 'The 9% rate comes from the company settings; its revenue limit is not checked.',
      LOSS_PERIOD: 'Year to date the result is a loss - there is no advance.',
      CAPITAL_GAINS_NOT_SEPARATED: 'Capital-gains income is not separated — the books do not show which revenue it is.',
      QUARTERLY: 'The amount covers the whole quarter and is paid after it ends.',
      QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Quarterly advances are available to a small taxpayer, and the company is not marked as one in its settings.',
    } as Record<string, string>,
    ZUS: 'Social security (ZUS)',
    movedFrom: 'statutory date {date} falls on a day off',
    today: 'today',
    tomorrow: 'tomorrow',
    overdue: 'overdue',
    daysLeft: 'days left:',
    NO_SETTINGS: 'No tax settings yet — without them we cannot compute the tax or show deadlines.',
    NO_DOCUMENTS: 'No documents this month yet.',
    OVERDUE_RECEIVABLES: 'Some invoices are past their due date — money you have been waiting for longer than you should.',
    OVERDUE_PAYABLES: 'Some payables are past their due date — money you have owed for longer than you promised.',
    aging: 'Open items — aging',
    agingLead: (asOf: string) => `As of ${asOf}, from the open items in the journal: who owes what and since when.`,
    agingReceivables: 'Receivables',
    agingPayables: 'Payables',
    agingOpen: 'open',
    agingOverdue: 'overdue',
    goToJournal: 'Go to open items in the journal',
    receivables: 'Unpaid invoices',
    receivablesOverdue: 'of which overdue',
    exemptionTitle: 'VAT exemption limit',
    exemptionUsed: 'Used since the start of the year',
    exemptionLimit: 'Limit',
    exemptionRemaining: 'Remaining',
    VAT_EXEMPTION_NEAR_LIMIT: 'You are approaching the VAT exemption limit — once it is exceeded you must register for VAT.',
    VAT_EXEMPTION_EXCEEDED: 'The VAT exemption limit has been exceeded. The exemption ends with the transaction that crossed it.',
    SUSPENDED: 'The business was suspended for the whole month, so there are no contributions and no tax advance. A JPK_V7 file may still be required — for intra-EU acquisitions, imported services and corrections (art. 99 s. 7a and 7b of the VAT Act).',
    goToSuspensions: 'See suspensions',
    zusTitle: 'Social security for the month',
    zusSocial: 'Social',
    zusSickness: 'Sickness',
    zusLaborFund: 'Labour Fund',
    zusHealth: 'Health',
    zusTotal: 'Total to pay',
    draDownload: 'ZUS DRA declaration',
    draHint: 'A file to import into eZUS or Płatnik — we do not send it for you.',
    advanceTitle: 'Income tax advance',
    advanceBase: 'Tax base year to date',
    advanceAmount: 'Due for this month',
    partners: 'Partners',
    partner: 'Partner',
    share: 'Share in profit',
    taxationForm: 'Taxed as',
    ESTIMATE: 'Amounts are calculated from the documents in Firmeto, assuming earlier contributions and advances were paid on time.',
    RYCZALT_NOT_SUPPORTED: 'Tax on the lump-sum scheme is not calculated yet — it needs the revenue record with rates.',
    MALY_ZUS_PLUS_APPROXIMATED: 'Mały ZUS Plus is computed from last year\u2019s income, and that year is not in Firmeto — the full base is shown, because a lower one would rest on nothing.',
    UNKNOWN_YEAR: 'We have no rates or thresholds for this year, so we do not calculate the amounts.',
    COMPANY_TAX: 'Company tax and shareholder contributions are settled outside this summary.',
    PARTNERS_SPLIT: 'A partnership pays no income tax - the partners do, each on their own share.',
    PARTNERS_MISSING: 'A partnership with no partner register: we compute as if for one person, which it is not. Add the partners in the company settings.',
    PARTNERS_SHARE_MISMATCH: 'The partner shares do not add up to the whole - one is missing or too large.',
    QUARTERLY_ADVANCE: 'The advance is quarterly: the amount covers the whole quarter and is paid by the 20th of the month after it ends. Social contributions stay monthly.',
    QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Quarterly advances are available to a small taxpayer, and the company is not marked as one in its settings.',
    BAD_DEBT_IN_INCOME: 'Income includes the recorded bad debt corrections: an unpaid cost goes back into income, unpaid revenue comes out of it (art. 26i of the PIT Act).',
    BAD_DEBT_NOT_IN_RYCZALT: 'Under the lump sum, bad debt relief changes revenue per rate (art. 11 of the Lump Sum Act), and we do not know which rate an unpaid document belongs to — so the recorded corrections are not counted here.',
    MALY_ZUS_PLUS_FROM_PREVIOUS_YEAR: 'The social contribution base is computed from last year\u2019s income: the average monthly income halved (Mały ZUS Plus, art. 18c of the Social Insurance System Act).',
    MALY_ZUS_PLUS_AT_FLOOR: 'The income would give a base below the lower bound, so we take 30% of the minimum wage — the same as under preferential contributions.',
    MALY_ZUS_PLUS_AT_CAP: 'The income would give a base above the full one, so we take the full base: at this income the relief changes nothing.',
    MALY_ZUS_PLUS_REVENUE_OVER_LIMIT: 'Last year\u2019s revenue exceeded the limit for the relief (PLN 120,000), so the full base applies.',
    MALY_ZUS_PLUS_TOO_FEW_DAYS: 'The business ran for fewer than 60 days last year, and the relief is not available for such a year.',
    MALY_ZUS_PLUS_DAYS_ASSUMED: 'We do not know the day the business started, so we assumed last year was a full one. If it started mid-year, the base is understated — fill the date in the company details.',
    MALY_ZUS_PLUS_FROM_REVENUE: 'Under the lump sum the records hold no income, so we derive it from revenue with the coefficient from the act.',
    MALY_ZUS_PLUS_LIMIT_NOT_CHECKED: 'We do not check two conditions our data cannot see: 36 months of using the relief within the last 60, and working for a former employer. Those have to be watched by hand.',
    MALY_ZUS_PLUS_SUSPENSION_DAYS: 'Days of suspension are not days of running the business, so we subtracted them before dividing the income — which makes the base higher than the annual income alone would give.',
    SUSPENDED_MONTH: 'The business was suspended for the whole month, so there is no insurance at all: neither social nor health.',
    SUSPENDED_PART_OF_MONTH: 'Part of the month was suspended, so the social contribution base was reduced in proportion to the days insured (art. 18 s. 9 of the Social Insurance System Act). The health contribution stays whole — it is monthly and indivisible.',
  },
  ryczalt: {
    lead: 'The revenue register is kept under the lump-sum tax on registered revenue.',
    otherForm: 'This company is taxed differently',
    goToLedger: 'Go to the revenue and expense ledger',
    changeForm: 'Change the form of taxation',
    byRate: 'By rate',
    open: 'Revenue record',
    title: 'Lump-sum revenue record',
    month: 'Month',
    date: 'Date',
    document: 'Document',
    counterparty: 'Counterparty',
    rate: 'Rate',
    amount: 'Revenue',
    total: 'Total',
    none: 'No revenue this month.',
  },
  settlements: {
    lead: 'Receivables and payables by document. Payments are entered by hand or from a statement.',
    receivablesTotal: 'Receivables total',
    payablesTotal: 'Payables total',
    longestDelay: 'Longest delay (days)',
    noDelay: 'nothing is overdue',
    delay: 'Delay',
    filter: 'Filter: document, contact',
    loadStatement: 'Load a statement',
    matchFutureTitle: 'Matching payments to invoices',
    matchFutureText:
      'We suggest a match, but a person confirms it. Eventually the bank file will point out which payment covers which invoice, partial amounts included.',
    open: 'Settlements',
    title: 'Settlements',
    receivables: 'Receivables',
    payables: 'Payables',
    onlyOpen: 'Open only',
    bank: 'Statement',
    bankHint: 'Upload an MT940 statement. We never post anything on our own — we suggest a match and you confirm it.',
    chooseFile: 'Statement file',
    importedCount: 'New operations',
    skippedCount: 'Already known',
    noTransactions: 'No operations waiting to be matched.',
    valueDate: 'Date',
    details: 'Transfer title',
    proposal: 'Suggestion',
    noProposal: 'no suggestion',
    reasons: {
      NUMBER_AND_AMOUNT: '(number and amount)',
      NUMBER_ONLY: '(number in title)',
      AMOUNT_ONLY: '(matching amount)',
      COUNTERPARTY_AND_AMOUNT: '(counterparty and amount)',
    },
    document: 'Document',
    counterparty: 'Counterparty',
    dueDate: 'Due',
    amountDue: 'Amount due',
    paid: 'Paid',
    remaining: 'Remaining',
    status: 'Status',
    overdueDays: 'days overdue:',
    addPayment: 'Add payment',
    savePayment: 'Save',
    exchangeRate: 'Rate on the payment day',
    exchangeRateHint: 'The document is in a foreign currency: give the average NBP rate from the last working day '
      + 'before the payment. The difference against the document rate becomes financial income or cost.',
    paidOn: 'Payment date',
    amount: 'Amount',
    none: 'Nothing is waiting to be settled.',
    statuses: {
      UNPAID: 'unpaid',
      PARTIAL: 'partial',
      PAID: 'settled',
      OVERPAID: 'overpaid',
    },
  },
  payments: {
    title: 'Payment method',
    officePays: 'This month is paid by the accounting office — its payment method and charges are on the office side.',
    officeTitle: 'Firmeto billing',
    officeLead: 'The office pays one amount for the companies in its package — up to the ceiling — plus the individual agreement fee, if any.',
    officeSubtotal: 'Companies in the package',
    officeCeiling: 'Package ceiling',
    officeAgreement: 'Individual agreement',
    officeTotal: 'To pay',
    officeCompanies: (count: number) => `companies: ${count}`,
    current: 'You pay with',
    none: 'No payment method yet. Without one we cannot collect the fee, and after two weeks in arrears the account becomes read-only.',
    method: 'Payment method',
    methods: { card: 'Card', blik: 'BLIK' } as Record<string, string>,
    provider: 'Provider',
    providerName: (provider: string) => ({ payu: 'PayU', przelewy24: 'Przelewy24', stripe: 'Stripe', fake: 'Test provider' })[provider] ?? provider,
    recommended: 'recommended',
    add: 'Add',
    change: 'Change',
    revoke: 'Revoke mandate',
    formAtProvider: 'You enter card details at the payment provider — Firmeto never sees them.',
    onlyOwner: 'The payment method is set by the owner or an administrator.',
    expires: (month: string) => `valid until ${month}`,
    waitingTitle: 'Waiting for the provider to confirm',
    waitingText: 'The mandate usually becomes active within seconds. Refresh the page if you still do not see it.',
    charges: 'Collected fees',
    noCharges: 'Nothing has been collected yet.',
    period: 'For month',
    amount: 'Amount',
    status: 'Status',
    statuses: { pending: 'in progress', succeeded: 'collected', failed: 'failed', refunded: 'refunded' } as Record<string, string>,
    arrearTitle: (month: string) => `The fee for ${month} is outstanding`,
    arrearText: (sum: string, reason: string) => `${sum} — ${reason}.`,
    readOnlyFrom: (day: string) => `We will try again in a few days. From ${day} the account becomes read-only until the fee is paid.`,
    readOnlyTitle: 'The account is read-only',
    readOnlyText: 'You can still see and download your data; saving returns once the outstanding fee is paid. Add or change the payment method below.',
    reasons: { mandate_missing: 'no payment method', card_declined: 'declined by the provider', insufficient_funds: 'insufficient funds', expired_card: 'card expired', authentication_required: 'the bank requires confirmation', declined: 'declined by the provider' } as Record<string, string>,
  },
  billing: {
    multiCompany: (count: number) => `Discount for ${count} businesses`,
    lead: 'No revenue — no fee. The fee never exceeds 20% of that month’s revenue.',
    revenueSub: 'the basis for the fee',
    howMuch: 'How this amount came about',
    capOf: (revenue: string) => `20% of revenue ${revenue}`,
    lower: 'The lower of the two',
    plan: 'Plan',
    payer: 'Paid by',
    channel: 'Purchase',
    noRevenueTitle: 'A month without revenue costs 0 PLN',
    noRevenueText: 'We do not charge it later and do not carry it over to the next month.',
    open: 'Fees',
    title: 'Firmeto fees',
    month: 'Month',
    revenue: 'Revenue',
    planPrice: 'Plan price',
    cap: '20% cap',
    toPay: 'To pay',
    history: 'Charge history',
    charged: 'Charged',
    note: 'Notes',
    surcharge: 'Surcharge',
    credit: 'Credit',
    none: 'No charges yet.',
    plans: { RYCZALT: 'Lump-sum plan', KPIR: 'Ledger plan', FULL: 'Full accounting plan' },
    payers: { COMPANY: 'paid by the company', OFFICE: 'paid by the office' },
    channels: { WEB: 'bought on the website', APP_STORE: 'App Store subscription', GOOGLE_PLAY: 'Google Play subscription' },
    reasons: {
      TRIAL: 'Free trial — no charge.',
      NO_REVENUE: 'No revenue this month — no charge.',
      BELOW_MINIMUM: 'The calculated fee is below 5 zł — we do not charge it.',
      FREE_AFTER_LEAVING_OFFICE: 'Free period after leaving the accounting office.',
      STORE_SUBSCRIPTION: 'Subscription paid in the app store — billing goes through the store.',
    },
    statuses: {
      PENDING_CHARGE: 'to be charged',
      CHARGED: 'charged',
      PENDING_APPROVAL: 'awaiting approval',
      APPROVED: 'approved',
      REJECTED: 'rejected',
    },
  },
  accruals: {
    title: 'Prepaid expenses',
    lead: 'A yearly insurance policy or a subscription paid up front is a cost of the months it covers, not only of the month the invoice arrived. The cost moves off the expense account to prepaid expenses and comes back one instalment a month, so every tax advance sees the instalment rather than the whole invoice.',
    none: 'No schedule yet.',
    noneHint:
      'A schedule is set up on a document already posted to the journal — the expense account comes from its entry, because there is nothing to take off an account the document never touched.',
    add: 'Spread the cost over months',
    cancel: 'Cancel',
    save: 'Create the schedule',
    document: 'Document',
    documentHint: 'Only documents posted to the journal. One document has one schedule.',
    description: 'What it covers',
    amount: 'Amount to spread',
    amountHint:
      'By default what the entry posted to the expense account. Less, when the invoice also covers something belonging to this month — a handling fee next to the policy, for instance.',
    from: 'First month',
    to: 'Last month',
    period: 'Period',
    monthly: 'Instalment',
    costAccount: 'Expense account',
    accrualAccount: 'Prepaid expenses account',
    accountsHint:
      'Empty means: the expense account from the document entry and prepaid expenses from the balance sheet position (640 in the standard chart). Name an account when the entry charged several of them, or when the cost is to wait longer than a year.',
    month: 'Month',
    release: 'Post the instalments',
    released: 'Instalments posted',
    releasedTotal: 'Posted',
    through: 'through',
    skipped: 'Schedules skipped',
    plan: 'Schedule',
    showPlan: 'Schedule',
    hidePlan: 'Hide the schedule',
    instalment: 'Instalment',
    remaining: 'Left',
    entry: 'Entry',
    waiting: 'waiting',
    remove: 'Withdraw the schedule',
    removeReason: 'Reason for withdrawing the schedule — a reversal of moving the cost to prepaid expenses:',
  },
  provisions: {
    title: 'Accrued liabilities',
    lead: 'The cost belongs to this period and the invoice has not arrived yet: the audit of this year\u2019s statement, annual bonuses, a service delivered but not invoiced (art. 39 s. 2 of the Accounting Act). Accruing it puts the cost in the month it belongs to and sets the liability aside; settling it releases the provision when the invoice arrives or when the obligation never arises.',
    none: 'No provisions yet.',
    noneHint:
      'A provision does not come from a document, because there is no document yet — the amount, the account and the months come from the bookkeeper, along with the reasoning behind the estimate.',
    add: 'Add a provision',
    addHint:
      'There is no document to pick here: the whole point is that the invoice has not arrived. The amount is an estimate, so next to it we ask where it came from.',
    cancel: 'Cancel',
    save: 'Add the provision',
    description: 'What it covers',
    basis: 'Where the amount comes from',
    basisHint:
      'For example: “the audit engagement of 12 Nov 2026, PLN 30,000 net”. An estimate with no reasoning cannot be defended at an audit or an inspection, and this is the only place the reasoning is kept.',
    amount: 'Amount',
    from: 'First month',
    to: 'Last month',
    periodHint:
      'The same month is fine: a provision for the statutory audit belongs to one month. Annual bonuses accrue over twelve.',
    period: 'Period',
    costAccount: 'Expense account',
    costAccountHint:
      'The account the cost goes to — the same one the invoice will land on later. The bookkeeper names it, because there is no posting to take it from.',
    provisionAccount: 'Provision account',
    provisionAccountHint:
      'Empty means: the account with the “accrued liabilities” role (641 in the model chart). The balance-sheet label is not enough here, because accrued liabilities and deferred income sit in the same position on the liabilities side.',
    month: 'Month',
    accrue: 'Accrue provisions',
    accrued: 'Accrued',
    accruedTotal: 'Accrued',
    through: 'through',
    balance: 'Provision balance',
    closed: 'settled',
    skipped: 'Provisions skipped',
    plan: 'Accruals',
    showPlan: 'Accruals',
    hidePlan: 'Hide accruals',
    instalment: 'Instalment',
    remaining: 'Remaining',
    entry: 'Entry',
    waiting: 'waiting',
    settle: 'Settle',
    settleAccount:
      'Which account should the provision go to? With an invoice — the expense account it charged (suggested). When the obligation never arose — the other operating income account.',
    settlements: 'Provision settlements',
    settledOn: 'Date',
    settledAmount: 'Amount',
    settledAccount: 'Account',
    remove: 'Withdraw the provision',
    removeReason: 'Why withdraw the provision — possible only while nothing has reached the books:',
  },
  assets: {
    lead: 'The fixed asset register and the depreciation write-offs that land in costs.',
    noneHint:
      'You can add a fixed asset here or tick it while entering a cost — the document then goes straight into the register instead of into costs at once.',
    depreciating: 'Depreciating',
    open: 'Fixed assets',
    title: 'Fixed assets and depreciation',
    none: 'No fixed assets yet.',
    add: 'Add fixed asset',
    save: 'Save',
    name: 'Name',
    inventoryNumber: 'Inventory number',
    kstCode: 'Classification code',
    acquired: 'Purchase date',
    inService: 'In service since',
    value: 'Initial value',
    method: 'Method',
    rate: 'Annual rate',
    rateHint: 'A fraction: 0.20 means 20%. Rates come from the statutory table.',
    month: 'Month',
    bookDepreciation: 'Post depreciation',
    booked: 'Entries posted',
    includeDisposed: 'Show disposed',
    showPlan: 'Plan',
    hidePlan: 'Hide plan',
    plan: 'Depreciation plan',
    period: 'Month',
    writeOff: 'Write-off',
    remaining: 'Remaining',
    bookedTotal: 'Posted in total',
    inLedger: 'In the ledger',
    inJournal: 'In the journal',
    posted: 'Journal entries',
    skipped: 'Assets skipped in the journal',
    removedFromBooks: 'The asset left the books',
    removedAccumulated: 'accumulated depreciation',
    removedNet: 'net value in costs',
    removedEntry: 'entry',
    removedMissing: 'The asset was withdrawn from the register but did not leave the books:',
    through: 'through',
    dispose: 'Dispose',
    disposalReason: 'Reason for disposal (sale, scrapping):',
    disposed: 'disposed',
    methods: { LINIOWA: 'straight-line', JEDNORAZOWA: 'one-time', DEGRESYWNA: 'declining balance' },
  },
  pitAnnual: {
    title: 'Annual settlement',
    priorYearLoss: 'Loss from previous years',
    revenue: 'Revenue for the year',
    income: 'Income for the year',
    badDebt: 'Bad debt correction',
    social: 'Social contributions paid',
    health: 'Health contribution paid',
    deducted: 'Deducted from the base',
    taxBase: 'Tax base',
    tax: 'Tax for the year',
    advances: 'Advances paid during the year',
    toPay: 'To pay',
    toRefund: 'Overpaid, to be refunded',
    forms: { PIT_36: 'PIT-36 · tax scale', PIT_36L: 'PIT-36L · flat tax', PIT_28: 'PIT-28 · lump sum' },
    notes: {
      ESTIMATE: 'Computed from the documents in Firmeto — an estimate, not a completed return.',
      ADVANCES_ASSUMED_PAID: 'We assume the advances computed during the year were paid.',
      BUSINESS_INCOME_ONLY: 'This is the tax on the business. A return also covers other sources — employment, rent, contracts — which we do not know about.',
      NO_RELIEFS: 'Reliefs and deductions are not included: child relief, IP Box, donations, joint filing with a spouse or the 1.5% for a public benefit organisation.',
      LOSS_FROM_USER: 'A loss from previous years counts only when you enter it — it depends on settlements we do not hold.',
      HEALTH_NOT_DEDUCTIBLE_ON_SCALE: 'On the tax scale the health contribution has not been deductible since 2022.',
      RYCZALT_HEALTH_HALF: 'Under the lump sum, half of the health contribution paid is deducted from revenue.',
      INVENTORY_MISSING: 'An approved stocktake is missing, so the annual income may be untrue — goods left on the shelf are not a cost of this year.',
      FULL_BOOKS_NOT_SUPPORTED: 'With full books the annual income comes from the books; we do not compute that yet.',
      PARTNERS_SPLIT: 'A partnership: each partner settles separately, on their share and in their own form.',
      UNKNOWN_YEAR: 'We do not have the tax parameters for this year.',
      BAD_DEBT_IN_INCOME: 'Income includes the recorded bad debt corrections for income tax (art. 26i of the PIT Act).',
      BAD_DEBT_NOT_IN_RYCZALT: 'Under the lump sum, bad debt relief changes revenue per rate (art. 11 of the Lump Sum Act) — we do not know which rate an unpaid document belongs to, so we do not count it.',
    },
  },
  healthAnnual: {
    title: 'Annual health contribution',
    deadline: (rok: number) => `settled in the April return — by 20 May ${rok}`,
    income: 'Income for the year',
    revenue: 'Revenue for the year',
    months: 'Months covered',
    basis: 'Annual base',
    rate: 'Rate',
    due: 'Contribution for the year',
    paid: 'Contributions paid during the year',
    toPay: 'To pay',
    toRefund: 'Overpaid, to be refunded',
    forms: {
      SKALA: 'Tax scale · 9% of income',
      LINIOWY: 'Flat tax · 4.9% of income',
      RYCZALT: 'Lump sum · revenue bracket',
      CIT: 'CIT',
    },
    notes: {
      ESTIMATE: 'Computed from the documents in Firmeto — an estimate, not a completed DRA return.',
      MONTHLY_ASSUMED_PAID: 'We assume the contributions computed during the year were paid.',
      BUSINESS_ONLY: 'We only know the business. If you have another basis for health insurance — employment, a second business, a partnership — the settlement covers that too.',
      MINIMUM_APPLIED: 'The minimum contribution applies: 9% of the minimum wage for every month covered. On a low income that is what you pay, and no less — including under the flat tax, where the minimum is computed at 9% rather than 4.9%.',
      RYCZALT_BRACKET_FROM_REVENUE: 'Under the lump sum the bracket comes from the **actual revenue of the year**. If you paid during the year on the previous year\u2019s revenue or in another bracket, that is where the difference comes from.',
      SOCIAL_NOT_DEDUCTED: 'We do not reduce the base by the social contributions paid — here they are a cost in the book.',
      BAD_DEBT_NOT_IN_BASIS: 'Bad debt corrections change taxable income, but we leave the health contribution base alone.',
      INVENTORY_MISSING: 'No approved stocktake, so the annual income may be wrong — and the contribution with it.',
      FULL_BOOKS_NOT_SUPPORTED: 'With full books the annual income is determined from the books; we do not compute that yet.',
      FORM_CHANGED: 'The form of taxation changed during the year. The annual settlement is then made separately for each form, and we compute one — check this figure with your bookkeeper.',
      PARTNERS_SPLIT: 'A partnership: each partner settles their own contribution, on their own share of the income.',
      UNKNOWN_YEAR: 'We have no social security figures for this year.',
    },
  },
  suspensions: {
    title: 'Business suspension',
    lead: 'A suspension is filed with CEIDG and lasts at least 30 days (art. 22–25 of the Entrepreneurs Act). It does not close the business: the tax number, the books and the unpaid invoices remain. It does change contributions, deadlines and depreciation, which is why periods are kept here rather than a switch.',
    none: 'No suspension recorded.',
    noneHint: 'Enter the period the business was suspended for — then no contributions or tax advance are computed for those months, and depreciation stops.',
    add: 'Record a suspension',
    save: 'Save',
    cancel: 'Cancel',
    startsOn: 'First day of the suspension',
    endsOn: 'Last day of the suspension',
    endsOnHint: 'Empty means “still suspended” — in CEIDG a suspension has no upper time limit. A future date is fine too: a suspension is filed in advance.',
    note: 'Reason',
    noteHint: 'For example “childcare”, “seasonal break”. At an inspection it explains why there are no contributions for those months.',
    days: 'Days',
    open: 'still suspended',
    tooShort: 'shorter than 30 days',
    resume: 'Resume business',
    resumePrompt: 'Last day of the suspension (the day before the business resumes):',
    remove: 'Delete the suspension',
    removeReason: 'Deleting it changes contributions and depreciation for those months. Give a reason (e.g. a wrong date):',
    effects: 'What follows from it: a fully suspended month has no contributions and no tax advance, and a partly suspended one has the social base reduced in proportion to the days (the health contribution stays whole). Depreciation stops from the month after the suspension. Days of suspension do not count as days of running the business for Mały ZUS Plus. A JPK_V7 file may be required even during a suspension — for intra-EU acquisitions, imported services and corrections.',
  },
  inventory: {
    lead: 'The opening and closing stocktake feed the income for the whole year, not for a month.',
    noneHint:
      'A stocktake is made on the first and the last day of the year. With no goods or materials it is made with a zero value — but it has to be made.',
    items: 'Stocktake items',
    open: 'Stocktaking',
    title: 'Stocktaking and annual income',
    year: 'Year',
    none: 'No stocktaking recorded for this year.',
    add: 'New stocktaking',
    save: 'Save',
    approve: 'Approve',
    delete: 'Delete',
    kind: 'Type',
    takenOn: 'Date',
    value: 'Value',
    status: 'Status',
    itemName: 'Name',
    quantity: 'Quantity',
    unit: 'Unit',
    unitPrice: 'Price',
    revenue: 'Revenue',
    purchases: 'Goods purchased',
    opening: 'Opening stock',
    closing: 'Closing stock',
    costOfGoodsSold: 'Cost of goods sold',
    income: 'Income',
    kinds: { OPENING: 'opening', CLOSING: 'closing', INTERIM: 'during the year' },
    statuses: { DRAFT: 'draft', APPROVED: 'approved' },
    warnings: {
      NO_OPENING_INVENTORY: 'No approved opening stock — income may be overstated.',
      NO_CLOSING_INVENTORY: 'No approved closing stock — goods still on the shelf count as a cost, so income is understated.',
      DRAFT_INVENTORY: 'There is a draft stocktaking. Only approved ones affect the income.',
      NOT_KPIR: 'This company does not keep the ledger — income is determined differently.',
    },
  },
  verification: {
    notice: 'Confirm your e-mail with the code we sent. Without it you cannot add a company.',
    code: 'Code from the message',
    confirm: 'Confirm',
    resend: 'Send the code again',
    resent: 'Code sent.',
  },
  badDebt: {
    title: 'Bad debt relief',
    lead: 'An unpaid invoice 90 days past its due date changes VAT on both sides. The buyer **must** reduce input VAT (art. 89b s. 1 of the VAT Act) and restore it after payment; the seller **may** correct output VAT (art. 89a s. 1). The correction belongs to the period in which the ninetieth day fell.',
    none: 'Nothing to correct in this period.',
    noneHint: 'This is where invoices 90 days past their due date appear, along with those paid after a correction. None of them is waiting for a decision.',
    purchases: 'Purchases — correction required',
    purchasesHint: 'These invoices were not paid within 90 days of their due date. Input VAT must be reduced — skipping it overstates the deduction.',
    sales: 'Sales — relief to consider',
    salesHint: 'These invoices were not paid by the customer. The relief is a right, not an obligation, and has conditions our data cannot see (such as whether the debtor is insolvent) — the decision is yours.',
    restorations: 'Paid after a correction — VAT comes back',
    restorationsHint: 'The debt was paid, so the corrected VAT returns in the period of payment (art. 89b s. 4 and art. 89a s. 4). Paying part of the debt returns part of the tax.',
    document: 'Document',
    counterparty: 'Counterparty',
    dueDate: 'Due date',
    ninetiethDay: '90th day',
    unpaid: 'Unpaid',
    vat: 'VAT of the correction',
    pick: 'Select',
    apply: 'Record the corrections',
    applyHint: 'Recorded corrections go into the JPK file for the proper period.',
    skip: 'Not correcting',
    skipReason: 'Why are you not correcting? The reason will be recorded (e.g. the debtor is in restructuring, art. 89b s. 1b):',
    late: 'correction belongs to',
    recorded: 'Recorded in this period',
    kind: 'Kind',
    reason: 'Reason',
    undo: 'Withdraw the decision',
    kinds: { DECREASE: 'tax reduced', INCREASE: 'restored after payment', SKIPPED: 'no correction' },
    taxes: 'Tax',
    taxNames: { VAT: 'VAT', INCOME: 'Income tax' },
    income: {
      lead: 'The same unpaid invoice also changes income tax. The debtor **must** add the unpaid cost back to the tax base (art. 18f s. 1 p. 2 of the CIT Act, art. 26i s. 1 p. 2 of the PIT Act); the creditor **may** subtract the unpaid revenue from it (s. 1 p. 1). The amounts are net, and the decision goes into no file — it goes into the advance and the annual settlement.',
      purchasesHint: 'These invoices were not paid within 90 days of their due date. The unpaid cost has to be added back to income — skipping it understates the advance.',
      salesHint: 'These invoices were not paid by the customer. The unpaid revenue may be taken out of income; it is a right, not an obligation, and has conditions our data cannot see (such as whether the debtor is insolvent).',
      restorationsHint: 'The debt was paid, so the correction comes back in the period of payment: the debtor regains the cost, the creditor reports the revenue. Paying part of the debt returns part of the amount.',
      amount: 'Net amount',
      applyHint: 'Recorded corrections change the income tax advance and the annual settlement. They do not go into the JPK file.',
      skipReason: 'Why are you not correcting? The reason will be recorded (e.g. the debtor is insolvent or the receivable has already been written off):',
    },
  },
  ledger: {
    ordinal: 'No.',
    entries: 'Ledger entries',
    columnsNote: 'columns as required by the KPiR regulation',
    revenueColumn: 'column 7 — sales of goods and services',
    open: 'Ledger',
    title: 'Ledger and VAT registers',
    month: 'Month',
    date: 'Date',
    document: 'Document',
    description: 'Description',
    counterparty: 'Counterparty',
    column: 'Col.',
    amount: 'Amount',
    none: 'No entries this month.',
    revenue: 'Revenue',
    costsTotal: 'Costs',
    result: 'Income',
    vatTitle: 'VAT for the period',
    vatDue: 'Output VAT',
    vatDeductible: 'Input VAT',
    vatToPay: 'To pay',
    vatToCarry: 'Surplus carried forward',
    quarterly: 'quarterly settlement',
  },
  office: {
    served: 'Served',
    awaitingConsent: 'Awaiting the company\u2019s consent',
    tab: 'Office',
    companiesTab: 'My companies',
    title: 'Office panel',
    none: 'You do not work in any accounting office yet.',
    subtitle: 'The accounting office workspace: client companies, monthly steps, accountant assignments.',
    noneHint:
      'Start an office if you keep books for other companies. If someone is to add you to theirs, ask for an invitation — it will arrive at your e-mail address.',
    create: 'Create office',
    name: 'Office name',
    nip: 'Office tax ID',
    month: 'Month',
    onlyMine: 'Only my clients',
    client: 'Client',
    assigned: 'Handled by',
    assignments: 'Accountant assignments',
    assignmentsHint: 'The agreement says the office serves the company. Only an assignment says who from '
      + 'the office may enter its books — and one client can have several.',
    assignmentRoles: { LEAD: 'lead', BACKUP: 'backup', VIEWER: 'read-only' },
    assignmentRole: 'Role for this client',
    assignPerson: 'Choose a person',
    assign: 'Assign',
    endAssignment: 'End assignment',
    nobodyAssigned: 'Nobody assigned yet',
    noServedClients: 'No company has accepted a service proposal yet.',
    invoices: 'Invoices',
    costs: 'Costs',
    revenue: 'Revenue',
    costsAmount: 'Costs',
    state: 'Status',
    ready: 'ready to settle',
    toDo: 'to do',
    noClients: 'The office has no clients yet.',
    members: 'Team',
    clients: 'Clients',
    role: 'Role',
    roles: {
      OFFICE_OWNER: 'Office owner',
      OFFICE_ADMIN: 'Administrator',
      ACCOUNTANT: 'Accountant',
      ASSISTANT: 'Assistant',
    },
  },
  invoices: {
    openInvoice: 'Open',
    backToList: 'Back to the list',
    detail: {
      preview: 'Invoice preview',
      previewOpen: 'Open the PDF',
      pdf: 'Download the PDF',
      draftNoDocument: 'A draft has no number yet — the document appears once it is issued.',
      payments: 'Payments',
      due: 'To pay',
      paid: 'Paid',
      remaining: 'Left',
      addPayment: 'Record a payment',
      paidOn: 'Payment date',
      amount: 'Amount',
      exchangeRate: 'Rate on the payment day',
      exchangeRateHint: 'The document is in a foreign currency: give the average NBP rate from the last working '
        + 'day before the payment. The difference against the invoice rate becomes financial income or cost.',
      method: 'Method',
      note: 'Note',
      noPayments: 'No payment recorded yet.',
      partialHint: 'An amount smaller than what is left is a partial payment: it settles what came in '
        + 'and the rest stays in the settlements.',
      deletePayment: 'Remove',
      confirmDeletePayment: (kwota: string) => `Remove the payment of ${kwota}? It goes back to the settlements as unpaid.`,
      internalNote: 'Internal note',
      internalNoteHint: 'The note stays inside the company: it is not on the printed invoice and not in the '
        + 'KSeF file. That is why it can be added to an invoice that is already issued.',
      saveNote: 'Save the note',
      noteSaved: 'Note saved.',
    },
    grossAmount: 'Gross',
    netAmount: 'Net',
    open: 'Invoices',
    title: 'Sales invoices',
    tabAll: 'All',
    tabUnpaid: 'Unpaid',
    tabCorrections: 'Corrections',
    filter: 'Filter: number, contact, amount',
    allYears: 'All years',
    payment: 'Payment',
    soon: 'planned — not in the code yet',
    ksefFutureTitle: 'Sending to KSeF',
    ksefFutureText:
      'Today only "KSeF file" works: a download of the document in the FA(3) format, which you send yourself. Once sending through the gateway exists, the "Status" column will carry the KSeF reference number and the acceptance receipt.',
    new: 'New invoice',
    none: 'No invoices yet.',
    number: 'Number',
    status: 'Status',
    issueDate: 'Issue date',
    saleDate: 'Sale date',
    dueDate: 'Due date',
    counterparty: 'Buyer',
    viesTitle: 'EU VAT number:',
    viesCheck: 'Check in VIES',
    viesWarning:
      'With the 0% or "np" rate for an EU counterparty, the right to it depends on a valid EU VAT number (art. 42 s. 1 p. 1 of the VAT Act). Check the number or hold other evidence — you can issue the invoice either way.',
    chooseCounterparty: 'Choose a counterparty',
    priceMode: 'Prices given',
    lines: 'Items',
    addLine: 'Add item',
    removeLine: 'Remove',
    lineName: 'Name',
    quantity: 'Qty',
    unit: 'Unit',
    unitPrice: 'Price',
    netUnitPrice: 'Net price',
    grossUnitPrice: 'Gross price',
    vatAmount: 'VAT amount',
    vatTotal: 'Total VAT',
    vatRate: 'VAT',
    ryczaltRate: 'Lump-sum rate',
    correct: 'Correct',
    ksefFile: 'KSeF file',
    correction: 'correction',
    correctionTo: 'Corrective invoice to',
    correctionHint: 'Enter the lines as they should be after the correction. We calculate the difference.',
    correctionCause: 'Cause',
    correctionReason: 'Reason',
    linesAfter: 'Lines after correction',
    causes: {
      MISTAKE: 'Mistake on the original invoice',
      EVENT: 'Event after issuing (discount, return)',
    },
    amount: 'Amount',
    fromCatalog: 'From catalogue',
    addToCatalog: 'Add to catalogue',
    editDraft: 'Edit draft',
    draftUnavailable: 'This is no longer a draft. Refresh the invoice list.',
    noMatches: 'No matches',
    searchFailed: 'Could not load results. Try again.',
    refineSearch: 'More results — type a more specific search.',
    noCatalog: 'Without catalogue item',
    saveDraft: 'Save draft',
    issue: 'Issue and assign number',
    delete: 'Delete draft',
    totals: 'Total',
    net: 'net',
    vat: 'VAT',
    gross: 'gross',
    draftHint: 'A draft can be edited freely. Once issued, the invoice gets a number and cannot be changed — corrections require a credit note.',
    statuses: { DRAFT: 'Draft', ISSUED: 'Issued', CANCELLED: 'Cancelled' },
    priceModes: { NET: 'net', GROSS: 'gross' },
  },
  bank: {
    checksumOk: 'Checksum is correct',
    whiteListTitle: 'VAT taxpayer white list',
    whiteListText:
      'Eventually we will check on save whether the account appears on the Ministry of Finance list — for payments above 15,000 PLN this has tax consequences. Today we only verify the checksum of the number.',
    open: 'Bank accounts',
    title: 'Company bank accounts',
    iban: 'Account number',
    currency: 'Currency',
    purpose: 'Purpose',
    bankName: 'Bank',
    add: 'Add account',
    save: 'Save account',
    none: 'No accounts yet.',
    remove: 'Remove from list',
    hint: 'The main account number goes on invoices. We verify the checksum as you type it in.',
    purposes: { MAIN: 'Main', VAT: 'VAT account', TAX_REFUND: 'Tax refund' },
  },
  numbering: {
    lead: 'Numbering can be changed as long as no document of that type has been issued in the year.',
    markers: 'Pattern markers',
    issued: 'Issued',
    locked: 'Locked — documents have been issued',
    changeable: 'Can be changed',
    open: 'Numbering',
    title: 'Document numbering',
    document: 'Document',
    pattern: 'Pattern',
    next: 'Next number',
    save: 'Save pattern',
    reset: 'Reset',
    resetYear: 'Yearly',
    resetMonth: 'Monthly',
    hint: '{nr} is replaced with the next number, {rok} with the year, {rr} with its last two digits. Month: {mm} → 09, {m} → 9, {mr} → IX, {msc} → WRZ (Polish abbreviations, fixed). Monthly numbering restarts every month, so the number must contain the month.',
    types: { INVOICE: 'Invoice', CORRECTION: 'Correction', PROFORMA: 'Proforma', RECEIPT: 'Receipt' },
  },
  journal: {
    open: 'Journal',
    title: 'Journal',
    lead: 'Postings in chronological order. Every event has two sides: the same amount on the debit '
      + 'of one account and the credit of another.',
    view: 'View',
    year: 'Financial year',
    month: 'Month',
    trialBalance: 'Trial balance',
    pending: 'To post',
    pendingHint: 'A document does not post itself. A journal entry cannot be edited — a correction is '
      + 'a reversal — so you see the whole entry here before it reaches the books.',
    nothingPending: 'Everything is posted.',
    postAll: 'Post all',
    postPicked: (ile: number) => `Post selected (${ile})`,
    trialBalanceHint: 'Opening balances are shown apart from the year\u2019s turnover — they are a '
      + 'carried-forward state, not something that happened this year.',
    newEntry: 'New entry',
    formHint: 'Account numbers come from the chart of accounts. Post to the lowest-level account; '
      + 'a settlement account needs a counterparty.',
    entryNo: 'No.',
    postingDate: 'Posting date',
    description: 'Description',
    lines: 'Lines',
    account: 'Account',
    name: 'Name',
    side: 'Side',
    amount: 'Amount',
    debit: 'Dr',
    credit: 'Cr',
    addLine: 'Add line',
    removeLine: 'Remove',
    balanced: 'Balanced',
    difference: 'Out by',
    save: 'Save entry',
    cancel: 'Cancel',
    empty: 'No entries in this period yet.',
    opening: 'opening balance',
    reversal: 'reversal',
    reverse: 'Reverse',
    reverseTitle: 'Reversal instead of editing',
    reverseHint: 'An entry is never changed. A reversal flips the sides with a new entry and the original '
      + 'stays — so the books show not only the final state but how it came about.',
    reverseReason: 'Reason for the reversal',
    openingDebit: 'Opening Dr',
    openingCredit: 'Opening Cr',
    turnoverDebit: 'Turnover Dr',
    turnoverCredit: 'Turnover Cr',
    closingDebit: 'Balance Dr',
    closingCredit: 'Balance Cr',
    totals: 'Total',
    controlOk: 'Control totals agree',
    controlFailed: 'Control totals do not agree',
    offBalanceNote: 'Off-balance-sheet accounts appear in the rows but stay out of the control totals: '
      + 'such a posting is single-sided and never balances.',
    entryCount: (count: number) => `Entries: ${count}`,
    fx: {
      tab: 'Currency revaluation',
      title: 'Currency revaluation as at',
      lead: 'At the balance sheet date, cash and settlements in foreign currencies are measured at that day\u2019s rate (art. 30 s. 1 of the Accounting Act) — otherwise the balance sheet shows a value that no longer exists. We do not suggest rates: enter the NBP mid rate applicable on the valuation date.',
      asOf: 'Valuation date',
      rate: 'Rate',
      item: 'Item',
      account: 'Account',
      balanceCurrency: 'Balance in currency',
      balance: 'In the books',
      revalued: 'Revalued',
      difference: 'Difference',
      gain: 'Financial income',
      loss: 'Financial costs',
      preview: 'Recalculate',
      post: 'Post the revaluation',
      posted: 'Revaluation posted — entry no.',
      nothingToPost: 'The rates have not moved, so there is nothing to post.',
      exists: 'A revaluation for this date is already in the books — entry no.',
      none: 'Nothing to revalue.',
      noneHint: 'Revaluation covers foreign-currency bank accounts and open settlements in a foreign currency. As at this date the company has neither.',
      taxNote:
        'Revaluation differences are unrealised. If the company determines exchange differences by the tax method (art. 9b s. 1 p. 1 of the CIT Act) they are neither taxable income nor a deductible cost — and the advance is computed from the books, so we do not exclude them today.',
      kinds: { ACCOUNT: 'currency account', SETTLEMENT: 'settlement' },
    },
    settlements: 'Open items',
    settlementsLead: 'Who owes what and since when. An item is created when a document is posted, a bank payment settles it, and the balance is computed from the entries — nothing is stored separately.',
    asOf: 'As of',
    onlyOpen: 'Open only',
    receivables: 'Receivables',
    payables: 'Payables',
    bucket: {
      NOT_DUE: 'not due',
      DAYS_1_30: '1–30 days',
      DAYS_31_60: '31–60 days',
      DAYS_61_90: '61–90 days',
      OVER_90: 'over 90 days',
    },
    total: 'Total',
    document: 'Document',
    documentDate: 'Date',
    dueDate: 'Due',
    settled: 'Settled',
    remaining: 'Remaining',
    overdue: (days: number) => `${days} days overdue`,
    noItems: 'There are no open items.',
    counterparty: 'Counterparty',
    counterpartyNone: '— no counterparty —',
    settlement: 'Open item',
    settlementNew: '— new item —',
    offset: 'Offset',
    offsetHint: 'A receivable and a payable of the same counterparty cancel out in a single entry — up to the smaller of the two, or less.',
    offsetReceivable: 'Receivable',
    offsetPayable: 'Payable',
    offsetAmount: 'Amount',
    offsetDate: 'Date',
    offsetSave: 'Save the offset',
    receivableLabel: 'receivable',
    payableLabel: 'payable',
    openingTitle: 'Opening balance from the previous year',
    openingLead: 'Balances of balance-sheet accounts as of the last day of the previous year, open items one by one with document and due date, previous year’s result on the prior years’ result account — in a single technical entry.',
    openingPreview: 'Show what carries over',
    openingRecord: 'Post the opening balance',
    openingReplace: 'Replace: reverse and post anew',
    openingExists: 'This year already has an opening balance. Replacing reverses it and recomputes from the previous year as of today.',
    openingResult: 'Previous year’s result',
    openingCounterparty: 'Counterparty',
    openingDocument: 'Document',
    openingDone: 'Opening balance posted.',
  },
  reports: {
    open: 'Statements',
    title: 'Balance sheet and profit and loss account',
    lead: 'Both tables come from the same balances as the trial balance, so the statements cannot '
      + 'say anything other than the books do.',
    year: 'Financial year',
    month: 'As at the end of',
    balanceSheet: 'Balance sheet',
    incomeStatement: 'Profit and loss account',
    assets: 'Assets',
    liabilities: 'Equity and liabilities',
    revenues: 'Revenue',
    costs: 'Costs',
    account: 'Account',
    name: 'Name',
    amount: 'Amount',
    total: 'Total',
    result: 'Result for the year',
    profit: 'Profit',
    loss: 'Loss',
    resultNote: 'The result sits on the liabilities side — it is what closes the balance sheet equation.',
    balanced: 'Balance sheet adds up',
    notBalanced: 'Balance sheet does not add up',
    notBalancedHint: 'Assets do not equal liabilities plus the result. Start from the trial balance — '
      + 'that is where you can see which account is out.',
    empty: 'There are no entries in this year yet, so the statements are empty.',
    goToJournal: 'Open the journal',
    statutoryNote: 'This is laid out by account, not by the schedule to the Accounting Act. The amounts '
      + 'and totals are final; the official version only needs accounts mapped to positions such as '
      + '"A. Fixed assets" or "B.II. Short-term receivables".',
    settlementsNote: 'In the statutory layout settlements are gross — balance per counterparty, receivables separate from payables (no offsetting). The by-account layout shows one balance per account.',
    previousYear: 'Previous year',
    currentYear: 'Current year',
    previousNoChart: 'The previous year has no chart of accounts, so the comparative column is empty.',
    previousDifferentTemplate: 'The previous year used a different appendix — positions do not correspond, so the comparative column in the statutory layout is empty.',
    layout: 'Layout',
    layoutStatutory: 'Statutory',
    layoutAccounts: 'By account',
    templates: { ZAL_1: 'Schedule 1 — full layout', ZAL_4: 'Schedule 4 — micro entity', ZAL_5: 'Schedule 5 — small entity' },
    position: 'Item',
    unmappedTitle: (ile: number) => ile === 1 ? 'One account with a balance has no statement item' : `${ile} accounts with a balance have no statement item`,
    unmappedHint: 'Their amounts are missing from the table, so the totals below are incomplete. Assign items in the chart of accounts — separately for a debit and a credit balance.',
    unmappedGoToChart: 'Assign in the chart of accounts',
    computedNote: 'Bold items are calculated from other items ("Profit (loss) on sales (A − B)"); "of which" items are part of the parent amount, not a component of it.',
    noChart: 'This year has no chart of accounts, so there is nothing to roll up into the statutory layout.',
    accountsNote: 'This is the by-account layout — the reference in which nothing can go missing. The statutory layout is next to it.',
  },
  chart: {
    open: 'Chart of accounts',
    positionDebit: 'Statement item — debit balance',
    positionCredit: 'Statement item — credit balance',
    positionNone: '— not assigned',
    positionHint: 'A two-sided account lands in different items depending on the balance side. An account without an item is shown by the statement as unassigned.',
    sections: { ASSETS: 'Assets', LIABILITIES: 'Equity and liabilities', INCOME: 'Profit and loss account' },
    title: 'Company chart of accounts',
    lead: 'The chart belongs to a fiscal year. Entries are posted to the lowest-level accounts — an account '
      + 'that has subsidiary accounts is the sum of them.',
    year: 'Fiscal year',
    empty: 'This year has no chart of accounts yet',
    emptyHint: 'Start from a template, or copy last year’s chart and change what needs changing.',
    create: 'Create the chart',
    source: 'Source',
    sourceTemplate: 'From a template',
    sourcePrevious: 'Copy of last year',
    sourceEmpty: 'Empty',
    previewHint: (count: number) => `This is how the chart will look — ${count} accounts. You can add analytics afterwards.`,
    previewEmpty: 'An empty chart makes sense when no template fits. You will add the accounts yourself.',
    template: 'Template',
    templateName: 'Template name',
    saveAsTemplate: 'Save as template',
    number: 'Account',
    numberHint: 'The number is a three-digit general ledger account plus a subsidiary number after a dash, '
      + 'for example 402-1. The general ledger account must exist before you open a subsidiary account under it.',
    name: 'Name',
    kind: 'Type',
    side: 'Normal balance',
    features: 'Features',
    tax: 'Tax treatment',
    role: 'Role in posting',
    sum: 'Sum of subsidiaries',
    settlement: 'Settlement account',
    currency: 'Foreign currency',
    inactive: 'withdrawn',
    activeAccount: 'Suggest when posting',
    add: 'Add account',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    noYear: 'There is no fiscal year yet, and the chart of accounts belongs to a year.',
    goToPeriods: 'Go to periods',
    notFullBooks: 'This company does not keep full accounts',
    booksUnknown: 'We do not know yet what books this company keeps',
    booksUnknownHint: 'There are no tax settings in force today. A chart of accounts belongs to full '
      + 'accounting — complete the settings and we will show the rest.',
    notFullBooksHint: 'The chart of accounts is a tool of full accounting. The type of books is set in the company settings.',
    onlyAccountant: 'The chart of accounts is kept by the accountant',
    onlyAccountantHint: 'Under full accounting only a person with the Accountant role posts entries. '
      + 'You can see the chart here, but you do not change it.',
    accountCount: (count: number) => `Accounts in the chart: ${count}`,
    kinds: { BALANCE: 'Balance sheet', RESULT: 'Income statement', OFFBALANCE: 'Off balance sheet' },
    sides: { DEBIT: 'Debit (asset)', CREDIT: 'Credit (liability)', BOTH: 'Both sides', NONE: '—' },
    taxes: {
      DEDUCTIBLE: 'Tax-deductible',
      NON_DEDUCTIBLE: 'Not deductible',
      TAXABLE: 'Taxable revenue',
      NON_TAXABLE: 'Non-taxable revenue',
    },
    statements: { ZAL_1: 'Annex 1', ZAL_4: 'Annex 4 — micro entity', ZAL_5: 'Annex 5 — small entity' },
    variants: { BY_KIND: 'By nature of expense', BY_FUNCTION: 'By function of expense', BOTH: 'Both formats' },
    scopes: { SYSTEM: 'Firmeto', OFFICE: 'Office', COMPANY: 'This company' },
    groups: {
      0: 'Non-current assets',
      1: 'Cash and bank accounts',
      2: 'Settlements and claims',
      3: 'Materials and goods',
      4: 'Costs by nature',
      5: 'Costs by type of activity',
      6: 'Finished goods and accruals',
      7: 'Revenue and the cost of earning it',
      8: 'Equity, provisions and the financial result',
      9: 'Off-balance-sheet accounts',
    },
    roles: {
      RECEIVABLES: 'Receivables from customers',
      PAYABLES: 'Payables to suppliers',
      VAT_DUE: 'Output VAT',
      VAT_INPUT: 'Input VAT',
      VAT_SETTLEMENT: 'VAT settlement',
      SALES_SERVICES: 'Sales of services',
      SALES_GOODS: 'Sales of goods',
      COST_OF_GOODS: 'Cost of goods sold',
      GOODS: 'Goods',
      COSTS_MATERIALS: 'Materials and energy',
      COSTS_SERVICES: 'External services',
      COSTS_TAXES: 'Taxes and fees',
      COSTS_SALARIES: 'Salary costs',
      COSTS_SOCIAL: 'Social security and other benefits',
      COSTS_OTHER: 'Other costs by kind',
      FIXED_ASSETS: 'Fixed assets',
      INTANGIBLE_ASSETS: 'Intangible assets',
      ACCUMULATED_DEPRECIATION: 'Accumulated depreciation of fixed assets',
      ACCUMULATED_AMORTISATION: 'Accumulated amortisation of intangibles',
      DEPRECIATION_COST: 'Depreciation (cost)',
      CASH: 'Cash',
      BANK: 'Current account',
      INCOME_TAX: 'Income tax',
      SOCIAL_SECURITY: 'Social security settlements (ZUS)',
      PAYROLL: 'Salaries',
      RESULT: 'Financial result',
      PRIOR_YEARS_RESULT: 'Prior years’ result',
    },
  },
  vatUe: {
    title: 'VAT-UE summary',
    lead: 'Anyone selling goods or services to EU taxpayers files a recapitulative statement — **monthly, even when VAT is settled quarterly** (art. 100 of the VAT Act). With no intra-community transactions there is nothing to file.',
    none: 'No intra-community transactions this month.',
    noneHint: 'This covers invoices to EU counterparties at the "np" rate (services) or 0% (supplies of goods) and cost documents marked as intra-community acquisitions.',
    supplies: 'Supplies of goods',
    acquisitions: 'Acquisitions of goods',
    services: 'Services',
    counterparty: 'Counterparty',
    country: 'Country',
    vatNumber: 'VAT number',
    amount: 'Amount',
    download: 'Download VAT-UE',
    vies: 'VIES',
    viesValid: 'valid',
    viesInvalid: 'invalid',
    viesUnknown: 'not checked',
    checkVies: 'Check the numbers in VIES',
    viesUnavailable: 'The register did not answer for:',
    problems: 'Items that cannot be reported',
    notes: {
      TRIANGULAR_NOT_DETECTED: 'Triangular transactions are not detected — every item says "does not apply".',
      VIES_ON_DEMAND: 'You check the numbers against VIES with the button next to the tables — the result is stored with its date, because that is the evidence of due diligence.',
      CALL_OFF_NOT_SUPPORTED: 'Call-off stock movements are not reported.',
      MONTHLY_ALWAYS: 'The statement is filed monthly, whether VAT is settled monthly or quarterly.',
    },
  },
  officeJpk: {
    title: 'Clients’ JPK for the month',
    lead: 'Prepare all clients’ files at once, download the bundle of metadata files, sign them with the office signature (UPL-1 power of attorney) and upload the signed files — Firmeto matches them by name and sends them.',
    prepare: 'Prepare for clients',
    download: 'Download the bundle to sign',
    upload: 'Upload signed files',
    problems: 'Not prepared',
    sent: (count: number) => `Sent: ${count}`,
  },
  jpkSend: {
    title: 'Submission to the Ministry of Finance',
    personLead: 'As a natural person you sign the file with the income amount from your tax return two years back (e.g. in 2026 — from the 2024 return). First name, last name and date of birth come from the filing details. We do not store the amount.',
    companyLead: 'A company signs the metadata file with a qualified or trusted signature. Prepare the submission, download the file, sign it (e.g. at podpis.gov.pl, XAdES format) and upload the signed file here.',
    income: 'Income from the return two years back (PLN)',
    send: 'Send to the tax office',
    prepare: 'Prepare for signing',
    downloadToSign: 'Download the file to sign',
    uploadSigned: 'Upload the signed file',
    history: 'Submissions',
    none: 'This file has not been sent yet.',
    reference: 'Reference number',
    upo: 'Download UPO',
    test: 'test environment',
    statuses: { AWAITING_SIGNATURE: 'awaiting signature', SENDING: 'sending', PROCESSING: 'being processed', ACCEPTED: 'accepted, UPO issued', REJECTED: 'rejected', FAILED: 'not sent' } as Record<string, string>,
  },
  jpk: {
    goToFiling: 'Go to filing details',
    title: 'JPK files',
    lead: 'JPK_V7M for the selected month — the VAT records together with the declaration. You can download the file or send it to the tax office from here.',
    leadQuarterly:
      'JPK_V7K for the selected month. With quarterly settlement the **records are filed monthly and the declaration once a quarter**: the file for the first and second month of a quarter carries only the records, and the one for the third carries that month\u2019s records and the declaration for the whole quarter. You sign and send the file with your own tool.',
    period: 'Month',
    download: 'Download JPK_V7M',
    downloadQuarterly: 'Download JPK_V7K',
    beforeDownload: 'Before downloading, check the filing details — without the tax office code the file cannot be produced.',
  },
  filing: {
    title: 'Filing details',
    lead: 'Needed only for JPK files and declarations. Not used for issuing invoices or computing tax.',
    taxOffice: 'Tax office',
    taxOfficeHint: 'Type a city or part of the name and pick the office from the list. We store the four-digit code that JPK files and returns require.',
    taxOfficeSearch: 'City or office name',
    taxOfficeNone: 'No office matches. Try just the city name.',
    taxOfficeSpecialised: 'for the largest taxpayers',
    taxOfficeSpecialisedHint: 'Specialised offices serve taxpayers with revenue above EUR 50 million, banks and capital groups. If that is not your company, pick the ordinary office for your municipality.',
    taxOfficeClear: 'Clear',
    firstName: 'Taxpayer first name',
    lastName: 'Taxpayer last name',
    birthDate: 'Date of birth',
    personHint: 'A sole trader is identified in JPK by first name, last name and date of birth; a company by its name alone.',
    email: 'E-mail address for declarations',
    phone: 'Contact phone',
    zusInsuranceTitleCode: 'Insurance title code (ZUS DRA)',
    zusInsuranceTitleCodeHint:
      'Six digits from the ZUS declaration: four digits of the code, entitlement to a pension, degree of disability. Usually 051000 on full contributions and 057000 on preferential ones — but we do not fill it in for you, because for Mały ZUS Plus and ulga na start the ZUS dictionary is not unambiguous.',
    save: 'Save details',
    saved: 'Saved.',
  },
  invitation: {
    title: 'Invitation to a company',
    lead: (who: string) => `${who} invites you to work on a company in Firmeto.`,
    invitedBy: 'Invited by',
    invitesAs: 'invites you as',
    company: 'Company',
    forEmail: 'For address',
    roleScope: {
      OWNER: 'Full access to the company, including Firmeto settlements and access management.',
      ADMIN: 'Documents, settings and access. Cannot change the company owner.',
      ACCOUNTANT: 'Bookkeeping and tax settlements, full view of documents. No access to Firmeto settlements.',
      EMPLOYEE: 'Issuing invoices and entering costs. No view of taxes and settlements.',
      BOARD_MEMBER: 'View of documents and company results. No changes.',
      VIEWER: 'View only — no entering or changing anything.',
    },
    accept: 'Accept invitation',
    signInToAccept: 'Sign in to accept',
    expires: (date: string) => `The invitation expires on ${date}. We record every acceptance in the company log.`,
    accepted: 'Invitation accepted. The company is on your list now.',
    back: 'Go to companies',
  },
};

const uk: Dictionary = {
  documentNotifications: {
    enable: 'Увімкнути звук документів',
    mute: 'Вимкнути звук документів',
    blocked: 'Звук потрібно активувати в цьому браузері.',
    unsupported: 'Цей браузер не підтримує звук сповіщень.',
    ready: 'Звук нових документів увімкнено',
    muted: 'Звук документів вимкнено',
  },
  appName: 'Firmeto',
  language: 'Мова',
  signIn: 'Увійти',
  signOut: 'Вийти',
  signingIn: 'Вхід…',
  loading: 'Завантаження…',

  shell: {
    navigation: 'Навігація компанії',
    sales: 'Продаж',
    purchases: 'Закупівлі',
    books: 'Книги',
    settlements: 'Розрахунки',
    setup: 'Налаштування',
    density: 'Щільність',
    comfortable: 'Комфортна',
    dense: 'Щільна',
    back: 'Назад до компаній',
  },
  start: {
    kicker: 'Почніть',
    claim: 'Немає доходу — немає плати.',
    lead: 'Онлайн-бухгалтерія для малих фірм і бухгалтерських бюро в Польщі. Рахунки, податки, ZUS і терміни в одному місці.',
    promises: (trialMonths: number, capPercent: number) => [
      'У місяці без доходу плата становить 0 зł',
      `Плата ніколи не перевищує ${capPercent}% доходу`,
      trialMonths === 1
        ? 'Перший місяць безкоштовно, без строкового договору'
        : `Перші ${trialMonths} місяці безкоштовно, без строкового договору`,
    ],
    euServers: 'Сервери в Європейському Союзі',
    whoAreYou: 'Хто ви?',
    whoAreYouLead: 'Від цього залежить, що побачите після входу. Вибір можна змінити пізніше.',
    paths: {
      company: {
        title: 'Веду фірму',
        lead: 'Одноосібна діяльність або товариство. Рахунки, витрати, податки й терміни.',
      },
      office: {
        title: 'Веду бухгалтерське бюро',
        lead: 'Панель із фірмами клієнтів, кроки місяця та призначення бухгалтерів.',
      },
      founding: {
        title: 'Відкриваю фірму',
        lead: 'Допоможемо обрати форму оподаткування та проведемо через перший розрахунок.',
      },
    },
    createAccount: 'Створити обліковий запис',
    haveAccount: 'У мене вже є обліковий запис — увійти',
    invitationHint: 'Маєте запрошення від бухгалтерського бюро? Відкрийте посилання з листа.',
  },
  register: {
    claim: 'Три кроки до першого рахунка.',
    stepNames: ['Обліковий запис', 'Фірма', 'Розрахунки'],
    euData: 'Дані залишаються в Європейському Союзі',
    backToStart: 'Повернутися на початок',
    signIn: 'Увійти',
    signInToContinue: 'Фірму створюють уже після входу. Увійдіть паролем, який щойно встановили.',
    codeSent: 'Ми надіслали на вашу адресу шестизначний код. Він дійсний 24 години.',
    codeResent: 'Код надіслано ще раз.',
    account: {
      title: 'Створіть обліковий запис',
      lead: 'Займе менше хвилини. Дані фірми поки не потрібні.',
      firstName: 'Імʼя',
      lastName: 'Прізвище',
      email: 'Адреса електронної пошти',
      emailHint: 'На цю адресу надішлемо підтвердження та нагадування про терміни.',
      password: 'Пароль',
      passwordHint: 'Щонайменше 12 знаків.',
      passwordRules: {
        length: 'Щонайменше 12 знаків',
        notEmail: 'Відрізняється від вашої адреси e-mail',
        maxLength: 'Не більше ніж 128 знаків',
      },
      showPassword: 'Показати',
      hidePassword: 'Сховати',
      generate: 'Згенерувати',
      generated: 'Пароль вписано в обидва поля та скопійовано в буфер обміну. Збережіть його в менеджері паролів.',
      repeatPassword: 'Повторіть пароль',
      passwordsDiffer: 'Паролі не збігаються.',
      uiLocale: 'Мова інтерфейсу',
      uiLocaleHint: 'Мова належить до вашого облікового запису, а не до фірми. Змінюється вище.',
      terms: 'Приймаю регламент і політику приватності. Бухгалтерські дані обробляємо лише в Європейському Союзі.',
      lawUpdates: 'Хочу отримувати повідомлення про зміни в законах щодо моєї форми оподаткування.',
      next: 'Далі — підтвердження адреси',
    },
    code: {
      title: 'Підтвердіть адресу',
      lead: (email: string) =>
        `Введіть код, який ми надіслали на ${email}. Без підтвердженої адреси фірму створити не вдасться.`,
      label: 'Код із листа',
      hint: 'Код дійсний 24 години.',
      confirm: 'Підтвердити та увійти',
      resend: 'Надіслати код ще раз',
    },
    company: {
      title: 'Дані фірми',
      lead: 'Номер NIP визначає фірму в реєстрах і на рахунках — решту можна виправити пізніше.',
      nip: 'NIP',
      nipLength: (digits: number) => `NIP має 10 цифр. Введено ${digits}.`,
      noRegistryTitle: 'Дані вводите самі',
      noRegistryLead:
        'Завантаження даних із реєстру GUS ще не підключено. Поки що впишіть назву й адресу вручну — перевіримо їх у реєстрі пізніше.',
      legalForm: 'Правова форма',
      jdg: 'Одноосібна діяльність',
      spZoo: 'Товариство з обмеженою відповідальністю',
      name: 'Назва фірми',
      nameHint: 'Так, як має виглядати на рахунку.',
      address: 'Вулиця і номер',
      postalCode: 'Поштовий індекс',
      city: 'Місто',
      startedOn: 'Дата початку діяльності',
      startedOnHint: 'Від неї рахується пільга на старт і ліміт звільнення від ПДВ у першому році.',
      next: 'Далі — розрахунки',
    },
    settlements: {
      title: 'Як ви розраховуєте фірму?',
      lead: 'Не знаєте? Оберіть «Не впевнений» — зʼясуємо це перед першим розрахунком, решта працюватиме.',
      perMonth: (net?: string) => (net ? `${net} зł нетто / міс.` : '—'),
      unsurePrice: 'визначимо разом',
      plans: {
        RYCZALT: { title: 'Рична́лт', lead: 'Облік доходів, ставка ричалту, без витрат.' },
        KPIR: { title: 'KPiR', lead: 'Книга доходів і витрат, витрати з відрахуванням ПДВ.' },
        FULL: { title: 'Повна бухгалтерія', lead: 'Журнал, план рахунків, проведення.' },
        UNSURE: { title: 'Не впевнений', lead: 'Фірма створиться одразу, податкові налаштування додамо пізніше.' },
      },
      incomeTax: 'Податок на дохід',
      skala: 'За шкалою — 12% і 32%',
      liniowy: 'Лінійний — 19%',
      cit: 'CIT — товариство',
      vat: 'ПДВ',
      vatActive: 'Активний платник ПДВ',
      vatExemptLimit: 'Звільнення до ліміту (ст. 113)',
      vatExemptKind: 'Предметне звільнення (ст. 43)',
      vatPeriod: 'Період розрахунку ПДВ',
      monthly: 'Місячний — JPK_V7M',
      quarterly: 'Квартальний — JPK_V7K',
      zus: 'Внески ZUS',
      zusSchemes: {
        ULGA_NA_START: 'Пільга на старт — перші 6 місяців',
        PREFERENCYJNY: 'Преференційні — 24 місяці',
        MALY_ZUS_PLUS: 'Малий ZUS Плюс',
        PELNY: 'Повні внески',
        BRAK: 'Не сплачую внесків із цієї діяльності',
      },
      from: 'Відколи розраховуєте у Firmeto',
      fromHint: 'Попередні місяці можна додати пізніше — налаштування мають дату дії.',
      feeRule: (trialMonths: number, capPercent: number) =>
        `Розумію правило оплати: 0 зł у місяці без доходу, ніколи більше ніж ${capPercent}% доходу. ` +
        (trialMonths === 1 ? 'Перший місяць безкоштовно.' : `Перші ${trialMonths} місяці безкоштовно.`),
      finish: 'Створити фірму й почати',
      unsureTitle: 'Податкові налаштування можуть зачекати',
      unsureLead:
        'Фірма створиться одразу, і ви зможете вводити документи. Податок не порахуємо, доки не буде відомо, як ви розраховуєтеся — пульт нагадуватиме про це.',
    },
    done: {
      title: 'Фірма готова',
      lead: 'Можна починати. Плата зʼявиться лише в місяці з доходом.',
      next: [
        { title: 'Додайте банківський рахунок', lead: 'Номер потрапляє на рахунки й дозволяє зіставляти платежі' },
        { title: 'Виставте перший рахунок', lead: 'Або впишіть витрату, яку вже маєте' },
        { title: 'Запросіть бухгалтера', lead: 'Роль «Бухгалтер», без доступу до розрахунків із Firmeto' },
      ],
      enter: 'Перейти до фірми',
    },
  },
  emailUndeliverable:
    'Листи на вашу адресу повертаються. Перевірте її — без робочої адреси не вдасться змінити пароль і отримувати сповіщення.',
  companies: {
    settlement: 'Розрахунок',
    title: 'Мої компанії',
    subtitle: 'Компанії, до яких ви маєте доступ. Виберіть одну, щоб відкрити її документи та книги.',
    addHint: 'Введіть NIP, решту заповнимо з реєстру',
    bookkeeping: 'Хто вестиме облік?',
    bookkeepingChoices: { SELF: 'Самостійно', ACCOUNTANT: 'Мій бухгалтер', OFFICE: 'Бухгалтерське бюро' },
    bookkeepingSelfFull: 'Товариство веде повну бухгалтерію, а її веде лише роль Бухгалтер. '
      + 'Надасте її собі на наступному екрані — для обліку власної фірми сертифікат не потрібен.',
    bookkeepingSelfSimple: 'Книгу доходів і витрат та єдиний податок ведете самі, без додаткових ролей.',
    bookkeepingAccountantHint: 'Запросите її на наступному екрані — отримає роль Бухгалтер і доступ до книг.',
    bookkeepingOfficeHint: 'Передайте бюро NIP фірми. Бюро додасть її до своїх клієнтів, '
      + 'а Ви затвердите пропозицію обслуговування в Особах.',
    empty: 'У вас ще немає компаній. Додайте першу, щоб почати.',
    add: 'Додати компанію',
    nip: 'Податковий номер (NIP)',
    fetch: 'Отримати з реєстру',
    fetching: 'Шукаю…',
    fetched: (source: string) => `Дані заповнено з реєстру: ${source}. Перевірте їх перед збереженням.`,
    fetchedWithVat: (source: string, vat: string) =>
      `Дані заповнено з реєстру: ${source}. Статус VAT: ${vat}. Перевірте їх перед збереженням.`,
    name: 'Назва',
    legalForm: 'Правова форма',
    city: 'Місто',
    businessStartedOn: 'Дата початку діяльності',
    businessStartedOnHint: 'Потрібна для ліміту звільнення від ПДВ у перший рік.',
    addressLine: 'Вулиця і номер',
    postalCode: 'Поштовий індекс',
    save: 'Зберегти компанію',
    saving: 'Зберігаю…',
    cancel: 'Скасувати',
    roles: 'Ваша роль',
    jdg: 'Фізична особа-підприємець',
    spzoo: 'Товариство з обмеженою відповідальністю',
    forms: {
      JDG: 'Одноосібна діяльність',
      SP_CYWILNA: 'Цивільне товариство',
      SP_JAWNA: 'Повне товариство',
      SP_PARTNERSKA: 'Партнерське товариство',
      SP_KOMANDYTOWA: 'Командитне товариство',
      SP_KOMANDYTOWO_AKCYJNA: 'Командитно-акціонерне товариство',
      SP_ZOO: 'Товариство з обмеженою відповідальністю',
      PROSTA_SA: 'Просте акціонерне товариство',
      SA: 'Акціонерне товариство',
      SPOLDZIELNIA: 'Кооператив',
      STOWARZYSZENIE: 'Асоціація (об’єднання)',
      FUNDACJA: 'Фонд',
    },
  },

  settings: {
    partners: {
      title: 'Учасники',
      lead: 'У цивільному, повному та професійному товаристві податок платять учасники — кожен зі своєї частки '
        + 'прибутку і кожен за власною формою оподаткування. Без цієї картотеки рахуємо один аванс так, '
        + 'ніби весь дохід заробила одна особа.',
      firstName: 'Ім’я',
      lastName: 'Прізвище',
      share: 'Частка в прибутку',
      shareHint: 'Дріб або відсоток: 0,5 або 50%. Частки всіх учасників мають складатися в ціле.',
      taxation: 'Форма оподаткування',
      zus: 'Схема ZUS',
      from: 'Від',
      to: 'До',
      add: 'Додати учасника',
      end: 'Завершити частку',
      endPrompt: 'Останній день у товаристві:',
      none: 'Картотека учасників порожня — тоді аванс рахуємо як для однієї особи.',
      sum: 'Сума часток',
    },
    lead: 'Спосіб розрахунку діє від зазначеної дати. Зміна форми протягом року — це новий запис, а не перезапис попереднього.',
    periods: 'Періоди дії',
    to: 'Діє до',
    openEnded: 'безстроково',
    inForce: 'Діє',
    future: 'Майбутній',
    yearNotCovered: (od: string, doKiedy: string) =>
      `Звітний рік ${od} – ${doKiedy} не повністю охоплений налаштуваннями`,
    yearNotCoveredHint: (odKiedy: string, poczatek: string) =>
      `Налаштування діють від ${odKiedy}, а звітний рік починається ${poczatek}. За місяці до цієї `
      + 'дати ми не знаємо, як фірма розраховується — плану рахунків і податкам за цю частину року '
      + 'немає на що спертися. Якщо нічого не змінювалося, перенесіть дату назад.',
    coverFromYearStart: (poczatek: string) => `Виправити на ${poczatek}`,
    edit: 'Виправити',
    editWarning: 'Ви виправляєте наявний запис',
    editWarningHint: 'Зміна рішення — це новий запис, а не виправлення: історія має залишитися, щоб '
      + 'перерахунок старого місяця дав той самий результат. Виправляйте запис лише тоді, коли '
      + 'помилилися під час введення. Уже закритого місяця зміна не торкнеться.',
    past: 'Історичний',
    title: 'Податкові налаштування',
    open: 'Налаштування',
    close: 'Згорнути',
    none: 'Налаштувань ще немає. Заповніть їх, перш ніж почати облік.',
    from: 'Діє з',
    taxationForm: 'Форма оподаткування',
    booksType: 'Облік',
    vatStatus: 'ПДВ',
    vatExemptionBasis: 'Підстава звільнення',
    zusScheme: 'Внески власника (ZUS)',
    cashMethod: 'Касовий метод PIT',
    smallTaxpayer: 'Малий платник',
    incomeTaxPeriod: 'Аванси з податку на дохід',
    incomeTaxPeriodHint:
      'Квартальні доступні малому платнику та тому, хто розпочинає діяльність (ст. 25 п. 1b закону про CIT, ст. 44 п. 3g закону про PIT). Умови не перевіряємо — вона залежить від доходу попереднього року. Період ПДВ налаштовується окремо.',
    advancePeriods: { MONTHLY: 'Місячні', QUARTERLY: 'Квартальні' },
    exportData: 'Завантажити всі дані',
    exportHint: 'Усе, що маємо про цю фірму, можна забрати одним файлом.',
    sicknessInsurance: 'Добровільне страхування на випадок хвороби',
    add: 'Додати налаштування',
    save: 'Зберегти налаштування',
    history: 'Історія змін',
    monthHint: 'Налаштування діють з першого дня місяця; попередні періоди залишаються без змін.',
    forms: { ryczalt: 'Єдиний податок', skala: 'Податкова шкала', liniowy: 'Лінійний податок', cit: 'CIT' },
    books: { ryczalt: 'Облік доходів', kpir: 'Книга доходів і витрат', full: 'Повний бухоблік' },
    vat: { active_monthly: 'Платник, щомісяця', active_quarterly: 'Платник, щокварталу', exempt: 'Звільнений' },
    zus: {
      ulga_na_start: 'Пільга на старт',
      preferencyjny: 'Пільговий',
      maly_zus_plus: 'Малий ZUS Plus',
      pelny: 'Повний',
      brak: 'Без внесків',
    },
  },
  access: {
    servedBy: 'Обслуговування бухгалтерським бюро',
    officeAccountants: 'Доступ до книг',
    nobodyYet: 'нікого ще не призначено',
    endOffice: 'Завершити співпрацю',
    endOfficeHint: 'Бюро втрачає доступ того самого дня. Книги залишаються в компанії.',
    confirmEndOffice: (office: string) =>
      `Завершити співпрацю з бюро ${office}? Його бухгалтери втратять доступ до книг цієї компанії.`,
    officeProposals: 'Пропозиції обслуговування',
    noBookkeeper: 'Ніхто не веде облік цієї фірми',
    ownDocumentsOnly: 'Бачить лише документи, які вносить сам',
    ownDocumentsOnlyShort: 'лише власні',
    ownDocumentsOnlyHint: 'За кількох менеджерів кожен бачив би чужі націнки, комісії та контрагентів. '
      + 'Звуження залишає лише ті рахунки, витрати й файли, які він додав сам.',
    selfAccountant: 'Роль Бухгалтер можна взяти й собі',
    selfAccountantHint: 'Вести облік власної фірми можна без сертифіката. Відповідальність за нього '
      + 'залишається на керівникові, а роль Бухгалтер вимагає двофакторного входу.',
    takeAccountantRole: 'Надати мені роль Бухгалтер',
    officeProposalsHint:
      'Бухгалтерське бюро хоче вести книги цієї компанії. До вашої згоди воно не бачить жодних даних і не може призначити бухгалтера.',
    office: 'Бюро',
    whoPays: 'Хто платить за Firmeto',
    payerOffice: 'Бюро',
    payerCompany: 'Компанія',
    acceptProposal: 'Прийняти',
    rejectProposal: 'Відхилити',
    lead: 'Доступ до цієї компанії. Кожне надання і відкликання доступу записуємо в історію змін.',
    person: 'Особа',
    whatEachRoleSees: 'Що бачить кожна роль',
    permission: 'Право',
    permissions: {
      documents: 'Рахунки та витрати',
      books: 'Книга і JPK',
      settings: 'Податкові налаштування',
      people: 'Особи та доступ',
      billing: 'Плата за Firmeto',
    },
    levels: { full: 'повне', add: 'додавання', view: 'перегляд' },
    permissionsNote:
      '«—» означає, що позиція взагалі не з’являється в навігації цієї особи. Ми не показуємо сірих кнопок, які не можна натиснути.',
    title: 'Люди та запрошення',
    open: 'Люди',
    people: 'Мають доступ',
    invitations: 'Запрошення',
    invite: 'Запросити',
    send: 'Надіслати запрошення',
    email: 'Адреса e-mail',
    role: 'Роль',
    remove: 'Забрати доступ',
    revoke: 'Скасувати',
    expires: 'Дійсне до',
    noInvitations: 'Запрошень немає.',
    hint: 'Запрошення дійсне 7 днів; прийняти його може лише той, хто увійшов із вказаною адресою.',
    statuses: { pending: 'Очікує', accepted: 'Прийняте', revoked: 'Скасоване', expired: 'Протерміноване' },
    roles: {
      OWNER: 'Власник',
      ADMIN: 'Адміністратор',
      ACCOUNTANT: 'Бухгалтер',
      EMPLOYEE: 'Працівник',
      BOARD_MEMBER: 'Член правління',
      VIEWER: 'Перегляд',
    },
    /*
     * Фемінітиви там, де вони усталені: власниця, адміністраторка, працівниця, членкиня.
     * «Бухгалтер» залишаємо без змін — «бухгалтерка» досі не є загальноприйнятою в офіційному
     * вжитку, а вигадана форма в документі виглядає гірше, ніж спільна.
     */
    rolesFemale: {
      OWNER: 'Власниця',
      ADMIN: 'Адміністраторка',
      ACCOUNTANT: 'Бухгалтер',
      EMPLOYEE: 'Працівниця',
      BOARD_MEMBER: 'Членкиня правління',
      VIEWER: 'Перегляд',
    },
  },
  counterparties: {
    lead: 'Картотека, яку використовують під час виставлення рахунків і введення витрат.',
    dialogLead: 'Контрагент збережеться одразу й потрапить у документ — чернетка залишиться на місці.',
    active: 'Активний',
    typeHint: 'Вид визначає, які ставки ПДВ доступні в рахунку.',
    nipHint: 'Перевіримо контрольну суму.',
    ratesTitle: 'Вид і ставки ПДВ',
    ratesHint: 'Перелік відповідає ставкам, доступним у рахунку в цьому застосунку.',
    open: 'Контрагенти',
    title: 'Картотека контрагентів',
    search: 'Пошук за назвою або номером',
    add: 'Додати контрагента',
    none: 'Картотека порожня.',
    showInactive: 'Показати неактивних',
    name: 'Назва',
    nip: 'Податковий номер (NIP)',
    vatEuId: 'Номер VAT ЄС',
    country: 'Країна',
    city: 'Місто',
    type: 'Тип',
    deactivate: 'Позначити як неактивного',
    inactive: 'неактивний',
    save: 'Зберегти контрагента',
    types: {
      DOMESTIC: 'Компанія в Польщі',
      EU: 'Компанія з ЄС',
      NON_EU: 'Компанія поза ЄС',
      PERSON: 'Приватна особа',
    },
  },
  products: {
    lead: 'Позиції, які підказуємо під час виставлення рахунка.',
    showInactive: 'Показати вилучені',
    grossPrice: 'Ціна брутто',
    grossPreview: 'Брутто',
    retire: 'Вилучити',
    open: 'Товари та послуги',
    title: 'Картотека товарів і послуг',
    search: 'Пошук за назвою або кодом',
    add: 'Додати позицію',
    edit: 'Редагувати',
    none: 'Картотека порожня.',
    code: 'Код',
    namePl: 'Назва (польською)',
    nameEn: 'Назва (англійською)',
    unit: 'Одиниця',
    vatRate: 'Ставка ПДВ',
    gtu: 'Код GTU',
    price: 'Ціна',
    save: 'Зберегти позицію',
    rates: { '23': '23%', '8': '8%', '5': '5%', '0': '0%', zw: 'звільнена', np: 'не підлягає', oo: 'зворотне нарахування' },
  },
  wizard: {
    open: 'Майстер',
    preview: 'Перегляд документа',
    previewOpen: 'Відкрити оригінал',
    previewUnsupported: 'Цей файл ми тут не покажемо — відкрийте оригінал у новій вкладці.',
    title: 'Майстер проведення',
    documentNumber: 'Номер документа',
    issueDate: 'Дата документа',
    category: 'Вид витрати',
    vatRate: 'Ставка ПДВ',
    net: 'Нетто',
    vat: 'ПДВ',
    gross: 'Брутто',
    stepOf: (step: number, total: number) => `Крок ${step} з ${total}`,
    steps: { kind: 'Що це за документ', counterparty: 'Від кого', document: 'Документ і суми', payment: 'Оплата', summary: 'Підсумок', statement: 'Виписка' },
    kinds: { COST_INVOICE: 'Рахунок витрат', RECEIPT: 'Чек', BANK_STATEMENT: 'Банківська виписка' },
    kindHints: {
      COST_INVOICE: 'Рахунок від постачальника з NIP — ПДВ до відшкодування, витрата до книги.',
      RECEIPT: 'Чек без рахунку — витрата без відшкодування ПДВ.',
      BANK_STATEMENT: 'Файл з банку (MT940) — платежі до зіставлення.',
    },
    counterpartyHint: 'Виберіть постачальника зі списку або додайте нового за NIP — дані підтягнемо з реєстру.',
    supplier: 'Постачальник',
    newSupplier: 'Новий постачальник',
    dueDate: 'Строк оплати',
    deduction: 'Відшкодування ПДВ',
    deductions: { FULL: 'повністю', HALF: 'половина (авто)', NONE: 'без відшкодування' },
    paid: 'Документ уже оплачено',
    paidOn: 'Дата оплати',
    method: 'Спосіб',
    methods: { transfer: 'переказ', cash: 'готівка', card: 'картка' },
    unpaidHint: 'Залишиться зобов’язанням до оплати — зі строком, якщо ви його вказали.',
    summaryLead: 'Так ми це запишемо. Ще нічого не створено — перевірте й підтвердьте.',
    costAmount: 'У витрати',
    deductibleVat: 'ПДВ до відшкодування',
    paidShort: 'оплачено',
    unpaidShort: 'не оплачено — чекає в розрахунках',
    summaryFull: 'При повному обліку проведення потрапить до списку «до проведення» в журналі — нічого не проводиться без перегляду.',
    summarySimple: 'Витрата потрапить до книги за місяць з дати документа.',
    statementLead: 'Завантажимо виписку і покажемо, які платежі відповідають яким документам. Зіставлення підтверджує людина.',
    importStatement: 'Завантажити виписку',
    back: 'Назад',
    next: 'Далі',
    finish: 'Підтвердити і зберегти',
    later: 'Закінчу пізніше',
    close: 'Закрити',
    doneFull: 'Документ витрат збережено. Проведення чекає на проведення в журналі.',
    doneSimple: 'Документ витрат збережено і внесено до книги.',
    doneStatement: 'Виписку завантажено. Зіставте платежі з документами.',
    goJournal: 'Перейти до журналу',
    goCosts: 'Перейти до витрат',
    goMatch: 'Перейти до зіставлення',
  },
  documents: {
    noneHint: 'Завантажені файли з’являться тут із датою, розміром і позначкою, чи їх уже внесено як документ.',
    scanFutureText:
      'З часом завантажений файл потрапляє до черги «Чекає на внесення», а з його вмісту ми підказуємо постачальника, номер, дати й суми під час додавання витрати. Сьогодні файл — лише додаток.',
    open: 'Документи',
    title: 'Архів документів',
    lead: 'Світлини та PDF зберігаємо в тому вигляді, в якому вони надійшли. Під час перевірки важливий оригінал.',
    drop: 'Перетягніть сюди світлини або файли PDF',
    choose: 'Вибрати файли',
    accepted: 'JPEG, PNG, HEIC або PDF, до 25 МБ.',
    none: 'Архів порожній.',
    fileName: 'Файл',
    added: 'Додано',
    size: 'Розмір',
    state: 'Стан',
    waiting: 'Чекає на внесення',
    posted: 'Внесений до витрат',
    delete: 'Видалити',
    uploaded: (added: number) => `Додано до архіву: ${added}.`,
    uploadedWithKnown: (added: number, known: number) =>
      `Додано до архіву: ${added}. Уже були там: ${known} — другої копії не робимо.`,
    confirmDelete: (fileName: string) => `Видалити ${fileName} з архіву? Цю дію не можна скасувати.`,
    inboxTitle: 'Скринька документів',
    inboxLead: 'Усе, що надійшло — з браузера, з телефона і поштою — чекає тут, доки хтось це внесе або відхилить із причиною. Оригінал лишається в архіві.',
    statuses: { NEW: 'новий', IN_PROGRESS: 'у роботі', BOOKED: 'проведений', REJECTED: 'відхилений' },
    tag: 'Мітка',
    source: 'Звідки',
    sourceUpload: 'завантажено',
    sourceEmail: 'e-mail',
    accept: 'Позначити як у роботі',
    reject: 'Відхилити',
    rejectPrompt: 'Причина відхилення — власник має знати, що виправити:',
    rejected: 'Відхилено',
    intakeTitle: 'Документи поштою',
    mailboxConfig: 'Налаштування скриньки',
    intakeLead: 'Надішліть лист із вкладеннями на адресу фірми — файли потраплять до скриньки. Адрес може бути кілька, кожна зі своєю міткою. Приймаємо лише від осіб із доступом до фірми та затверджених відправників; чужі листи відкидаємо без відповіді.',
    intakeAddress: 'Адреса',
    intakeTag: 'Мітка вкладень',
    intakeTagHint: 'напр. рахунки, виписки, чеки',
    intakeCreate: 'Згенерувати адресу',
    intakeRevoke: 'Анулювати',
    intakeRevoked: 'анульовано',
    intakeRevokeConfirm: (address: string) => `Анулювати ${address}? Відтепер листи на цю адресу не прийматимуться. Можна згенерувати нову.`,
    intakeNone: 'Адрес ще немає.',
    intakeCopied: 'Скопійовано.',
    sendersTitle: 'Затверджені відправники',
    sendersLead: 'Особи з доступом до фірми затверджені автоматично. Тут допишіть інші адреси, напр. скриньку бюро або сканера.',
    senderEmail: 'Адреса e-mail',
    senderAdd: 'Додати',
    senderRevoke: 'Видалити',
    sendersNone: 'Лише особи з доступом до фірми.',
  },
  annual: {
    cit: {
      title: 'Податок на прибуток за рік (CIT-8)',
      hint: 'Рахуємо з тієї самої бази, що й податкова примітка у звітності: результат з книг, мінус звільнені '
        + 'доходи, плюс витрати, що не є податковими. Тимчасові різниці та збитки минулих років подає '
        + 'бухгалтер — без них сума попередня.',
      base: 'База',
      rate: 'Ставка',
      taxForYear: 'Податок за рік',
      fromAdvances: 'Покрито авансами',
      difference: 'Залишилось доплатити',
      priorLoss: 'Збиток минулих років до відрахування',
      recalculate: 'Перерахувати',
      notApplicable: 'Ця компанія не платить CIT або не веде повні книги.',
    },
    activity: {
      title: 'Звіт правління',
      lead: 'Документ правління, а не бухгалтера: його підписує керівник підприємства, а за колегіального '
        + 'правління — усі його члени. Числа рахуємо з тієї самої проєкції, що й фінансову звітність — '
        + 'ми їх не переписуємо.',
      indicators: 'Показники',
      indicator: 'Показник',
      value: 'Значення',
      basis: 'З чого',
      suggestions: 'Пропозиції з книг',
      suggestionsHint: 'Жоден абзац не потрапляє в документ, доки ви його не приймете. Пропозиція дописується '
        + 'до тексту розділу, де її можна виправити або видалити.',
      add: 'Дописати до розділу',
      sections: 'Розділи документа',
      required: 'обов’язковий',
      optional: 'необов’язковий',
      fromData: 'зміст з даних',
      proposal: 'пропозиція — не прийнята',
      save: 'Зберегти тексти',
      saved: 'Тексти збережено.',
      missing: 'Без цих розділів документ не складемо:',
      ready: 'Документ готовий',
      pdf: 'Скласти PDF',
      docx: 'Скласти DOCX',
      download: 'Завантажити документ',
      signers: 'Підписують',
    },
    blank: {
      title: 'Порожній зразок звітності',
      hint: 'Усі позиції обраного додатка без сум — для розмови з клієнтом або щоб зібрати дані поза '
        + 'застосунком. Нічого не створює і нічого не змінює.',
      pdf: 'Завантажити PDF',
      xlsx: 'Завантажити таблицю',
    },
    modes: {
      title: 'Звідки суми',
      BOOKS: 'З книг у Firmeto',
      DORMANT: 'Компанія без діяльності',
      MANUAL: 'Книги поза Firmeto',
      booksHint: 'Суми рахує проєкція із сальдо — так само, як на екрані балансу.',
      dormantHint: 'Рік без обороту: кілька сум, вписаних вручну (капітал, гроші, зобов’язання).',
      manualHint: 'Книги за цей рік ведуться деінде — суми позицій вписуєте вручну.',
      template: 'Зразок звітності',
      templates: { ZAL_4: 'Мікропідприємство (додаток 4)', ZAL_5: 'Мале підприємство (додаток 5)', ZAL_1: 'Інше підприємство (додаток 1)' },
      variant: 'Варіант звіту про прибутки і збитки',
      variants: { BY_KIND: 'за елементами', BY_FUNCTION: 'за функціями' },
    },
    amounts: {
      title: 'Суми звітності',
      lead: 'Суми вписують лише в складові позиції: підсумки й позиції з формулами рахує застосунок. '
        + 'Результат зі звіту про прибутки і збитки потрапляє в пасив сам — тому не вийде подати звітність, '
        + 'у якій баланс каже інше, ніж звіт про прибутки.',
      position: 'Позиція',
      amount: 'Сума',
      previous: 'Попередній рік',
      save: 'Зберегти суми',
      saved: 'Суми збережено.',
      balanced: 'Баланс сходиться',
      unbalanced: 'Баланс не сходиться',
      difference: 'Різниця',
      totals: 'Активи · пасиви · результат',
      onlyManual: 'Цей екран для звітності, складеної поза книгами. Ця рахується з книг.',
    },
    office: {
      title: 'Фінансовий рік клієнтів',
      year: 'Рік',
      client: 'Клієнт',
      status: 'Звітність',
      prepared: 'Складено',
      approved: 'Затверджено',
      filed: 'Подано',
      confirmed: 'Підтверджено',
      mention: 'Згадка в реєстрі',
      noReport: 'немає',
      none: 'Жоден клієнт не має фінансового року, що закінчується цього року.',
    },
    open: 'Річна звітність',
    title: 'Річна фінансова звітність до реєстру',
    lead: 'Майстер веде діями, а не полями. Суми балансу та звіту про прибутки і збитки беруться з книг — '
      + 'тут немає поля, у яке їх можна було б вписати.',
    tabs: {
      year: 'Рік і версія',
      form: 'Вступ і примітки',
      file: 'Файл звітності',
      signatures: 'Підписи',
      resolutions: 'Рішення',
      krs: 'Подання до реєстру',
      registers: 'Правління та учасники',
    },
    statuses: {
      draft: 'Чернетка',
      assembled: 'Файл складено',
      signing: 'Збираємо підписи',
      signed: 'Підписано',
      approved: 'Затверджено',
      filed: 'Подано до реєстру',
      filed_unapproved: 'Подано до затвердження',
      confirmed: 'Підтверджено',
      abandoned: 'Залишено',
    },
    year: {
      pick: 'Фінансовий рік',
      noYears: 'Ця компанія ще не має фінансового року.',
      noYearsHint: 'Фінансовий рік створюють на екрані „Періоди”. Без нього немає з чого складати звітність.',
      start: 'Розпочати звітність',
      noReport: 'За цей рік звітності ще немає',
      noReportHint: 'Звітність складається з книг цього року. Закривати рік не обов’язково, але звітність '
        + 'з відкритого року має позначку „робоча версія”.',
      versions: 'Версії',
      version: (numer: number) => `версія ${numer}`,
      versionReason: 'Причина',
      draftMark: 'робоча версія',
      draftMarkHint: 'Рік відкритий або файл склала особа, яка не веде книги — документ говорить про це у тексті, '
        + 'бо інакше потрапив би до учасників як готова звітність.',
      stale: 'Книги змінилися після створення файла',
      staleHint: 'Ми через це не змінюємо стан і не видаляємо файл. Складіть файл заново, поки його ніхто не підписав.',
      calendar: 'Річний календар',
      calendarHint: 'Біля кожного строку дві дати: законна і останній робочий день перед нею. Невідомо, чи строк '
        + 'переноситься на неробочий день, а в суботу немає кому подати документ.',
      statutory: 'Законний строк',
      safe: 'Останній робочий день',
      state: 'Стан',
      done: 'зроблено',
      overdue: 'прострочено',
      daysLeft: (dni: number) => `через ${dni} днів`,
      obligations: {
        statement_prepared: 'Складання звітності',
        cit8: 'Декларація CIT-8',
        approval: 'Затвердження зборами',
        krs_filing: 'Подання до реєстру',
        krs_confirmation: 'Згадка або підтвердження',
      },
    },
    form: {
      title: 'Вступ і примітки',
      lead: 'Поля походять з офіційного зразка звітності: їхні коди — це назви елементів зі схеми Міністерства '
        + 'фінансів, а не вигадані нами.',
      save: 'Зберегти відповіді',
      saved: 'Відповіді збережено.',
      missing: 'Без цих полів файл не пройде схему:',
      complete: 'Формуляр заповнено повністю.',
      sections: {
        INTRODUCTION: 'Вступ',
        NOTES: 'Примітки',
        SEAT: 'Місцезнаходження',
        TAX: 'Податкове узгодження',
      },
    },
    file: {
      title: 'Файл звітності',
      lead: 'Файл складається з книг і формуляра, а потім лежить в архіві компанії разом зі своєю контрольною сумою.',
      generate: 'Скласти файл з книг',
      regenerate: 'Скласти файл заново',
      taxReconciliation: 'Додати узгодження результату з базою оподаткування',
      download: 'Завантажити XML',
      pdf: 'Скласти роздрук (PDF)',
      pdfDownload: 'Завантажити PDF',
      pdfHint: 'Роздрук для читання: баланс, звіт про прибутки і збитки та тексти з формуляра. До реєстру йде файл XML — роздрук залишається в компанії і не є документом, який підписують.',
      files: 'Файли комплекту',
      role: 'Роль',
      sha256: 'Контрольна сума',
      generatedAt: 'Створено',
      roles: {
        statement_xml: 'Звітність (XML)',
        statement_xml_signed: 'Звітність з підписами',
        statement_pdf: 'Звітність (PDF)',
        activity_report: 'Звіт правління',
        resolution_approval: 'Рішення про затвердження',
        resolution_profit: 'Рішення про розподіл прибутку',
        resolution_discharge: 'Рішення про звільнення від відповідальності',
        minutes: 'Протокол зборів',
        statement_52_2b: 'Заява за ст. 52 п. 2b',
        auditor_report: 'Звіт аудитора',
        rdf_confirmation: 'Підтвердження з репозиторію',
      },
      newVersion: 'Нова версія',
      reason: 'Причина нової версії',
      reasonHint: 'Причина залишається при версії назавжди — через рік ніхто не пам’ятає, чому звітність має дві.',
      noAmounts: 'Суми не вписують ані тут, ані будь-де в майстрі: зміна йде через книги, а файл складається заново.',
    },
    signatures: {
      title: 'Підписи звітності',
      lead: 'Звітність підписує особа, яка веде книги, і всі члени правління (ст. 52 п. 2 Закону про бухгалтерський облік).',
      deadline: 'Строк складання',
      person: 'Особа',
      function: 'Функція',
      state: 'Стан',
      method: 'Спосіб',
      when: 'Коли',
      states: {
        missing: 'не підписав',
        signed: 'підписав',
        statement: 'подав заяву',
        refusal: 'відмовився від заяви',
      },
      methods: {
        qualified: 'кваліфікований підпис',
        trusted_profile: 'довірений профіль',
        personal: 'особистий підпис',
        statement: 'заява за ст. 52 п. 2b',
        refusal: 'відмова від заяви',
      },
      capacities: { board_member: 'член правління', bookkeeper: 'особа, яка веде книги' },
      downloadToSign: 'Завантажити файл для підпису',
      downloadHint: 'Ви отримуєте файл з підписами, зібраними до цього часу: кожен підпис додається до попередніх, '
        + 'а підписання чистого файла втратило б уже зібрані.',
      upload: 'Завантажити підписаний файл',
      capacity: 'У якій якості',
      officer: 'Особа з картотеки правління',
      personName: 'Ім’я та прізвище',
      note: 'Обґрунтування або примітка',
      send: 'Завантажити',
      complete: 'Підписи повні',
      incomplete: 'Підписи ще не повні',
      extra: 'Підписи осіб поза правлінням',
      noStatement: 'Спочатку складіть файл звітності — без нього немає чого підписувати.',
      declaration: {
        title: 'Заява або відмова за ст. 52 п. 2b',
        hint: 'Коли звітність підписує один член правління, інші подають заяви про те, що вона відповідає вимогам '
          + 'закону, або відмови з обґрунтуванням. Ми створюємо текст; підпис ставлять поза застосунком '
          + 'і завантажують назад.',
        who: 'Хто подає заяву',
        kind: 'Вид',
        statement: 'Заява',
        refusal: 'Відмова',
        justification: 'Обґрунтування (обов’язкове при відмові)',
        generate: 'Створити текст (PDF)',
        generated: (plik: string) => `${plik} лежить в архіві компанії — підпишіть його і завантажте назад.`,
      },
    },
    resolutions: {
      title: 'Рішення зборів',
      lead: 'Які рішення потрібні, випливає з результату року і з капіталів, тому суми приходять обчисленими — '
        + 'вписувати нічого не треба.',
      reason: 'Чому',
      create: 'Створити проєкт',
      adopt: 'Записати як ухвалене',
      adoptedOn: 'Дата ухвалення',
      number: 'Номер',
      pdf: 'PDF',
      draft: 'проєкт',
      adopted: 'ухвалене',
      gaps: 'Чого не вистачає в даних:',
      minutes: {
        title: 'Протокол зборів',
        date: 'Дата зборів',
        place: 'Місце',
        chairman: 'Голова',
        secretary: 'Секретар',
        unanimous: 'Рішення ухвалено одноголосно',
        generate: 'Скласти протокол (PDF)',
        download: 'Завантажити протокол',
        needsResolutions: 'Протокол описує перебіг зборів — спочатку ухваліть хоча б одне рішення.',
      },
    },
    krs: {
      title: 'Подання до Репозиторію фінансових документів',
      lead: 'Машинного інтерфейсу для подання немає, тому застосунок робить усе, крім останнього кліку: складає '
        + 'пакет, каже, чого не вистачає, і веде крок за кроком через Портал судових реєстрів.',
      checklist: 'Контрольний список комплекту',
      required: 'обов’язково',
      optional: 'необов’язково',
      present: 'є',
      missing: 'немає',
      ready: 'Комплект готовий до подання',
      notReady: 'Комплект ще не готовий',
      people: 'Хто може підписати подання',
      pesel: 'PESEL розкритий у реєстрі',
      instructions: 'Крок за кроком у Порталі судових реєстрів',
      deadlines: 'Строки',
      zip: 'Завантажити пакет (ZIP)',
      filing: {
        title: 'Записати подання',
        submittedOn: 'Дата подання',
        number: 'Номер подання',
        record: 'Записати подання',
        unapproved: 'подано до затвердження — після затвердження потрібно подати ще раз '
          + '(ст. 69 п. 2 Закону про бухгалтерський облік)',
      },
      confirmation: {
        title: 'Підтвердження з репозиторію',
        confirmedOn: 'Дата підтвердження',
        upload: 'Завантажити підтвердження',
      },
      register: {
        title: 'Згадки в реєстрі',
        lead: 'Згадка в реєстрі — це доказ подання, незалежний від нас і від клієнта. Мовчання реєстру нічого '
          + 'не скасовує.',
        number: 'Номер у реєстрі',
        noNumber: 'Компанія не має номера в реєстрі — додайте його в налаштуваннях компанії.',
        checkedAt: 'Перевірено',
        never: 'ми ще не питали',
        check: 'Перевірити в реєстрі',
        mentions: 'Згадки',
        period: 'Період',
        filedOn: 'Подано',
        approval: 'Рішення',
        activity: 'Звіт правління',
        audit: 'Аудит',
        missingYears: 'Роки без згадки про звітність:',
        none: 'У реєстрі ще немає жодної згадки про звітність цієї компанії.',
      },
    },
    registers: {
      officers: {
        title: 'Правління',
        lead: 'Картотека тримає періоди, а не стан на сьогодні: рішення за певний рік стосуються правління того року.',
        firstName: 'Ім’я',
        lastName: 'Прізвище',
        role: 'Функція',
        from: 'Від',
        to: 'До',
        pesel: 'PESEL у реєстрі',
        peselHint: 'Без особи з номером PESEL, розкритим у реєстрі, безкоштовне подання до репозиторію не підпише '
          + 'ніхто з правління.',
        add: 'Додати особу',
        end: 'Завершити функцію',
        roles: {
          PRESIDENT: 'голова правління',
          BOARD_MEMBER: 'член правління',
          PROXY: 'комерційний представник',
          LIQUIDATOR: 'ліквідатор',
        },
        none: 'Картотека правління порожня — без неї ми не знаємо, хто має підписати звітність.',
      },
      shareholders: {
        title: 'Учасники',
        lead: 'З картотеки учасників складається список присутніх у протоколі та розподіл прибутку.',
        name: 'Учасник',
        shares: 'Частки',
        votes: 'Голоси',
        nominal: 'Номінальна вартість',
        capital: 'Капітал',
        add: 'Додати учасника',
        end: 'Завершити',
        none: 'Картотека учасників порожня.',
      },
    },
  },
  periods: {
    closedMonths: 'Місяців закрито',
    settled: 'розраховується',
    stillRunning: 'ще триває',
    noneClosed: 'Жоден місяць не закрито',
    noneClosedHint:
      'Поки місяць відкритий, запис заднім числом змінить суми в декларації, яку вже подано. Закривайте місяць після надсилання JPK.',
    open: 'Періоди',
    title: 'Закриття періодів',
    lead: 'До закритого місяця не потрапить жоден новий запис. Зміну вносять коригуванням із поточною '
      + 'датою — завдяки цьому книга збігається з декларацією, яку вже подано.',
    years: 'Обліковий рік',
    noYears: 'Облікового року ще немає.',
    yearMissing: (rok: number) => `Звітний рік ${rok} ще не відкрито`,
    yearMissingHint: 'Звітний рік створюється один раз, наступний треба відкрити. Без нього план '
      + 'рахунків і журнал показують торішній рік, якого сьогоднішні податкові налаштування не сягають.',
    openYear: (rok: number) => `Відкрити ${rok}`,
    addYear: 'Додати обліковий рік',
    saveYear: 'Зберегти рік',
    yearHint: 'Обліковий рік одноосібної діяльності завжди календарний. Компанія може мати інший, '
      + 'але не довший ніж вісімнадцять місяців.',
    closeYear: 'Закрити рік',
    readiness: {
      blocked: 'Рік не можна закрити, доки не зроблено це:',
      warnings: 'Контрольний список має зауваження:',
      confirm: 'Закрити рік попри ці зауваження?',
      result: (numer: number, kwota: string) => `Результат року перенесено записом № ${numer}: ${kwota} zł`,
      noResult: 'Рік закрито. Записів результату не було, тому переносити нічого.',
      checks: {
        months_open: 'Відкриті місяці',
        unposted_invoices: 'Незаписані рахунки',
        unposted_costs: 'Незаписані документи витрат',
        trial_balance: 'Оборотно-сальдова відомість не збігається',
        accounts_without_position: 'Рахунки із сальдо без позиції звіту',
        opening_balance: 'Немає вхідного балансу',
        draft_invoices: 'Рахунки залишилися чернетками',
        inbox_documents: 'Документи чекають у скриньці',
        settlements_without_counterparty: 'Рядки розрахунків без контрагента',
        depreciation: 'Основні засоби без проведеної амортизації',
        closing_inventory: 'Немає інвентаризації на кінець року',
        open_provisions: 'Резерви із сальдо після завершення періоду',
        result_transfer: 'Результат до перенесення',
      },
    },
    months: 'Місяці',
    month: 'Місяць',
    state: 'Стан',
    from: 'Від',
    to: 'До',
    closedAt: 'Закрито',
    open_: 'Відкритий',
    closed: 'Закритий',
    close: 'Закрити',
    reopen: 'Відкрити',
    reopenTitle: (period: string) => `Відкриття періоду ${period}`,
    reason: 'Причина',
    reasonHint: 'Причина потрапляє до журналу й залишається там назавжди.',
    cancel: 'Скасувати',
    confirmClose: (period: string) => `Закрити ${period}? Після закриття ви не додасте до цього місяця документа.`,
    confirmWithDrafts: (period: string, drafts: number) =>
      `У місяці ${period} залишилися чернетки рахунків: ${drafts}. Після закриття їх не виставити. Закрити попри це?`,
    confirmCloseYear: (from: string, to: string) =>
      `Закрити рік ${from} — ${to}? Це закриє всі місяці цього року.`,
  },
  costs: {
    open: 'Витрати',
    bookAll: 'Провести всі',
    list: 'Список',
    allMonths: 'Усі',
    column: 'кол.',
    draftsWaiting: 'Документи чекають на проведення',
    draftsWaitingHint:
      'Поки вони чернетки, вони не потрапляють ні до KPiR, ні до ПДВ — тому витрати місяця на панелі можуть бути нульові.',
    draftAfterSave: 'Збережений документ потрапляє до чернеток. До KPiR і ПДВ він входить лише після проведення.',
    draftAfterSaveHint: 'Після збереження документ має стан «Чернетка» і ще ні на що не впливає.',
    document: 'Документ',
    qualification: 'Кваліфікація',
    rates: 'Розподіл за ставками ПДВ',
    addRate: 'Додати ставку',
    summary: 'Підсумок',
    goesTo: 'Потрапить до',
    kpir: 'KPiR',
    vatRegister: 'Реєстр ПДВ',
    purchases: 'закупівлі',
    vatInput: 'Вхідний ПДВ',
    saveAndNext: 'Зберегти й додати наступний',
    costDateHint: 'Визначає місяць у KPiR.',
    supplierHint: 'Нового додасте в картотеці контрагентів.',
    fixedAssetHint: 'Внести до реєстру основних засобів',
    intraCommunity: 'Внутрішньоспільнотове придбання',
    intraCommunityHint: 'Купівля товарів у контрагента з ЄС — увійде до інформації VAT-UE (імпорт послуг ні)',
    scanFutureTitle: 'Зчитування зі скана',
    scanFutureText:
      'З часом скан із вкладки «Документи» заповнить номер, постачальника, дати й суми, а поля з низькою впевненістю розпізнавання буде підсвічено для підтвердження. Сьогодні скан — лише додаток, усі поля вводяться вручну.',
    title: 'Документи витрат',
    new: 'Додати витрату',
    none: 'Документів витрат немає.',
    number: 'Номер документа',
    supplier: 'Постачальник',
    chooseSupplier: 'Оберіть постачальника',
    issueDate: 'Дата документа',
    costDate: 'Дата понесення',
    category: 'Вид витрат',
    deduction: 'Відрахування ПДВ',
    fixedAsset: 'Основний засіб',
    net: 'Нетто',
    vat: 'ПДВ',
    deductible: 'ПДВ до відрахування',
    costAmount: 'У витрати',
    correctionTo: 'Коригування до документа',
    correctionHint: 'Введіть суми різниці так, як вони вказані в документі постачальника — зазвичай зі знаком мінус.',
    causes: {
      MISTAKE: 'Помилка в первинному документі',
      EVENT: 'Пізніша подія (знижка, повернення)',
    },
    status: 'Стан',
    save: 'Зберегти витрату',
    book: 'Провести',
    delete: 'Видалити',
    hint: 'У витрати входить нетто плюс ПДВ, який не можна відрахувати — при половинному відрахуванні та у звільненої компанії суми різні.',
    categories: {
      GOODS: 'Товари й матеріали (кол. 10)',
      PURCHASE_COSTS: 'Супутні витрати закупівлі (кол. 11)',
      SALARIES: 'Зарплати (кол. 12)',
      OTHER: 'Інші витрати (кол. 13)',
    },
    deductions: { FULL: 'Повне (100%)', HALF: 'Половина (транспорт)', NONE: 'Без відрахування' },
    statuses: { DRAFT: 'Чернетка', BOOKED: 'Проведено' },
  },
  dashboard: {
    fixSettings: 'Заповнити налаштування',
    noDeadlines: 'Цього місяця немає термінів.',
    open: 'Панель',
    title: 'Підсумок місяця',
    month: 'Місяць',
    revenue: 'Дохід',
    costs: 'Витрати',
    income: 'Прибуток',
    vatToPay: 'ПДВ до сплати',
    vatToCarry: 'ПДВ на наступний період',
    obligations: 'Терміни',
    toPay: 'До сплати цього місяця',
    goToSettlements: 'Перейти до розрахунків',
    goToPeople: 'Перейти до осіб',
    NO_BOOKKEEPER: 'Повна бухгалтерія, але ніхто не має ролі Бухгалтер — немає кому вести книги.',
    period: 'Період',
    dueDate: 'Термін',
    VAT: 'ПДВ і JPK',
    PIT_ADVANCE: 'Авансовий платіж з податку на дохід',
    CIT_ADVANCE: 'Авансовий платіж з CIT',
    HEALTH_ANNUAL: 'Річний розрахунок внеску на охорону здоров’я',
    citBase: 'База CIT наростаючим підсумком',
    citNotes: {
      ESTIMATE: 'Рахуємо з книг у Firmeto — це оцінка, а не декларація.',
      LOSS_NOT_DEDUCTED: 'Збитки минулих років не віднімаємо; врахуйте їх у річному розрахунку.',
      TRANSITIONAL_NOT_INCLUDED: 'Тимчасових різниць розрахунок не знає — вони входять до річної декларації.',
      SMALL_RATE_FROM_SETTINGS: 'Ставка 9% з налаштувань компанії; ліміт доходу для неї не перевіряємо.',
      LOSS_PERIOD: 'Наростаючим підсумком вийшов збиток — авансу немає.',
      CAPITAL_GAINS_NOT_SEPARATED: 'Дохід від капіталу не розділяємо — з обліку не видно, які надходження ним є.',
      QUARTERLY: 'Сума стосується всього кварталу і сплачується після його закінчення.',
      QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Квартальні аванси доступні малому платнику, а фірма так не позначена в налаштуваннях.',
    } as Record<string, string>,
    ZUS: 'Внески ZUS',
    movedFrom: 'законний термін {date} припадає на вихідний',
    today: 'сьогодні',
    tomorrow: 'завтра',
    overdue: 'термін минув',
    daysLeft: 'залишилось днів:',
    NO_SETTINGS: 'Немає податкових налаштувань — без них не порахуємо податок і не покажемо терміни.',
    NO_DOCUMENTS: 'Цього місяця ще немає жодних документів.',
    OVERDUE_RECEIVABLES: 'Є рахунки після терміну оплати — це гроші, на які ви чекаєте довше, ніж мали б.',
    OVERDUE_PAYABLES: 'Є зобов’язання після терміну оплати — це гроші, які ви винні довше, ніж обіцяли.',
    aging: 'Розрахунки — за строками',
    agingLead: (asOf: string) => `Стан на ${asOf} з розрахунків у журналі: хто скільки винен і відколи.`,
    agingReceivables: 'Дебіторська заборгованість',
    agingPayables: 'Кредиторська заборгованість',
    agingOpen: 'відкритих',
    agingOverdue: 'прострочених',
    goToJournal: 'Перейти до розрахунків у журналі',
    receivables: 'Неоплачені рахунки',
    receivablesOverdue: 'з них прострочені',
    exemptionTitle: 'Ліміт звільнення від ПДВ',
    exemptionUsed: 'Використано від початку року',
    exemptionLimit: 'Ліміт',
    exemptionRemaining: 'Залишилось',
    VAT_EXEMPTION_NEAR_LIMIT: 'Ви наближаєтесь до ліміту звільнення від ПДВ — після його перевищення потрібно зареєструватися платником ПДВ.',
    VAT_EXEMPTION_EXCEEDED: 'Ліміт звільнення від ПДВ перевищено. Звільнення закінчується на операції, яка його перевищила.',
    SUSPENDED: 'Діяльність була цього місяця повністю зупинена, тож немає ні внесків, ні авансу з податку. Файл JPK_V7 усе ж може бути потрібний — за придбання товарів з ЄС, імпорту послуг і коригувань (ст. 99 п. 7a і 7b закону про ПДВ).',
    goToSuspensions: 'Переглянути зупинення',
    zusTitle: 'Внески ZUS за місяць',
    zusSocial: 'Соціальні',
    zusSickness: 'На випадок хвороби',
    zusLaborFund: 'Фонд праці',
    zusHealth: 'Медичний',
    zusTotal: 'Разом до сплати',
    draDownload: 'Декларація ZUS DRA',
    draHint: 'Файл для завантаження в eZUS або Płatnik — ми не надсилаємо його за вас.',
    advanceTitle: 'Авансовий платіж з податку на дохід',
    advanceBase: 'База наростаючим підсумком',
    advanceAmount: 'До сплати за цей місяць',
    partners: 'Учасники',
    partner: 'Учасник',
    share: 'Частка в прибутку',
    taxationForm: 'Оподаткування',
    ESTIMATE: 'Суми рахуємо з документів у Firmeto, припускаючи, що попередні внески та аванси сплачено вчасно.',
    RYCZALT_NOT_SUPPORTED: 'Податок на єдиній ставці ще не рахуємо — потрібен облік доходів зі ставками.',
    MALY_ZUS_PLUS_APPROXIMATED: 'Mały ZUS Plus рахується з доходу попереднього року, а цього року немає у Firmeto — показуємо повну базу, бо нижча ні з чого б не випливала.',
    UNKNOWN_YEAR: 'Немає ставок і порогів на цей рік, тому сум не рахуємо.',
    COMPANY_TAX: 'Податок товариства та внески учасників рахуються поза цим зведенням.',
    PARTNERS_SPLIT: 'Партнерство не платить податок на дохід — його платять учасники, кожен зі своєї частки.',
    PARTNERS_MISSING: 'Партнерство без картотеки учасників: рахуємо як для однієї особи, а це не так. Додайте учасників у налаштуваннях компанії.',
    PARTNERS_SHARE_MISMATCH: 'Частки учасників не складаються в ціле — чиєїсь частки бракує або вона завелика.',
    QUARTERLY_ADVANCE: 'Аванс квартальний: сума стосується всього кварталу і сплачується до 20-го числа місяця після його закінчення. Внески ZUS залишаються місячними.',
    QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Квартальні аванси доступні малому платнику, а фірма так не позначена в налаштуваннях.',
    BAD_DEBT_IN_INCOME: 'Дохід враховує збережені коригування безнадійних боргів: несплачена витрата повертається до доходу, несплачений дохід із нього виходить (ст. 26i закону про PIT).',
    BAD_DEBT_NOT_IN_RYCZALT: 'За паушального податку пільга на безнадійні борги змінює дохід за ставкою (ст. 11 закону про паушальний податок), а ми не знаємо, до якої ставки віднести несплачений документ — тож збережених коригувань тут не рахуємо.',
    MALY_ZUS_PLUS_FROM_PREVIOUS_YEAR: 'База соціальних внесків порахована з доходу попереднього року: середній місячний дохід, поділений навпіл (Mały ZUS Plus, ст. 18c закону про систему соціального страхування).',
    MALY_ZUS_PLUS_AT_FLOOR: 'З доходу вийшла б база нижча за нижню межу, тож беремо 30% мінімальної зарплати — стільки ж, як за преференційних внесків.',
    MALY_ZUS_PLUS_AT_CAP: 'З доходу вийшла б база вища за повну, тож беремо повну: за такого доходу пільга внеску не змінює.',
    MALY_ZUS_PLUS_REVENUE_OVER_LIMIT: 'Виручка попереднього року перевищила ліміт пільги (120 000 зл), тож рахуємо повну базу.',
    MALY_ZUS_PLUS_TOO_FEW_DAYS: 'Діяльність у попередньому році тривала менше 60 днів, а за такого року пільга не належить.',
    MALY_ZUS_PLUS_DAYS_ASSUMED: 'Ми не знаємо дня початку діяльності, тож прийняли, що попередній рік був повним. Якщо фірма почала всередині року, база занижена — додайте дату в даних фірми.',
    MALY_ZUS_PLUS_FROM_REVENUE: 'За паушального податку облік не знає доходу, тож виводимо його з виручки коефіцієнтом із закону.',
    MALY_ZUS_PLUS_LIMIT_NOT_CHECKED: 'Не перевіряємо двох умов, яких з наших даних не видно: 36 місяців користування пільгою протягом останніх 60 і виконання діяльності для колишнього роботодавця. За цим треба стежити самому.',
    MALY_ZUS_PLUS_SUSPENSION_DAYS: 'Дні зупинення діяльності не є днями її ведення, тож ми відняли їх при діленні доходу — і через це база вийшла вища, ніж дав би сам річний дохід.',
    SUSPENDED_MONTH: 'Діяльність була цього місяця повністю зупинена, тож немає страхування: ні соціального, ні медичного.',
    SUSPENDED_PART_OF_MONTH: 'Частина місяця була зупинена, тож базу соціальних внесків ми зменшили пропорційно до днів страхування (ст. 18 п. 9 закону про систему соціального страхування). Внесок на охорону здоров’я залишається цілий — він місячний і неподільний.',
  },
  ryczalt: {
    lead: 'Реєстр доходів ведуть за спрощеною ставкою від зареєстрованого доходу.',
    otherForm: 'Ця компанія оподатковується інакше',
    goToLedger: 'Перейти до книги доходів і витрат',
    changeForm: 'Змінити форму оподаткування',
    byRate: 'За ставками',
    open: 'Облік доходів',
    title: 'Облік доходів (єдина ставка)',
    month: 'Місяць',
    date: 'Дата',
    document: 'Документ',
    counterparty: 'Контрагент',
    rate: 'Ставка',
    amount: 'Дохід',
    total: 'Разом',
    none: 'Цього місяця доходів немає.',
  },
  settlements: {
    lead: 'Дебіторська і кредиторська заборгованість за документами. Платежі вводять вручну або з виписки.',
    receivablesTotal: 'Дебіторська заборгованість',
    payablesTotal: 'Кредиторська заборгованість',
    longestDelay: 'Найдовша затримка (днів)',
    noDelay: 'нічого не прострочено',
    delay: 'Затримка',
    filter: 'Фільтр: документ, контрагент',
    loadStatement: 'Завантажити виписку',
    matchFutureTitle: 'Зіставлення платежів із рахунками',
    matchFutureText:
      'Ми підказуємо зіставлення, але підтверджує його людина. З часом файл з банку сам вкаже, який платіж покриває який рахунок, разом із частковими сумами.',
    open: 'Розрахунки',
    title: 'Розрахунки',
    receivables: 'Дебіторська заборгованість',
    payables: 'Кредиторська заборгованість',
    onlyOpen: 'Лише незакриті',
    bank: 'Виписка',
    bankHint: 'Завантажте виписку MT940. Нічого не проводимо самі — пропонуємо відповідність, а ви підтверджуєте.',
    chooseFile: 'Файл виписки',
    importedCount: 'Нових операцій',
    skippedCount: 'Уже відомих',
    noTransactions: 'Немає операцій, що чекають на зіставлення.',
    valueDate: 'Дата',
    details: 'Призначення платежу',
    proposal: 'Підказка',
    noProposal: 'без підказки',
    reasons: {
      NUMBER_AND_AMOUNT: '(номер і сума)',
      NUMBER_ONLY: '(номер у призначенні)',
      AMOUNT_ONLY: '(сума збігається)',
      COUNTERPARTY_AND_AMOUNT: '(контрагент і сума)',
    },
    document: 'Документ',
    counterparty: 'Контрагент',
    dueDate: 'Термін',
    amountDue: 'До сплати',
    paid: 'Сплачено',
    remaining: 'Залишилось',
    status: 'Стан',
    overdueDays: 'днів прострочення:',
    addPayment: 'Записати оплату',
    savePayment: 'Зберегти',
    exchangeRate: 'Курс на день оплати',
    exchangeRateHint: 'Документ у іноземній валюті: вкажіть середній курс NBP з останнього робочого дня перед '
      + 'оплатою. З різниці щодо курсу документа постає фінансовий дохід або витрата.',
    paidOn: 'Дата оплати',
    amount: 'Сума',
    none: 'Немає чого розраховувати.',
    statuses: {
      UNPAID: 'не оплачено',
      PARTIAL: 'частково',
      PAID: 'закрито',
      OVERPAID: 'переплата',
    },
  },
  payments: {
    title: 'Спосіб оплати',
    officePays: 'За цей місяць платить бухгалтерське бюро — спосіб оплати та списання на боці бюро.',
    officeTitle: 'Розрахунок із Firmeto',
    officeLead: 'Бюро платить однією сумою за фірми в пакеті — не більше за стелю — та плату за індивідуальною угодою, якщо вона є.',
    officeSubtotal: 'Фірми в пакеті',
    officeCeiling: 'Стеля пакета',
    officeAgreement: 'Індивідуальна угода',
    officeTotal: 'До сплати',
    officeCompanies: (count: number) => `фірм: ${count}`,
    current: 'Зараз ви платите',
    none: 'Спосіб оплати ще не додано. Без нього ми не стягнемо плату, а через два тижні заборгованості обліковий запис стане доступним лише для читання.',
    method: 'Форма оплати',
    methods: { card: 'Картка', blik: 'BLIK' } as Record<string, string>,
    provider: 'Оператор',
    providerName: (provider: string) => ({ payu: 'PayU', przelewy24: 'Przelewy24', stripe: 'Stripe', fake: 'Тестовий оператор' })[provider] ?? provider,
    recommended: 'рекомендований',
    add: 'Додати',
    change: 'Змінити',
    revoke: 'Відкликати згоду',
    formAtProvider: 'Дані картки ви вводите в платіжного оператора — Firmeto їх не бачить.',
    onlyOwner: 'Спосіб оплати встановлює власник або адміністратор.',
    expires: (month: string) => `дійсна до ${month}`,
    waitingTitle: 'Чекаємо на підтвердження оператора',
    waitingText: 'Згода зазвичай стає активною за кілька секунд. Оновіть сторінку, якщо її досі не видно.',
    charges: 'Стягнені платежі',
    noCharges: 'Ми ще нічого не стягнули.',
    period: 'За місяць',
    amount: 'Сума',
    status: 'Стан',
    statuses: { pending: 'в обробці', succeeded: 'стягнено', failed: 'невдало', refunded: 'повернено' } as Record<string, string>,
    arrearTitle: (month: string) => `Плата за ${month} очікує оплати`,
    arrearText: (sum: string, reason: string) => `${sum} — ${reason}.`,
    readOnlyFrom: (day: string) => `Ми повторимо спробу за кілька днів. З ${day} обліковий запис буде лише для читання до оплати.`,
    readOnlyTitle: 'Обліковий запис лише для читання',
    readOnlyText: 'Дані можна переглядати й завантажувати, а збереження повернеться після оплати заборгованості. Додайте або змініть спосіб оплати нижче.',
    reasons: { mandate_missing: 'немає способу оплати', card_declined: 'оператор відхилив списання', insufficient_funds: 'недостатньо коштів', expired_card: 'термін дії картки минув', authentication_required: 'банк вимагає підтвердження', declined: 'оператор відхилив списання' } as Record<string, string>,
  },
  billing: {
    multiCompany: (count: number) => `Знижка за ${count} діяльності`,
    lead: 'Не заробляєте — не платите. Плата ніколи не перевищує 20% доходу за цей місяць.',
    revenueSub: 'база для нарахування плати',
    howMuch: 'Звідки взялася ця сума',
    capOf: (revenue: string) => `20% доходу ${revenue}`,
    lower: 'Менша з двох сум',
    plan: 'План',
    payer: 'Платить',
    channel: 'Купівля',
    noRevenueTitle: 'Місяць без доходу коштує 0 zł',
    noRevenueText: 'Ми не донараховуємо плату пізніше і не переносимо її на наступний місяць.',
    open: 'Оплати',
    title: 'Оплати за Firmeto',
    month: 'Місяць',
    revenue: 'Дохід',
    planPrice: 'Ціна плану',
    cap: 'Ліміт 20%',
    toPay: 'До сплати',
    history: 'Історія нарахувань',
    charged: 'Нараховано',
    note: 'Примітки',
    surcharge: 'Доплата',
    credit: 'Кредит',
    none: 'Нарахувань ще немає.',
    plans: { RYCZALT: 'План «єдина ставка»', KPIR: 'План КПіР', FULL: 'План повного обліку' },
    payers: { COMPANY: 'платить фірма', OFFICE: 'платить бюро' },
    channels: { WEB: 'купівля на сайті', APP_STORE: 'підписка App Store', GOOGLE_PLAY: 'підписка Google Play' },
    reasons: {
      TRIAL: 'Пробний період — оплати немає.',
      NO_REVENUE: 'Цього місяця немає доходу — оплати немає.',
      BELOW_MINIMUM: 'Нарахована сума менша за 5 злотих — не стягуємо її.',
      FREE_AFTER_LEAVING_OFFICE: 'Безкоштовний період після виходу з бюро.',
      STORE_SUBSCRIPTION: 'Підписка оплачується у магазині застосунків — розрахунок іде через магазин.',
    },
    statuses: {
      PENDING_CHARGE: 'до нарахування',
      CHARGED: 'нараховано',
      PENDING_APPROVAL: 'очікує підтвердження',
      APPROVED: 'підтверджено',
      REJECTED: 'відхилено',
    },
  },
  accruals: {
    title: 'Витрати майбутніх періодів',
    lead: 'Поліс на рік або передплачений абонемент — це витрати тих місяців, яких вони стосуються, а не лише того, коли надійшов рахунок. Витрата сходить з витратного рахунку на витрати майбутніх періодів і повертається однією частиною на місяць, тож кожен авансовий платіж бачить частину, а не весь рахунок.',
    none: 'Ще немає жодного графіка.',
    noneHint:
      'Графік складають на документі, який уже проведений у журналі — витратний рахунок беремо з його проведення, бо з рахунку, якого документ не торкнувся, нічого знімати.',
    add: 'Розкласти витрату на місяці',
    cancel: 'Скасувати',
    save: 'Створити графік',
    document: 'Документ',
    documentHint: 'Лише документи, проведені в журналі. Один документ має один графік.',
    description: 'Чого стосується',
    amount: 'Сума до розкладання',
    amountHint:
      'Типово стільки, скільки проведення записало на витратному рахунку. Менше, коли рахунок охоплює також те, що належить цьому місяцю — наприклад, одноразовий збір поряд із полісом.',
    from: 'Перший місяць',
    to: 'Останній місяць',
    period: 'Період',
    monthly: 'Частина',
    costAccount: 'Витратний рахунок',
    accrualAccount: 'Рахунок витрат майбутніх періодів',
    accountsHint:
      'Порожнє означає: витратний рахунок із проведення документа, а витрати майбутніх періодів — із позиції балансу (у зразковому плані 640). Вкажіть рахунок, коли проведення торкнулося кількох рахунків або коли витрата має чекати довше ніж рік.',
    month: 'Місяць',
    release: 'Провести частини',
    released: 'Проведено частин',
    releasedTotal: 'Проведено',
    through: 'до',
    skipped: 'Пропущені графіки',
    plan: 'Графік',
    showPlan: 'Графік',
    hidePlan: 'Сховати графік',
    instalment: 'Частина',
    remaining: 'Залишається',
    entry: 'Запис',
    waiting: 'чекає',
    remove: 'Відкликати графік',
    removeReason: 'Причина відкликання графіка — сторно перенесення витрати на витрати майбутніх періодів:',
  },
  provisions: {
    title: 'Пасивні міжперіодні розрахунки',
    lead: 'Витрата належить до цього періоду, а рахунка ще немає: аудит звіту за цей рік, річні премії, послуга виконана й не виставлена (ст. 39 п. 2 закону про бухгалтерський облік). Нарахування вписує витрату до місяця, якого вона стосується, і відкладає зобов’язання в пасивах; розрахунок знімає резерв, коли рахунок прийде або коли зобов’язання не виникне.',
    none: 'Резервів ще немає.',
    noneHint:
      'Резерв не виникає з документа, бо документа ще немає — суму, рахунок і місяці подає бухгалтер разом з обґрунтуванням оцінки.',
    add: 'Створити резерв',
    addHint:
      'Тут не вказують документа: сенс у тому, що рахунка ще немає. Сума — це оцінка, тому поряд питаємо, з чого вона вийшла.',
    cancel: 'Скасувати',
    save: 'Створити резерв',
    description: 'Чого стосується',
    basis: 'З чого вийшла сума',
    basisHint:
      'Наприклад: «договір з аудиторською фірмою від 12.11.2026, 30 000 зл нетто». Оцінка без обґрунтування незахищена під час аудиту й перевірки, а це єдине місце, де обґрунтування залишається.',
    amount: 'Сума',
    from: 'Перший місяць',
    to: 'Останній місяць',
    periodHint:
      'Той самий місяць — це нормально: резерв на аудит звіту стосується одного місяця. Річні премії нараховуються протягом дванадцяти.',
    period: 'Період',
    costAccount: 'Рахунок витрат',
    costAccountHint:
      'Рахунок, на який увійде витрата — той самий, на якому потім стане рахунок від контрагента. Його вказує бухгалтер, бо немає проведення, з якого його взяти.',
    provisionAccount: 'Рахунок резерву',
    provisionAccountHint:
      'Порожнє означає: рахунок з роллю «пасивні міжперіодні розрахунки» (у зразковому плані 641). Мітки позиції балансу тут не достатньо, бо резерв і доходи майбутніх періодів стоять у тій самій позиції пасивів.',
    month: 'Місяць',
    accrue: 'Нарахувати резерви',
    accrued: 'Нараховано',
    accruedTotal: 'Нараховано',
    through: 'до',
    balance: 'Сальдо резерву',
    closed: 'розрахований',
    skipped: 'Пропущені резерви',
    plan: 'Нарахування',
    showPlan: 'Нарахування',
    hidePlan: 'Сховати нарахування',
    instalment: 'Частина',
    remaining: 'Залишок',
    entry: 'Запис',
    waiting: 'очікує',
    settle: 'Розрахувати',
    settleAccount:
      'На який рахунок зняти резерв? За рахунком — рахунок витрат, який резерв обтяжував (підказано). Коли зобов’язання не виникло — рахунок інших операційних доходів.',
    settlements: 'Розрахунки резерву',
    settledOn: 'Дата',
    settledAmount: 'Сума',
    settledAccount: 'Рахунок',
    remove: 'Відкликати резерв',
    removeReason: 'Причина відкликання резерву — можливо лише поки нічого не потрапило до книг:',
  },
  assets: {
    lead: 'Реєстр основних засобів та амортизаційні відрахування, що потрапляють у витрати.',
    noneHint:
      'Основний засіб можна додати тут або позначити під час введення витрати — тоді документ одразу потрапить до реєстру, а не у витрати одноразово.',
    depreciating: 'Амортизується',
    open: 'Основні засоби',
    title: 'Основні засоби та амортизація',
    none: 'Основних засобів ще немає.',
    add: 'Додати основний засіб',
    save: 'Зберегти',
    name: 'Назва',
    inventoryNumber: 'Інвентарний номер',
    kstCode: 'Код класифікації',
    acquired: 'Дата придбання',
    inService: 'Введення в експлуатацію',
    value: 'Первісна вартість',
    method: 'Метод',
    rate: 'Річна ставка',
    rateHint: 'Дріб: 0.20 означає 20%. Ставку беремо з переліку норм амортизації.',
    month: 'Місяць',
    bookDepreciation: 'Провести амортизацію',
    booked: 'Проведено записів',
    includeDisposed: 'Показати списані',
    showPlan: 'План',
    hidePlan: 'Сховати план',
    plan: 'План амортизації',
    period: 'Місяць',
    writeOff: 'Відрахування',
    remaining: 'Залишилось',
    bookedTotal: 'Проведено разом',
    inLedger: 'У книзі',
    inJournal: 'У журналі',
    posted: 'Записів у журналі',
    skipped: 'Засоби, пропущені в журналі',
    removedFromBooks: 'Засіб зійшов з обліку',
    removedAccumulated: 'амортизація',
    removedNet: 'залишкова вартість у витратах',
    removedEntry: 'запис',
    removedMissing: 'Засіб виведено з реєстру, але він не зійшов з обліку:',
    through: 'до',
    dispose: 'Списати',
    disposalReason: 'Причина списання (продаж, ліквідація):',
    disposed: 'списано',
    methods: { LINIOWA: 'прямолінійний', JEDNORAZOWA: 'одноразовий', DEGRESYWNA: 'зменшуваного залишку' },
  },
  pitAnnual: {
    title: 'Річний розрахунок',
    priorYearLoss: 'Збиток минулих років',
    revenue: 'Дохід за рік',
    income: 'Прибуток за рік',
    badDebt: 'Коригування безнадійних боргів',
    social: 'Сплачені соціальні внески',
    health: 'Сплачений внесок на охорону здоров’я',
    deducted: 'Відраховано від бази',
    taxBase: 'База оподаткування',
    tax: 'Податок за рік',
    advances: 'Аванси, сплачені протягом року',
    toPay: 'До доплати',
    toRefund: 'Переплата до повернення',
    forms: { PIT_36: 'PIT-36 · податкова шкала', PIT_36L: 'PIT-36L · лінійний податок', PIT_28: 'PIT-28 · паушальний' },
    notes: {
      ESTIMATE: 'Рахуємо з документів у Firmeto — це оцінка, а не заповнена декларація.',
      ADVANCES_ASSUMED_PAID: 'Припускаємо, що аванси, пораховані протягом року, було сплачено.',
      BUSINESS_INCOME_ONLY: 'Це податок від діяльності. Декларація охоплює також інші джерела — роботу за наймом, оренду, договори — яких ми не знаємо.',
      NO_RELIEFS: 'Пільг і відрахувань не рахуємо: на дітей, IP Box, пожертв, спільного розрахунку з подружжям і 1,5% для організації суспільної користі.',
      LOSS_FROM_USER: 'Збиток минулих років враховуємо лише тоді, коли ви його впишете — він залежить від розрахунків, яких у нас немає.',
      HEALTH_NOT_DEDUCTIBLE_ON_SCALE: 'За податковою шкалою внесок на охорону здоров’я не відраховується з 2022 року.',
      RYCZALT_HEALTH_HALF: 'За паушального податку від доходу відраховується половина сплаченого внеску на охорону здоров’я.',
      INVENTORY_MISSING: 'Бракує затвердженого залишку, тож річний прибуток може бути неправдивим — товар на полиці не є витратою цього року.',
      FULL_BOOKS_NOT_SUPPORTED: 'За повного обліку річний прибуток визначають з книг; цього ще не рахуємо.',
      PARTNERS_SPLIT: 'Товариство: кожен учасник розраховується сам, зі своєї частки і власною формою.',
      UNKNOWN_YEAR: 'Не маємо податкових показників на цей рік.',
      BAD_DEBT_IN_INCOME: 'Дохід враховує збережені коригування безнадійних боргів у податку на дохід (ст. 26i закону про PIT).',
      BAD_DEBT_NOT_IN_RYCZALT: 'За паушального податку пільга на безнадійні борги змінює дохід за ставкою (ст. 11 закону про паушальний податок) — ми не знаємо, до якої ставки віднести несплачений документ, тож її не рахуємо.',
    },
  },
  healthAnnual: {
    title: 'Річний внесок на охорону здоров’я',
    deadline: (rok: number) => `розрахунок у декларації за квітень — до 20 травня ${rok}`,
    income: 'Дохід за рік',
    revenue: 'Виручка за рік',
    months: 'Місяці страхування',
    basis: 'Річна база',
    rate: 'Ставка',
    due: 'Внесок за рік',
    paid: 'Внески сплачені протягом року',
    toPay: 'До доплати',
    toRefund: 'Переплата до повернення',
    forms: {
      SKALA: 'Податкова шкала · 9% доходу',
      LINIOWY: 'Лінійний податок · 4,9% доходу',
      RYCZALT: 'Паушальний податок · поріг виручки',
      CIT: 'CIT',
    },
    notes: {
      ESTIMATE: 'Рахуємо з документів у Firmeto — це оцінка, а не заповнена декларація DRA.',
      MONTHLY_ASSUMED_PAID: 'Припускаємо, що внески, порахованi протягом року, були сплачені.',
      BUSINESS_ONLY: 'Ми знаємо лише діяльність. Якщо у вас є інша підстава для медичного страхування — робота за наймом, друга фірма, товариство — розрахунок охоплює і її.',
      MINIMUM_APPLIED: 'Вийшов мінімальний внесок: 9% мінімальної зарплати за кожен місяць страхування. За низького доходу платять саме стільки — також за лінійного податку, де мінімум рахується ставкою 9%, а не 4,9%.',
      RYCZALT_BRACKET_FROM_REVENUE: 'За паушального податку поріг визначає **фактична виручка року**. Якщо протягом року ви платили за виручкою минулого року або за іншим порогом, саме звідси різниця.',
      SOCIAL_NOT_DEDUCTED: 'Базу не зменшуємо на сплачені соціальні внески — у нас вони є витратою в книзі.',
      BAD_DEBT_NOT_IN_BASIS: 'Коригування безнадійних боргів змінюють податковий дохід, але базу внеску ми не змінюємо.',
      INVENTORY_MISSING: 'Немає затвердженого залишку, тож річний дохід може бути неправдивим — а з ним і внесок.',
      FULL_BOOKS_NOT_SUPPORTED: 'За повних книг річний дохід визначають з книг; цього ми ще не рахуємо.',
      FORM_CHANGED: 'Форма оподаткування змінилася протягом року. Тоді річний розрахунок роблять окремо для кожної форми, а ми рахуємо одну — перевірте цю суму з бухгалтером.',
      PARTNERS_SPLIT: 'Партнерське товариство: кожен партнер розраховує внесок сам, зі своєї частини доходу.',
      UNKNOWN_YEAR: 'Немає показників ZUS на цей рік.',
    },
  },
  suspensions: {
    title: 'Зупинення діяльності',
    lead: 'Зупинення заявляють у CEIDG і воно триває щонайменше 30 днів (ст. 22–25 закону про підприємців). Воно не закриває фірму: NIP, книги та неоплачені рахунки залишаються. Проте змінює внески, терміни й амортизацію, тому тут тримаємо періоди, а не перемикач.',
    none: 'Зупинення не записане.',
    noneHint: 'Впишіть період, у якому діяльність була зупинена — тоді за ці місяці не порахуємо ні внесків, ані авансу, а амортизація зупиниться.',
    add: 'Вписати зупинення',
    save: 'Зберегти',
    cancel: 'Скасувати',
    startsOn: 'Перший день зупинення',
    endsOn: 'Останній день зупинення',
    endsOnHint: 'Порожнє означає «досі зупинена» — у CEIDG зупинення не має верхньої межі часу. Дату з майбутнього теж можна вписати: зупинення заявляють заздалегідь.',
    note: 'Причина',
    noteHint: 'Наприклад «догляд за дитиною», «сезонна перерва». Під час перевірки це пояснює, чому за ці місяці немає внесків.',
    days: 'Дні',
    open: 'досі зупинена',
    tooShort: 'коротше за 30 днів',
    resume: 'Відновлення діяльності',
    resumePrompt: 'Останній день зупинення (день перед відновленням діяльності):',
    remove: 'Видалити зупинення',
    removeReason: 'Видалення змінить внески й амортизацію за ті місяці. Вкажіть причину (напр. помилка в даті):',
    effects: 'Що з цього випливає: за місяць, зупинений повністю, немає ні внесків, ні авансу з податку, а за місяць, зупинений частково, база соціальних внесків зменшується пропорційно до днів (внесок на охорону здоров’я залишається цілий). Амортизація зупиняється з місяця, наступного після зупинення. Дні зупинення не рахуються до днів ведення діяльності за Mały ZUS Plus. Файл JPK_V7 може бути потрібний навіть під час зупинення — за придбання товарів з ЄС, імпорту послуг і коригувань.',
  },
  inventory: {
    lead: 'Початковий і кінцевий залишок входять до розрахунку доходу за весь рік, а не за місяць.',
    noneHint:
      'Опис складають на перший і останній день року. За відсутності товарів і матеріалів його роблять із нульовою вартістю — але зробити треба.',
    items: 'Позиції опису',
    open: 'Інвентаризація',
    title: 'Інвентаризація та річний прибуток',
    year: 'Рік',
    none: 'Цього року інвентаризації ще немає.',
    add: 'Нова інвентаризація',
    save: 'Зберегти',
    approve: 'Затвердити',
    delete: 'Видалити',
    kind: 'Вид',
    takenOn: 'Дата',
    value: 'Вартість',
    status: 'Стан',
    itemName: 'Назва',
    quantity: 'Кількість',
    unit: 'Од.',
    unitPrice: 'Ціна',
    revenue: 'Дохід',
    purchases: 'Закупівлі товарів',
    opening: 'Залишок на початок',
    closing: 'Залишок на кінець',
    costOfGoodsSold: 'Вартість проданих товарів',
    income: 'Прибуток',
    kinds: { OPENING: 'на початок', CLOSING: 'на кінець', INTERIM: 'протягом року' },
    statuses: { DRAFT: 'чернетка', APPROVED: 'затверджено' },
    warnings: {
      NO_OPENING_INVENTORY: 'Немає затвердженого залишку на початок — прибуток може бути завищений.',
      NO_CLOSING_INVENTORY: 'Немає затвердженого залишку на кінець — товар на складі рахується як витрата, тож прибуток занижений.',
      DRAFT_INVENTORY: 'Є інвентаризація в чернетці. До розрахунку беремо лише затверджені.',
      NOT_KPIR: 'Ця фірма не веде КПіР — прибуток визначається інакше.',
    },
  },
  verification: {
    notice: 'Підтвердіть адресу пошти кодом, який ми надіслали. Без цього не додасте фірму.',
    code: 'Код із листа',
    confirm: 'Підтвердити',
    resend: 'Надіслати код ще раз',
    resent: 'Код надіслано.',
  },
  badDebt: {
    title: 'Пільга на безнадійні борги',
    lead: 'Несплачений рахунок через 90 днів після терміну оплати змінює ПДВ з обох боків. Покупець **мусить** зменшити вхідний ПДВ (ст. 89b п. 1 закону про ПДВ) і повернути його після оплати; продавець **може** скоригувати ПДВ до сплати (ст. 89a п. 1). Коригування належить до періоду, у якому минув дев’яностий день.',
    none: 'У цьому періоді немає чого коригувати.',
    noneHint: 'Тут показуємо рахунки, яким минуло 90 днів від терміну оплати, і ті, що оплачені після коригування. Жоден не чекає на рішення.',
    purchases: 'Закупівлі — обов’язкове коригування',
    purchasesHint: 'За ці рахунки фірма не заплатила протягом 90 днів від терміну. Вхідний ПДВ треба зменшити — пропуск завищує відрахування.',
    sales: 'Продаж — пільга до розгляду',
    salesHint: 'За ці рахунки не заплатив клієнт. Пільга — це право, а не обов’язок, і має умови, яких з наших даних не видно (наприклад, чи боржник не в банкрутстві) — рішення за вами.',
    restorations: 'Оплачені після коригування — ПДВ повертається',
    restorationsHint: 'Борг оплачено, тож скоригований ПДВ повертається у періоді оплати (ст. 89b п. 4 і ст. 89a п. 4). При оплаті частини боргу повертається частина податку.',
    document: 'Документ',
    counterparty: 'Контрагент',
    dueDate: 'Термін оплати',
    ninetiethDay: '90-й день',
    unpaid: 'Не оплачено',
    vat: 'ПДВ коригування',
    pick: 'Позначити',
    apply: 'Зберегти коригування',
    applyHint: 'Збережені коригування увійдуть до файлу JPK за відповідний період.',
    skip: 'Не коригую',
    skipReason: 'Чому не коригуєте? Причину буде збережено (напр. боржник у реструктуризації, ст. 89b п. 1b):',
    late: 'коригування належить до',
    recorded: 'Збережено в цьому періоді',
    kind: 'Вид',
    reason: 'Причина',
    undo: 'Відкликати рішення',
    kinds: { DECREASE: 'зменшення податку', INCREASE: 'повернення після оплати', SKIPPED: 'без коригування' },
    taxes: 'Податок',
    taxNames: { VAT: 'ПДВ', INCOME: 'Податок на дохід' },
    income: {
      lead: 'Той самий несплачений рахунок змінює також податок на дохід. Боржник **мусить** додати несплачену витрату до бази (ст. 18f п. 1 пп. 2 закону про CIT, ст. 26i п. 1 пп. 2 закону про PIT), кредитор **може** відняти від неї несплачений дохід (п. 1 пп. 1). Суми — нетто, а рішення не йде в жоден файл: воно входить до авансу і до річного розрахунку.',
      purchasesHint: 'За ці рахунки фірма не заплатила протягом 90 днів від терміну. Несплачену витрату треба додати до доходу — пропуск занижує аванс.',
      salesHint: 'За ці рахунки не заплатив клієнт. Несплачений дохід можна вийняти з доходу; це право, а не обов’язок, і має умови, яких з наших даних не видно (наприклад, чи боржник не в банкрутстві).',
      restorationsHint: 'Борг оплачено, тож коригування повертається у періоді оплати: боржник повертає витрату, кредитор показує дохід. При оплаті частини боргу повертається частина суми.',
      amount: 'Сума нетто',
      applyHint: 'Збережені коригування змінять аванс з податку на дохід і річний розрахунок. До файлу JPK вони не входять.',
      skipReason: 'Чому не коригуєте? Причину буде збережено (напр. боржник у банкрутстві або заборгованість уже списана):',
    },
  },
  ledger: {
    ordinal: '№',
    entries: 'Записи в книзі',
    columnsNote: 'колонки згідно з положенням про KPiR',
    revenueColumn: 'колонка 7 — продаж товарів і послуг',
    open: 'Книга',
    title: 'Книга та реєстри ПДВ',
    month: 'Місяць',
    date: 'Дата',
    document: 'Документ',
    description: 'Опис',
    counterparty: 'Контрагент',
    column: 'Кол.',
    amount: 'Сума',
    none: 'Записів цього місяця немає.',
    revenue: 'Дохід',
    costsTotal: 'Витрати',
    result: 'Прибуток',
    vatTitle: 'ПДВ за період',
    vatDue: 'Нарахований',
    vatDeductible: 'До відрахування',
    vatToPay: 'До сплати',
    vatToCarry: 'Надлишок на наступний період',
    quarterly: 'квартальний розрахунок',
  },
  office: {
    served: 'Обслуговується',
    awaitingConsent: 'Чекає на згоду компанії',
    tab: 'Бюро',
    companiesTab: 'Мої компанії',
    title: 'Панель бюро',
    none: 'Ви ще не працюєте в жодному бухгалтерському бюро.',
    subtitle: 'Робоче місце бухгалтерського бюро: компанії клієнтів, кроки місяця, розподіл бухгалтерів.',
    noneHint:
      'Створіть бюро, якщо ведете облік чужих компаній. Якщо вас мають додати до бюро, попросіть запрошення — воно прийде на вашу електронну пошту.',
    create: 'Створити бюро',
    name: 'Назва бюро',
    nip: 'NIP бюро',
    month: 'Місяць',
    onlyMine: 'Тільки мої клієнти',
    client: 'Клієнт',
    assigned: 'Веде',
    assignments: 'Призначення бухгалтерів',
    assignmentsHint: 'Договір каже, що бюро обслуговує фірму. Лише призначення каже, хто саме з бюро '
      + 'заходить до її книг — і на одного клієнта їх може бути кілька.',
    assignmentRoles: { LEAD: 'веде', BACKUP: 'заміна', VIEWER: 'перегляд' },
    assignmentRole: 'Роль у клієнта',
    assignPerson: 'Оберіть особу',
    assign: 'Призначити',
    endAssignment: 'Завершити призначення',
    nobodyAssigned: 'Ще нікого не призначено',
    noServedClients: 'Жодна фірма ще не прийняла пропозицію обслуговування.',
    invoices: 'Рахунки',
    costs: 'Витрати',
    revenue: 'Дохід',
    costsAmount: 'Витрати',
    state: 'Стан',
    ready: 'готова до розрахунку',
    toDo: 'до виконання',
    noClients: 'У бюро ще немає клієнтів.',
    members: 'Команда',
    clients: 'Клієнти',
    role: 'Роль',
    roles: {
      OFFICE_OWNER: 'Власник бюро',
      OFFICE_ADMIN: 'Адміністратор',
      ACCOUNTANT: 'Бухгалтер',
      ASSISTANT: 'Асистент',
    },
  },
  invoices: {
    openInvoice: 'Відкрити',
    backToList: 'Повернутися до списку',
    detail: {
      preview: 'Перегляд рахунка',
      previewOpen: 'Відкрити PDF',
      pdf: 'Завантажити PDF',
      draftNoDocument: 'Чернетка ще не має номера — документ з’явиться після виставлення.',
      payments: 'Оплати',
      due: 'До сплати',
      paid: 'Сплачено',
      remaining: 'Залишилось',
      addPayment: 'Записати оплату',
      paidOn: 'Дата оплати',
      amount: 'Сума',
      exchangeRate: 'Курс на день оплати',
      exchangeRateHint: 'Документ у іноземній валюті: вкажіть середній курс NBP з останнього робочого дня '
        + 'перед оплатою. З різниці щодо курсу рахунка постає фінансовий дохід або витрата.',
      method: 'Спосіб',
      note: 'Примітка',
      noPayments: 'Оплат ще немає.',
      partialHint: 'Сума менша за залишок — це часткова оплата: зарахуємо те, що надійшло, '
        + 'а решта залишиться в розрахунках.',
      deletePayment: 'Видалити',
      confirmDeletePayment: (kwota: string) => `Видалити оплату ${kwota}? Вона повернеться в розрахунки як несплачена.`,
      internalNote: 'Внутрішня примітка',
      internalNoteHint: 'Примітка залишається в компанії: її немає ані на роздруку рахунка, ані у файлі KSeF. '
        + 'Тому її можна дописати й до вже виставленого рахунка.',
      saveNote: 'Зберегти примітку',
      noteSaved: 'Примітку збережено.',
    },
    grossAmount: 'Брутто',
    netAmount: 'Нетто',
    open: 'Рахунки-фактури',
    title: 'Рахунки-фактури продажу',
    tabAll: 'Усі',
    tabUnpaid: 'Неоплачені',
    tabCorrections: 'Коригування',
    filter: 'Фільтр: номер, контрагент, сума',
    allYears: 'Усі роки',
    payment: 'Оплата',
    soon: 'у планах — ще немає в коді',
    ksefFutureTitle: 'Надсилання до KSeF',
    ksefFutureText:
      'Сьогодні працює лише «Файл для KSeF»: завантаження документа у форматі FA(3), який ви надсилаєте самі. Коли з’явиться надсилання через шлюз, у колонці «Стан» буде референсний номер KSeF і підтвердження прийняття.',
    new: 'Новий рахунок',
    none: 'Рахунків немає.',
    number: 'Номер',
    status: 'Стан',
    issueDate: 'Дата виставлення',
    saleDate: 'Дата продажу',
    dueDate: 'Термін оплати',
    counterparty: 'Покупець',
    viesTitle: 'Номер VAT-UE:',
    viesCheck: 'Перевірити у VIES',
    viesWarning:
      'За ставки 0% або «np» для контрагента з ЄС право на неї залежить від дійсного номера VAT-UE (ст. 42 п. 1 підп. 1 закону про ПДВ). Перевірте номер або майте інший доказ — рахунок виставите в будь-якому разі.',
    chooseCounterparty: 'Оберіть контрагента',
    priceMode: 'Ціни вказано',
    lines: 'Позиції',
    addLine: 'Додати позицію',
    removeLine: 'Видалити',
    lineName: 'Назва',
    quantity: 'К-сть',
    unit: 'Од.',
    unitPrice: 'Ціна',
    netUnitPrice: 'Ціна нетто',
    grossUnitPrice: 'Ціна брутто',
    vatAmount: 'Сума ПДВ',
    vatTotal: 'ПДВ разом',
    vatRate: 'ПДВ',
    ryczaltRate: 'Єдина ставка',
    correct: 'Коригування',
    ksefFile: 'Файл для KSeF',
    correction: 'коригування',
    correctionTo: 'Коригувальний рахунок до',
    correctionHint: 'Вкажіть позиції такими, якими вони мають бути після коригування. Різницю порахуємо самі.',
    correctionCause: 'Причина',
    correctionReason: 'Опис причини',
    linesAfter: 'Позиції після коригування',
    causes: {
      MISTAKE: 'Помилка в первинному рахунку',
      EVENT: 'Подія після виставлення (знижка, повернення)',
    },
    amount: 'Сума',
    fromCatalog: 'З картотеки',
    addToCatalog: 'Додати до каталогу',
    editDraft: 'Редагувати чернетку',
    draftUnavailable: 'Це вже не чернетка. Оновіть список рахунків.',
    noMatches: 'Нічого не знайдено',
    searchFailed: 'Не вдалося завантажити результати. Спробуйте ще раз.',
    refineSearch: 'Є ще результати — уточніть пошук.',
    noCatalog: 'Без позиції каталогу',
    saveDraft: 'Зберегти чернетку',
    issue: 'Затвердити і присвоїти номер',
    delete: 'Видалити чернетку',
    totals: 'Разом',
    net: 'нетто',
    vat: 'ПДВ',
    gross: 'брутто',
    draftHint: 'Чернетку можна змінювати вільно. Після затвердження рахунок отримує номер і його вже не змінити — виправлення робиться коригуванням.',
    statuses: { DRAFT: 'Чернетка', ISSUED: 'Виставлений', CANCELLED: 'Анульований' },
    priceModes: { NET: 'нетто', GROSS: 'брутто' },
  },
  bank: {
    checksumOk: 'Контрольна сума правильна',
    whiteListTitle: 'Білий список платників ПДВ',
    whiteListText:
      'З часом під час збереження перевірятимемо, чи рахунок є в переліку Міністерства фінансів — для платежів понад 15 000 zł це має податкові наслідки. Сьогодні перевіряємо лише контрольну суму номера.',
    open: 'Рахунки',
    title: 'Банківські рахунки компанії',
    iban: 'Номер рахунку',
    currency: 'Валюта',
    purpose: 'Призначення',
    bankName: 'Банк',
    add: 'Додати рахунок',
    save: 'Зберегти рахунок',
    none: 'Рахунків немає.',
    remove: 'Прибрати зі списку',
    hint: 'Номер основного рахунку потрапляє на рахунки-фактури. Контрольну суму перевіряємо одразу.',
    purposes: { MAIN: 'Основний', VAT: 'Рахунок ПДВ', TAX_REFUND: 'Для повернення податку' },
  },
  numbering: {
    lead: 'Нумерацію можна змінювати, доки цього року не вийшов жоден документ цього типу.',
    markers: 'Маркери шаблону',
    issued: 'Виставлено',
    locked: 'Заблокований — документи вже вийшли',
    changeable: 'Можна змінювати',
    open: 'Нумерація',
    title: 'Нумерація документів',
    document: 'Документ',
    pattern: 'Шаблон',
    next: 'Наступний номер',
    save: 'Зберегти шаблон',
    reset: 'Спосіб',
    resetYear: 'Річна',
    resetMonth: 'Щомісячна',
    hint: 'Замість {nr} підставляється номер, {rok} — рік, {rr} — дві цифри року. Місяць: {mm} → 09, {m} → 9, {mr} → IX, {msc} → WRZ (польські скорочення, незмінні). За щомісячної нумерації лічильник щомісяця повертається до одиниці, тому номер має містити місяць.',
    types: { INVOICE: 'Рахунок-фактура', CORRECTION: 'Коригування', PROFORMA: 'Проформа', RECEIPT: 'Рахунок' },
  },
  journal: {
    open: 'Журнал',
    title: 'Журнал',
    lead: 'Хронологічний перелік проведень. Кожна подія стоїть по двох боках: та сама сума '
      + 'за дебетом одного рахунку і за кредитом іншого.',
    view: 'Вигляд',
    year: 'Звітний рік',
    month: 'Місяць',
    trialBalance: 'Обороти й сальдо',
    pending: 'До проведення',
    pendingHint: 'Документ не проводиться сам. Проведення в журналі незмінне — виправлення це сторно — '
      + 'тож увесь запис видно тут, перш ніж він потрапить до книг.',
    nothingPending: 'Усе проведено.',
    postAll: 'Провести всі',
    postPicked: (ile: number) => `Провести позначені (${ile})`,
    trialBalanceHint: 'Вхідне сальдо показано окремо від оборотів року — це перенесений стан, '
      + 'а не подія, що сталася цього року.',
    newEntry: 'Нове проведення',
    formHint: 'Номер рахунку береться з плану рахунків. Проводять на рахунку найнижчого рівня, '
      + 'а розрахунковий рахунок потребує контрагента.',
    entryNo: '№',
    postingDate: 'Дата проведення',
    description: 'Опис',
    lines: 'Рядки',
    account: 'Рахунок',
    name: 'Назва',
    side: 'Бік',
    amount: 'Сума',
    debit: 'Дт',
    credit: 'Кт',
    addLine: 'Додати рядок',
    removeLine: 'Вилучити',
    balanced: 'Збалансовано',
    difference: 'Різниця',
    save: 'Зберегти проведення',
    cancel: 'Скасувати',
    empty: 'У цьому періоді ще немає жодного проведення.',
    opening: 'вхідне сальдо',
    reversal: 'сторно',
    reverse: 'Сторно',
    reverseTitle: 'Сторно замість виправлення',
    reverseHint: 'Проведення не змінюють. Сторно перевертає боки новим проведенням, а оригінал '
      + 'залишається — тож видно не лише кінцевий стан, а й те, як він постав.',
    reverseReason: 'Підстава сторно',
    openingDebit: 'Вх. Дт',
    openingCredit: 'Вх. Кт',
    turnoverDebit: 'Оборот Дт',
    turnoverCredit: 'Оборот Кт',
    closingDebit: 'Сальдо Дт',
    closingCredit: 'Сальдо Кт',
    totals: 'Разом',
    controlOk: 'Контроль збігається',
    controlFailed: 'Контроль не збігається',
    offBalanceNote: 'Позабалансові рахунки є в рядках, але поза контрольними сумами: таке проведення '
      + 'однобічне й ніколи не збалансується.',
    entryCount: (count: number) => `Проведень: ${count}`,
    fx: {
      tab: 'Переоцінка валют',
      title: 'Переоцінка валют на дату',
      lead: 'На дату балансу грошові кошти та розрахунки в іноземних валютах оцінюють за курсом цього дня (ст. 30 п. 1 закону про бухгалтерський облік) — інакше баланс показує вартість, якої вже немає. Курсів не підказуємо: введіть середній курс NBP, чинний на дату оцінки.',
      asOf: 'Дата оцінки',
      rate: 'Курс',
      item: 'Позиція',
      account: 'Рахунок',
      balanceCurrency: 'Сальдо у валюті',
      balance: 'В обліку',
      revalued: 'Після оцінки',
      difference: 'Різниця',
      gain: 'Фінансові доходи',
      loss: 'Фінансові витрати',
      preview: 'Перерахувати',
      post: 'Провести оцінку',
      posted: 'Оцінку проведено — запис №',
      nothingToPost: 'Курси не змінилися, тож проводити нічого.',
      exists: 'Оцінка на цю дату вже в обліку — запис №',
      none: 'Немає чого оцінювати.',
      noneHint: 'Оцінка охоплює валютні рахунки та відкриті розрахунки в іноземній валюті. На цю дату фірма не має жодного з них.',
      taxNote:
        'Різниці з оцінки є нереалізованими. Якщо фірма визначає курсові різниці податковим методом (ст. 9b п. 1 підп. 1 закону про CIT), вони не є ні доходом, ні витратою для податку — а аванс рахуємо з обліку, тож сьогодні їх не вилучаємо.',
      kinds: { ACCOUNT: 'валютний рахунок', SETTLEMENT: 'розрахунок' },
    },
    settlements: 'Розрахунки',
    settlementsLead: 'Хто скільки винен і відколи. Позиція виникає при проведенні документа, платіж з виписки її погашає, а сальдо рахується з проведень — окремо нічого не зберігається.',
    asOf: 'На дату',
    onlyOpen: 'Лише непогашені',
    receivables: 'Дебіторська заборгованість',
    payables: 'Кредиторська заборгованість',
    bucket: {
      NOT_DUE: 'у строк',
      DAYS_1_30: '1–30 днів',
      DAYS_31_60: '31–60 днів',
      DAYS_61_90: '61–90 днів',
      OVER_90: 'понад 90 днів',
    },
    total: 'Разом',
    document: 'Документ',
    documentDate: 'Дата',
    dueDate: 'Строк',
    settled: 'Погашено',
    remaining: 'Залишок',
    overdue: (days: number) => `${days} днів прострочення`,
    noItems: 'Непогашених розрахунків немає.',
    counterparty: 'Контрагент',
    counterpartyNone: '— без контрагента —',
    settlement: 'Розрахунок',
    settlementNew: '— нова позиція —',
    offset: 'Взаємозалік',
    offsetHint: 'Дебіторська і кредиторська заборгованість одного контрагента погашаються одним проведенням — до меншої з них або менше.',
    offsetReceivable: 'Дебіторська',
    offsetPayable: 'Кредиторська',
    offsetAmount: 'Сума',
    offsetDate: 'Дата',
    offsetSave: 'Зберегти взаємозалік',
    receivableLabel: 'дебіторська',
    payableLabel: 'кредиторська',
    openingTitle: 'Вхідний баланс з попереднього року',
    openingLead: 'Сальдо балансових рахунків на останній день попереднього року, розрахунки позиціями з документом і строком, результат попереднього року на рахунку результату минулих років — одним технічним проведенням.',
    openingPreview: 'Показати, що перейде',
    openingRecord: 'Провести вхідний баланс',
    openingReplace: 'Замінити: сторно і нове проведення',
    openingExists: 'Цей рік уже має вхідний баланс. Заміна сторнує його і перерахує зі стану попереднього року на сьогодні.',
    openingResult: 'Результат попереднього року',
    openingCounterparty: 'Контрагент',
    openingDocument: 'Документ',
    openingDone: 'Вхідний баланс проведено.',
  },
  reports: {
    open: 'Звітність',
    title: 'Баланс і звіт про прибутки та збитки',
    lead: 'Обидві таблиці рахуються з тих самих сальдо, що й оборотно-сальдова відомість, тож звіт '
      + 'не може сказати нічого іншого, ніж книги.',
    year: 'Звітний рік',
    month: 'Стан на кінець',
    balanceSheet: 'Баланс',
    incomeStatement: 'Звіт про прибутки та збитки',
    assets: 'Активи',
    liabilities: 'Пасиви',
    revenues: 'Доходи',
    costs: 'Витрати',
    account: 'Рахунок',
    name: 'Назва',
    amount: 'Сума',
    total: 'Разом',
    result: 'Результат року',
    profit: 'Прибуток',
    loss: 'Збиток',
    resultNote: 'Результат стоїть у пасивах — саме він замикає рівняння балансу.',
    balanced: 'Баланс сходиться',
    notBalanced: 'Баланс не сходиться',
    notBalancedHint: 'Активи не дорівнюють пасивам разом із результатом. Почніть з оборотно-сальдової '
      + 'відомості — там видно, який рахунок вибивається.',
    empty: 'Цього року ще немає жодного запису, тому звіт порожній.',
    goToJournal: 'Відкрити журнал',
    statutoryNote: 'Це розклад за рахунками, а не за додатком до закону про бухгалтерський облік. Суми '
      + 'остаточні; для офіційної версії бракує лише прив’язки рахунків до позицій на кшталт '
      + '«A. Необоротні активи» чи «B.II. Короткострокова дебіторська заборгованість».',
    settlementsNote: 'У форматі за законом розрахунки розгорнуті — сальдо за контрагентом, дебіторська окремо від кредиторської (без взаємозаліку). Формат за рахунками показує одне сальдо на рахунок.',
    previousYear: 'Попередній рік',
    currentYear: 'Поточний рік',
    previousNoChart: 'Попередній рік не має плану рахунків, тому порівняльна колонка порожня.',
    previousDifferentTemplate: 'Попередній рік був в іншому додатку — позиції не відповідають одна одній, тому порівняльна колонка у форматі за законом порожня.',
    layout: 'Формат',
    layoutStatutory: 'За законом',
    layoutAccounts: 'За рахунками',
    templates: { ZAL_1: 'Додаток 1 — повна форма', ZAL_4: 'Додаток 4 — мікропідприємство', ZAL_5: 'Додаток 5 — мале підприємство' },
    position: 'Стаття',
    unmappedTitle: (ile: number) => ile === 1 ? 'Один рахунок із сальдо не має статті звіту' : `${ile} рахунків із сальдо не мають статті звіту`,
    unmappedHint: 'Їхніх сум немає в таблиці, тож підсумки нижче неповні. Призначте статті в плані рахунків — окремо для дебетового й кредитового сальдо.',
    unmappedGoToChart: 'Призначити в плані рахунків',
    computedNote: 'Жирні статті обчислюються з інших («Прибуток (збиток) від продажу (A − B)»); статті «у тому числі» є частиною суми батьківської статті, а не її складником.',
    noChart: 'Цей рік не має плану рахунків, тож немає що згортати у формат за законом.',
    accountsNote: 'Це формат за рахунками — опорний, у якому ніщо не може загубитися. Формат за законом поруч.',
  },
  chart: {
    open: 'План рахунків',
    positionDebit: 'Стаття звіту — дебетове сальдо',
    positionCredit: 'Стаття звіту — кредитове сальдо',
    positionNone: '— не призначено',
    positionHint: 'Активно-пасивний рахунок потрапляє в різні статті залежно від сторони сальдо. Рахунок без статті звіт покаже як непризначений.',
    sections: { ASSETS: 'Активи', LIABILITIES: 'Пасиви', INCOME: 'Звіт про прибутки та збитки' },
    title: 'Робочий план рахунків',
    lead: 'План належить до облікового року. Проводять на рахунках найнижчого рівня — рахунок '
      + 'з аналітикою є сумою своїх аналітичних рахунків.',
    year: 'Обліковий рік',
    empty: 'Цей рік ще не має плану рахунків',
    emptyHint: 'Почніть із шаблону або скопіюйте план за попередній рік і виправте, що потрібно.',
    create: 'Створити план рахунків',
    source: 'Звідки',
    sourceTemplate: 'Із шаблону',
    sourcePrevious: 'Копія попереднього року',
    sourceEmpty: 'Порожній',
    previewHint: (count: number) => `Так виглядатиме план — рахунків: ${count}. Аналітику додасте після створення.`,
    previewEmpty: 'Порожній план створюють тоді, коли жоден зразок не підходить. Рахунки додасте самі.',
    template: 'Шаблон',
    templateName: 'Назва шаблону',
    saveAsTemplate: 'Зберегти як шаблон',
    number: 'Рахунок',
    numberHint: 'Номер — це тризначний синтетичний рахунок і аналітика після дефіса, наприклад 402-1. '
      + 'Синтетичний рахунок має існувати, перш ніж відкрити до нього аналітику.',
    name: 'Назва',
    kind: 'Вид',
    side: 'Сальдо',
    features: 'Ознаки',
    tax: 'Податкова кваліфікація',
    role: 'Роль при проведенні',
    sum: 'Сума аналітики',
    settlement: 'Розрахунковий',
    currency: 'Валютний',
    inactive: 'не використовується',
    activeAccount: 'Підказувати при проведенні',
    add: 'Додати рахунок',
    edit: 'Змінити',
    delete: 'Видалити',
    save: 'Зберегти',
    cancel: 'Скасувати',
    noYear: 'Облікового року ще немає, а план рахунків належить до року.',
    goToPeriods: 'Перейти до періодів',
    notFullBooks: 'Ця фірма не веде повної бухгалтерії',
    booksUnknown: 'Ми ще не знаємо, який облік веде ця фірма',
    booksUnknownHint: 'Немає податкових налаштувань, чинних сьогодні. План рахунків ведуть за повної '
      + 'бухгалтерії — заповніть налаштування, і ми покажемо решту.',
    notFullBooksHint: 'План рахунків — інструмент повної бухгалтерії. Вид обліку змінюється в налаштуваннях фірми.',
    onlyAccountant: 'План рахунків веде бухгалтер',
    onlyAccountantHint: 'За повної бухгалтерії проводить лише особа з роллю «Бухгалтер». '
      + 'Тут ви бачите план, але не змінюєте його.',
    accountCount: (count: number) => `Рахунків у плані: ${count}`,
    kinds: { BALANCE: 'Балансовий', RESULT: 'Результатний', OFFBALANCE: 'Позабалансовий' },
    sides: { DEBIT: 'Активний', CREDIT: 'Пасивний', BOTH: 'Активно-пасивний', NONE: '—' },
    taxes: {
      DEDUCTIBLE: 'Витрати, що зменшують дохід',
      NON_DEDUCTIBLE: 'Не зменшує дохід',
      TAXABLE: 'Оподатковуваний дохід',
      NON_TAXABLE: 'Неоподатковуваний дохід',
    },
    statements: { ZAL_1: 'Додаток 1', ZAL_4: 'Додаток 4 — мікропідприємство', ZAL_5: 'Додаток 5 — мале підприємство' },
    variants: { BY_KIND: 'За елементами витрат', BY_FUNCTION: 'За функціями витрат', BOTH: 'Обидва варіанти' },
    scopes: { SYSTEM: 'Firmeto', OFFICE: 'Бюро', COMPANY: 'Ця фірма' },
    groups: {
      0: 'Необоротні активи',
      1: 'Грошові кошти та банківські рахунки',
      2: 'Розрахунки та претензії',
      3: 'Матеріали і товари',
      4: 'Витрати за елементами',
      5: 'Витрати за видами діяльності',
      6: 'Готова продукція та витрати майбутніх періодів',
      7: 'Доходи та витрати на їх отримання',
      8: 'Капітал, резерви та фінансовий результат',
      9: 'Позабалансові рахунки',
    },
    roles: {
      RECEIVABLES: 'Розрахунки з покупцями',
      PAYABLES: 'Розрахунки з постачальниками',
      VAT_DUE: 'Нарахований ПДВ',
      VAT_INPUT: 'Вхідний ПДВ',
      VAT_SETTLEMENT: 'Розрахунки з ПДВ',
      SALES_SERVICES: 'Продаж послуг',
      SALES_GOODS: 'Продаж товарів',
      COST_OF_GOODS: 'Собівартість реалізованих товарів',
      GOODS: 'Товари',
      COSTS_MATERIALS: 'Матеріали та енергія',
      COSTS_SERVICES: 'Сторонні послуги',
      COSTS_TAXES: 'Податки та збори',
      COSTS_SALARIES: 'Витрати на оплату праці',
      COSTS_SOCIAL: 'Соціальне страхування та інші виплати',
      COSTS_OTHER: 'Інші витрати за видами',
      FIXED_ASSETS: 'Основні засоби',
      INTANGIBLE_ASSETS: 'Нематеріальні активи',
      ACCUMULATED_DEPRECIATION: 'Амортизація основних засобів',
      ACCUMULATED_AMORTISATION: 'Амортизація нематеріальних активів',
      DEPRECIATION_COST: 'Амортизація (витрати)',
      CASH: 'Каса',
      BANK: 'Поточний рахунок',
      INCOME_TAX: 'Податок на дохід',
      SOCIAL_SECURITY: 'Розрахунки з ZUS',
      PAYROLL: 'Заробітна плата',
      RESULT: 'Фінансовий результат',
      PRIOR_YEARS_RESULT: 'Результат минулих років',
    },
  },
  vatUe: {
    title: 'Інформація VAT-UE',
    lead: 'Хто продає товари або послуги платникам з ЄС, подає підсумкову інформацію — **щомісяця, навіть при квартальному розрахунку ПДВ** (ст. 100 закону про ПДВ). Без внутрішньоспільнотових операцій інформації не подають.',
    none: 'Цього місяця немає внутрішньоспільнотових операцій.',
    noneHint: 'Сюди входять рахунки для контрагентів з ЄС зі ставкою «np» (послуги) або 0% (постачання товарів) і витратні документи, позначені як внутрішньоспільнотове придбання.',
    supplies: 'Постачання товарів',
    acquisitions: 'Придбання товарів',
    services: 'Надання послуг',
    counterparty: 'Контрагент',
    country: 'Країна',
    vatNumber: 'Номер ПДВ',
    amount: 'Вартість',
    download: 'Завантажити VAT-UE',
    vies: 'VIES',
    viesValid: 'дійсний',
    viesInvalid: 'недійсний',
    viesUnknown: 'не перевірено',
    checkVies: 'Перевірити номери у VIES',
    viesUnavailable: 'Реєстр не відповів для номерів:',
    problems: 'Позиції, які не вдається показати',
    notes: {
      TRIANGULAR_NOT_DETECTED: 'Тристоронніх операцій не розпізнаємо — у кожній позиції стоїть «не стосується».',
      VIES_ON_DEMAND: 'Номери перевіряєте у VIES кнопкою поруч — результат зберігається з датою, бо саме він є доказом належної обачності.',
      CALL_OFF_NOT_SUPPORTED: 'Переміщень у процедурі складу call-off не показуємо.',
      MONTHLY_ALWAYS: 'Інформацію подають щомісяця, незалежно від того, чи ПДВ розраховуєте місячно, чи квартально.',
    },
  },
  officeJpk: {
    title: 'JPK клієнтів за місяць',
    lead: 'Підготуйте файли всіх клієнтів одразу, завантажте пакет файлів метаданих, підпишіть їх підписом бюро (довіреність UPL-1) і завантажте підписані файли — Firmeto зіставить їх за назвою й надішле.',
    prepare: 'Підготувати для клієнтів',
    download: 'Завантажити пакет для підпису',
    upload: 'Завантажити підписані файли',
    problems: 'Не підготовлено',
    sent: (count: number) => `Надіслано: ${count}`,
  },
  jpkSend: {
    title: 'Надсилання до Міністерства фінансів',
    personLead: 'Як фізична особа ви підписуєте файл сумою доходу з податкової декларації дворічної давності (напр. у 2026 — з декларації за 2024). Ім’я, прізвище та дату народження беремо з даних для декларацій. Суму ми не зберігаємо.',
    companyLead: 'Компанія підписує файл метаданих кваліфікованим або довіреним підписом. Підготуйте надсилання, завантажте файл, підпишіть його (напр. на podpis.gov.pl, у форматі XAdES) і завантажте підписаний файл сюди.',
    income: 'Дохід із декларації дворічної давності (зл)',
    send: 'Надіслати до податкової',
    prepare: 'Підготувати до підпису',
    downloadToSign: 'Завантажити файл для підпису',
    uploadSigned: 'Завантажити підписаний файл',
    history: 'Надсилання',
    none: 'Цей файл ще не надсилали.',
    reference: 'Референсний номер',
    upo: 'Завантажити UPO',
    test: 'тестове середовище',
    statuses: { AWAITING_SIGNATURE: 'чекає на підпис', SENDING: 'надсилання', PROCESSING: 'податкова обробляє', ACCEPTED: 'прийнято, є UPO', REJECTED: 'відхилено', FAILED: 'не надіслано' } as Record<string, string>,
  },
  jpk: {
    goToFiling: 'Перейти до даних для декларації',
    title: 'Файли JPK',
    lead: 'JPK_V7M за обраний місяць — облік ПДВ разом із декларацією. Файл можна завантажити або надіслати до податкової звідси.',
    leadQuarterly:
      'JPK_V7K за обраний місяць. При квартальному розрахунку **облік подають щомісяця, а декларацію раз на квартал**: файл за перший і другий місяць кварталу несе лише облік, а за третій — облік цього місяця й декларацію за весь квартал. Файл підписуєте й надсилаєте власним інструментом.',
    period: 'Місяць',
    download: 'Завантажити JPK_V7M',
    downloadQuarterly: 'Завантажити JPK_V7K',
    beforeDownload: 'Перед завантаженням перевірте дані для декларації — без коду податкової файл не виникне.',
  },
  filing: {
    title: 'Дані для декларації',
    lead: 'Потрібні лише для файлів JPK і декларацій. Для виставлення рахунка чи обчислення податку не використовуються.',
    taxOffice: 'Податкова інспекція',
    taxOfficeHint: 'Введіть місто або частину назви й виберіть установу зі списку. Зберігаємо чотиризначний код, якого вимагають JPK і декларації.',
    taxOfficeSearch: 'Місто або назва установи',
    taxOfficeNone: 'Жодна установа не підходить. Спробуйте лише назву міста.',
    taxOfficeSpecialised: 'для найбільших платників',
    taxOfficeSpecialisedHint: 'Спеціалізовані установи обслуговують платників із доходом понад 50 млн євро, банки та групи компаній. Якщо це не ваша фірма, виберіть звичайну установу для своєї ґміни.',
    taxOfficeClear: 'Очистити',
    firstName: 'Імʼя платника',
    lastName: 'Прізвище платника',
    birthDate: 'Дата народження',
    personHint: 'Одноосібна діяльність подається у JPK іменем, прізвищем і датою народження; товариство — самою назвою.',
    email: 'Адреса ел. пошти для декларацій',
    phone: 'Контактний телефон',
    zusInsuranceTitleCode: 'Код підстави страхування (ZUS DRA)',
    zusInsuranceTitleCodeHint:
      'Шість цифр з декларації ZUS: чотири цифри коду, право на пенсію, ступінь інвалідності. За повних внесків зазвичай 051000, за преференційних 057000 — але коду не вписуємо за вас, бо за Mały ZUS Plus і пільги на старт словник ZUS неоднозначний.',
    save: 'Зберегти дані',
    saved: 'Збережено.',
  },
  invitation: {
    title: 'Запрошення до компанії',
    lead: (who: string) => `${who} запрошує вас до обслуговування фірми у Firmeto.`,
    invitedBy: 'Запрошує',
    invitesAs: 'запрошує як',
    company: 'Фірма',
    forEmail: 'Для адреси',
    roleScope: {
      OWNER: 'Повний доступ до фірми, зокрема до розрахунків із Firmeto та керування доступами.',
      ADMIN: 'Документи, налаштування й доступи. Без зміни власника фірми.',
      ACCOUNTANT: 'Проведення й податкові розрахунки, повний перегляд документів. Без доступу до розрахунків із Firmeto.',
      EMPLOYEE: 'Виставлення рахунків і внесення витрат. Без перегляду податків і розрахунків.',
      BOARD_MEMBER: 'Перегляд документів і результатів товариства. Без внесення змін.',
      VIEWER: 'Лише перегляд — без внесення й зміни будь-чого.',
    },
    accept: 'Прийняти запрошення',
    signInToAccept: 'Увійдіть, щоб прийняти',
    expires: (date: string) => `Запрошення діє до ${date}. Кожне прийняття записуємо в журнал змін фірми.`,
    accepted: 'Запрошення прийнято. Компанія вже у вашому списку.',
    back: 'До компаній',
  },
};

const ru: Dictionary = {
  documentNotifications: {
    enable: 'Включить звук документов',
    mute: 'Выключить звук документов',
    blocked: 'Звук нужно активировать в этом браузере.',
    unsupported: 'Этот браузер не поддерживает звук уведомлений.',
    ready: 'Звук новых документов включён',
    muted: 'Звук документов выключен',
  },
  appName: 'Firmeto',
  language: 'Язык',
  signIn: 'Войти',
  signOut: 'Выйти',
  signingIn: 'Вход…',
  loading: 'Загрузка…',

  shell: {
    navigation: 'Навигация компании',
    sales: 'Продажи',
    purchases: 'Закупки',
    books: 'Книги',
    settlements: 'Расчёты',
    setup: 'Настройки',
    density: 'Плотность',
    comfortable: 'Комфортная',
    dense: 'Плотная',
    back: 'Назад к компаниям',
  },
  start: {
    kicker: 'Начните',
    claim: 'Нет дохода — нет платы.',
    lead: 'Онлайн-бухгалтерия для малых фирм и бухгалтерских бюро в Польше. Счета, налоги, ZUS и сроки в одном месте.',
    promises: (trialMonths: number, capPercent: number) => [
      'В месяце без дохода плата составляет 0 зł',
      `Плата никогда не превышает ${capPercent}% дохода`,
      trialMonths === 1
        ? 'Первый месяц бесплатно, без срочного договора'
        : `Первые ${trialMonths} месяца бесплатно, без срочного договора`,
    ],
    euServers: 'Серверы в Европейском союзе',
    whoAreYou: 'Кто вы?',
    whoAreYouLead: 'От этого зависит, что вы увидите после входа. Выбор можно изменить позже.',
    paths: {
      company: {
        title: 'Веду фирму',
        lead: 'Индивидуальная деятельность или общество. Счета, расходы, налоги и сроки.',
      },
      office: {
        title: 'Веду бухгалтерское бюро',
        lead: 'Панель с фирмами клиентов, шаги месяца и распределение бухгалтеров.',
      },
      founding: {
        title: 'Открываю фирму',
        lead: 'Поможем выбрать форму налогообложения и проведём через первый расчёт.',
      },
    },
    createAccount: 'Создать учётную запись',
    haveAccount: 'У меня уже есть учётная запись — войти',
    invitationHint: 'Есть приглашение от бухгалтерского бюро? Откройте ссылку из письма.',
  },
  register: {
    claim: 'Три шага до первого счёта.',
    stepNames: ['Учётная запись', 'Фирма', 'Расчёты'],
    euData: 'Данные остаются в Европейском союзе',
    backToStart: 'Вернуться к началу',
    signIn: 'Войти',
    signInToContinue: 'Фирму создают уже после входа. Войдите паролем, который только что задали.',
    codeSent: 'Мы отправили на ваш адрес шестизначный код. Он действует 24 часа.',
    codeResent: 'Код отправлен ещё раз.',
    account: {
      title: 'Создайте учётную запись',
      lead: 'Займёт меньше минуты. Данные фирмы пока не нужны.',
      firstName: 'Имя',
      lastName: 'Фамилия',
      email: 'Адрес электронной почты',
      emailHint: 'На этот адрес отправим подтверждение и напоминания о сроках.',
      password: 'Пароль',
      passwordHint: 'Минимум 12 знаков.',
      passwordRules: {
        length: 'Не менее 12 знаков',
        notEmail: 'Отличается от вашего адреса e-mail',
        maxLength: 'Не более 128 знаков',
      },
      showPassword: 'Показать',
      hidePassword: 'Скрыть',
      generate: 'Сгенерировать',
      generated: 'Пароль вписан в оба поля и скопирован в буфер обмена. Сохраните его в менеджере паролей.',
      repeatPassword: 'Повторите пароль',
      passwordsDiffer: 'Пароли не совпадают.',
      uiLocale: 'Язык интерфейса',
      uiLocaleHint: 'Язык относится к вашей учётной записи, а не к фирме. Меняется выше.',
      terms: 'Принимаю регламент и политику конфиденциальности. Бухгалтерские данные обрабатываем только в Европейском союзе.',
      lawUpdates: 'Хочу получать сообщения об изменениях в законах, касающихся моей формы налогообложения.',
      next: 'Далее — подтверждение адреса',
    },
    code: {
      title: 'Подтвердите адрес',
      lead: (email: string) =>
        `Введите код, который мы отправили на ${email}. Без подтверждённого адреса фирму создать нельзя.`,
      label: 'Код из письма',
      hint: 'Код действует 24 часа.',
      confirm: 'Подтвердить и войти',
      resend: 'Отправить код ещё раз',
    },
    company: {
      title: 'Данные фирмы',
      lead: 'Номер NIP определяет фирму в реестрах и на счетах — остальное можно поправить позже.',
      nip: 'NIP',
      nipLength: (digits: number) => `У NIP 10 цифр. Введено ${digits}.`,
      noRegistryTitle: 'Данные вводите сами',
      noRegistryLead:
        'Загрузка данных из реестра GUS ещё не подключена. Пока впишите название и адрес вручную — проверим их в реестре позже.',
      legalForm: 'Правовая форма',
      jdg: 'Индивидуальная деятельность',
      spZoo: 'Общество с ограниченной ответственностью',
      name: 'Название фирмы',
      nameHint: 'Так, как должно выглядеть на счёте.',
      address: 'Улица и номер',
      postalCode: 'Почтовый индекс',
      city: 'Город',
      startedOn: 'Дата начала деятельности',
      startedOnHint: 'От неё считается льгота на старт и лимит освобождения от НДС в первом году.',
      next: 'Далее — расчёты',
    },
    settlements: {
      title: 'Как вы рассчитываете фирму?',
      lead: 'Не знаете? Выберите «Не уверен» — определим это до первого расчёта, остальное будет работать.',
      perMonth: (net?: string) => (net ? `${net} зł нетто / мес.` : '—'),
      unsurePrice: 'определим вместе',
      plans: {
        RYCZALT: { title: 'Ричалт', lead: 'Учёт доходов, ставка ричалта, без расходов.' },
        KPIR: { title: 'KPiR', lead: 'Книга доходов и расходов, расходы с вычетом НДС.' },
        FULL: { title: 'Полная бухгалтерия', lead: 'Журнал, план счетов, проводки.' },
        UNSURE: { title: 'Не уверен', lead: 'Фирма появится сразу, налоговые настройки добавим позже.' },
      },
      incomeTax: 'Подоходный налог',
      skala: 'По шкале — 12% и 32%',
      liniowy: 'Линейный — 19%',
      cit: 'CIT — общество',
      vat: 'НДС',
      vatActive: 'Активный плательщик НДС',
      vatExemptLimit: 'Освобождение до лимита (ст. 113)',
      vatExemptKind: 'Предметное освобождение (ст. 43)',
      vatPeriod: 'Период расчёта НДС',
      monthly: 'Месячный — JPK_V7M',
      quarterly: 'Квартальный — JPK_V7K',
      zus: 'Взносы ZUS',
      zusSchemes: {
        ULGA_NA_START: 'Льгота на старт — первые 6 месяцев',
        PREFERENCYJNY: 'Льготные — 24 месяца',
        MALY_ZUS_PLUS: 'Малый ZUS Плюс',
        PELNY: 'Полные взносы',
        BRAK: 'Не плачу взносов от этой деятельности',
      },
      from: 'С какого месяца рассчитываете в Firmeto',
      fromHint: 'Предыдущие месяцы можно добавить позже — у настроек есть дата действия.',
      feeRule: (trialMonths: number, capPercent: number) =>
        `Понимаю правило оплаты: 0 зł в месяце без дохода, никогда больше ${capPercent}% дохода. ` +
        (trialMonths === 1 ? 'Первый месяц бесплатно.' : `Первые ${trialMonths} месяца бесплатно.`),
      finish: 'Создать фирму и начать',
      unsureTitle: 'Налоговые настройки могут подождать',
      unsureLead:
        'Фирма появится сразу, и вы сможете вводить документы. Налог не посчитаем, пока не будет известно, как вы рассчитываетесь — пульт будет об этом напоминать.',
    },
    done: {
      title: 'Фирма готова',
      lead: 'Можно начинать. Плата появится только в месяце с доходом.',
      next: [
        { title: 'Добавьте банковский счёт', lead: 'Номер попадает на счета и позволяет сопоставлять платежи' },
        { title: 'Выставьте первый счёт', lead: 'Или впишите расход, который уже есть' },
        { title: 'Пригласите бухгалтера', lead: 'Роль «Бухгалтер», без доступа к расчётам с Firmeto' },
      ],
      enter: 'Перейти к фирме',
    },
  },
  emailUndeliverable:
    'Письма на ваш адрес возвращаются. Проверьте его — без рабочего адреса не получится сменить пароль и получать уведомления.',
  companies: {
    settlement: 'Расчёт',
    title: 'Мои компании',
    subtitle: 'Компании, к которым у вас есть доступ. Выберите одну, чтобы открыть её документы и книги.',
    addHint: 'Введите NIP, остальное заполним из реестра',
    bookkeeping: 'Кто будет вести учёт?',
    bookkeepingChoices: { SELF: 'Самостоятельно', ACCOUNTANT: 'Мой бухгалтер', OFFICE: 'Бухгалтерское бюро' },
    bookkeepingSelfFull: 'Общество ведёт полную бухгалтерию, а её ведёт только роль Бухгалтер. '
      + 'Вы дадите её себе на следующем экране — для учёта собственной фирмы сертификат не нужен.',
    bookkeepingSelfSimple: 'Книгу доходов и расходов и паушальный налог вы ведёте сами, без дополнительных ролей.',
    bookkeepingAccountantHint: 'Пригласите её на следующем экране — получит роль Бухгалтер и доступ к книгам.',
    bookkeepingOfficeHint: 'Передайте бюро NIP фирмы. Бюро добавит её к своим клиентам, '
      + 'а Вы утвердите предложение обслуживания в Лицах.',
    empty: 'У вас пока нет компаний. Добавьте первую, чтобы начать.',
    add: 'Добавить компанию',
    nip: 'Налоговый номер (NIP)',
    fetch: 'Получить из реестра',
    fetching: 'Ищу…',
    fetched: (source: string) => `Данные заполнены из реестра: ${source}. Проверьте их перед сохранением.`,
    fetchedWithVat: (source: string, vat: string) =>
      `Данные заполнены из реестра: ${source}. Статус VAT: ${vat}. Проверьте их перед сохранением.`,
    name: 'Название',
    legalForm: 'Правовая форма',
    city: 'Город',
    businessStartedOn: 'Дата начала деятельности',
    businessStartedOnHint: 'Нужна для лимита освобождения от НДС в первый год.',
    addressLine: 'Улица и номер',
    postalCode: 'Почтовый индекс',
    save: 'Сохранить компанию',
    saving: 'Сохраняю…',
    cancel: 'Отмена',
    roles: 'Ваша роль',
    jdg: 'Индивидуальный предприниматель',
    spzoo: 'Общество с ограниченной ответственностью',
    forms: {
      JDG: 'Индивидуальная деятельность',
      SP_CYWILNA: 'Гражданское товарищество',
      SP_JAWNA: 'Полное товарищество',
      SP_PARTNERSKA: 'Партнёрское товарищество',
      SP_KOMANDYTOWA: 'Коммандитное товарищество',
      SP_KOMANDYTOWO_AKCYJNA: 'Коммандитно-акционерное товарищество',
      SP_ZOO: 'Общество с ограниченной ответственностью',
      PROSTA_SA: 'Простое акционерное общество',
      SA: 'Акционерное общество',
      SPOLDZIELNIA: 'Кооператив',
      STOWARZYSZENIE: 'Ассоциация (объединение)',
      FUNDACJA: 'Фонд',
    },
  },

  settings: {
    partners: {
      title: 'Участники',
      lead: 'В гражданском, полном и профессиональном товариществе налог платят участники — каждый со своей '
        + 'доли прибыли и каждый по своей форме налогообложения. Без этой картотеки считаем один аванс так, '
        + 'будто весь доход заработал один человек.',
      firstName: 'Имя',
      lastName: 'Фамилия',
      share: 'Доля в прибыли',
      shareHint: 'Дробь или процент: 0,5 или 50%. Доли всех участников должны складываться в целое.',
      taxation: 'Форма налогообложения',
      zus: 'Схема ZUS',
      from: 'С',
      to: 'По',
      add: 'Добавить участника',
      end: 'Завершить долю',
      endPrompt: 'Последний день в товариществе:',
      none: 'Картотека участников пуста — тогда аванс считаем как для одного человека.',
      sum: 'Сумма долей',
    },
    lead: 'Способ расчёта действует с указанной даты. Смена формы в течение года — это новая запись, а не перезапись предыдущей.',
    periods: 'Периоды действия',
    to: 'Действует до',
    openEnded: 'бессрочно',
    inForce: 'Действует',
    future: 'Будущий',
    yearNotCovered: (od: string, doKiedy: string) =>
      `Отчётный год ${od} – ${doKiedy} не полностью охвачен настройками`,
    yearNotCoveredHint: (odKiedy: string, poczatek: string) =>
      `Настройки действуют с ${odKiedy}, а отчётный год начинается ${poczatek}. За месяцы до этой `
      + 'даты мы не знаем, как фирма рассчитывается — плану счетов и налогам за эту часть года '
      + 'не на что опереться. Если ничего не менялось, перенесите дату назад.',
    coverFromYearStart: (poczatek: string) => `Исправить на ${poczatek}`,
    edit: 'Исправить',
    editWarning: 'Вы исправляете существующую запись',
    editWarningHint: 'Изменение решения — это новая запись, а не исправление: история должна остаться, '
      + 'чтобы пересчёт старого месяца дал тот же результат. Исправляйте запись только тогда, когда '
      + 'ошиблись при вводе. Уже закрытый месяц изменение не затронет.',
    past: 'Исторический',
    title: 'Налоговые настройки',
    open: 'Настройки',
    close: 'Свернуть',
    none: 'Настроек пока нет. Заполните их, прежде чем начинать учёт.',
    from: 'Действует с',
    taxationForm: 'Форма налогообложения',
    booksType: 'Учёт',
    vatStatus: 'НДС',
    vatExemptionBasis: 'Основание освобождения',
    zusScheme: 'Взносы владельца (ZUS)',
    cashMethod: 'Кассовый метод PIT',
    smallTaxpayer: 'Малый налогоплательщик',
    incomeTaxPeriod: 'Авансы по налогу на доход',
    incomeTaxPeriodHint:
      'Квартальные доступны малому налогоплательщику и тому, кто начинает деятельность (ст. 25 п. 1b закона о CIT, ст. 44 п. 3g закона о PIT). Условие не проверяем — оно зависит от дохода предыдущего года. Период НДС настраивается отдельно.',
    advancePeriods: { MONTHLY: 'Месячные', QUARTERLY: 'Квартальные' },
    exportData: 'Скачать все данные',
    exportHint: 'Всё, что у нас есть об этой фирме, можно забрать одним файлом.',
    sicknessInsurance: 'Добровольное страхование на случай болезни',
    add: 'Добавить настройки',
    save: 'Сохранить настройки',
    history: 'История изменений',
    monthHint: 'Настройки действуют с первого дня месяца; прошлые периоды остаются без изменений.',
    forms: { ryczalt: 'Единый налог', skala: 'Налоговая шкала', liniowy: 'Линейный налог', cit: 'CIT' },
    books: { ryczalt: 'Учёт доходов', kpir: 'Книга доходов и расходов', full: 'Полный бухучёт' },
    vat: { active_monthly: 'Плательщик, ежемесячно', active_quarterly: 'Плательщик, ежеквартально', exempt: 'Освобождён' },
    zus: {
      ulga_na_start: 'Льгота на старт',
      preferencyjny: 'Льготный',
      maly_zus_plus: 'Малый ZUS Plus',
      pelny: 'Полный',
      brak: 'Без взносов',
    },
  },
  access: {
    servedBy: 'Обслуживание бухгалтерским бюро',
    officeAccountants: 'Доступ к книгам',
    nobodyYet: 'никто ещё не назначен',
    endOffice: 'Завершить сотрудничество',
    endOfficeHint: 'Бюро теряет доступ в тот же день. Книги остаются в компании.',
    confirmEndOffice: (office: string) =>
      `Завершить сотрудничество с бюро ${office}? Его бухгалтеры потеряют доступ к книгам этой компании.`,
    officeProposals: 'Предложения обслуживания',
    noBookkeeper: 'Никто не ведёт учёт этой фирмы',
    ownDocumentsOnly: 'Видит только документы, которые вносит сам',
    ownDocumentsOnlyShort: 'только свои',
    ownDocumentsOnlyHint: 'При нескольких менеджерах каждый видел бы чужие наценки, комиссии и контрагентов. '
      + 'Сужение оставляет только те счета, расходы и файлы, которые он добавил сам.',
    selfAccountant: 'Роль Бухгалтер можно взять и себе',
    selfAccountantHint: 'Вести учёт собственной фирмы можно без сертификата. Ответственность за него '
      + 'остаётся на руководителе, а роль Бухгалтер требует двухфакторного входа.',
    takeAccountantRole: 'Дать мне роль Бухгалтер',
    officeProposalsHint:
      'Бухгалтерское бюро хочет вести книги этой компании. До вашего согласия оно не видит никаких данных и не может назначить бухгалтера.',
    office: 'Бюро',
    whoPays: 'Кто платит за Firmeto',
    payerOffice: 'Бюро',
    payerCompany: 'Компания',
    acceptProposal: 'Принять',
    rejectProposal: 'Отклонить',
    lead: 'Доступ к этой компании. Каждое предоставление и отзыв доступа записываем в историю изменений.',
    person: 'Человек',
    whatEachRoleSees: 'Что видит каждая роль',
    permission: 'Право',
    permissions: {
      documents: 'Счета и расходы',
      books: 'Книга и JPK',
      settings: 'Налоговые настройки',
      people: 'Люди и доступ',
      billing: 'Плата за Firmeto',
    },
    levels: { full: 'полное', add: 'добавление', view: 'просмотр' },
    permissionsNote:
      '«—» означает, что пункт вообще не появляется в навигации этого человека. Мы не показываем серых кнопок, которые нельзя нажать.',
    title: 'Люди и приглашения',
    open: 'Люди',
    people: 'Имеют доступ',
    invitations: 'Приглашения',
    invite: 'Пригласить',
    send: 'Отправить приглашение',
    email: 'Адрес e-mail',
    role: 'Роль',
    remove: 'Забрать доступ',
    revoke: 'Отменить',
    expires: 'Действует до',
    noInvitations: 'Приглашений нет.',
    hint: 'Приглашение действует 7 дней; принять его может только тот, кто вошёл с указанным адресом.',
    statuses: { pending: 'Ожидает', accepted: 'Принято', revoked: 'Отменено', expired: 'Истекло' },
    roles: {
      OWNER: 'Владелец',
      ADMIN: 'Администратор',
      ACCOUNTANT: 'Бухгалтер',
      EMPLOYEE: 'Сотрудник',
      BOARD_MEMBER: 'Член правления',
      VIEWER: 'Просмотр',
    },
    /*
     * Женские формы только там, где они нейтральны: владелица, сотрудница. «Бухгалтер»
     * и «администратор» остаются общими — разговорные формы звучат снисходительно, а это
     * подпись рядом с именем конкретного человека.
     */
    rolesFemale: {
      OWNER: 'Владелица',
      ADMIN: 'Администратор',
      ACCOUNTANT: 'Бухгалтер',
      EMPLOYEE: 'Сотрудница',
      BOARD_MEMBER: 'Член правления',
      VIEWER: 'Просмотр',
    },
  },
  counterparties: {
    lead: 'Картотека, используемая при выставлении счетов и вводе расходов.',
    dialogLead: 'Контрагент сохранится сразу и попадёт в документ — черновик останется на месте.',
    active: 'Активный',
    typeHint: 'Вид определяет, какие ставки НДС доступны в счёте.',
    nipHint: 'Проверим контрольную сумму.',
    ratesTitle: 'Вид и ставки НДС',
    ratesHint: 'Перечень соответствует ставкам, доступным в счёте в этом приложении.',
    open: 'Контрагенты',
    title: 'Картотека контрагентов',
    search: 'Поиск по названию или номеру',
    add: 'Добавить контрагента',
    none: 'Картотека пуста.',
    showInactive: 'Показать неактивных',
    name: 'Название',
    nip: 'Налоговый номер (NIP)',
    vatEuId: 'Номер VAT ЕС',
    country: 'Страна',
    city: 'Город',
    type: 'Тип',
    deactivate: 'Пометить как неактивного',
    inactive: 'неактивный',
    save: 'Сохранить контрагента',
    types: {
      DOMESTIC: 'Компания в Польше',
      EU: 'Компания из ЕС',
      NON_EU: 'Компания вне ЕС',
      PERSON: 'Частное лицо',
    },
  },
  products: {
    lead: 'Позиции, подсказываемые при выставлении счёта.',
    showInactive: 'Показать отозванные',
    grossPrice: 'Цена брутто',
    grossPreview: 'Брутто',
    retire: 'Отозвать',
    open: 'Товары и услуги',
    title: 'Картотека товаров и услуг',
    search: 'Поиск по названию или коду',
    add: 'Добавить позицию',
    edit: 'Редактировать',
    none: 'Картотека пуста.',
    code: 'Код',
    namePl: 'Название (по-польски)',
    nameEn: 'Название (по-английски)',
    unit: 'Единица',
    vatRate: 'Ставка НДС',
    gtu: 'Код GTU',
    price: 'Цена',
    save: 'Сохранить позицию',
    rates: { '23': '23%', '8': '8%', '5': '5%', '0': '0%', zw: 'освобождена', np: 'не облагается', oo: 'обратное начисление' },
  },
  wizard: {
    open: 'Мастер',
    preview: 'Просмотр документа',
    previewOpen: 'Открыть оригинал',
    previewUnsupported: 'Этот файл здесь не покажем — откройте оригинал в новой вкладке.',
    title: 'Мастер проведения',
    documentNumber: 'Номер документа',
    issueDate: 'Дата документа',
    category: 'Вид расхода',
    vatRate: 'Ставка НДС',
    net: 'Нетто',
    vat: 'НДС',
    gross: 'Брутто',
    stepOf: (step: number, total: number) => `Шаг ${step} из ${total}`,
    steps: { kind: 'Что это за документ', counterparty: 'От кого', document: 'Документ и суммы', payment: 'Оплата', summary: 'Итог', statement: 'Выписка' },
    kinds: { COST_INVOICE: 'Счёт на расходы', RECEIPT: 'Чек', BANK_STATEMENT: 'Банковская выписка' },
    kindHints: {
      COST_INVOICE: 'Счёт от поставщика с NIP — НДС к вычету, расход в книгу.',
      RECEIPT: 'Чек без счёта — расход без вычета НДС.',
      BANK_STATEMENT: 'Файл из банка (MT940) — платежи для сопоставления.',
    },
    counterpartyHint: 'Выберите поставщика из списка или добавьте нового по NIP — данные подтянем из реестра.',
    supplier: 'Поставщик',
    newSupplier: 'Новый поставщик',
    dueDate: 'Срок оплаты',
    deduction: 'Вычет НДС',
    deductions: { FULL: 'полностью', HALF: 'половина (авто)', NONE: 'без вычета' },
    paid: 'Документ уже оплачен',
    paidOn: 'Дата оплаты',
    method: 'Способ',
    methods: { transfer: 'перевод', cash: 'наличные', card: 'карта' },
    unpaidHint: 'Останется обязательством к оплате — со сроком, если вы его указали.',
    summaryLead: 'Так мы это запишем. Пока ничего не создано — проверьте и подтвердите.',
    costAmount: 'В расходы',
    deductibleVat: 'НДС к вычету',
    paidShort: 'оплачен',
    unpaidShort: 'не оплачен — ждёт в расчётах',
    summaryFull: 'При полном учёте проводка попадёт в список «к проведению» в журнале — ничего не проводится без просмотра.',
    summarySimple: 'Расход попадёт в книгу за месяц с даты документа.',
    statementLead: 'Загрузим выписку и покажем, какие платежи соответствуют каким документам. Сопоставления подтверждает человек.',
    importStatement: 'Загрузить выписку',
    back: 'Назад',
    next: 'Далее',
    finish: 'Подтвердить и сохранить',
    later: 'Закончу позже',
    close: 'Закрыть',
    doneFull: 'Документ расходов сохранён. Проводка ждёт проведения в журнале.',
    doneSimple: 'Документ расходов сохранён и внесён в книгу.',
    doneStatement: 'Выписка загружена. Сопоставьте платежи с документами.',
    goJournal: 'Перейти в журнал',
    goCosts: 'Перейти к расходам',
    goMatch: 'Перейти к сопоставлению',
  },
  documents: {
    noneHint: 'Загруженные файлы появятся здесь с датой, размером и отметкой, внесены ли они уже как документ.',
    scanFutureText:
      'Со временем загруженный файл попадает в очередь «Ждёт внесения», а из его содержимого мы подсказываем поставщика, номер, даты и суммы при добавлении расхода. Сегодня файл — только вложение.',
    open: 'Документы',
    title: 'Архив документов',
    lead: 'Снимки и PDF храним в том виде, в каком они пришли. При проверке важен оригинал.',
    drop: 'Перетащите сюда снимки или файлы PDF',
    choose: 'Выбрать файлы',
    accepted: 'JPEG, PNG, HEIC или PDF, до 25 МБ.',
    none: 'Архив пуст.',
    fileName: 'Файл',
    added: 'Добавлен',
    size: 'Размер',
    state: 'Состояние',
    waiting: 'Ждёт внесения',
    posted: 'Внесён в расходы',
    delete: 'Удалить',
    uploaded: (added: number) => `Добавлено в архив: ${added}.`,
    uploadedWithKnown: (added: number, known: number) =>
      `Добавлено в архив: ${added}. Уже были там: ${known} — вторую копию не делаем.`,
    confirmDelete: (fileName: string) => `Удалить ${fileName} из архива? Это действие нельзя отменить.`,
    inboxTitle: 'Ящик документов',
    inboxLead: 'Всё, что поступило — из браузера, с телефона и по почте — ждёт здесь, пока кто-то это внесёт или отклонит с причиной. Оригинал остаётся в архиве.',
    statuses: { NEW: 'новый', IN_PROGRESS: 'в работе', BOOKED: 'проведён', REJECTED: 'отклонён' },
    tag: 'Метка',
    source: 'Откуда',
    sourceUpload: 'загружен',
    sourceEmail: 'e-mail',
    accept: 'Отметить как в работе',
    reject: 'Отклонить',
    rejectPrompt: 'Причина отклонения — владелец должен знать, что исправить:',
    rejected: 'Отклонён',
    intakeTitle: 'Документы по почте',
    mailboxConfig: 'Настройка ящика',
    intakeLead: 'Отправьте письмо с вложениями на адрес фирмы — файлы попадут в ящик. Адресов может быть несколько, каждый со своей меткой. Принимаем только от людей с доступом к фирме и утверждённых отправителей; чужие письма отбрасываем без ответа.',
    intakeAddress: 'Адрес',
    intakeTag: 'Метка вложений',
    intakeTagHint: 'напр. счета, выписки, чеки',
    intakeCreate: 'Сгенерировать адрес',
    intakeRevoke: 'Аннулировать',
    intakeRevoked: 'аннулирован',
    intakeRevokeConfirm: (address: string) => `Аннулировать ${address}? С этого момента письма на этот адрес приниматься не будут. Можно сгенерировать новый.`,
    intakeNone: 'Адресов пока нет.',
    intakeCopied: 'Скопировано.',
    sendersTitle: 'Утверждённые отправители',
    sendersLead: 'Люди с доступом к фирме утверждены автоматически. Здесь добавьте другие адреса, напр. ящик бюро или сканера.',
    senderEmail: 'Адрес e-mail',
    senderAdd: 'Добавить',
    senderRevoke: 'Удалить',
    sendersNone: 'Только люди с доступом к фирме.',
  },
  annual: {
    cit: {
      title: 'Налог на прибыль за год (CIT-8)',
      hint: 'Считаем из той же базы, что и налоговое примечание в отчётности: результат из книг, минус '
        + 'освобождённые доходы, плюс расходы, не являющиеся налоговыми. Временные разницы и убытки прошлых '
        + 'лет подаёт бухгалтер — без них сумма предварительная.',
      base: 'База',
      rate: 'Ставка',
      taxForYear: 'Налог за год',
      fromAdvances: 'Покрыто авансами',
      difference: 'Осталось доплатить',
      priorLoss: 'Убыток прошлых лет к вычету',
      recalculate: 'Пересчитать',
      notApplicable: 'Эта компания не платит CIT или не ведёт полные книги.',
    },
    activity: {
      title: 'Отчёт правления',
      lead: 'Документ правления, а не бухгалтера: его подписывает руководитель предприятия, а при '
        + 'коллегиальном правлении — все его члены. Числа считаем из той же проекции, что и финансовую '
        + 'отчётность — мы их не переписываем.',
      indicators: 'Показатели',
      indicator: 'Показатель',
      value: 'Значение',
      basis: 'Из чего',
      suggestions: 'Предложения из книг',
      suggestionsHint: 'Ни один абзац не попадает в документ, пока вы его не примете. Предложение дописывается '
        + 'к тексту раздела, где его можно исправить или удалить.',
      add: 'Дописать в раздел',
      sections: 'Разделы документа',
      required: 'обязательный',
      optional: 'необязательный',
      fromData: 'содержание из данных',
      proposal: 'предложение — не принято',
      save: 'Сохранить тексты',
      saved: 'Тексты сохранены.',
      missing: 'Без этих разделов документ не соберём:',
      ready: 'Документ готов',
      pdf: 'Собрать PDF',
      docx: 'Собрать DOCX',
      download: 'Скачать документ',
      signers: 'Подписывают',
    },
    blank: {
      title: 'Пустой образец отчётности',
      hint: 'Все позиции выбранного приложения без сумм — для разговора с клиентом или чтобы собрать данные '
        + 'вне приложения. Ничего не создаёт и ничего не меняет.',
      pdf: 'Скачать PDF',
      xlsx: 'Скачать таблицу',
    },
    modes: {
      title: 'Откуда суммы',
      BOOKS: 'Из книг в Firmeto',
      DORMANT: 'Компания без деятельности',
      MANUAL: 'Книги вне Firmeto',
      booksHint: 'Суммы считает проекция из сальдо — так же, как на экране баланса.',
      dormantHint: 'Год без оборотов: несколько сумм, вписанных вручную (капитал, деньги, обязательства).',
      manualHint: 'Книги за этот год ведутся в другом месте — суммы позиций вписываете вручную.',
      template: 'Образец отчётности',
      templates: { ZAL_4: 'Микропредприятие (приложение 4)', ZAL_5: 'Малое предприятие (приложение 5)', ZAL_1: 'Иное предприятие (приложение 1)' },
      variant: 'Вариант отчёта о прибылях и убытках',
      variants: { BY_KIND: 'по элементам', BY_FUNCTION: 'по функциям' },
    },
    amounts: {
      title: 'Суммы отчётности',
      lead: 'Суммы вписывают только в составные позиции: итоги и позиции с формулами считает приложение. '
        + 'Результат из отчёта о прибылях и убытках попадает в пассив сам — поэтому нельзя подать отчётность, '
        + 'в которой баланс говорит одно, а отчёт о прибылях другое.',
      position: 'Позиция',
      amount: 'Сумма',
      previous: 'Предыдущий год',
      save: 'Сохранить суммы',
      saved: 'Суммы сохранены.',
      balanced: 'Баланс сходится',
      unbalanced: 'Баланс не сходится',
      difference: 'Разница',
      totals: 'Активы · пассивы · результат',
      onlyManual: 'Этот экран для отчётности, составленной вне книг. Эта считается из книг.',
    },
    office: {
      title: 'Финансовый год клиентов',
      year: 'Год',
      client: 'Клиент',
      status: 'Отчётность',
      prepared: 'Составлено',
      approved: 'Утверждено',
      filed: 'Подано',
      confirmed: 'Подтверждено',
      mention: 'Упоминание в реестре',
      noReport: 'нет',
      none: 'Ни у одного клиента нет финансового года, заканчивающегося в этом году.',
    },
    open: 'Годовая отчётность',
    title: 'Годовая финансовая отчётность в реестр',
    lead: 'Мастер ведёт действиями, а не полями. Суммы баланса и отчёта о прибылях и убытках берутся из книг — '
      + 'здесь нет поля, в которое их можно было бы вписать.',
    tabs: {
      year: 'Год и версия',
      form: 'Введение и примечания',
      file: 'Файл отчётности',
      signatures: 'Подписи',
      resolutions: 'Решения',
      krs: 'Подача в реестр',
      registers: 'Правление и участники',
    },
    statuses: {
      draft: 'Черновик',
      assembled: 'Файл собран',
      signing: 'Собираем подписи',
      signed: 'Подписано',
      approved: 'Утверждено',
      filed: 'Подано в реестр',
      filed_unapproved: 'Подано до утверждения',
      confirmed: 'Подтверждено',
      abandoned: 'Оставлено',
    },
    year: {
      pick: 'Финансовый год',
      noYears: 'У этой компании ещё нет финансового года.',
      noYearsHint: 'Финансовый год создают на экране „Периоды”. Без него отчётность не из чего составить.',
      start: 'Начать отчётность',
      noReport: 'За этот год отчётности ещё нет',
      noReportHint: 'Отчётность составляется из книг этого года. Закрывать год не обязательно, но отчётность '
        + 'из открытого года несёт пометку „рабочая версия”.',
      versions: 'Версии',
      version: (numer: number) => `версия ${numer}`,
      versionReason: 'Причина',
      draftMark: 'рабочая версия',
      draftMarkHint: 'Год открыт или файл собрал тот, кто не ведёт книги — документ говорит об этом в тексте, '
        + 'иначе он попал бы к участникам как готовая отчётность.',
      stale: 'Книги изменились после сборки файла',
      staleHint: 'Мы из-за этого не меняем состояние и не удаляем файл. Соберите файл заново, пока его никто '
        + 'не подписал.',
      calendar: 'Годовой календарь',
      calendarHint: 'У каждого срока две даты: установленная законом и последний рабочий день перед ней. Неизвестно, '
        + 'переносится ли такой срок на нерабочий день, а в субботу документ подать некому.',
      statutory: 'Срок по закону',
      safe: 'Последний рабочий день',
      state: 'Состояние',
      done: 'сделано',
      overdue: 'просрочено',
      daysLeft: (dni: number) => `через ${dni} дней`,
      obligations: {
        statement_prepared: 'Составление отчётности',
        cit8: 'Декларация CIT-8',
        approval: 'Утверждение собранием',
        krs_filing: 'Подача в реестр',
        krs_confirmation: 'Упоминание или подтверждение',
      },
    },
    form: {
      title: 'Введение и примечания',
      lead: 'Поля взяты из официального образца отчётности: их коды — это названия элементов из схемы Министерства '
        + 'финансов, а не придуманные нами.',
      save: 'Сохранить ответы',
      saved: 'Ответы сохранены.',
      missing: 'Без этих полей файл не пройдёт схему:',
      complete: 'Формуляр заполнен полностью.',
      sections: {
        INTRODUCTION: 'Введение',
        NOTES: 'Примечания',
        SEAT: 'Место нахождения',
        TAX: 'Налоговая сверка',
      },
    },
    file: {
      title: 'Файл отчётности',
      lead: 'Файл собирается из книг и формуляра, а затем лежит в архиве компании вместе со своей контрольной суммой.',
      generate: 'Собрать файл из книг',
      regenerate: 'Собрать файл заново',
      taxReconciliation: 'Добавить сверку результата с налоговой базой',
      download: 'Скачать XML',
      pdf: 'Собрать распечатку (PDF)',
      pdfDownload: 'Скачать PDF',
      pdfHint: 'Распечатка для чтения: баланс, отчёт о прибылях и убытках и тексты из формуляра. В реестр идёт файл XML — распечатка остаётся в компании и не является документом, который подписывают.',
      files: 'Файлы комплекта',
      role: 'Роль',
      sha256: 'Контрольная сумма',
      generatedAt: 'Собран',
      roles: {
        statement_xml: 'Отчётность (XML)',
        statement_xml_signed: 'Отчётность с подписями',
        statement_pdf: 'Отчётность (PDF)',
        activity_report: 'Отчёт правления',
        resolution_approval: 'Решение об утверждении',
        resolution_profit: 'Решение о распределении прибыли',
        resolution_discharge: 'Решение об освобождении от ответственности',
        minutes: 'Протокол собрания',
        statement_52_2b: 'Заявление по ст. 52 п. 2b',
        auditor_report: 'Отчёт аудитора',
        rdf_confirmation: 'Подтверждение из репозитория',
      },
      newVersion: 'Новая версия',
      reason: 'Причина новой версии',
      reasonHint: 'Причина остаётся при версии навсегда — через год никто не помнит, почему отчётность имеет две.',
      noAmounts: 'Суммы не вписывают ни здесь, ни где-либо ещё в мастере: изменение идёт через книги, а файл '
        + 'собирается заново.',
    },
    signatures: {
      title: 'Подписи отчётности',
      lead: 'Отчётность подписывает тот, кто ведёт книги, и все члены правления '
        + '(ст. 52 п. 2 Закона о бухгалтерском учёте).',
      deadline: 'Срок составления',
      person: 'Человек',
      function: 'Функция',
      state: 'Состояние',
      method: 'Способ',
      when: 'Когда',
      states: {
        missing: 'не подписал',
        signed: 'подписал',
        statement: 'подал заявление',
        refusal: 'отказался от заявления',
      },
      methods: {
        qualified: 'квалифицированная подпись',
        trusted_profile: 'доверенный профиль',
        personal: 'личная подпись',
        statement: 'заявление по ст. 52 п. 2b',
        refusal: 'отказ от заявления',
      },
      capacities: { board_member: 'член правления', bookkeeper: 'тот, кто ведёт книги' },
      downloadToSign: 'Скачать файл для подписи',
      downloadHint: 'Вы получаете файл с подписями, собранными до сих пор: каждая подпись добавляется к предыдущим, '
        + 'а подписание чистого файла потеряло бы уже собранные.',
      upload: 'Загрузить подписанный файл',
      capacity: 'В каком качестве',
      officer: 'Человек из картотеки правления',
      personName: 'Имя и фамилия',
      note: 'Обоснование или примечание',
      send: 'Загрузить',
      complete: 'Подписи собраны полностью',
      incomplete: 'Подписи ещё не полные',
      extra: 'Подписи людей вне правления',
      noStatement: 'Сначала соберите файл отчётности — без него нечего подписывать.',
      declaration: {
        title: 'Заявление или отказ по ст. 52 п. 2b',
        hint: 'Когда отчётность подписывает один член правления, остальные подают заявления о том, что она '
          + 'соответствует требованиям закона, или отказы с обоснованием. Мы создаём текст; подпись ставят вне '
          + 'приложения и загружают обратно.',
        who: 'Кто подаёт заявление',
        kind: 'Вид',
        statement: 'Заявление',
        refusal: 'Отказ',
        justification: 'Обоснование (обязательно при отказе)',
        generate: 'Создать текст (PDF)',
        generated: (plik: string) => `${plik} лежит в архиве компании — подпишите его и загрузите обратно.`,
      },
    },
    resolutions: {
      title: 'Решения собрания',
      lead: 'Какие решения нужны, следует из результата года и из капиталов, поэтому суммы приходят рассчитанными — '
        + 'вписывать нечего.',
      reason: 'Почему',
      create: 'Создать проект',
      adopt: 'Записать как принятое',
      adoptedOn: 'Дата принятия',
      number: 'Номер',
      pdf: 'PDF',
      draft: 'проект',
      adopted: 'принято',
      gaps: 'Чего не хватает в данных:',
      minutes: {
        title: 'Протокол собрания',
        date: 'Дата собрания',
        place: 'Место',
        chairman: 'Председатель',
        secretary: 'Секретарь',
        unanimous: 'Решения приняты единогласно',
        generate: 'Собрать протокол (PDF)',
        download: 'Скачать протокол',
        needsResolutions: 'Протокол описывает ход собрания — сначала примите хотя бы одно решение.',
      },
    },
    krs: {
      title: 'Подача в Репозиторий финансовых документов',
      lead: 'Машинного интерфейса для подачи нет, поэтому приложение делает всё, кроме последнего щелчка: собирает '
        + 'пакет, говорит, чего не хватает, и ведёт шаг за шагом через Портал судебных реестров.',
      checklist: 'Контрольный список комплекта',
      required: 'обязательно',
      optional: 'необязательно',
      present: 'есть',
      missing: 'нет',
      ready: 'Комплект готов к подаче',
      notReady: 'Комплект ещё не готов',
      people: 'Кто может подписать подачу',
      pesel: 'PESEL раскрыт в реестре',
      instructions: 'Шаг за шагом в Портале судебных реестров',
      deadlines: 'Сроки',
      zip: 'Скачать пакет (ZIP)',
      filing: {
        title: 'Записать подачу',
        submittedOn: 'Дата подачи',
        number: 'Номер подачи',
        record: 'Записать подачу',
        unapproved: 'подано до утверждения — после утверждения нужно подать ещё раз '
          + '(ст. 69 п. 2 Закона о бухгалтерском учёте)',
      },
      confirmation: {
        title: 'Подтверждение из репозитория',
        confirmedOn: 'Дата подтверждения',
        upload: 'Загрузить подтверждение',
      },
      register: {
        title: 'Упоминания в реестре',
        lead: 'Упоминание в реестре — доказательство подачи, независимое от нас и от клиента. Молчание реестра ничего '
          + 'не отменяет.',
        number: 'Номер в реестре',
        noNumber: 'У компании не указан номер в реестре — добавьте его в настройках компании.',
        checkedAt: 'Проверено',
        never: 'мы ещё не спрашивали',
        check: 'Проверить в реестре',
        mentions: 'Упоминания',
        period: 'Период',
        filedOn: 'Подано',
        approval: 'Решение',
        activity: 'Отчёт правления',
        audit: 'Аудит',
        missingYears: 'Годы без упоминания об отчётности:',
        none: 'В реестре ещё нет ни одного упоминания об отчётности этой компании.',
      },
    },
    registers: {
      officers: {
        title: 'Правление',
        lead: 'Картотека хранит периоды, а не состояние на сегодня: решения за определённый год касаются правления '
          + 'того года.',
        firstName: 'Имя',
        lastName: 'Фамилия',
        role: 'Функция',
        from: 'С',
        to: 'По',
        pesel: 'PESEL в реестре',
        peselHint: 'Без человека, чей номер PESEL раскрыт в реестре, бесплатную подачу в репозиторий не подпишет '
          + 'никто из правления.',
        add: 'Добавить человека',
        end: 'Завершить функцию',
        roles: {
          PRESIDENT: 'председатель правления',
          BOARD_MEMBER: 'член правления',
          PROXY: 'коммерческий представитель',
          LIQUIDATOR: 'ликвидатор',
        },
        none: 'Картотека правления пуста — без неё мы не знаем, кто должен подписать отчётность.',
      },
      shareholders: {
        title: 'Участники',
        lead: 'Из картотеки участников составляется список присутствующих в протоколе и распределение прибыли.',
        name: 'Участник',
        shares: 'Доли',
        votes: 'Голоса',
        nominal: 'Номинальная стоимость',
        capital: 'Капитал',
        add: 'Добавить участника',
        end: 'Завершить',
        none: 'Картотека участников пуста.',
      },
    },
  },
  periods: {
    closedMonths: 'Месяцев закрыто',
    settled: 'рассчитывается',
    stillRunning: 'ещё идёт',
    noneClosed: 'Ни один месяц не закрыт',
    noneClosedHint:
      'Пока месяц открыт, запись задним числом изменит суммы в декларации, которая уже подана. Закрывайте месяц после отправки JPK.',
    open: 'Периоды',
    title: 'Закрытие периодов',
    lead: 'В закрытый месяц не попадёт ни одна новая запись. Изменение вносят корректировкой с текущей '
      + 'датой — благодаря этому книга совпадает с декларацией, которая уже подана.',
    years: 'Отчётный год',
    noYears: 'Отчётного года ещё нет.',
    yearMissing: (rok: number) => `Отчётный год ${rok} ещё не открыт`,
    yearMissingHint: 'Отчётный год создаётся один раз, следующий нужно открыть. Без него план счетов '
      + 'и журнал показывают прошлогодний год, которого сегодняшние налоговые настройки не достают.',
    openYear: (rok: number) => `Открыть ${rok}`,
    addYear: 'Добавить отчётный год',
    saveYear: 'Сохранить год',
    yearHint: 'Отчётный год индивидуальной деятельности всегда календарный. У компании может быть другой, '
      + 'но не длиннее восемнадцати месяцев.',
    closeYear: 'Закрыть год',
    readiness: {
      blocked: 'Год нельзя закрыть, пока не сделано это:',
      warnings: 'В контрольном списке есть замечания:',
      confirm: 'Закрыть год несмотря на эти замечания?',
      result: (numer: number, kwota: string) => `Результат года перенесён записью № ${numer}: ${kwota} zł`,
      noResult: 'Год закрыт. Записей результата не было, переносить нечего.',
      checks: {
        months_open: 'Открытые месяцы',
        unposted_invoices: 'Непроведённые счета',
        unposted_costs: 'Непроведённые документы расходов',
        trial_balance: 'Оборотно-сальдовая ведомость не сходится',
        accounts_without_position: 'Счета с сальдо без позиции отчёта',
        opening_balance: 'Нет входящего баланса',
        draft_invoices: 'Счета остались черновиками',
        inbox_documents: 'Документы ждут в ящике',
        settlements_without_counterparty: 'Строки расчётов без контрагента',
        depreciation: 'Основные средства без проведённой амортизации',
        closing_inventory: 'Нет инвентаризации на конец года',
        open_provisions: 'Резервы с сальдо после окончания периода',
        result_transfer: 'Результат к переносу',
      },
    },
    months: 'Месяцы',
    month: 'Месяц',
    state: 'Состояние',
    from: 'С',
    to: 'По',
    closedAt: 'Закрыт',
    open_: 'Открытый',
    closed: 'Закрытый',
    close: 'Закрыть',
    reopen: 'Открыть',
    reopenTitle: (period: string) => `Открытие периода ${period}`,
    reason: 'Причина',
    reasonHint: 'Причина попадает в журнал и остаётся там навсегда.',
    cancel: 'Отменить',
    confirmClose: (period: string) => `Закрыть ${period}? После закрытия вы не добавите в этот месяц документ.`,
    confirmWithDrafts: (period: string, drafts: number) =>
      `В месяце ${period} остались черновики счетов: ${drafts}. После закрытия их не выставить. Закрыть всё равно?`,
    confirmCloseYear: (from: string, to: string) =>
      `Закрыть год ${from} — ${to}? Это закроет все месяцы этого года.`,
  },
  costs: {
    open: 'Расходы',
    bookAll: 'Провести все',
    list: 'Список',
    allMonths: 'Все',
    column: 'кол.',
    draftsWaiting: 'Документы ждут проведения',
    draftsWaitingHint:
      'Пока они черновики, они не попадают ни в KPiR, ни в НДС — поэтому расходы месяца на панели могут быть нулевыми.',
    draftAfterSave: 'Сохранённый документ попадает в черновики. В KPiR и НДС он входит только после проведения.',
    draftAfterSaveHint: 'После сохранения документ имеет статус «Черновик» и пока ни на что не влияет.',
    document: 'Документ',
    qualification: 'Квалификация',
    rates: 'Разбивка по ставкам НДС',
    addRate: 'Добавить ставку',
    summary: 'Итог',
    goesTo: 'Попадёт в',
    kpir: 'KPiR',
    vatRegister: 'Реестр НДС',
    purchases: 'закупки',
    vatInput: 'Входящий НДС',
    saveAndNext: 'Сохранить и добавить следующий',
    costDateHint: 'Определяет месяц в KPiR.',
    supplierHint: 'Нового добавите в картотеке контрагентов.',
    fixedAssetHint: 'Внести в реестр основных средств',
    intraCommunity: 'Внутрисоюзное приобретение',
    intraCommunityHint: 'Покупка товаров у контрагента из ЕС — войдёт в информацию VAT-UE (импорт услуг нет)',
    scanFutureTitle: 'Чтение со скана',
    scanFutureText:
      'Со временем скан из вкладки «Документы» заполнит номер, поставщика, даты и суммы, а поля с низкой уверенностью распознавания будут подсвечены для подтверждения. Сегодня скан — только вложение, все поля вводятся вручную.',
    title: 'Документы расходов',
    new: 'Добавить расход',
    none: 'Документов расходов нет.',
    number: 'Номер документа',
    supplier: 'Поставщик',
    chooseSupplier: 'Выберите поставщика',
    issueDate: 'Дата документа',
    costDate: 'Дата несения',
    category: 'Вид расхода',
    deduction: 'Вычет НДС',
    fixedAsset: 'Основное средство',
    net: 'Нетто',
    vat: 'НДС',
    deductible: 'НДС к вычету',
    costAmount: 'В расходы',
    correctionTo: 'Корректировка к документу',
    correctionHint: 'Введите суммы разницы так, как они указаны в документе поставщика — обычно со знаком минус.',
    causes: {
      MISTAKE: 'Ошибка в первоначальном документе',
      EVENT: 'Позднее событие (скидка, возврат)',
    },
    status: 'Состояние',
    save: 'Сохранить расход',
    book: 'Провести',
    delete: 'Удалить',
    hint: 'В расходы входит нетто плюс НДС, который нельзя вычесть — при половинном вычете и у освобождённой компании суммы разные.',
    categories: {
      GOODS: 'Товары и материалы (кол. 10)',
      PURCHASE_COSTS: 'Сопутствующие расходы закупки (кол. 11)',
      SALARIES: 'Зарплаты (кол. 12)',
      OTHER: 'Прочие расходы (кол. 13)',
    },
    deductions: { FULL: 'Полный (100%)', HALF: 'Половина (транспорт)', NONE: 'Без вычета' },
    statuses: { DRAFT: 'Черновик', BOOKED: 'Проведён' },
  },
  dashboard: {
    fixSettings: 'Заполнить настройки',
    noDeadlines: 'В этом месяце нет сроков.',
    open: 'Панель',
    title: 'Итоги месяца',
    month: 'Месяц',
    revenue: 'Доход',
    costs: 'Расходы',
    income: 'Прибыль',
    vatToPay: 'НДС к уплате',
    vatToCarry: 'НДС к переносу',
    obligations: 'Сроки',
    toPay: 'К уплате в этом месяце',
    goToSettlements: 'Перейти к расчётам',
    goToPeople: 'Перейти к лицам',
    NO_BOOKKEEPER: 'Полная бухгалтерия, но ни у кого нет роли Бухгалтер — некому вести книги.',
    period: 'Период',
    dueDate: 'Срок',
    VAT: 'НДС и JPK',
    PIT_ADVANCE: 'Авансовый платёж по подоходному налогу',
    CIT_ADVANCE: 'Авансовый платёж по CIT',
    HEALTH_ANNUAL: 'Годовой расчёт взноса на здравоохранение',
    citBase: 'База CIT нарастающим итогом',
    citNotes: {
      ESTIMATE: 'Считаем из книг в Firmeto — это оценка, а не декларация.',
      LOSS_NOT_DEDUCTED: 'Убытки прошлых лет не вычитаем; учтите их в годовом расчёте.',
      TRANSITIONAL_NOT_INCLUDED: 'Временных разниц расчёт не знает — они входят в годовую декларацию.',
      SMALL_RATE_FROM_SETTINGS: 'Ставка 9% из настроек компании; лимит дохода для неё не проверяем.',
      LOSS_PERIOD: 'Нарастающим итогом вышел убыток — аванса нет.',
      CAPITAL_GAINS_NOT_SEPARATED: 'Доход от капитала не разделяем — из учёта не видно, какие поступления им являются.',
      QUARTERLY: 'Сумма относится ко всему кварталу и уплачивается после его окончания.',
      QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Квартальные авансы доступны малому налогоплательщику, а фирма так не отмечена в настройках.',
    } as Record<string, string>,
    ZUS: 'Взносы ZUS',
    movedFrom: 'законный срок {date} выпадает на выходной',
    today: 'сегодня',
    tomorrow: 'завтра',
    overdue: 'срок прошёл',
    daysLeft: 'осталось дней:',
    NO_SETTINGS: 'Нет налоговых настроек — без них не рассчитаем налог и не покажем сроки.',
    NO_DOCUMENTS: 'В этом месяце пока нет документов.',
    OVERDUE_RECEIVABLES: 'Есть счета после срока оплаты — это деньги, которых вы ждёте дольше, чем следует.',
    OVERDUE_PAYABLES: 'Есть обязательства после срока оплаты — это деньги, которые вы должны дольше, чем обещали.',
    aging: 'Расчёты — по срокам',
    agingLead: (asOf: string) => `Состояние на ${asOf} из расчётов в журнале: кто сколько должен и с какого времени.`,
    agingReceivables: 'Дебиторская задолженность',
    agingPayables: 'Кредиторская задолженность',
    agingOpen: 'открытых',
    agingOverdue: 'просроченных',
    goToJournal: 'Перейти к расчётам в журнале',
    receivables: 'Неоплаченные счета',
    receivablesOverdue: 'из них просроченные',
    exemptionTitle: 'Лимит освобождения от НДС',
    exemptionUsed: 'Использовано с начала года',
    exemptionLimit: 'Лимит',
    exemptionRemaining: 'Осталось',
    VAT_EXEMPTION_NEAR_LIMIT: 'Вы приближаетесь к лимиту освобождения от НДС — после его превышения нужно зарегистрироваться плательщиком НДС.',
    VAT_EXEMPTION_EXCEEDED: 'Лимит освобождения от НДС превышен. Освобождение заканчивается на сделке, которая его превысила.',
    SUSPENDED: 'Деятельность в этом месяце была полностью приостановлена, поэтому нет ни взносов, ни аванса по налогу. Файл JPK_V7 всё же может быть нужен — при приобретении товаров из ЕС, импорте услуг и корректировках (ст. 99 п. 7a и 7b закона о НДС).',
    goToSuspensions: 'Посмотреть приостановления',
    zusTitle: 'Взносы ZUS за месяц',
    zusSocial: 'Социальные',
    zusSickness: 'На случай болезни',
    zusLaborFund: 'Фонд труда',
    zusHealth: 'Медицинский',
    zusTotal: 'Итого к уплате',
    draDownload: 'Декларация ZUS DRA',
    draHint: 'Файл для загрузки в eZUS или Płatnik — мы не отправляем его за вас.',
    advanceTitle: 'Авансовый платёж по подоходному налогу',
    advanceBase: 'База нарастающим итогом',
    advanceAmount: 'К уплате за этот месяц',
    partners: 'Участники',
    partner: 'Участник',
    share: 'Доля в прибыли',
    taxationForm: 'Налогообложение',
    ESTIMATE: 'Суммы считаем из документов в Firmeto, предполагая, что предыдущие взносы и авансы уплачены в срок.',
    RYCZALT_NOT_SUPPORTED: 'Налог на едином тарифе пока не считаем — нужен учёт доходов со ставками.',
    MALY_ZUS_PLUS_APPROXIMATED: 'Mały ZUS Plus считается из дохода прошлого года, а этого года нет в Firmeto — показываем полную базу, потому что более низкая ни из чего бы не следовала.',
    UNKNOWN_YEAR: 'Нет ставок и порогов на этот год, поэтому суммы не считаем.',
    COMPANY_TAX: 'Налог общества и взносы участников считаются вне этой сводки.',
    PARTNERS_SPLIT: 'Товарищество не платит подоходный налог — его платят участники, каждый со своей доли.',
    PARTNERS_MISSING: 'Товарищество без картотеки участников: считаем как для одного человека, а это не так. Добавьте участников в настройках компании.',
    PARTNERS_SHARE_MISMATCH: 'Доли участников не складываются в целое — чьей-то доли не хватает или она слишком велика.',
    QUARTERLY_ADVANCE: 'Аванс квартальный: сумма относится ко всему кварталу и уплачивается до 20-го числа месяца после его окончания. Взносы ZUS остаются месячными.',
    QUARTERLY_WITHOUT_SMALL_TAXPAYER: 'Квартальные авансы доступны малому налогоплательщику, а фирма так не отмечена в настройках.',
    BAD_DEBT_IN_INCOME: 'Доход учитывает сохранённые корректировки безнадёжных долгов: неоплаченный расход возвращается в доход, неоплаченный доход из него выходит (ст. 26i закона о PIT).',
    BAD_DEBT_NOT_IN_RYCZALT: 'При паушальном налоге льгота на безнадёжные долги меняет доход по ставке (ст. 11 закона о паушальном налоге), а мы не знаем, к какой ставке отнести неоплаченный документ — поэтому сохранённые корректировки здесь не считаем.',
    MALY_ZUS_PLUS_FROM_PREVIOUS_YEAR: 'База социальных взносов посчитана из дохода прошлого года: средний месячный доход, поделённый пополам (Mały ZUS Plus, ст. 18c закона о системе социального страхования).',
    MALY_ZUS_PLUS_AT_FLOOR: 'Из дохода вышла бы база ниже нижней границы, поэтому берём 30% минимальной зарплаты — столько же, как при преференциальных взносах.',
    MALY_ZUS_PLUS_AT_CAP: 'Из дохода вышла бы база выше полной, поэтому берём полную: при таком доходе льгота взнос не меняет.',
    MALY_ZUS_PLUS_REVENUE_OVER_LIMIT: 'Выручка прошлого года превысила лимит льготы (120 000 зл), поэтому считаем полную базу.',
    MALY_ZUS_PLUS_TOO_FEW_DAYS: 'Деятельность в прошлом году длилась меньше 60 дней, а при таком годе льгота не полагается.',
    MALY_ZUS_PLUS_DAYS_ASSUMED: 'Мы не знаем дня начала деятельности, поэтому приняли, что прошлый год был полным. Если фирма начала посреди года, база занижена — добавьте дату в данных фирмы.',
    MALY_ZUS_PLUS_FROM_REVENUE: 'При паушальном налоге учёт не знает дохода, поэтому выводим его из выручки коэффициентом из закона.',
    MALY_ZUS_PLUS_LIMIT_NOT_CHECKED: 'Не проверяем двух условий, которых из наших данных не видно: 36 месяцев пользования льготой в течение последних 60 и выполнения деятельности для прежнего работодателя. За этим нужно следить самому.',
    MALY_ZUS_PLUS_SUSPENSION_DAYS: 'Дни приостановления деятельности не являются днями её ведения, поэтому мы вычли их при делении дохода — и из-за этого база вышла выше, чем дал бы сам годовой доход.',
    SUSPENDED_MONTH: 'Деятельность в этом месяце была полностью приостановлена, поэтому страхования нет: ни социального, ни медицинского.',
    SUSPENDED_PART_OF_MONTH: 'Часть месяца была приостановлена, поэтому базу социальных взносов мы уменьшили пропорционально дням страхования (ст. 18 п. 9 закона о системе социального страхования). Взнос на здравоохранение остаётся целым — он месячный и неделимый.',
  },
  ryczalt: {
    lead: 'Реестр доходов ведут при упрощённой ставке от зарегистрированного дохода.',
    otherForm: 'Эта компания облагается налогом иначе',
    goToLedger: 'Перейти к книге доходов и расходов',
    changeForm: 'Изменить форму налогообложения',
    byRate: 'По ставкам',
    open: 'Учёт доходов',
    title: 'Учёт доходов (единая ставка)',
    month: 'Месяц',
    date: 'Дата',
    document: 'Документ',
    counterparty: 'Контрагент',
    rate: 'Ставка',
    amount: 'Доход',
    total: 'Итого',
    none: 'В этом месяце доходов нет.',
  },
  settlements: {
    lead: 'Дебиторская и кредиторская задолженность по документам. Платежи вводят вручную или из выписки.',
    receivablesTotal: 'Дебиторская задолженность',
    payablesTotal: 'Кредиторская задолженность',
    longestDelay: 'Самая долгая задержка (дней)',
    noDelay: 'ничего не просрочено',
    delay: 'Задержка',
    filter: 'Фильтр: документ, контрагент',
    loadStatement: 'Загрузить выписку',
    matchFutureTitle: 'Сопоставление платежей со счетами',
    matchFutureText:
      'Мы подсказываем сопоставление, но подтверждает его человек. Со временем файл из банка сам укажет, какой платёж покрывает какой счёт, включая частичные суммы.',
    open: 'Расчёты',
    title: 'Расчёты',
    receivables: 'Дебиторская задолженность',
    payables: 'Кредиторская задолженность',
    onlyOpen: 'Только незакрытые',
    bank: 'Выписка',
    bankHint: 'Загрузите выписку MT940. Ничего не проводим сами — предлагаем сопоставление, а вы подтверждаете.',
    chooseFile: 'Файл выписки',
    importedCount: 'Новых операций',
    skippedCount: 'Уже известных',
    noTransactions: 'Нет операций, ожидающих сопоставления.',
    valueDate: 'Дата',
    details: 'Назначение платежа',
    proposal: 'Подсказка',
    noProposal: 'без подсказки',
    reasons: {
      NUMBER_AND_AMOUNT: '(номер и сумма)',
      NUMBER_ONLY: '(номер в назначении)',
      AMOUNT_ONLY: '(сумма совпадает)',
      COUNTERPARTY_AND_AMOUNT: '(контрагент и сумма)',
    },
    document: 'Документ',
    counterparty: 'Контрагент',
    dueDate: 'Срок',
    amountDue: 'К оплате',
    paid: 'Оплачено',
    remaining: 'Осталось',
    status: 'Состояние',
    overdueDays: 'дней просрочки:',
    addPayment: 'Записать оплату',
    savePayment: 'Сохранить',
    exchangeRate: 'Курс на день оплаты',
    exchangeRateHint: 'Документ в иностранной валюте: укажите средний курс NBP последнего рабочего дня перед '
      + 'оплатой. Из разницы к курсу документа возникает финансовый доход или расход.',
    paidOn: 'Дата оплаты',
    amount: 'Сумма',
    none: 'Нечего закрывать.',
    statuses: {
      UNPAID: 'не оплачено',
      PARTIAL: 'частично',
      PAID: 'закрыто',
      OVERPAID: 'переплата',
    },
  },
  payments: {
    title: 'Способ оплаты',
    officePays: 'За этот месяц платит бухгалтерское бюро — способ оплаты и списания на стороне бюро.',
    officeTitle: 'Расчёт с Firmeto',
    officeLead: 'Бюро платит одной суммой за фирмы в пакете — не больше потолка — и плату по индивидуальному договору, если он есть.',
    officeSubtotal: 'Фирмы в пакете',
    officeCeiling: 'Потолок пакета',
    officeAgreement: 'Индивидуальный договор',
    officeTotal: 'К оплате',
    officeCompanies: (count: number) => `фирм: ${count}`,
    current: 'Сейчас вы платите',
    none: 'Способ оплаты ещё не добавлен. Без него мы не спишем плату, а через две недели задолженности учётная запись станет доступна только для чтения.',
    method: 'Форма оплаты',
    methods: { card: 'Карта', blik: 'BLIK' } as Record<string, string>,
    provider: 'Оператор',
    providerName: (provider: string) => ({ payu: 'PayU', przelewy24: 'Przelewy24', stripe: 'Stripe', fake: 'Тестовый оператор' })[provider] ?? provider,
    recommended: 'рекомендуемый',
    add: 'Добавить',
    change: 'Изменить',
    revoke: 'Отозвать согласие',
    formAtProvider: 'Данные карты вы вводите у платёжного оператора — Firmeto их не видит.',
    onlyOwner: 'Способ оплаты устанавливает владелец или администратор.',
    expires: (month: string) => `действительна до ${month}`,
    waitingTitle: 'Ждём подтверждения от оператора',
    waitingText: 'Согласие обычно становится активным через несколько секунд. Обновите страницу, если его всё ещё не видно.',
    charges: 'Списанные платежи',
    noCharges: 'Мы ещё ничего не списали.',
    period: 'За месяц',
    amount: 'Сумма',
    status: 'Состояние',
    statuses: { pending: 'в обработке', succeeded: 'списано', failed: 'неудачно', refunded: 'возвращено' } as Record<string, string>,
    arrearTitle: (month: string) => `Плата за ${month} ожидает оплаты`,
    arrearText: (sum: string, reason: string) => `${sum} — ${reason}.`,
    readOnlyFrom: (day: string) => `Мы повторим попытку через несколько дней. С ${day} учётная запись будет только для чтения до оплаты.`,
    readOnlyTitle: 'Учётная запись только для чтения',
    readOnlyText: 'Данные можно просматривать и скачивать, а сохранение вернётся после оплаты задолженности. Добавьте или измените способ оплаты ниже.',
    reasons: { mandate_missing: 'нет способа оплаты', card_declined: 'оператор отклонил списание', insufficient_funds: 'недостаточно средств', expired_card: 'срок действия карты истёк', authentication_required: 'банк требует подтверждения', declined: 'оператор отклонил списание' } as Record<string, string>,
  },
  billing: {
    multiCompany: (count: number) => `Скидка за ${count} видов деятельности`,
    lead: 'Не зарабатываете — не платите. Плата никогда не превышает 20% дохода за этот месяц.',
    revenueSub: 'база для расчёта платы',
    howMuch: 'Откуда взялась эта сумма',
    capOf: (revenue: string) => `20% дохода ${revenue}`,
    lower: 'Меньшая из двух сумм',
    plan: 'План',
    payer: 'Платит',
    channel: 'Покупка',
    noRevenueTitle: 'Месяц без дохода стоит 0 zł',
    noRevenueText: 'Мы не доначисляем плату позже и не переносим её на следующий месяц.',
    open: 'Оплаты',
    title: 'Оплаты за Firmeto',
    month: 'Месяц',
    revenue: 'Доход',
    planPrice: 'Цена плана',
    cap: 'Лимит 20%',
    toPay: 'К оплате',
    history: 'История начислений',
    charged: 'Начислено',
    note: 'Примечания',
    surcharge: 'Доплата',
    credit: 'Кредит',
    none: 'Начислений пока нет.',
    plans: { RYCZALT: 'План «единая ставка»', KPIR: 'План КПиР', FULL: 'План полного учёта' },
    payers: { COMPANY: 'платит фирма', OFFICE: 'платит бюро' },
    channels: { WEB: 'покупка на сайте', APP_STORE: 'подписка App Store', GOOGLE_PLAY: 'подписка Google Play' },
    reasons: {
      TRIAL: 'Пробный период — оплаты нет.',
      NO_REVENUE: 'В этом месяце нет дохода — оплаты нет.',
      BELOW_MINIMUM: 'Рассчитанная сумма меньше 5 злотых — не взимаем её.',
      FREE_AFTER_LEAVING_OFFICE: 'Бесплатный период после ухода из бюро.',
      STORE_SUBSCRIPTION: 'Подписка оплачивается в магазине приложений — расчёт идёт через магазин.',
    },
    statuses: {
      PENDING_CHARGE: 'к начислению',
      CHARGED: 'начислено',
      PENDING_APPROVAL: 'ожидает подтверждения',
      APPROVED: 'подтверждён',
      REJECTED: 'отклонён',
    },
  },
  accruals: {
    title: 'Расходы будущих периодов',
    lead: 'Полис на год или абонемент, оплаченный вперёд, — это расход тех месяцев, к которым он относится, а не только того, когда пришёл счёт. Расход сходит со счёта затрат на расходы будущих периодов и возвращается одной частью в месяц, поэтому каждый авансовый платёж видит часть, а не весь счёт.',
    none: 'Пока нет ни одного графика.',
    noneHint:
      'График составляют на документе, который уже проведён в журнале — счёт затрат берём из его проводки, потому что со счёта, которого документ не коснулся, нечего снимать.',
    add: 'Разложить расход на месяцы',
    cancel: 'Отменить',
    save: 'Создать график',
    document: 'Документ',
    documentHint: 'Только документы, проведённые в журнале. У одного документа один график.',
    description: 'К чему относится',
    amount: 'Сумма к разложению',
    amountHint:
      'По умолчанию столько, сколько проводка записала на счёт затрат. Меньше, когда счёт охватывает и то, что относится к этому месяцу — например, разовый сбор рядом с полисом.',
    from: 'Первый месяц',
    to: 'Последний месяц',
    period: 'Период',
    monthly: 'Часть',
    costAccount: 'Счёт затрат',
    accrualAccount: 'Счёт расходов будущих периодов',
    accountsHint:
      'Пусто означает: счёт затрат из проводки документа, а расходы будущих периодов — из позиции баланса (в образцовом плане 640). Укажите счёт, когда проводка задела несколько счетов или когда расход должен ждать дольше года.',
    month: 'Месяц',
    release: 'Провести части',
    released: 'Проведено частей',
    releasedTotal: 'Проведено',
    through: 'до',
    skipped: 'Пропущенные графики',
    plan: 'График',
    showPlan: 'График',
    hidePlan: 'Скрыть график',
    instalment: 'Часть',
    remaining: 'Остаётся',
    entry: 'Запись',
    waiting: 'ждёт',
    remove: 'Отозвать график',
    removeReason: 'Причина отзыва графика — сторно переноса расхода на расходы будущих периодов:',
  },
  provisions: {
    title: 'Пассивные межпериодные расчёты',
    lead: 'Расход относится к этому периоду, а счёта ещё нет: аудит отчёта за этот год, годовые премии, услуга оказана и не выставлена (ст. 39 п. 2 закона о бухгалтерском учёте). Начисление вписывает расход в месяц, к которому он относится, и откладывает обязательство в пассивах; расчёт снимает резерв, когда счёт придёт или когда обязательство не возникнет.',
    none: 'Резервов пока нет.',
    noneHint:
      'Резерв не возникает из документа, потому что документа ещё нет — сумму, счёт и месяцы указывает бухгалтер вместе с обоснованием оценки.',
    add: 'Создать резерв',
    addHint:
      'Здесь не указывают документ: смысл в том, что счёта ещё нет. Сумма — это оценка, поэтому рядом спрашиваем, из чего она вышла.',
    cancel: 'Отменить',
    save: 'Создать резерв',
    description: 'К чему относится',
    basis: 'Из чего вышла сумма',
    basisHint:
      'Например: «договор с аудиторской фирмой от 12.11.2026, 30 000 зл нетто». Оценка без обоснования незащитима при аудите и при проверке, а это единственное место, где обоснование остаётся.',
    amount: 'Сумма',
    from: 'Первый месяц',
    to: 'Последний месяц',
    periodHint:
      'Тот же месяц — это нормально: резерв на аудит отчёта относится к одному месяцу. Годовые премии начисляются в течение двенадцати.',
    period: 'Период',
    costAccount: 'Счёт расходов',
    costAccountHint:
      'Счёт, на который войдёт расход — тот же, на котором потом окажется счёт от контрагента. Его указывает бухгалтер, потому что нет проводки, из которой его взять.',
    provisionAccount: 'Счёт резерва',
    provisionAccountHint:
      'Пустое означает: счёт с ролью «пассивные межпериодные расчёты» (в образцовом плане 641). Метки позиции баланса здесь недостаточно, потому что резерв и доходы будущих периодов стоят в одной позиции пассивов.',
    month: 'Месяц',
    accrue: 'Начислить резервы',
    accrued: 'Начислено',
    accruedTotal: 'Начислено',
    through: 'до',
    balance: 'Сальдо резерва',
    closed: 'рассчитан',
    skipped: 'Пропущенные резервы',
    plan: 'Начисления',
    showPlan: 'Начисления',
    hidePlan: 'Скрыть начисления',
    instalment: 'Часть',
    remaining: 'Остаток',
    entry: 'Запись',
    waiting: 'ожидает',
    settle: 'Рассчитать',
    settleAccount:
      'На какой счёт снять резерв? По счёту — счёт расходов, который резерв обременял (подсказано). Когда обязательство не возникло — счёт прочих операционных доходов.',
    settlements: 'Расчёты резерва',
    settledOn: 'Дата',
    settledAmount: 'Сумма',
    settledAccount: 'Счёт',
    remove: 'Отозвать резерв',
    removeReason: 'Причина отзыва резерва — возможно только пока ничего не попало в книги:',
  },
  assets: {
    lead: 'Реестр основных средств и амортизационные отчисления, попадающие в расходы.',
    noneHint:
      'Основное средство можно добавить здесь или отметить при вводе расхода — тогда документ сразу попадёт в реестр, а не в расходы единовременно.',
    depreciating: 'Амортизируется',
    open: 'Основные средства',
    title: 'Основные средства и амортизация',
    none: 'Основных средств пока нет.',
    add: 'Добавить основное средство',
    save: 'Сохранить',
    name: 'Название',
    inventoryNumber: 'Инвентарный номер',
    kstCode: 'Код классификации',
    acquired: 'Дата приобретения',
    inService: 'Ввод в эксплуатацию',
    value: 'Первоначальная стоимость',
    method: 'Метод',
    rate: 'Годовая ставка',
    rateHint: 'Дробь: 0.20 означает 20%. Ставку берём из перечня норм амортизации.',
    month: 'Месяц',
    bookDepreciation: 'Провести амортизацию',
    booked: 'Проведено записей',
    includeDisposed: 'Показать списанные',
    showPlan: 'План',
    hidePlan: 'Скрыть план',
    plan: 'План амортизации',
    period: 'Месяц',
    writeOff: 'Отчисление',
    remaining: 'Осталось',
    bookedTotal: 'Проведено всего',
    inLedger: 'В книге',
    inJournal: 'В журнале',
    posted: 'Записей в журнале',
    skipped: 'Средства, пропущенные в журнале',
    removedFromBooks: 'Средство сошло с учёта',
    removedAccumulated: 'амортизация',
    removedNet: 'остаточная стоимость в расходах',
    removedEntry: 'запись',
    removedMissing: 'Средство выведено из реестра, но не сошло с учёта:',
    through: 'до',
    dispose: 'Списать',
    disposalReason: 'Причина списания (продажа, ликвидация):',
    disposed: 'списано',
    methods: { LINIOWA: 'линейный', JEDNORAZOWA: 'единовременный', DEGRESYWNA: 'уменьшаемого остатка' },
  },
  pitAnnual: {
    title: 'Годовой расчёт',
    priorYearLoss: 'Убыток прошлых лет',
    revenue: 'Доход за год',
    income: 'Прибыль за год',
    badDebt: 'Корректировка безнадёжных долгов',
    social: 'Уплаченные социальные взносы',
    health: 'Уплаченный взнос на здравоохранение',
    deducted: 'Вычтено из базы',
    taxBase: 'База налогообложения',
    tax: 'Налог за год',
    advances: 'Авансы, уплаченные в течение года',
    toPay: 'К доплате',
    toRefund: 'Переплата к возврату',
    forms: { PIT_36: 'PIT-36 · налоговая шкала', PIT_36L: 'PIT-36L · линейный налог', PIT_28: 'PIT-28 · паушальный' },
    notes: {
      ESTIMATE: 'Считаем из документов в Firmeto — это оценка, а не заполненная декларация.',
      ADVANCES_ASSUMED_PAID: 'Предполагаем, что авансы, посчитанные в течение года, были уплачены.',
      BUSINESS_INCOME_ONLY: 'Это налог от деятельности. Декларация охватывает и другие источники — работу по найму, аренду, договоры — которых мы не знаем.',
      NO_RELIEFS: 'Льготы и вычеты не считаем: на детей, IP Box, пожертвования, совместный расчёт с супругом и 1,5% для организации общественной пользы.',
      LOSS_FROM_USER: 'Убыток прошлых лет учитываем только тогда, когда вы его впишете — он зависит от расчётов, которых у нас нет.',
      HEALTH_NOT_DEDUCTIBLE_ON_SCALE: 'По налоговой шкале взнос на здравоохранение не вычитается с 2022 года.',
      RYCZALT_HEALTH_HALF: 'При паушальном налоге с дохода вычитается половина уплаченного взноса на здравоохранение.',
      INVENTORY_MISSING: 'Не хватает утверждённого остатка, поэтому годовая прибыль может быть неверной — товар на полке не является расходом этого года.',
      FULL_BOOKS_NOT_SUPPORTED: 'При полном учёте годовую прибыль определяют из книг; этого пока не считаем.',
      PARTNERS_SPLIT: 'Товарищество: каждый участник рассчитывается сам, со своей доли и собственной формой.',
      UNKNOWN_YEAR: 'У нас нет налоговых показателей на этот год.',
      BAD_DEBT_IN_INCOME: 'Доход учитывает сохранённые корректировки безнадёжных долгов в налоге на доход (ст. 26i закона о PIT).',
      BAD_DEBT_NOT_IN_RYCZALT: 'При паушальном налоге льгота на безнадёжные долги меняет доход по ставке (ст. 11 закона о паушальном налоге) — мы не знаем, к какой ставке отнести неоплаченный документ, поэтому её не считаем.',
    },
  },
  healthAnnual: {
    title: 'Годовой взнос на здравоохранение',
    deadline: (rok: number) => `расчёт в декларации за апрель — до 20 мая ${rok}`,
    income: 'Доход за год',
    revenue: 'Выручка за год',
    months: 'Месяцы страхования',
    basis: 'Годовая база',
    rate: 'Ставка',
    due: 'Взнос за год',
    paid: 'Взносы уплаченные в течение года',
    toPay: 'К доплате',
    toRefund: 'Переплата к возврату',
    forms: {
      SKALA: 'Налоговая шкала · 9% дохода',
      LINIOWY: 'Линейный налог · 4,9% дохода',
      RYCZALT: 'Паушальный налог · порог выручки',
      CIT: 'CIT',
    },
    notes: {
      ESTIMATE: 'Считаем из документов в Firmeto — это оценка, а не заполненная декларация DRA.',
      MONTHLY_ASSUMED_PAID: 'Предполагаем, что взносы, посчитанные в течение года, были уплачены.',
      BUSINESS_ONLY: 'Мы знаем только деятельность. Если у вас есть другое основание для медицинского страхования — работа по найму, вторая фирма, товарищество — расчёт охватывает и его.',
      MINIMUM_APPLIED: 'Вышел минимальный взнос: 9% минимальной зарплаты за каждый месяц страхования. При низком доходе платят именно столько — также при линейном налоге, где минимум считается ставкой 9%, а не 4,9%.',
      RYCZALT_BRACKET_FROM_REVENUE: 'При паушальном налоге порог определяет **фактическая выручка года**. Если в течение года вы платили по выручке прошлого года или по другому порогу, отсюда и разница.',
      SOCIAL_NOT_DEDUCTED: 'Базу не уменьшаем на уплаченные социальные взносы — у нас они расход в книге.',
      BAD_DEBT_NOT_IN_BASIS: 'Корректировки безнадёжных долгов меняют налоговый доход, но базу взноса мы не трогаем.',
      INVENTORY_MISSING: 'Нет утверждённого остатка, поэтому годовой доход может быть неверным — а с ним и взнос.',
      FULL_BOOKS_NOT_SUPPORTED: 'При полных книгах годовой доход определяют из книг; этого мы ещё не считаем.',
      FORM_CHANGED: 'Форма налогообложения изменилась в течение года. Тогда годовой расчёт делают отдельно для каждой формы, а мы считаем одну — проверьте эту сумму с бухгалтером.',
      PARTNERS_SPLIT: 'Партнёрское товарищество: каждый партнёр рассчитывает взнос сам, со своей части дохода.',
      UNKNOWN_YEAR: 'Нет показателей ZUS на этот год.',
    },
  },
  suspensions: {
    title: 'Приостановление деятельности',
    lead: 'Приостановление заявляют в CEIDG, и оно длится не менее 30 дней (ст. 22–25 закона о предпринимателях). Оно не закрывает фирму: NIP, книги и неоплаченные счёта остаются. Но меняет взносы, сроки и амортизацию, поэтому здесь храним периоды, а не переключатель.',
    none: 'Приостановление не записано.',
    noneHint: 'Впишите период, в котором деятельность была приостановлена — тогда за эти месяцы не посчитаем ни взносов, ни аванса, а амортизация остановится.',
    add: 'Вписать приостановление',
    save: 'Сохранить',
    cancel: 'Отменить',
    startsOn: 'Первый день приостановления',
    endsOn: 'Последний день приостановления',
    endsOnHint: 'Пустое означает «всё ещё приостановлена» — в CEIDG приостановление не имеет верхней границы времени. Дату из будущего тоже можно вписать: приостановление заявляют заранее.',
    note: 'Причина',
    noteHint: 'Например «уход за ребёнком», «сезонный перерыв». При проверке это объясняет, почему за эти месяцы нет взносов.',
    days: 'Дни',
    open: 'всё ещё приостановлена',
    tooShort: 'короче 30 дней',
    resume: 'Возобновление деятельности',
    resumePrompt: 'Последний день приостановления (день перед возобновлением деятельности):',
    remove: 'Удалить приостановление',
    removeReason: 'Удаление изменит взносы и амортизацию за те месяцы. Укажите причину (напр. ошибка в дате):',
    effects: 'Что из этого следует: за месяц, приостановленный полностью, нет ни взносов, ни аванса по налогу, а за месяц, приостановленный частично, база социальных взносов уменьшается пропорционально дням (взнос на здравоохранение остаётся целым). Амортизация останавливается с месяца, следующего за приостановлением. Дни приостановления не считаются днями ведения деятельности при Mały ZUS Plus. Файл JPK_V7 может быть нужен даже во время приостановления — при приобретении товаров из ЕС, импорте услуг и корректировках.',
  },
  inventory: {
    lead: 'Начальный и конечный остаток входят в расчёт дохода за весь год, а не за месяц.',
    noneHint:
      'Опись составляют на первый и последний день года. При отсутствии товаров и материалов её делают с нулевой стоимостью — но сделать нужно.',
    items: 'Позиции описи',
    open: 'Инвентаризация',
    title: 'Инвентаризация и годовой доход',
    year: 'Год',
    none: 'В этом году инвентаризации ещё нет.',
    add: 'Новая инвентаризация',
    save: 'Сохранить',
    approve: 'Утвердить',
    delete: 'Удалить',
    kind: 'Вид',
    takenOn: 'Дата',
    value: 'Стоимость',
    status: 'Состояние',
    itemName: 'Название',
    quantity: 'Количество',
    unit: 'Ед.',
    unitPrice: 'Цена',
    revenue: 'Доход',
    purchases: 'Закупки товаров',
    opening: 'Остаток на начало',
    closing: 'Остаток на конец',
    costOfGoodsSold: 'Стоимость проданных товаров',
    income: 'Прибыль',
    kinds: { OPENING: 'на начало', CLOSING: 'на конец', INTERIM: 'в течение года' },
    statuses: { DRAFT: 'черновик', APPROVED: 'утверждено' },
    warnings: {
      NO_OPENING_INVENTORY: 'Нет утверждённого остатка на начало — прибыль может быть завышена.',
      NO_CLOSING_INVENTORY: 'Нет утверждённого остатка на конец — товар на складе считается расходом, поэтому прибыль занижена.',
      DRAFT_INVENTORY: 'Есть инвентаризация в черновике. В расчёт берём только утверждённые.',
      NOT_KPIR: 'Эта фирма не ведёт КПиР — доход определяется иначе.',
    },
  },
  verification: {
    notice: 'Подтвердите адрес почты кодом, который мы отправили. Без этого нельзя добавить фирму.',
    code: 'Код из письма',
    confirm: 'Подтвердить',
    resend: 'Отправить код ещё раз',
    resent: 'Код отправлен.',
  },
  badDebt: {
    title: 'Льгота на безнадёжные долги',
    lead: 'Неоплаченный счёт через 90 дней после срока оплаты меняет НДС с обеих сторон. Покупатель **обязан** уменьшить входной НДС (ст. 89b п. 1 закона о НДС) и вернуть его после оплаты; продавец **может** скорректировать НДС к уплате (ст. 89a п. 1). Корректировка относится к периоду, в котором истёк девяностый день.',
    none: 'В этом периоде нечего корректировать.',
    noneHint: 'Здесь показываем счёта, которым истекло 90 дней от срока оплаты, и те, что оплачены после корректировки. Ни один не ждёт решения.',
    purchases: 'Закупки — обязательная корректировка',
    purchasesHint: 'За эти счёта фирма не заплатила в течение 90 дней от срока. Входной НДС нужно уменьшить — пропуск завышает вычет.',
    sales: 'Продажа — льгота на рассмотрение',
    salesHint: 'За эти счёта не заплатил клиент. Льгота — это право, а не обязанность, и у неё есть условия, которых из наших данных не видно (например, не в банкротстве ли должник) — решение за вами.',
    restorations: 'Оплаченные после корректировки — НДС возвращается',
    restorationsHint: 'Долг оплачен, поэтому скорректированный НДС возвращается в периоде оплаты (ст. 89b п. 4 и ст. 89a п. 4). При оплате части долга возвращается часть налога.',
    document: 'Документ',
    counterparty: 'Контрагент',
    dueDate: 'Срок оплаты',
    ninetiethDay: '90-й день',
    unpaid: 'Не оплачено',
    vat: 'НДС корректировки',
    pick: 'Отметить',
    apply: 'Сохранить корректировки',
    applyHint: 'Сохранённые корректировки войдут в файл JPK за соответствующий период.',
    skip: 'Не корректирую',
    skipReason: 'Почему не корректируете? Причина будет сохранена (напр. должник в реструктуризации, ст. 89b п. 1b):',
    late: 'корректировка относится к',
    recorded: 'Сохранено в этом периоде',
    kind: 'Вид',
    reason: 'Причина',
    undo: 'Отозвать решение',
    kinds: { DECREASE: 'уменьшение налога', INCREASE: 'возврат после оплаты', SKIPPED: 'без корректировки' },
    taxes: 'Налог',
    taxNames: { VAT: 'НДС', INCOME: 'Налог на доход' },
    income: {
      lead: 'Тот же неоплаченный счёт меняет и налог на доход. Должник **обязан** добавить неоплаченный расход к базе (ст. 18f п. 1 подп. 2 закона о CIT, ст. 26i п. 1 подп. 2 закона о PIT), кредитор **может** вычесть из неё неоплаченный доход (п. 1 подп. 1). Суммы — нетто, а решение не идёт ни в один файл: оно входит в аванс и в годовой расчёт.',
      purchasesHint: 'За эти счёта фирма не заплатила в течение 90 дней от срока. Неоплаченный расход нужно добавить к доходу — пропуск занижает аванс.',
      salesHint: 'За эти счёта не заплатил клиент. Неоплаченный доход можно вынуть из дохода; это право, а не обязанность, и у него есть условия, которых из наших данных не видно (например, не в банкротстве ли должник).',
      restorationsHint: 'Долг оплачен, поэтому корректировка возвращается в периоде оплаты: должник возвращает расход, кредитор показывает доход. При оплате части долга возвращается часть суммы.',
      amount: 'Сумма нетто',
      applyHint: 'Сохранённые корректировки изменят аванс по налогу на доход и годовой расчёт. В файл JPK они не входят.',
      skipReason: 'Почему не корректируете? Причина будет сохранена (напр. должник в банкротстве или задолженность уже списана):',
    },
  },
  ledger: {
    ordinal: '№',
    entries: 'Записи в книге',
    columnsNote: 'колонки согласно положению о KPiR',
    revenueColumn: 'колонка 7 — продажа товаров и услуг',
    open: 'Книга',
    title: 'Книга и реестры НДС',
    month: 'Месяц',
    date: 'Дата',
    document: 'Документ',
    description: 'Описание',
    counterparty: 'Контрагент',
    column: 'Кол.',
    amount: 'Сумма',
    none: 'Записей за этот месяц нет.',
    revenue: 'Доход',
    costsTotal: 'Расходы',
    result: 'Прибыль',
    vatTitle: 'НДС за период',
    vatDue: 'Начисленный',
    vatDeductible: 'К вычету',
    vatToPay: 'К уплате',
    vatToCarry: 'Излишек к переносу',
    quarterly: 'квартальный расчёт',
  },
  office: {
    served: 'Обслуживается',
    awaitingConsent: 'Ждёт согласия компании',
    tab: 'Бюро',
    companiesTab: 'Мои компании',
    title: 'Панель бюро',
    none: 'Вы пока не работаете ни в одном бухгалтерском бюро.',
    subtitle: 'Рабочее место бухгалтерского бюро: компании клиентов, шаги месяца, распределение бухгалтеров.',
    noneHint:
      'Создайте бюро, если ведёте учёт чужих компаний. Если вас должны добавить в бюро, попросите приглашение — оно придёт на вашу электронную почту.',
    create: 'Создать бюро',
    name: 'Название бюро',
    nip: 'NIP бюро',
    month: 'Месяц',
    onlyMine: 'Только мои клиенты',
    client: 'Клиент',
    assigned: 'Ведёт',
    assignments: 'Назначения бухгалтеров',
    assignmentsHint: 'Договор говорит, что бюро обслуживает фирму. Только назначение говорит, кто именно '
      + 'из бюро входит в её книги — и на одного клиента их может быть несколько.',
    assignmentRoles: { LEAD: 'ведёт', BACKUP: 'замена', VIEWER: 'просмотр' },
    assignmentRole: 'Роль у клиента',
    assignPerson: 'Выберите человека',
    assign: 'Назначить',
    endAssignment: 'Завершить назначение',
    nobodyAssigned: 'Пока никто не назначен',
    noServedClients: 'Ни одна фирма ещё не приняла предложение обслуживания.',
    invoices: 'Счета',
    costs: 'Расходы',
    revenue: 'Доход',
    costsAmount: 'Расходы',
    state: 'Состояние',
    ready: 'готова к расчёту',
    toDo: 'к выполнению',
    noClients: 'У бюро пока нет клиентов.',
    members: 'Команда',
    clients: 'Клиенты',
    role: 'Роль',
    roles: {
      OFFICE_OWNER: 'Владелец бюро',
      OFFICE_ADMIN: 'Администратор',
      ACCOUNTANT: 'Бухгалтер',
      ASSISTANT: 'Ассистент',
    },
  },
  invoices: {
    openInvoice: 'Открыть',
    backToList: 'Вернуться к списку',
    detail: {
      preview: 'Просмотр счёта',
      previewOpen: 'Открыть PDF',
      pdf: 'Скачать PDF',
      draftNoDocument: 'У черновика ещё нет номера — документ появится после выставления.',
      payments: 'Оплаты',
      due: 'К оплате',
      paid: 'Оплачено',
      remaining: 'Осталось',
      addPayment: 'Записать оплату',
      paidOn: 'Дата оплаты',
      amount: 'Сумма',
      exchangeRate: 'Курс на день оплаты',
      exchangeRateHint: 'Документ в иностранной валюте: укажите средний курс NBP последнего рабочего дня '
        + 'перед оплатой. Из разницы к курсу счёта возникает финансовый доход или расход.',
      method: 'Способ',
      note: 'Примечание',
      noPayments: 'Оплат ещё нет.',
      partialHint: 'Сумма меньше остатка — это частичная оплата: зачтём то, что поступило, '
        + 'а остаток останется в расчётах.',
      deletePayment: 'Удалить',
      confirmDeletePayment: (kwota: string) => `Удалить оплату ${kwota}? Она вернётся в расчёты как неоплаченная.`,
      internalNote: 'Внутреннее примечание',
      internalNoteHint: 'Примечание остаётся в компании: его нет ни на распечатке счёта, ни в файле KSeF. '
        + 'Поэтому его можно дописать и к уже выставленному счёту.',
      saveNote: 'Сохранить примечание',
      noteSaved: 'Примечание сохранено.',
    },
    grossAmount: 'Брутто',
    netAmount: 'Нетто',
    open: 'Счета-фактуры',
    title: 'Счета-фактуры продажи',
    tabAll: 'Все',
    tabUnpaid: 'Неоплаченные',
    tabCorrections: 'Корректировки',
    filter: 'Фильтр: номер, контрагент, сумма',
    allYears: 'Все годы',
    payment: 'Оплата',
    soon: 'в планах — пока нет в коде',
    ksefFutureTitle: 'Отправка в KSeF',
    ksefFutureText:
      'Сегодня работает только «Файл для KSeF»: загрузка документа в формате FA(3), который вы отправляете сами. Когда появится отправка через шлюз, в колонке «Состояние» будет референсный номер KSeF и подтверждение приёма.',
    new: 'Новый счёт',
    none: 'Счетов нет.',
    number: 'Номер',
    status: 'Состояние',
    issueDate: 'Дата выставления',
    saleDate: 'Дата продажи',
    dueDate: 'Срок оплаты',
    counterparty: 'Покупатель',
    viesTitle: 'Номер VAT-UE:',
    viesCheck: 'Проверить в VIES',
    viesWarning:
      'При ставке 0% или «np» для контрагента из ЕС право на неё зависит от действительного номера VAT-UE (ст. 42 п. 1 подп. 1 закона о НДС). Проверьте номер или имейте другое доказательство — счёт выставите в любом случае.',
    chooseCounterparty: 'Выберите контрагента',
    priceMode: 'Цены указаны',
    lines: 'Позиции',
    addLine: 'Добавить позицию',
    removeLine: 'Удалить',
    lineName: 'Название',
    quantity: 'Кол-во',
    unit: 'Ед.',
    unitPrice: 'Цена',
    netUnitPrice: 'Цена нетто',
    grossUnitPrice: 'Цена брутто',
    vatAmount: 'Сумма НДС',
    vatTotal: 'НДС итого',
    vatRate: 'НДС',
    ryczaltRate: 'Единая ставка',
    correct: 'Корректировка',
    ksefFile: 'Файл для KSeF',
    correction: 'корректировка',
    correctionTo: 'Корректирующий счёт к',
    correctionHint: 'Укажите позиции такими, какими они должны быть после корректировки. Разницу посчитаем сами.',
    correctionCause: 'Причина',
    correctionReason: 'Описание причины',
    linesAfter: 'Позиции после корректировки',
    causes: {
      MISTAKE: 'Ошибка в первоначальном счёте',
      EVENT: 'Событие после выставления (скидка, возврат)',
    },
    amount: 'Сумма',
    fromCatalog: 'Из картотеки',
    addToCatalog: 'Добавить в каталог',
    editDraft: 'Редактировать черновик',
    draftUnavailable: 'Это уже не черновик. Обновите список счетов.',
    noMatches: 'Ничего не найдено',
    searchFailed: 'Не удалось загрузить результаты. Попробуйте ещё раз.',
    refineSearch: 'Есть ещё результаты — уточните поиск.',
    noCatalog: 'Без позиции каталога',
    saveDraft: 'Сохранить черновик',
    issue: 'Утвердить и присвоить номер',
    delete: 'Удалить черновик',
    totals: 'Итого',
    net: 'нетто',
    vat: 'НДС',
    gross: 'брутто',
    draftHint: 'Черновик можно менять свободно. После утверждения счёт получает номер и уже не изменяется — исправление вносится корректировкой.',
    statuses: { DRAFT: 'Черновик', ISSUED: 'Выставлен', CANCELLED: 'Аннулирован' },
    priceModes: { NET: 'нетто', GROSS: 'брутто' },
  },
  bank: {
    checksumOk: 'Контрольная сумма верна',
    whiteListTitle: 'Белый список плательщиков НДС',
    whiteListText:
      'Со временем при сохранении будем проверять, есть ли счёт в перечне Министерства финансов — для платежей свыше 15 000 zł это имеет налоговые последствия. Сегодня проверяем только контрольную сумму номера.',
    open: 'Счета',
    title: 'Банковские счета компании',
    iban: 'Номер счёта',
    currency: 'Валюта',
    purpose: 'Назначение',
    bankName: 'Банк',
    add: 'Добавить счёт',
    save: 'Сохранить счёт',
    none: 'Счетов нет.',
    remove: 'Убрать из списка',
    hint: 'Номер основного счёта попадает в счета-фактуры. Контрольную сумму проверяем сразу.',
    purposes: { MAIN: 'Основной', VAT: 'Счёт НДС', TAX_REFUND: 'Для возврата налога' },
  },
  numbering: {
    lead: 'Нумерацию можно менять, пока в этом году не вышел ни один документ этого типа.',
    markers: 'Маркеры шаблона',
    issued: 'Выставлено',
    locked: 'Заблокирован — документы уже вышли',
    changeable: 'Можно менять',
    open: 'Нумерация',
    title: 'Нумерация документов',
    document: 'Документ',
    pattern: 'Шаблон',
    next: 'Следующий номер',
    save: 'Сохранить шаблон',
    reset: 'Способ',
    resetYear: 'Годовая',
    resetMonth: 'Помесячная',
    hint: 'Вместо {nr} подставляется номер, {rok} — год, {rr} — две цифры года. Месяц: {mm} → 09, {m} → 9, {mr} → IX, {msc} → WRZ (польские сокращения, неизменные). При помесячной нумерации счётчик каждый месяц возвращается к единице, поэтому номер должен содержать месяц.',
    types: { INVOICE: 'Счёт-фактура', CORRECTION: 'Корректировка', PROFORMA: 'Проформа', RECEIPT: 'Счёт' },
  },
  journal: {
    open: 'Журнал',
    title: 'Журнал',
    lead: 'Хронологический перечень проводок. Каждое событие стоит по двум сторонам: та же сумма '
      + 'по дебету одного счёта и по кредиту другого.',
    view: 'Вид',
    year: 'Отчётный год',
    month: 'Месяц',
    trialBalance: 'Обороты и сальдо',
    pending: 'К проводке',
    pendingHint: 'Документ не проводится сам. Проводка в журнале неизменна — исправление это сторно — '
      + 'поэтому всю запись видно здесь, прежде чем она попадёт в книги.',
    nothingPending: 'Всё проведено.',
    postAll: 'Провести все',
    postPicked: (ile: number) => `Провести отмеченные (${ile})`,
    trialBalanceHint: 'Входящее сальдо показано отдельно от оборотов года — это перенесённое '
      + 'состояние, а не событие, случившееся в этом году.',
    newEntry: 'Новая проводка',
    formHint: 'Номер счёта берётся из плана счетов. Проводят на счёте низшего уровня, '
      + 'а расчётный счёт требует контрагента.',
    entryNo: '№',
    postingDate: 'Дата проводки',
    description: 'Описание',
    lines: 'Строки',
    account: 'Счёт',
    name: 'Название',
    side: 'Сторона',
    amount: 'Сумма',
    debit: 'Дт',
    credit: 'Кт',
    addLine: 'Добавить строку',
    removeLine: 'Удалить',
    balanced: 'Сходится',
    difference: 'Разница',
    save: 'Сохранить проводку',
    cancel: 'Отмена',
    empty: 'В этом периоде ещё нет ни одной проводки.',
    opening: 'входящее сальдо',
    reversal: 'сторно',
    reverse: 'Сторно',
    reverseTitle: 'Сторно вместо исправления',
    reverseHint: 'Проводку не меняют. Сторно переворачивает стороны новой проводкой, а оригинал '
      + 'остаётся — видно не только конечное состояние, но и то, как оно возникло.',
    reverseReason: 'Основание сторно',
    openingDebit: 'Вх. Дт',
    openingCredit: 'Вх. Кт',
    turnoverDebit: 'Оборот Дт',
    turnoverCredit: 'Оборот Кт',
    closingDebit: 'Сальдо Дт',
    closingCredit: 'Сальдо Кт',
    totals: 'Итого',
    controlOk: 'Контроль сходится',
    controlFailed: 'Контроль не сходится',
    offBalanceNote: 'Забалансовые счета есть в строках, но вне контрольных сумм: такая проводка '
      + 'односторонняя и никогда не сойдётся.',
    entryCount: (count: number) => `Проводок: ${count}`,
    fx: {
      tab: 'Переоценка валют',
      title: 'Переоценка валют на дату',
      lead: 'На дату баланса денежные средства и расчёты в иностранных валютах оценивают по курсу этого дня (ст. 30 п. 1 закона о бухгалтерском учёте) — иначе баланс показывает стоимость, которой уже нет. Курсы не подсказываем: введите средний курс NBP, действующий на дату оценки.',
      asOf: 'Дата оценки',
      rate: 'Курс',
      item: 'Позиция',
      account: 'Счёт',
      balanceCurrency: 'Сальдо в валюте',
      balance: 'В учёте',
      revalued: 'После оценки',
      difference: 'Разница',
      gain: 'Финансовые доходы',
      loss: 'Финансовые расходы',
      preview: 'Пересчитать',
      post: 'Провести оценку',
      posted: 'Оценка проведена — запись №',
      nothingToPost: 'Курсы не изменились, поэтому проводить нечего.',
      exists: 'Оценка на эту дату уже в учёте — запись №',
      none: 'Нечего оценивать.',
      noneHint: 'Оценка охватывает валютные счета и открытые расчёты в иностранной валюте. На эту дату у фирмы нет ни одного из них.',
      taxNote:
        'Разницы от оценки являются нереализованными. Если фирма определяет курсовые разницы налоговым методом (ст. 9b п. 1 подп. 1 закона о CIT), они не являются ни доходом, ни расходом для налога — а аванс считаем из учёта, поэтому сегодня их не исключаем.',
      kinds: { ACCOUNT: 'валютный счёт', SETTLEMENT: 'расчёт' },
    },
    settlements: 'Расчёты',
    settlementsLead: 'Кто сколько должен и с какого времени. Позиция возникает при проведении документа, платёж из выписки её погашает, а сальдо считается из проводок — отдельно ничего не хранится.',
    asOf: 'На дату',
    onlyOpen: 'Только непогашенные',
    receivables: 'Дебиторская задолженность',
    payables: 'Кредиторская задолженность',
    bucket: {
      NOT_DUE: 'в срок',
      DAYS_1_30: '1–30 дней',
      DAYS_31_60: '31–60 дней',
      DAYS_61_90: '61–90 дней',
      OVER_90: 'более 90 дней',
    },
    total: 'Итого',
    document: 'Документ',
    documentDate: 'Дата',
    dueDate: 'Срок',
    settled: 'Погашено',
    remaining: 'Остаток',
    overdue: (days: number) => `${days} дней просрочки`,
    noItems: 'Непогашенных расчётов нет.',
    counterparty: 'Контрагент',
    counterpartyNone: '— без контрагента —',
    settlement: 'Расчёт',
    settlementNew: '— новая позиция —',
    offset: 'Взаимозачёт',
    offsetHint: 'Дебиторская и кредиторская задолженность одного контрагента погашаются одной проводкой — до меньшей из них или меньше.',
    offsetReceivable: 'Дебиторская',
    offsetPayable: 'Кредиторская',
    offsetAmount: 'Сумма',
    offsetDate: 'Дата',
    offsetSave: 'Сохранить взаимозачёт',
    receivableLabel: 'дебиторская',
    payableLabel: 'кредиторская',
    openingTitle: 'Входящий баланс из предыдущего года',
    openingLead: 'Сальдо балансовых счетов на последний день предыдущего года, расчёты позициями с документом и сроком, результат предыдущего года на счёте результата прошлых лет — одной технической проводкой.',
    openingPreview: 'Показать, что перейдёт',
    openingRecord: 'Провести входящий баланс',
    openingReplace: 'Заменить: сторно и новая проводка',
    openingExists: 'У этого года уже есть входящий баланс. Замена сторнирует его и пересчитает по состоянию предыдущего года на сегодня.',
    openingResult: 'Результат предыдущего года',
    openingCounterparty: 'Контрагент',
    openingDocument: 'Документ',
    openingDone: 'Входящий баланс проведён.',
  },
  reports: {
    open: 'Отчётность',
    title: 'Баланс и отчёт о прибылях и убытках',
    lead: 'Обе таблицы считаются из тех же сальдо, что и оборотно-сальдовая ведомость, поэтому отчёт '
      + 'не может сказать ничего иного, чем книги.',
    year: 'Отчётный год',
    month: 'Состояние на конец',
    balanceSheet: 'Баланс',
    incomeStatement: 'Отчёт о прибылях и убытках',
    assets: 'Активы',
    liabilities: 'Пассивы',
    revenues: 'Доходы',
    costs: 'Расходы',
    account: 'Счёт',
    name: 'Название',
    amount: 'Сумма',
    total: 'Итого',
    result: 'Результат года',
    profit: 'Прибыль',
    loss: 'Убыток',
    resultNote: 'Результат стоит в пассивах — именно он замыкает уравнение баланса.',
    balanced: 'Баланс сходится',
    notBalanced: 'Баланс не сходится',
    notBalancedHint: 'Активы не равны пассивам вместе с результатом. Начните с оборотно-сальдовой '
      + 'ведомости — там видно, какой счёт выбивается.',
    empty: 'В этом году ещё нет ни одной записи, поэтому отчёт пуст.',
    goToJournal: 'Открыть журнал',
    statutoryNote: 'Это раскладка по счетам, а не по приложению к закону о бухгалтерском учёте. Суммы '
      + 'окончательные; для официальной версии не хватает лишь привязки счетов к позициям вроде '
      + '«A. Внеоборотные активы» или «B.II. Краткосрочная дебиторская задолженность».',
    settlementsNote: 'В формате по закону расчёты развёрнуты — сальдо по контрагенту, дебиторская отдельно от кредиторской (без взаимозачёта). Формат по счетам показывает одно сальдо на счёт.',
    previousYear: 'Предыдущий год',
    currentYear: 'Текущий год',
    previousNoChart: 'У предыдущего года нет плана счетов, поэтому сравнительная колонка пуста.',
    previousDifferentTemplate: 'Предыдущий год был в другом приложении — позиции не соответствуют друг другу, поэтому сравнительная колонка в формате по закону пуста.',
    layout: 'Формат',
    layoutStatutory: 'По закону',
    layoutAccounts: 'По счетам',
    templates: { ZAL_1: 'Приложение 1 — полная форма', ZAL_4: 'Приложение 4 — микропредприятие', ZAL_5: 'Приложение 5 — малое предприятие' },
    position: 'Статья',
    unmappedTitle: (ile: number) => ile === 1 ? 'Один счёт с сальдо не имеет статьи отчёта' : `${ile} счетов с сальдо не имеют статьи отчёта`,
    unmappedHint: 'Их сумм нет в таблице, поэтому итоги ниже неполные. Назначьте статьи в плане счетов — отдельно для дебетового и кредитового сальдо.',
    unmappedGoToChart: 'Назначить в плане счетов',
    computedNote: 'Жирные статьи вычисляются из других («Прибыль (убыток) от продаж (A − B)»); статьи «в том числе» — часть суммы родительской статьи, а не её слагаемое.',
    noChart: 'У этого года нет плана счетов, поэтому нечего сворачивать в формат по закону.',
    accountsNote: 'Это формат по счетам — опорный, в котором ничто не может потеряться. Формат по закону рядом.',
  },
  chart: {
    open: 'План счетов',
    positionDebit: 'Статья отчёта — дебетовое сальдо',
    positionCredit: 'Статья отчёта — кредитовое сальдо',
    positionNone: '— не назначено',
    positionHint: 'Активно-пассивный счёт попадает в разные статьи в зависимости от стороны сальдо. Счёт без статьи отчёт покажет как неназначенный.',
    sections: { ASSETS: 'Активы', LIABILITIES: 'Пассивы', INCOME: 'Отчёт о прибылях и убытках' },
    title: 'Рабочий план счетов',
    lead: 'План принадлежит отчётному году. Проводят на счетах самого низкого уровня — счёт '
      + 'с аналитикой является суммой своих аналитических счетов.',
    year: 'Отчётный год',
    empty: 'У этого года ещё нет плана счетов',
    emptyHint: 'Начните с шаблона или скопируйте план за предыдущий год и исправьте, что нужно.',
    create: 'Создать план счетов',
    source: 'Откуда',
    sourceTemplate: 'Из шаблона',
    sourcePrevious: 'Копия предыдущего года',
    sourceEmpty: 'Пустой',
    previewHint: (count: number) => `Так будет выглядеть план — счетов: ${count}. Аналитику добавите после создания.`,
    previewEmpty: 'Пустой план создают тогда, когда ни один образец не подходит. Счета добавите сами.',
    template: 'Шаблон',
    templateName: 'Название шаблона',
    saveAsTemplate: 'Сохранить как шаблон',
    number: 'Счёт',
    numberHint: 'Номер — это трёхзначный синтетический счёт и аналитика после дефиса, например 402-1. '
      + 'Синтетический счёт должен существовать, прежде чем открыть к нему аналитику.',
    name: 'Название',
    kind: 'Вид',
    side: 'Сальдо',
    features: 'Признаки',
    tax: 'Налоговая квалификация',
    role: 'Роль при проведении',
    sum: 'Сумма аналитики',
    settlement: 'Расчётный',
    currency: 'Валютный',
    inactive: 'не используется',
    activeAccount: 'Подсказывать при проведении',
    add: 'Добавить счёт',
    edit: 'Изменить',
    delete: 'Удалить',
    save: 'Сохранить',
    cancel: 'Отменить',
    noYear: 'Отчётного года ещё нет, а план счетов принадлежит году.',
    goToPeriods: 'Перейти к периодам',
    notFullBooks: 'Эта фирма не ведёт полную бухгалтерию',
    booksUnknown: 'Мы ещё не знаем, какой учёт ведёт эта фирма',
    booksUnknownHint: 'Нет налоговых настроек, действующих сегодня. План счетов ведут при полной '
      + 'бухгалтерии — заполните настройки, и мы покажем остальное.',
    notFullBooksHint: 'План счетов — инструмент полной бухгалтерии. Вид учёта меняется в настройках фирмы.',
    onlyAccountant: 'План счетов ведёт бухгалтер',
    onlyAccountantHint: 'При полной бухгалтерии проводит только человек с ролью «Бухгалтер». '
      + 'Здесь вы видите план, но не меняете его.',
    accountCount: (count: number) => `Счетов в плане: ${count}`,
    kinds: { BALANCE: 'Балансовый', RESULT: 'Результатный', OFFBALANCE: 'Забалансовый' },
    sides: { DEBIT: 'Активный', CREDIT: 'Пассивный', BOTH: 'Активно-пассивный', NONE: '—' },
    taxes: {
      DEDUCTIBLE: 'Расходы, уменьшающие доход',
      NON_DEDUCTIBLE: 'Не уменьшает доход',
      TAXABLE: 'Налогооблагаемый доход',
      NON_TAXABLE: 'Необлагаемый доход',
    },
    statements: { ZAL_1: 'Приложение 1', ZAL_4: 'Приложение 4 — микропредприятие', ZAL_5: 'Приложение 5 — малое предприятие' },
    variants: { BY_KIND: 'По элементам расходов', BY_FUNCTION: 'По функциям расходов', BOTH: 'Оба варианта' },
    scopes: { SYSTEM: 'Firmeto', OFFICE: 'Бюро', COMPANY: 'Эта фирма' },
    groups: {
      0: 'Внеоборотные активы',
      1: 'Денежные средства и банковские счета',
      2: 'Расчёты и претензии',
      3: 'Материалы и товары',
      4: 'Расходы по элементам',
      5: 'Расходы по видам деятельности',
      6: 'Готовая продукция и расходы будущих периодов',
      7: 'Доходы и расходы на их получение',
      8: 'Капитал, резервы и финансовый результат',
      9: 'Забалансовые счета',
    },
    roles: {
      RECEIVABLES: 'Расчёты с покупателями',
      PAYABLES: 'Расчёты с поставщиками',
      VAT_DUE: 'Начисленный НДС',
      VAT_INPUT: 'Входящий НДС',
      VAT_SETTLEMENT: 'Расчёты по НДС',
      SALES_SERVICES: 'Продажа услуг',
      SALES_GOODS: 'Продажа товаров',
      COST_OF_GOODS: 'Себестоимость проданных товаров',
      GOODS: 'Товары',
      COSTS_MATERIALS: 'Материалы и энергия',
      COSTS_SERVICES: 'Услуги сторонних организаций',
      COSTS_TAXES: 'Налоги и сборы',
      COSTS_SALARIES: 'Расходы на оплату труда',
      COSTS_SOCIAL: 'Социальное страхование и прочие выплаты',
      COSTS_OTHER: 'Прочие расходы по видам',
      FIXED_ASSETS: 'Основные средства',
      INTANGIBLE_ASSETS: 'Нематериальные активы',
      ACCUMULATED_DEPRECIATION: 'Амортизация основных средств',
      ACCUMULATED_AMORTISATION: 'Амортизация нематериальных активов',
      DEPRECIATION_COST: 'Амортизация (расходы)',
      CASH: 'Касса',
      BANK: 'Расчётный счёт',
      INCOME_TAX: 'Подоходный налог',
      SOCIAL_SECURITY: 'Расчёты с ZUS',
      PAYROLL: 'Заработная плата',
      RESULT: 'Финансовый результат',
      PRIOR_YEARS_RESULT: 'Результат прошлых лет',
    },
  },
  vatUe: {
    title: 'Информация VAT-UE',
    lead: 'Кто продаёт товары или услуги плательщикам из ЕС, подаёт итоговую информацию — **ежемесячно, даже при квартальном расчёте НДС** (ст. 100 закона о НДС). Без внутрисоюзных операций информацию не подают.',
    none: 'В этом месяце нет внутрисоюзных операций.',
    noneHint: 'Сюда входят счёта для контрагентов из ЕС со ставкой «np» (услуги) или 0% (поставки товаров) и расходные документы, отмеченные как внутрисоюзное приобретение.',
    supplies: 'Поставки товаров',
    acquisitions: 'Приобретения товаров',
    services: 'Оказание услуг',
    counterparty: 'Контрагент',
    country: 'Страна',
    vatNumber: 'Номер НДС',
    amount: 'Стоимость',
    download: 'Скачать VAT-UE',
    vies: 'VIES',
    viesValid: 'действителен',
    viesInvalid: 'недействителен',
    viesUnknown: 'не проверено',
    checkVies: 'Проверить номера в VIES',
    viesUnavailable: 'Реестр не ответил для номеров:',
    problems: 'Позиции, которые не удаётся показать',
    notes: {
      TRIANGULAR_NOT_DETECTED: 'Трёхсторонние операции не распознаём — в каждой позиции стоит «не относится».',
      VIES_ON_DEMAND: 'Номера проверяете в VIES кнопкой рядом — результат сохраняется с датой, потому что именно он является доказательством должной осмотрительности.',
      CALL_OFF_NOT_SUPPORTED: 'Перемещения в процедуре склада call-off не показываем.',
      MONTHLY_ALWAYS: 'Информацию подают ежемесячно, независимо от того, рассчитываете ли НДС помесячно или поквартально.',
    },
  },
  officeJpk: {
    title: 'JPK клиентов за месяц',
    lead: 'Подготовьте файлы всех клиентов сразу, скачайте пакет файлов метаданных, подпишите их подписью бюро (доверенность UPL-1) и загрузите подписанные файлы — Firmeto сопоставит их по имени и отправит.',
    prepare: 'Подготовить для клиентов',
    download: 'Скачать пакет для подписи',
    upload: 'Загрузить подписанные файлы',
    problems: 'Не подготовлено',
    sent: (count: number) => `Отправлено: ${count}`,
  },
  jpkSend: {
    title: 'Отправка в Министерство финансов',
    personLead: 'Как физическое лицо вы подписываете файл суммой дохода из налоговой декларации двухлетней давности (напр. в 2026 — из декларации за 2024). Имя, фамилию и дату рождения берём из данных для деклараций. Сумму мы не сохраняем.',
    companyLead: 'Компания подписывает файл метаданных квалифицированной или доверенной подписью. Подготовьте отправку, скачайте файл, подпишите его (напр. на podpis.gov.pl, в формате XAdES) и загрузите подписанный файл сюда.',
    income: 'Доход из декларации двухлетней давности (зл)',
    send: 'Отправить в налоговую',
    prepare: 'Подготовить к подписи',
    downloadToSign: 'Скачать файл для подписи',
    uploadSigned: 'Загрузить подписанный файл',
    history: 'Отправки',
    none: 'Этот файл ещё не отправлялся.',
    reference: 'Референсный номер',
    upo: 'Скачать UPO',
    test: 'тестовая среда',
    statuses: { AWAITING_SIGNATURE: 'ждёт подписи', SENDING: 'отправка', PROCESSING: 'налоговая обрабатывает', ACCEPTED: 'принято, есть UPO', REJECTED: 'отклонено', FAILED: 'не отправлено' } as Record<string, string>,
  },
  jpk: {
    goToFiling: 'Перейти к данным для декларации',
    title: 'Файлы JPK',
    lead: 'JPK_V7M за выбранный месяц — учёт НДС вместе с декларацией. Файл можно скачать или отправить в налоговую отсюда.',
    leadQuarterly:
      'JPK_V7K за выбранный месяц. При квартальном расчёте **учёт подают ежемесячно, а декларацию раз в квартал**: файл за первый и второй месяц квартала несёт только учёт, а за третий — учёт этого месяца и декларацию за весь квартал. Файл подписываете и отправляете своим инструментом.',
    period: 'Месяц',
    download: 'Скачать JPK_V7M',
    downloadQuarterly: 'Скачать JPK_V7K',
    beforeDownload: 'Перед скачиванием проверьте данные для декларации — без кода налоговой файл не появится.',
  },
  filing: {
    title: 'Данные для декларации',
    lead: 'Нужны только для файлов JPK и деклараций. Для выставления счёта или расчёта налога не используются.',
    taxOffice: 'Налоговая инспекция',
    taxOfficeHint: 'Введите город или часть названия и выберите учреждение из списка. Сохраняем четырёхзначный код, который требуют JPK и декларации.',
    taxOfficeSearch: 'Город или название учреждения',
    taxOfficeNone: 'Ни одно учреждение не подходит. Попробуйте только название города.',
    taxOfficeSpecialised: 'для крупнейших налогоплательщиков',
    taxOfficeSpecialisedHint: 'Специализированные учреждения обслуживают налогоплательщиков с доходом свыше 50 млн евро, банки и группы компаний. Если это не ваша фирма, выберите обычное учреждение для своей гмины.',
    taxOfficeClear: 'Очистить',
    firstName: 'Имя плательщика',
    lastName: 'Фамилия плательщика',
    birthDate: 'Дата рождения',
    personHint: 'Индивидуальная деятельность представляется в JPK именем, фамилией и датой рождения; общество — только названием.',
    email: 'Адрес эл. почты для деклараций',
    phone: 'Контактный телефон',
    zusInsuranceTitleCode: 'Код основания страхования (ZUS DRA)',
    zusInsuranceTitleCodeHint:
      'Шесть цифр из декларации ZUS: четыре цифры кода, право на пенсию, степень инвалидности. При полных взносах обычно 051000, при преференциальных 057000 — но код не вписываем за вас, потому что при Mały ZUS Plus и льготе на старт словарь ZUS неоднозначен.',
    save: 'Сохранить данные',
    saved: 'Сохранено.',
  },
  invitation: {
    title: 'Приглашение в компанию',
    lead: (who: string) => `${who} приглашает вас к обслуживанию фирмы в Firmeto.`,
    invitedBy: 'Приглашает',
    invitesAs: 'приглашает как',
    company: 'Фирма',
    forEmail: 'Для адреса',
    roleScope: {
      OWNER: 'Полный доступ к фирме, включая расчёты с Firmeto и управление доступами.',
      ADMIN: 'Документы, настройки и доступы. Без смены владельца фирмы.',
      ACCOUNTANT: 'Проводки и налоговые расчёты, полный просмотр документов. Без доступа к расчётам с Firmeto.',
      EMPLOYEE: 'Выставление счетов и внесение расходов. Без просмотра налогов и расчётов.',
      BOARD_MEMBER: 'Просмотр документов и результатов общества. Без внесения изменений.',
      VIEWER: 'Только просмотр — без внесения и изменения чего-либо.',
    },
    accept: 'Принять приглашение',
    signInToAccept: 'Войдите, чтобы принять',
    expires: (date: string) => `Приглашение действует до ${date}. Каждое принятие записываем в журнал изменений фирмы.`,
    accepted: 'Приглашение принято. Компания уже в вашем списке.',
    back: 'К компаниям',
  },
};

const dictionaries: Record<Locale, Dictionary> = { pl, en, uk, ru };

export function dictionary(locale: Locale): Dictionary {
  return dictionaries[locale];
}

export function isLocale(value: string | null | undefined): value is Locale {
  return (locales as readonly string[]).includes(value ?? '');
}

/** Język z ustawień przeglądarki; polski, gdy nie znamy żadnego z preferowanych. */
export function detectLocale(): Locale {
  const saved = window.localStorage.getItem('firmeto-locale');
  if (isLocale(saved)) return saved;
  for (const preferred of navigator.languages ?? []) {
    const code = preferred.slice(0, 2).toLowerCase();
    if (isLocale(code)) return code;
  }
  return defaultLocale;
}

export function rememberLocale(locale: Locale): void {
  window.localStorage.setItem('firmeto-locale', locale);
}
